import * as i0 from '@angular/core';
import { InjectionToken, Directive, Inject, Input, HostListener, NgModule, Self, Optional, Pipe, Component, ViewChild, HostBinding, Injectable, EventEmitter, Output, Host, ViewChildren, ChangeDetectionStrategy, ElementRef, ContentChild, LOCALE_ID } from '@angular/core';
import * as i6$2 from '@angular/cdk/drag-drop';
import { moveItemInArray, CdkDragHandle, DragDropModule } from '@angular/cdk/drag-drop';
import { Subject, fromEvent, merge, Observable, timer, of, throwError, concat, combineLatest, forkJoin } from 'rxjs';
import * as i1$3 from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import * as i1$2 from '@angular/common';
import { DOCUMENT, CommonModule, DecimalPipe, DatePipe } from '@angular/common';
import * as i3 from '@angular/forms';
import { UntypedFormGroup, UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i2 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i6$1 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i8 from '@angular/material/tooltip';
import { MatTooltip, MAT_TOOLTIP_SCROLL_STRATEGY, MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule } from '@angular/material/tooltip';
import { DomPortalHost, PortalInjector, ComponentPortal, PortalModule } from '@angular/cdk/portal';
import * as i3$2 from '@angular/material/radio';
import { MatRadioModule, MatRadioChange, MatRadioButton } from '@angular/material/radio';
import * as i4$1 from '@angular/material/form-field';
import { MatFormFieldModule } from '@angular/material/form-field';
import * as i5 from '@angular/material/input';
import { MatInputModule } from '@angular/material/input';
import * as i7 from '@angular/cdk/text-field';
import { takeUntil, filter, debounceTime, tap, switchMap, retry, map, take, retryWhen, concatMap } from 'rxjs/operators';
import * as i1 from '@angular/material/snack-bar';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import * as i3$1 from '@angular/cdk/overlay';
import * as i4 from '@angular/cdk/platform';
import * as i1$1 from '@angular/cdk/a11y';
import { A11yModule, FocusKeyManager } from '@angular/cdk/a11y';
import * as i6 from '@angular/cdk/bidi';
import * as i3$3 from '@angular/material/checkbox';
import { MatCheckboxModule, MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import * as i4$2 from '@angular/material/select';
import { MatSelectModule, MatSelect } from '@angular/material/select';
import * as i5$1 from '@angular/material/core';
import { MAT_RIPPLE_GLOBAL_OPTIONS, MatNativeDateModule } from '@angular/material/core';
import * as i8$1 from '@angular/material/tabs';
import { MatTabsModule } from '@angular/material/tabs';
import * as i1$4 from '@angular/common/http';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import * as i7$1 from '@angular/material/progress-spinner';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import * as i6$3 from '@angular/material/datepicker';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { parseISO, addYears, addMonths, addWeeks, addDays } from 'date-fns';
import { v4 } from 'uuid';
import * as i2$1 from '@angular/material/card';
import { MatCardModule } from '@angular/material/card';
import * as i5$2 from '@angular/cdk/stepper';
import { CdkStepper, CdkStepperModule } from '@angular/cdk/stepper';
import * as i2$2 from '@swimlane/ngx-charts';
import { NgxChartsModule } from '@swimlane/ngx-charts';

var SurveyItemType;
(function (SurveyItemType) {
    SurveyItemType[SurveyItemType["QUESTION"] = 0] = "QUESTION";
    SurveyItemType[SurveyItemType["QUESTION_GROUP"] = 1] = "QUESTION_GROUP";
})(SurveyItemType || (SurveyItemType = {}));
class SurveyItem {
    constructor(model = {}) {
        this.title = '';
        this.meta = {};
        for (let prop in model) {
            this[prop] = model[prop];
        }
        if (!this.recommendations) {
            this.recommendations = [];
        }
    }
    get hasRecommendations() {
        return this.recommendations.length > 0;
    }
}

/**
 * Please note these must be in the same order as the PHP equivalent found
 * @see wp\plugin\lib\models\survey-item.php
 */
var QuestionType;
(function (QuestionType) {
    QuestionType[QuestionType["YES_NO_UNCERTAIN"] = 0] = "YES_NO_UNCERTAIN";
    QuestionType[QuestionType["SCALE"] = 1] = "SCALE";
    QuestionType[QuestionType["TEXT"] = 2] = "TEXT";
    QuestionType[QuestionType["DATE"] = 3] = "DATE";
    QuestionType[QuestionType["PICKLIST"] = 4] = "PICKLIST";
    QuestionType[QuestionType["NUMBER"] = 5] = "NUMBER";
    QuestionType[QuestionType["BOOLEAN"] = 6] = "BOOLEAN";
    QuestionType[QuestionType["CHECKLIST"] = 7] = "CHECKLIST";
    QuestionType[QuestionType["RADIOSET"] = 8] = "RADIOSET";
    QuestionType[QuestionType["TEXTAREA"] = 9] = "TEXTAREA";
})(QuestionType || (QuestionType = {}));
/**
 * Types of questions that can have conditional questions
 */
const ConditionalQuestionTypes = [
    QuestionType.YES_NO_UNCERTAIN,
    QuestionType.PICKLIST,
    QuestionType.BOOLEAN,
    QuestionType.CHECKLIST,
    QuestionType.RADIOSET
];
class QuestionChangeEvent extends Event {
    constructor(data) {
        super('change', { bubbles: true });
        this.isFirstChange = data.firstChange;
        this.question = data.question;
    }
}
const requiredErrorMessage = $localize `:alpha.surveys|Survey Required Field Error@@surveyRequiredFieldError:You forgot to answer this question.`;
/**
 * Helper function to sum the properties of conditionals matching a value
 * For some reason declaring this function as a constant causes a conflicting namespaces warning when running 'ng build components'
 * Declaring it in ES2015 syntax gets around the warning
 */
function sumConditionals(items, checkValue, checkProperty) {
    const matchedConditionals = items.filter(q => {
        let conditionMatched = false;
        if (checkValue instanceof Array) {
            conditionMatched = checkValue.includes(q.meta.conditionalOn);
        }
        else {
            conditionMatched = checkValue === q.meta.conditionalOn;
        }
        if (!conditionMatched) {
            return false;
        }
        return q[checkProperty] !== null;
    }).map(q => q[checkProperty]);
    return matchedConditionals.length ? matchedConditionals.reduce((a, b) => a + b, 0) : null;
}
;
/**
 * Question
 */
class Question extends SurveyItem {
    set error(val) {
        this._error = val;
        this.events.next({});
    }
    get error() {
        return this._error;
    }
    constructor(data = {}) {
        super(data);
        this.type = SurveyItemType.QUESTION;
        this.events = new Subject();
        if (this.conditionalQuestions) {
            this.conditionalQuestions = new QuestionGroup(this.conditionalQuestions);
        }
    }
    addConditionalQuestion(data = {}) {
        if (!this.conditionalQuestions) {
            this.conditionalQuestions = new QuestionGroup({
                conditional: true
            });
        }
        const item = new Question(Object.assign(data, {
            type: SurveyItemType.QUESTION,
            conditional: true
        }));
        this.conditionalQuestions.addQuestion(item);
    }
    validate() {
        let valid = true;
        if (!this.complete) {
            this.error = requiredErrorMessage;
            valid = false;
        }
        else {
            this.error = '';
        }
        if (this.conditionalQuestions) {
            for (const question of this.conditionalQuestions.items) {
                if (this.isConditionSatisfied(question)) {
                    if (!question.validate()) {
                        valid = false;
                    }
                }
            }
        }
        return valid;
    }
    get complete() {
        return (typeof (this.response) !== 'undefined' && this.response !==
            "" && this.response !== null) || this.meta.optional || this.optional;
    }
    /**
     * Get the maximum score value for this question
     */
    get maximumScore() {
        /**
         * Listset questions
         */
        if ([QuestionType.PICKLIST, QuestionType.CHECKLIST, QuestionType.RADIOSET].indexOf(this.questionType) >= 0 && this.meta.listValues) {
            // get the maximum possible values from the listset options.			
            let max = this.meta.listValues.map(o => {
                let scoreValue = o.scoreValue ?? 0;
                // if this question is conditional, we need to get the max score from the matching conditional options
                if (this.conditionalQuestions) {
                    const maxCScore = sumConditionals(this.conditionalQuestions.items, o.id, 'maximumScore');
                    scoreValue = maxCScore !== null ? maxCScore : scoreValue;
                }
                return scoreValue;
            }).sort((a, b) => b - a).filter(v => v !== -1);
            // no valuese provided at all
            if (!max.length) {
                return null;
            }
            // if it's a checklist, the max score is the sum of all options
            if (this.questionType === QuestionType.CHECKLIST) {
                return max.reduce((a, b) => a + b, 0);
            }
            return max[0] ?? null;
        }
        /**
         * Boolean question
         */
        if (this.questionType === QuestionType.BOOLEAN) {
            let maxYes = this.meta.scoreBoolean1 ?? 0;
            let maxNo = this.meta.scoreBoolean2 ?? 0;
            if (this.conditionalQuestions) {
                const maxCYes = sumConditionals(this.conditionalQuestions.items, 1, 'maximumScore');
                const maxCNo = sumConditionals(this.conditionalQuestions.items, 2, 'maximumScore');
                maxYes = maxCYes !== null ? maxCYes : maxYes;
                maxNo = maxCNo !== null ? maxCNo : maxNo;
            }
            return Math.max(maxYes, maxNo);
        }
        /**
         * Yes/No/Uncertain Question
         */
        if (this.questionType === QuestionType.YES_NO_UNCERTAIN) {
            let maxY = this.meta.scoreYNU1 ?? 0;
            let maxN = this.meta.scoreYNU2 ?? 0;
            let maxU = this.meta.scoreYNU3 ?? 0;
            if (this.conditionalQuestions) {
                const maxCY = sumConditionals(this.conditionalQuestions.items, 1, 'maximumScore');
                const maxCN = sumConditionals(this.conditionalQuestions.items, 2, 'maximumScore');
                const maxCU = sumConditionals(this.conditionalQuestions.items, 3, 'maximumScore');
                maxY = maxCY !== null ? maxCY : maxY;
                maxN = maxCN !== null ? maxCN : maxN;
                maxU = maxCU !== null ? maxCU : maxU;
            }
            return Math.max(maxY, maxN, maxU);
        }
        /**
         * Number question
         */
        if (this.questionType === QuestionType.NUMBER && this.meta.scoreRange.length) {
            let max = this.meta.scoreRange.map(o => o.value ?? 0).sort((a, b) => b - a);
            return max[0] ?? null;
        }
        if (this.meta.scoreValue) {
            return this.meta.scoreValue;
        }
        return null;
    }
    /**
     * Get the actual score from the user's response
     * Note: This logic is replicated in the PHP version of the Question class. It exists there so that it can be generated when sending to SalesForce or showing in the back end
     */
    get score() {
        const isListSetQuestion = [QuestionType.PICKLIST, QuestionType.CHECKLIST, QuestionType.RADIOSET].indexOf(this.questionType) >= 0;
        if (this.conditionalQuestions) {
            let sum = 0;
            let csum = null;
            // checklist additional handling because response contains multiple choices
            if (this.questionType === QuestionType.CHECKLIST) {
                // filter matching list values and sum their conditionals
                this.meta.listValues.filter(v => this.response instanceof Array ? this.response.indexOf(v.id) >= 0 : this.response === v.id).forEach(listOption => {
                    const listValueSum = sumConditionals(this.conditionalQuestions.items, listOption.id, 'score');
                    if (null !== listValueSum) {
                        sum += listValueSum;
                    }
                    else {
                        sum += listOption.scoreValue ?? 0;
                    }
                });
            }
            // otherwise if we can get a non-null value from matching conditionals, add the sum
            else if (null !== (csum = sumConditionals(this.conditionalQuestions.items, this.response, 'score'))) {
                sum += csum;
            }
            // otherwise if it's a list question, find the chosen option and add it's assigned score
            else if (isListSetQuestion) {
                // none of the conditionals have values, find the matching list value and use it's score
                const matched = this.meta.listValues.filter(v => this.response instanceof Array ? this.response.indexOf(v.id) >= 0 : this.response === v.id).shift();
                sum += matched.scoreValue ?? 0;
            }
            // add all scores for conditional items that are marked as wildcard
            this.conditionalQuestions.items.filter(q => q.meta.conditionalOn === '*').forEach(conditional => {
                const cscore = conditional.score;
                if (null !== cscore) {
                    sum += cscore;
                }
            });
            // finally, return the sum of the conditional questions
            return sum;
        }
        /** Standard scoring for questions without conditionals */
        // listValue questions
        if (isListSetQuestion && this.meta.listValues) {
            // if it's a checklist, find all the list values that match the responses and sum their values
            if (this.questionType === QuestionType.CHECKLIST) {
                return this.meta.listValues.filter(v => this.response instanceof Array ? this.response.indexOf(v.id) >= 0 : this.response === v.id).map(o => o.scoreValue ?? 0).reduce((a, b) => a + b, 0);
            }
            // find the one that matches
            const match = this.meta.listValues.find(v => v.id === this.response);
            return match?.scoreValue ?? null;
        }
        // boolean question
        if (this.questionType === QuestionType.BOOLEAN && (typeof (this.meta.scoreBoolean1) !== 'undefined' || typeof (this.meta.scoreBoolean2) !== 'undefined')) {
            return this.response === 1 ? this.meta.scoreBoolean1 ?? null : this.meta.scoreBoolean2 ?? null;
        }
        // yes/no/uncertain
        if (this.questionType === QuestionType.YES_NO_UNCERTAIN) {
            if (this.response === 1) {
                return this.meta.scoreYNU1 ?? null;
            }
            if (this.response === 2) {
                return this.meta.scoreYNU2 ?? null;
            }
            if (this.response === 3) {
                return this.meta.scoreYNU3 ?? null;
            }
        }
        // number question
        if (this.questionType === QuestionType.NUMBER && this.meta.scoreRange.length) {
            const match = this.meta.scoreRange.filter(r => r.range <= this.response).sort((a, b) => b.range - a.range).shift();
            return match?.value ?? null;
        }
        return null;
    }
    // cannot set the score
    set score(value) { }
    isConditionSatisfied(question) {
        return typeof (this.response) !== 'undefined'
            && ((question?.meta?.conditionalOn === '*' || question?.meta?.conditionalOn === this.response)
                ||
                    (this.response && this.questionType === QuestionType.CHECKLIST && this.response.indexOf(question?.meta?.conditionalOn) >= 0));
    }
    toJSON() {
        // avoid cyclic reference
        const json = Object.assign({}, this);
        delete json.events;
        return json;
    }
}
const QUESTION = new InjectionToken('QUESTION');
class QuestionGroup extends SurveyItem {
    constructor(data = {}) {
        super(data);
        this.type = SurveyItemType.QUESTION_GROUP;
        this.items = (this.items || []).map((itemData) => {
            let item;
            if (typeof itemData.type === 'number' && itemData.type === SurveyItemType.QUESTION_GROUP || itemData.type === 'group') {
                item = new QuestionGroup(itemData);
            }
            else if (typeof itemData.type === 'number' && itemData.type === SurveyItemType.QUESTION || itemData.type === 'question') {
                item = new Question(itemData);
            }
            return item;
        });
    }
    addGroup(qg) {
        this.items.push(qg);
    }
    addQuestion(q) {
        this.items.push(q);
    }
    getQuestionById(id) {
        const found = this.items.find(item => item.type === SurveyItemType.QUESTION && item.id === id);
        if (found) {
            return found;
        }
        let i = 0;
        for (i; i < this.items.length; i++) {
            if (this.items[i].type === SurveyItemType.QUESTION_GROUP) {
                let subFound = this.items[i].getQuestionById(id);
                if (subFound) {
                    return subFound;
                }
            }
        }
        return null;
    }
    get fuzzyItems() {
        let items = [];
        const _get = (item) => {
            if (item.fuzzy) {
                items.push(item);
            }
            if (item.type === SurveyItemType.QUESTION_GROUP) {
                item.items.forEach(subItem => _get(subItem));
            }
        };
        this.items.forEach(item => _get(item));
        return items;
    }
    isQuestionBefore(question, compare) {
        const qs = this.getQuestions();
        const q1 = qs.indexOf(question);
        const q2 = qs.indexOf(compare);
        return q1 + 1 === q2;
    }
    isQuestionEarlier(question, compare) {
        const qs = this.getQuestions();
        const q1 = qs.indexOf(question);
        const q2 = qs.indexOf(compare);
        return q1 < q2;
    }
    isQuestionAfter(question, compare) {
        const qs = this.getQuestions(false);
        const q1 = qs.indexOf(question);
        const q2 = qs.indexOf(compare);
        return q1 - 1 === q2;
    }
    hasAnsweredQuestionAfter(question) {
        const qs = this.getQuestions(false);
        const q = qs.indexOf(question);
        for (let i = q + 1; i < qs.length; i++) {
            if (this.questions[i].response !== undefined) {
                return true;
            }
        }
        return false;
    }
    getQuestions(onlyScorable = false) {
        const questions = [];
        const _get = (item) => {
            if (item.type === SurveyItemType.QUESTION) {
                questions.push(item);
                if (item.conditionalQuestions?.items.length) {
                    item.conditionalQuestions.items.forEach(subItem => _get(subItem));
                }
            }
            else if (item.type === SurveyItemType.QUESTION_GROUP && (!onlyScorable || !item.meta.doNotScore)) {
                item.items.forEach(subItem => _get(subItem));
            }
        };
        this.items.forEach(item => _get(item));
        return questions;
    }
    get questions() {
        return this.getQuestions();
    }
    get numQuestions() {
        return this.items.filter(q => q.type === SurveyItemType.QUESTION).length;
    }
    /**
     * Check if we have recommendations
     */
    get hasRecommendations() {
        if (this.recommendations.length > 0) {
            return true;
        }
        for (let i = 0; i < this.items.length; i++) {
            if (this.items[i].hasRecommendations) {
                return true;
            }
        }
        return false;
    }
    /**
     * Get a flat list of all the groups within this group
     */
    get groups() {
        const groups = [];
        const _get = (item) => {
            if (item.type === SurveyItemType.QUESTION_GROUP) {
                groups.push(item);
                item.items.forEach(subItem => _get(subItem));
            }
        };
        this.items.forEach(item => _get(item));
        return groups;
    }
    /**
     * Get the maximum score from any group or question in this group container
     */
    get maximumScore() {
        let score = 0;
        for (let i = 0; i < this.items.length; i++) {
            score += this.items[i].maximumScore;
        }
        return score > 0 ? score : null;
    }
    /**
     * Get the actual score from the user's response
     */
    get score() {
        let score = 0;
        for (let i = 0; i < this.items.length; i++) {
            score += this.items[i].score;
        }
        return score > 0 ? score : null;
    }
    // cannot set the score
    set score(value) { }
    /**
     *
     */
    get complete() {
        const qs = this.questions;
        for (let i = 0; i < qs.length; i++) {
            if (qs[i].conditional) {
                continue;
            }
            if (!qs[i].complete) {
                return false;
            }
            //conditional questions
            if (qs[i]?.conditionalQuestions?.items?.length) {
                for (const cq of qs[i].conditionalQuestions.items) {
                    if (qs[i].isConditionSatisfied(cq)) {
                        if (!cq.complete) {
                            return false;
                        }
                    }
                }
            }
        }
        return true;
    }
    validate() {
        const qs = this.questions;
        let valid = true;
        for (let i = 0; i < qs.length; i++) {
            if (!qs[i].conditional && !qs[i].validate()) {
                valid = false;
            }
        }
        return valid;
    }
    /**
     *
     */
    get enumerate() {
        return this.meta.enumerate;
    }
}

var SurveyIntent;
(function (SurveyIntent) {
    SurveyIntent[SurveyIntent["COMPARE"] = 0] = "COMPARE";
    SurveyIntent[SurveyIntent["COLLECT"] = 1] = "COLLECT";
})(SurveyIntent || (SurveyIntent = {}));
var SurveyPresentation;
(function (SurveyPresentation) {
    SurveyPresentation[SurveyPresentation["NORMAL"] = 0] = "NORMAL";
    SurveyPresentation[SurveyPresentation["PSYCHOMETRIC"] = 1] = "PSYCHOMETRIC";
})(SurveyPresentation || (SurveyPresentation = {}));
/**
 * Whether to enumerate questions altogether or by the section they're in
 */
var NumerateType;
(function (NumerateType) {
    NumerateType[NumerateType["NONE"] = 0] = "NONE";
    NumerateType[NumerateType["AGGREGATE"] = 1] = "AGGREGATE";
    NumerateType[NumerateType["GROUP"] = 2] = "GROUP";
})(NumerateType || (NumerateType = {}));
class SurveyIntentChangeEvent {
    constructor(data) {
        this.newIntent = data.newIntent;
        this.oldIntent = data.oldIntent;
    }
}
class SurveyPresentationChangeEvent {
    constructor(data) {
        this.newPresentation = data.newPresentation;
        this.oldPresentation = data.oldPresentation;
    }
}
class Survey extends QuestionGroup {
    get intent() { return this._intent; }
    get presentation() { return this._presentation; }
    constructor(data = {}) {
        super(data);
        this.events = new Subject();
        this.enumerationType = NumerateType.AGGREGATE;
        if (!this.meta || !this.meta.insert_id) {
            this.meta = Object.assign(this.meta || {}, { insert_id: 1 });
        }
    }
    set intent(val) {
        const changeEvent = new SurveyIntentChangeEvent({ newIntent: val, oldIntent: this._intent });
        this._intent = val;
        if (this.events) {
            this.events.next(changeEvent);
        }
    }
    set presentation(val) {
        const changeEvent = new SurveyPresentationChangeEvent({ newPresentation: val, oldPresentation: this._presentation });
        this._presentation = val;
        if (this.events) {
            this.events.next(changeEvent);
        }
    }
    triggerRefresh() {
        // trigger change detection on question editors
        this.events.next({});
    }
    clone() {
        const json = JSON.parse(JSON.stringify(this));
        return new Survey(this.prepareCloneData(json));
    }
    toFormJSON() {
        const json = Object.assign({}, this);
        return this.prepareCloneData(json);
    }
    addQuestion(q) {
        q.id = this.meta.insert_id++;
        super.addQuestion(q);
    }
    addGroup(g) {
        g.id = this.meta.insert_id++;
        super.addGroup(g);
    }
    getQuestionNumerateIndex(question) {
        if (this.presentation === SurveyPresentation.PSYCHOMETRIC) {
            return question.meta.customOrder ? "Q" + question.meta.customOrder : '';
        }
        let index = 0, i = 0;
        for (i; i < this.items.length; i++) {
            let item = this.items[i];
            if (item instanceof QuestionGroup && item.enumerate) {
                let groupQuestions = item.questions;
                for (let c = 0; c < groupQuestions.length; c++) {
                    index++;
                    if (groupQuestions[c] === question) {
                        return this.enumerationType === NumerateType.AGGREGATE ? index : c + 1;
                    }
                }
            }
        }
    }
    /**
     * Get the chain of ancestor groups for a given question or group
     */
    getItemAncestors(referenceItem) {
        // return array
        const ancestors = [];
        /**
         * recursive function that will walk through child items until it finds a match for the referenceItem
         * once a match is found it returns true and the calling method will add it's item to the ancestors array
         */
        const walkItem = (item) => {
            // if the current item being walked is the reference item we've found the end of the chain return true to indicate this item is part of the chain
            if (item === referenceItem) {
                return true;
            }
            // if the item being checked is a question there won't be any children, so return false
            if (item instanceof Question) {
                return false;
            }
            // run the walkItem method on the group's child items
            for (let i = 0; i < item.items.length; i++) {
                if (walkItem(item.items[i])) {
                    ancestors.push(item);
                    return true;
                }
            }
            return false;
        };
        // begin collecting ancestors at the top level
        this.items.forEach(item => walkItem(item));
        // return the array
        return ancestors;
    }
    prepareCloneData(json) {
        delete json.events;
        delete json._intent;
        delete json._presentation;
        json.intent = this._intent;
        json.presentation = this._presentation;
        return json;
    }
}

/**
 * Parse the change on a questions meta values, updating fuzzy if needed
 * @param question
 * @param metaKey
 * @param newValue
 */
const processFuzzyQuestionChange = (question, metaKey, newValue) => {
    if (newValue !== question.meta[metaKey] && question.fuzzy) {
        delete question.fuzzy.meta[metaKey];
    }
    validateFuzzyObject(question);
    question.meta[metaKey] = newValue;
};
/**
 * Check all the properties in the fuzzy object, clearing them out if nothing nested
 */
const validateFuzzyObject = (question) => {
    if (!question.fuzzy) {
        return;
    }
    if (question.fuzzy.meta?.listValues && Object.keys(question.fuzzy.meta.listValues).length === 0) {
        delete question.fuzzy.meta.listValues;
    }
    if (question.fuzzy.meta && Object.keys(question.fuzzy.meta).length === 0) {
        delete question.fuzzy.meta;
    }
    if (Object.keys(question.fuzzy).length === 0) {
        delete question.fuzzy;
    }
};
/**
 * Directive to bind an input to a question meta property
 */
class FuzzyMetaPropertyDirective {
    onBlur(event) {
        const newValue = event.target.value;
        // update the fuzzy object on the question
        processFuzzyQuestionChange(this.question, this.fuzzyMetaProperty, newValue);
        // trigger change detection on question editors
        this.survey.events.next({});
    }
    constructor(question, element, survey) {
        this.question = question;
        this.element = element;
        this.survey = survey;
    }
    get input() {
        return this.element.nativeElement;
    }
    ngOnInit() {
        if (this.question.meta[this.fuzzyMetaProperty]) {
            this.input.value = this.question.meta[this.fuzzyMetaProperty];
        }
    }
}
FuzzyMetaPropertyDirective.ɵfac = function FuzzyMetaPropertyDirective_Factory(t) { return new (t || FuzzyMetaPropertyDirective)(i0.ɵɵdirectiveInject(QUESTION), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(Survey)); };
FuzzyMetaPropertyDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: FuzzyMetaPropertyDirective, selectors: [["", "fuzzyMetaProperty", ""]], hostBindings: function FuzzyMetaPropertyDirective_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("blur", function FuzzyMetaPropertyDirective_blur_HostBindingHandler($event) { return ctx.onBlur($event); });
    } }, inputs: { fuzzyMetaProperty: "fuzzyMetaProperty" } });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FuzzyMetaPropertyDirective, [{
        type: Directive,
        args: [{
                selector: '[fuzzyMetaProperty]'
            }]
    }], function () { return [{ type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }, { type: i0.ElementRef }, { type: Survey }]; }, { fuzzyMetaProperty: [{
            type: Input
        }], onBlur: [{
            type: HostListener,
            args: ['blur', ['$event']]
        }] }); })();

const defaultSurveyPermissions = {
    questions: {
        add: true,
        edit: true,
        remove: true,
        picklist: {
            add: true,
            edit: true,
            remove: true
        },
        checklist: {
            add: true,
            edit: true,
            remove: true
        },
        radioset: {
            add: true,
            edit: true,
            remove: true
        }
    },
    groups: {
        add: true,
        edit: true,
        remove: true,
    },
    recommendations: {
        add: true,
        edit: true,
        remove: true,
    },
    survey: {
        edit: true
    },
    readonly: false
};
function assignFalse(perms) {
    for (let prop in perms) {
        if (typeof (perms[prop]) === 'boolean') {
            perms[prop] = false;
        }
        else {
            perms[prop] = assignFalse(perms[prop]);
        }
    }
    return perms;
}
function parsePermissions(perms) {
    const defaults = JSON.parse(JSON.stringify(defaultSurveyPermissions));
    if (perms.readonly) {
        perms.questions = false;
        perms.groups = false;
        perms.recommendations = false;
        perms.survey = false;
    }
    if (perms.questions === false) {
        perms.questions = assignFalse(defaults.questions);
    }
    else {
        perms.questions = Object.assign(defaults.questions, perms.questions);
    }
    if (perms.picklist) {
        perms.questions.picklist = Object.assign(defaults.questions.picklist, perms.picklist);
        delete perms.picklist;
    }
    if (perms.radioset) {
        perms.questions.radioset = Object.assign(defaults.questions.radioset, perms.radioset);
        delete perms.radioset;
    }
    if (perms.checklist) {
        perms.questions.checklist = Object.assign(defaults.questions.checklist, perms.checklist);
        delete perms.checklist;
    }
    if (perms.groups === false) {
        perms.groups = assignFalse(defaults.groups);
    }
    else {
        perms.groups = Object.assign(defaults.groups, perms.groups);
    }
    if (perms.recommendations === false) {
        perms.recommendations = assignFalse(defaults.recommendations);
    }
    else {
        perms.recommendations = Object.assign(defaults.recommendations, perms.recommendations);
    }
    if (perms.survey === false) {
        perms.survey = assignFalse(defaults.survey);
    }
    else {
        perms.survey = Object.assign(defaults.survey, perms.survey);
    }
    return perms;
}
class SurveyEditorConfig {
    constructor(cfg) {
        cfg.permissions = parsePermissions(cfg.permissions || {});
        cfg.supports = Object.assign({ versioning: false, recommendations: false, conditionalQuestions: false, title: true }, cfg.supports || {});
        cfg.i18n = Object.assign({
            questions: {
                tabLabel: 'Questions',
                addLabel: 'Add Question'
            },
            recommendations: {
                surveyRecommendationsLabel: 'Survey Recommendations',
                surveyRecommendationsPlaceholder: 'This text will be shown before any other Survey Recommendations.'
            }
        }, cfg.i18n || {});
        for (let prop in cfg) {
            this[prop] = cfg[prop];
        }
        //console.info( this );
    }
}
const cloneConfig = (editorConfig, cfg) => {
    const cloned = JSON.parse(JSON.stringify(editorConfig));
    const parsed = Object.assign(cloned, cfg);
    const newcfg = new SurveyEditorConfig(parsed);
    return newcfg;
};
const SURVEY_EDITOR_CONFIG = new InjectionToken('SURVEY_EDITOR_CONFIG');

/**
 * This directive is used to offset the dom element using the directive by the offset of a specified element
 * The measurement needs to be rechecked so we're using a kind of hacky way to capture the ngDoCheck method of the consuming component
 * This allows us to recheck when the changedetector is run but not have to register resize listeners
 */
class OffsetTopParentDirective {
    constructor(el) {
        this.el = el;
    }
    ngOnInit() {
        if (this.offsetTopDoCheck) {
            if (!this.offsetTopDoCheck.ngDoCheck) {
                console.error("offsetTopDoCheck input requires a component that implements the ngDoCheck method");
                return;
            }
            const original = this.offsetTopDoCheck.ngDoCheck;
            this.offsetTopDoCheck.ngDoCheck = () => {
                original.apply(this.offsetTopDoCheck, null);
                this.setOffset();
            };
        }
    }
    ngDoCheck() {
        if (this.offsetTopParent) {
            this.setOffset();
        }
    }
    setOffset() {
        this.el.nativeElement.style.marginTop = -this.offsetTopParent.offsetTop + 'px';
    }
}
OffsetTopParentDirective.ɵfac = function OffsetTopParentDirective_Factory(t) { return new (t || OffsetTopParentDirective)(i0.ɵɵdirectiveInject(i0.ElementRef)); };
OffsetTopParentDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: OffsetTopParentDirective, selectors: [["", "offsetTopParent", ""]], inputs: { offsetTopParent: "offsetTopParent", offsetTopDoCheck: "offsetTopDoCheck" } });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OffsetTopParentDirective, [{
        type: Directive,
        args: [{
                selector: '[offsetTopParent]'
            }]
    }], function () { return [{ type: i0.ElementRef }]; }, { offsetTopParent: [{
            type: Input
        }], offsetTopDoCheck: [{
            type: Input
        }] }); })();
class EditorLayoutDirectivesModule {
}
EditorLayoutDirectivesModule.ɵfac = function EditorLayoutDirectivesModule_Factory(t) { return new (t || EditorLayoutDirectivesModule)(); };
EditorLayoutDirectivesModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: EditorLayoutDirectivesModule });
EditorLayoutDirectivesModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({});
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(EditorLayoutDirectivesModule, [{
        type: NgModule,
        args: [{
                imports: [],
                declarations: [OffsetTopParentDirective],
                exports: [OffsetTopParentDirective]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(EditorLayoutDirectivesModule, { declarations: [OffsetTopParentDirective], exports: [OffsetTopParentDirective] }); })();

let dismissed = false, snackBarRef = null;
/**
 * When placed on an HTML input this will listen for change events and show a snackbar notification if the question has recommendations.
 * It can be dismissed so it won't show again.
 * It will also change a score input to readonly with a tooltip if there are conditional questions that match the input conditional and have scores
 */
class ScoreInputDirective extends MatTooltip {
    constructor(snackBar, ctrl, overlay, elementRef, scrollDispatcher, viewContainerRef, ngZone, platform, ariaDescriber, focusMonitor, scrollStrategy, dir, defaultOptions, _document, survey) {
        super(overlay, elementRef, scrollDispatcher, viewContainerRef, ngZone, platform, ariaDescriber, focusMonitor, scrollStrategy, dir, defaultOptions, null);
        this.snackBar = snackBar;
        this.ctrl = ctrl;
        this.elementRef = elementRef;
        this.survey = survey;
        this.destroy$ = new Subject();
    }
    // listen to value changes on the control and store the value privately
    ngOnInit() {
        // capture the actual value
        this.ctrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => this.originalValue = value);
    }
    /**
     * on change detection, see if there are conditionals for this input, if so, make it readonly with a toolip
     */
    ngDoCheck() {
        // ref to the input element
        const input = this.elementRef.nativeElement;
        // if this question has conditionals then we change the input to readonly with a tooltip indicating that the value is calculated
        if (this.hasConditionals) {
            input.value = '';
            input.placeholder = '-';
            input.readOnly = true;
            input.type = 'text';
            this.message = 'This value will be calculated by the sum of the conditional responses.';
        }
        // otherwise if no conditionals reset to a numeric field
        else {
            this.message = '';
            input.type = 'number';
            input.readOnly = false;
            if (this.originalValue !== undefined) {
                input.value = this.originalValue;
            }
        }
    }
    /**
     * Cleanup listeners
     */
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
    /**
     * On change, open snack bar notice if there are recommendations that might be affected by the change
     */
    onChange(e) {
        // if it's been dismissed or there's an open snackbar, don't open
        if (dismissed || snackBarRef) {
            return;
        }
        // check this question and it's ancestors for any that have recommendations
        const affectedSurveyItems = [this.question, ...this.survey.getItemAncestors(this.question)].filter(group => group.recommendations.length > 0);
        // if no items will be affected, don't open
        if (affectedSurveyItems.length === 0) {
            return;
        }
        snackBarRef = this.snackBar.open("There are recommendations that will be affected by the change to this score. Please ensure their configured ranges still apply.", "Don't show this again", { duration: 5000 });
        snackBarRef.afterDismissed().subscribe(info => (snackBarRef = null, dismissed = info.dismissedByAction));
    }
    /**
     * Check if this question has conditionals that match the input conditional value, and that those conditional questions have values assigned to them
     */
    get hasConditionals() {
        if (!this.question.conditionalQuestions?.items.length) {
            return false;
        }
        const value = parseInt(this.scoreInputConditional, 10);
        const conditionals = this.question.conditionalQuestions.items.filter(conditionalQuestion => conditionalQuestion.meta.conditionalOn === value);
        const maxConditionalScore = conditionals.map(c => c.maximumScore).reduce((a, b) => a + b, 0);
        return maxConditionalScore > 0;
    }
}
ScoreInputDirective.ɵfac = function ScoreInputDirective_Factory(t) { return new (t || ScoreInputDirective)(i0.ɵɵdirectiveInject(i1.MatSnackBar), i0.ɵɵdirectiveInject(i3.NgControl, 2), i0.ɵɵdirectiveInject(i3$1.Overlay), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i3$1.ScrollDispatcher), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i4.Platform), i0.ɵɵdirectiveInject(i1$1.AriaDescriber), i0.ɵɵdirectiveInject(i1$1.FocusMonitor), i0.ɵɵdirectiveInject(MAT_TOOLTIP_SCROLL_STRATEGY), i0.ɵɵdirectiveInject(i6.Directionality, 8), i0.ɵɵdirectiveInject(MAT_TOOLTIP_DEFAULT_OPTIONS, 8), i0.ɵɵdirectiveInject(DOCUMENT), i0.ɵɵdirectiveInject(Survey)); };
ScoreInputDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: ScoreInputDirective, selectors: [["", "scoreInput", ""]], hostBindings: function ScoreInputDirective_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("change", function ScoreInputDirective_change_HostBindingHandler($event) { return ctx.onChange($event); });
    } }, inputs: { question: ["scoreInput", "question"], scoreInputConditional: "scoreInputConditional" }, features: [i0.ɵɵInheritDefinitionFeature] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ScoreInputDirective, [{
        type: Directive,
        args: [{
                selector: '[scoreInput]'
            }]
    }], function () { return [{ type: i1.MatSnackBar }, { type: i3.NgControl, decorators: [{
                type: Self
            }] }, { type: i3$1.Overlay }, { type: i0.ElementRef }, { type: i3$1.ScrollDispatcher }, { type: i0.ViewContainerRef }, { type: i0.NgZone }, { type: i4.Platform }, { type: i1$1.AriaDescriber }, { type: i1$1.FocusMonitor }, { type: undefined, decorators: [{
                type: Inject,
                args: [MAT_TOOLTIP_SCROLL_STRATEGY]
            }] }, { type: i6.Directionality, decorators: [{
                type: Optional
            }] }, { type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [MAT_TOOLTIP_DEFAULT_OPTIONS]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [DOCUMENT]
            }] }, { type: Survey }]; }, { question: [{
            type: Input,
            args: ['scoreInput']
        }], scoreInputConditional: [{
            type: Input
        }], onChange: [{
            type: HostListener,
            args: ['change', ['$event']]
        }] }); })();
class ScoreInputDirectiveModule {
}
ScoreInputDirectiveModule.ɵfac = function ScoreInputDirectiveModule_Factory(t) { return new (t || ScoreInputDirectiveModule)(); };
ScoreInputDirectiveModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: ScoreInputDirectiveModule });
ScoreInputDirectiveModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({});
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ScoreInputDirectiveModule, [{
        type: NgModule,
        args: [{
                imports: [],
                declarations: [ScoreInputDirective],
                exports: [ScoreInputDirective]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(ScoreInputDirectiveModule, { declarations: [ScoreInputDirective], exports: [ScoreInputDirective] }); })();

/**
 * Represents the values saved on the response for this type of question ( we don't want to save 0 as a value )
 */
class YNULabelValues {
}
YNULabelValues.YES = 1;
YNULabelValues.NO = 2;
YNULabelValues.UNCERTAIN = 3;
/**
 * An enum containing the different sets of labels representing a YES/NO/UNCERTAIN type of question
 */
var YNULabelSets;
(function (YNULabelSets) {
    YNULabelSets[YNULabelSets["YES_NO_UNCERTAIN"] = 0] = "YES_NO_UNCERTAIN";
    YNULabelSets[YNULabelSets["AGREE_DISAGREE_UNSURE"] = 1] = "AGREE_DISAGREE_UNSURE";
})(YNULabelSets || (YNULabelSets = {}));
/**
 * Return a localized YES/NO/UNCERTAIN label
 *
 * @param value - One of the YNULabelValues
 * @param set   - The YNULabelSets enum to return labels for
 */
const transformYNULabel = (value, set) => {
    /**
       * Array containing the Localized strings for the different ways of expressing YES / NO / UNCERTAIN
       * index of the objects must line up with the YNULabelSets enum
      */
    const YNULabelSetStrings = [
        {
            1: $localize `:common.words|Common Words - Yes@@commonWordsYes:Yes`,
            2: $localize `:common.words|Common Words - No@@commonWordsNo:No`,
            3: $localize `:common.words|Common Words - Uncertain@@commonWordUncertain:Uncertain`
        },
        {
            1: $localize `:common.words|Common Words - Agree@@commonWordAgree:Agree`,
            2: $localize `:common.words|Common Words - Disagree@@commonWordDisagree:Disagree`,
            3: $localize `:common.words|Common Words - Unsure@@commonWordUnsure:Unsure`
        }
    ];
    return YNULabelSetStrings[set][value];
};

class YesNoUncertainLabelPipe {
    transform(value) {
        value = parseInt(value);
        // hardcoded type right now. If we need to provide one differently we can explore that
        // @todo
        const labelType = YNULabelSets.AGREE_DISAGREE_UNSURE;
        const label = transformYNULabel(value, labelType);
        return label;
    }
}
YesNoUncertainLabelPipe.ɵfac = function YesNoUncertainLabelPipe_Factory(t) { return new (t || YesNoUncertainLabelPipe)(); };
YesNoUncertainLabelPipe.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "ynuLabel", type: YesNoUncertainLabelPipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(YesNoUncertainLabelPipe, [{
        type: Pipe,
        args: [{
                name: 'ynuLabel'
            }]
    }], null, null); })();

const _c0$d = ["autosize"];
function QuestionEditorYesNoUncertain_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 1)(2, "h5", 2);
    i0.ɵɵtext(3, "Assign Scores");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "table", 3)(5, "thead")(6, "tr")(7, "th", 4);
    i0.ɵɵtext(8, "Answer");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(9, "th", 5);
    i0.ɵɵtext(10, "Value");
    i0.ɵɵelementEnd()()();
    i0.ɵɵelementStart(11, "tbody")(12, "tr")(13, "td", 4);
    i0.ɵɵtext(14);
    i0.ɵɵpipe(15, "ynuLabel");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(16, "td", 6)(17, "input", 7);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorYesNoUncertain_ng_container_0_Template_input_ngModelChange_17_listener($event) { i0.ɵɵrestoreView(_r3); const ctx_r2 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r2.question.meta.scoreYNU1 = $event); });
    i0.ɵɵelementEnd()()();
    i0.ɵɵelementStart(18, "tr")(19, "td", 4);
    i0.ɵɵtext(20);
    i0.ɵɵpipe(21, "ynuLabel");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(22, "td", 6)(23, "input", 7);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorYesNoUncertain_ng_container_0_Template_input_ngModelChange_23_listener($event) { i0.ɵɵrestoreView(_r3); const ctx_r4 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r4.question.meta.scoreYNU2 = $event); });
    i0.ɵɵelementEnd()()();
    i0.ɵɵelementStart(24, "tr")(25, "td", 4);
    i0.ɵɵtext(26);
    i0.ɵɵpipe(27, "ynuLabel");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(28, "td", 6)(29, "input", 7);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorYesNoUncertain_ng_container_0_Template_input_ngModelChange_29_listener($event) { i0.ɵɵrestoreView(_r3); const ctx_r5 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r5.question.meta.scoreYNU3 = $event); });
    i0.ɵɵelementEnd()()()()()();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("offsetTopParent", ctx_r0.el.nativeElement)("offsetTopDoCheck", ctx_r0);
    i0.ɵɵadvance(13);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(15, 14, 1));
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("scoreInput", ctx_r0.question)("scoreInputConditional", 1)("ngModel", ctx_r0.question.meta.scoreYNU1);
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(21, 16, 2));
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("scoreInput", ctx_r0.question)("scoreInputConditional", 2)("ngModel", ctx_r0.question.meta.scoreYNU2);
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(27, 18, 3));
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("scoreInput", ctx_r0.question)("scoreInputConditional", 3)("ngModel", ctx_r0.question.meta.scoreYNU3);
} }
function QuestionEditorYesNoUncertain_ng_container_1_div_1_Template(rf, ctx) { if (rf & 1) {
    const _r9 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 12)(1, "label");
    i0.ɵɵtext(2, "Preferred Response");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "mat-radio-group", 13);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorYesNoUncertain_ng_container_1_div_1_Template_mat_radio_group_ngModelChange_3_listener($event) { i0.ɵɵrestoreView(_r9); const ctx_r8 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r8.question.meta.preferredResponse = $event); });
    i0.ɵɵelementStart(4, "mat-radio-button", 14);
    i0.ɵɵtext(5);
    i0.ɵɵpipe(6, "ynuLabel");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(7, "mat-radio-button", 14);
    i0.ɵɵtext(8);
    i0.ɵɵpipe(9, "ynuLabel");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(10, "mat-radio-button", 14);
    i0.ɵɵtext(11);
    i0.ɵɵpipe(12, "ynuLabel");
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const ctx_r6 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngModel", ctx_r6.question.meta.preferredResponse);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("value", 1);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(6, 7, 1));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("value", 2);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(9, 9, 2));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("value", 3);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(12, 11, 3));
} }
function QuestionEditorYesNoUncertain_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, QuestionEditorYesNoUncertain_ng_container_1_div_1_Template, 13, 13, "div", 8);
    i0.ɵɵelementStart(2, "div", 9)(3, "mat-form-field")(4, "mat-label");
    i0.ɵɵtext(5, "Feedback when responses do not match:");
    i0.ɵɵelementEnd();
    i0.ɵɵelement(6, "textarea", 10, 11);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.editorConfig.permissions.questions.edit);
} }
class QuestionEditorYesNoUncertain {
    get isSurveyStyleCompare() {
        return this.survey.intent === SurveyIntent.COMPARE;
    }
    get isSurveyStyleCollect() {
        return this.survey.intent === SurveyIntent.COLLECT;
    }
    constructor(el, survey, changeDetectorRef, question, editorConfig) {
        this.el = el;
        this.survey = survey;
        this.changeDetectorRef = changeDetectorRef;
        this.question = question;
        this.editorConfig = editorConfig;
        this.SurveyIntent = SurveyIntent;
        this.surveyChange = survey.events.subscribe(e => {
            this.changeDetectorRef.detectChanges();
        });
    }
    ngOnDestroy() {
        this.surveyChange.unsubscribe();
    }
    // needed for the offsetTopParent directive
    ngDoCheck() { }
}
QuestionEditorYesNoUncertain.ɵfac = function QuestionEditorYesNoUncertain_Factory(t) { return new (t || QuestionEditorYesNoUncertain)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(Survey), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(QUESTION), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG)); };
QuestionEditorYesNoUncertain.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionEditorYesNoUncertain, selectors: [["yes-no-uncertain"]], viewQuery: function QuestionEditorYesNoUncertain_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0$d, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.autosize = _t.first);
    } }, hostVars: 4, hostBindings: function QuestionEditorYesNoUncertain_HostBindings(rf, ctx) { if (rf & 2) {
        i0.ɵɵclassProp("survey-style-compare", ctx.isSurveyStyleCompare)("survey-style-collect", ctx.isSurveyStyleCollect);
    } }, decls: 2, vars: 2, consts: [[4, "ngIf"], [1, "listset-options", 3, "offsetTopParent", "offsetTopDoCheck"], [1, "listset-options-title"], ["cellspacing", "0", "cellpadding", "0", 1, "simple-table"], [1, "cell-text"], [1, "cell-value"], [1, "cell-value", "cell-numeric"], ["type", "number", "matInput", "", 1, "inline-input", 3, "scoreInput", "scoreInputConditional", "ngModel", "ngModelChange"], ["id", "comparePreferredResponse", 4, "ngIf"], ["id", "compareFeedbackMismatch"], ["matInput", "", "fuzzyMetaProperty", "feedbackMismatch", "cdkTextareaAutosize", "", "cdkAutosizeMinRows", "1", "cdkAutosizeMaxRows", "5"], ["autosize", "cdkTextareaAutosize"], ["id", "comparePreferredResponse"], [3, "ngModel", "ngModelChange"], [3, "value"]], template: function QuestionEditorYesNoUncertain_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, QuestionEditorYesNoUncertain_ng_container_0_Template, 30, 20, "ng-container", 0);
        i0.ɵɵtemplate(1, QuestionEditorYesNoUncertain_ng_container_1_Template, 8, 1, "ng-container", 0);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.editorConfig.recommendations && ctx.editorConfig.permissions.recommendations.edit);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.survey.intent === ctx.SurveyIntent.COMPARE);
    } }, dependencies: [i1$2.NgIf, i3.DefaultValueAccessor, i3.NumberValueAccessor, i3.NgControlStatus, i3.NgModel, i3$2.MatRadioGroup, i3$2.MatRadioButton, i4$1.MatFormField, i4$1.MatLabel, i5.MatInput, i7.CdkTextareaAutosize, OffsetTopParentDirective, ScoreInputDirective, FuzzyMetaPropertyDirective, YesNoUncertainLabelPipe], styles: [".survey-style-collect[_nghost-%COMP%]{display:flex}.listset-options[_ngcontent-%COMP%]{width:170px}.listset-options[_ngcontent-%COMP%]:first-child{margin-left:220px}.listset-options-title[_ngcontent-%COMP%]{margin-top:18px}#comparePreferredResponse[_ngcontent-%COMP%]{display:flex;align-items:center;margin-top:24px}#comparePreferredResponse[_ngcontent-%COMP%]   label[_ngcontent-%COMP%]{font-weight:700;margin-right:30px;cursor:default}#comparePreferredResponse[_ngcontent-%COMP%]   mat-radio-group[_ngcontent-%COMP%]   mat-radio-button[_ngcontent-%COMP%]{margin-right:15px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorYesNoUncertain, [{
        type: Component,
        args: [{ selector: 'yes-no-uncertain', template: "<ng-container *ngIf=\"editorConfig.recommendations && editorConfig.permissions.recommendations.edit\">\n\n\t<div class=\"listset-options\"\n\t\t [offsetTopParent]=\"el.nativeElement\"\n\t\t [offsetTopDoCheck]=\"this\">\n\n\t\t<h5 class=\"listset-options-title\">Assign Scores</h5>\n\n\t\t<table class=\"simple-table\"\n\t\t\t   cellspacing=\"0\"\n\t\t\t   cellpadding=\"0\">\n\t\t\t<thead>\n\t\t\t\t<tr>\n\t\t\t\t\t<th class=\"cell-text\">Answer</th>\n\t\t\t\t\t<th class=\"cell-value\">Value</th>\n\t\t\t\t</tr>\n\t\t\t</thead>\n\t\t\t<tbody>\n\n\t\t\t\t<tr>\n\t\t\t\t\t<td class=\"cell-text\">{{ 1 | ynuLabel }}</td>\n\t\t\t\t\t<td class=\"cell-value cell-numeric\">\n\t\t\t\t\t\t<input type=\"number\"\n\t\t\t\t\t\t\t   class=\"inline-input\"\n\t\t\t\t\t\t\t   matInput\n\t\t\t\t\t\t\t   [scoreInput]=\"question\"\n\t\t\t\t\t\t\t   [scoreInputConditional]=\"1\"\n\t\t\t\t\t\t\t   [(ngModel)]=\"question.meta.scoreYNU1\" />\n\t\t\t\t\t</td>\n\t\t\t\t</tr>\n\n\t\t\t\t<tr>\n\t\t\t\t\t<td class=\"cell-text\">{{ 2 | ynuLabel }}</td>\n\t\t\t\t\t<td class=\"cell-value cell-numeric\">\n\t\t\t\t\t\t<input type=\"number\"\n\t\t\t\t\t\t\t   class=\"inline-input\"\n\t\t\t\t\t\t\t   matInput\n\t\t\t\t\t\t\t   [scoreInput]=\"question\"\n\t\t\t\t\t\t\t   [scoreInputConditional]=\"2\"\n\t\t\t\t\t\t\t   [(ngModel)]=\"question.meta.scoreYNU2\" />\n\t\t\t\t\t</td>\n\t\t\t\t</tr>\n\n\t\t\t\t<tr>\n\t\t\t\t\t<td class=\"cell-text\">{{ 3 | ynuLabel }}</td>\n\t\t\t\t\t<td class=\"cell-value cell-numeric\">\n\t\t\t\t\t\t<input type=\"number\"\n\t\t\t\t\t\t\t   class=\"inline-input\"\n\t\t\t\t\t\t\t   matInput\n\t\t\t\t\t\t\t   [scoreInput]=\"question\"\n\t\t\t\t\t\t\t   [scoreInputConditional]=\"3\"\n\t\t\t\t\t\t\t   [(ngModel)]=\"question.meta.scoreYNU3\" />\n\t\t\t\t\t</td>\n\t\t\t\t</tr>\n\t\t\t</tbody>\n\t\t</table>\n\n\t</div>\n\n</ng-container>\n\n<ng-container *ngIf=\"survey.intent === SurveyIntent.COMPARE\">\n\t<div id=\"comparePreferredResponse\"\n\t\t *ngIf=\"editorConfig.permissions.questions.edit\">\n\t\t<label>Preferred Response</label>\n\t\t<mat-radio-group [(ngModel)]=\"question.meta.preferredResponse\">\n\t\t\t<mat-radio-button [value]=\"1\">{{ 1 | ynuLabel }}</mat-radio-button>\n\t\t\t<mat-radio-button [value]=\"2\">{{ 2 | ynuLabel }}</mat-radio-button>\n\t\t\t<mat-radio-button [value]=\"3\">{{ 3 | ynuLabel }}</mat-radio-button>\n\t\t</mat-radio-group>\n\t</div>\n\n\t<div id=\"compareFeedbackMismatch\">\n\t\t<mat-form-field>\n\t\t\t<mat-label>Feedback when responses do not match:</mat-label>\n\t\t\t<textarea matInput\n\t\t\t\t\t  fuzzyMetaProperty=\"feedbackMismatch\"\n\t\t\t\t\t  cdkTextareaAutosize\n\t\t\t\t\t  cdkAutosizeMinRows=\"1\"\n\t\t\t\t\t  cdkAutosizeMaxRows=\"5\"\n\t\t\t\t\t  #autosize=\"cdkTextareaAutosize\"></textarea>\n\t\t</mat-form-field>\n\t</div>\n\n</ng-container>\n", styles: [":host.survey-style-collect{display:flex}.listset-options{width:170px}.listset-options:first-child{margin-left:220px}.listset-options-title{margin-top:18px}#comparePreferredResponse{display:flex;align-items:center;margin-top:24px}#comparePreferredResponse label{font-weight:700;margin-right:30px;cursor:default}#comparePreferredResponse mat-radio-group mat-radio-button{margin-right:15px}\n"] }]
    }], function () { return [{ type: i0.ElementRef }, { type: Survey }, { type: i0.ChangeDetectorRef }, { type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }]; }, { autosize: [{
            type: ViewChild,
            args: ['autosize', { static: false }]
        }], isSurveyStyleCompare: [{
            type: HostBinding,
            args: ['class.survey-style-compare']
        }], isSurveyStyleCollect: [{
            type: HostBinding,
            args: ['class.survey-style-collect']
        }] }); })();

/**
 * Generates a picklist editor
 *
 */
class QuestionEditorPicklistComponent {
    constructor(changeDetectorRef) {
        this.changeDetectorRef = changeDetectorRef;
    }
}
QuestionEditorPicklistComponent.ɵfac = function QuestionEditorPicklistComponent_Factory(t) { return new (t || QuestionEditorPicklistComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); };
QuestionEditorPicklistComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionEditorPicklistComponent, selectors: [["picklist-editor"]], decls: 1, vars: 0, consts: [["questionType", "picklist"]], template: function QuestionEditorPicklistComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelement(0, "listset-editor", 0);
    } }, encapsulation: 2 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorPicklistComponent, [{
        type: Component,
        args: [{
                selector: 'picklist-editor',
                template: `
		<listset-editor
			questionType="picklist"
		>
		</listset-editor>
	`
            }]
    }], function () { return [{ type: i0.ChangeDetectorRef }]; }, null); })();

/**
 * Generates a checklist editor
 *
 */
class QuestionEditorChecklistComponent {
    constructor(changeDetectorRef) {
        this.changeDetectorRef = changeDetectorRef;
    }
}
QuestionEditorChecklistComponent.ɵfac = function QuestionEditorChecklistComponent_Factory(t) { return new (t || QuestionEditorChecklistComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); };
QuestionEditorChecklistComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionEditorChecklistComponent, selectors: [["checklist-editor"]], decls: 1, vars: 0, consts: [["questionType", "checklist"]], template: function QuestionEditorChecklistComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelement(0, "listset-editor", 0);
    } }, encapsulation: 2 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorChecklistComponent, [{
        type: Component,
        args: [{
                selector: 'checklist-editor',
                template: `
		<listset-editor
			questionType="checklist"
		>
		</listset-editor>
	`
            }]
    }], function () { return [{ type: i0.ChangeDetectorRef }]; }, null); })();

/**
 * Generates a radioset editor
 *
 */
class QuestionEditorRadiosetComponent {
    constructor(changeDetectorRef) {
        this.changeDetectorRef = changeDetectorRef;
    }
}
QuestionEditorRadiosetComponent.ɵfac = function QuestionEditorRadiosetComponent_Factory(t) { return new (t || QuestionEditorRadiosetComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); };
QuestionEditorRadiosetComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionEditorRadiosetComponent, selectors: [["radioset-editor"]], decls: 1, vars: 0, consts: [["questionType", "radioset"]], template: function QuestionEditorRadiosetComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelement(0, "listset-editor", 0);
    } }, encapsulation: 2 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorRadiosetComponent, [{
        type: Component,
        args: [{
                selector: 'radioset-editor',
                template: `
		<listset-editor
			questionType="radioset"
		>
		</listset-editor>
	`
            }]
    }], function () { return [{ type: i0.ChangeDetectorRef }]; }, null); })();

class QuestionEditorScaleComponent {
    constructor(changeDetectorRef, question) {
        this.changeDetectorRef = changeDetectorRef;
        this.question = question;
    }
}
QuestionEditorScaleComponent.ɵfac = function QuestionEditorScaleComponent_Factory(t) { return new (t || QuestionEditorScaleComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(QUESTION)); };
QuestionEditorScaleComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionEditorScaleComponent, selectors: [["question-editor-scale"]], decls: 0, vars: 0, template: function QuestionEditorScaleComponent_Template(rf, ctx) { }, encapsulation: 2 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorScaleComponent, [{
        type: Component,
        args: [{ selector: 'question-editor-scale', template: "" }]
    }], function () { return [{ type: i0.ChangeDetectorRef }, { type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }]; }, null); })();

function OptionControlComponent_mat_checkbox_0_Template(rf, ctx) { if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-checkbox", 2);
    i0.ɵɵlistener("ngModelChange", function OptionControlComponent_mat_checkbox_0_Template_mat_checkbox_ngModelChange_0_listener($event) { i0.ɵɵrestoreView(_r3); const ctx_r2 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r2.question.meta.optional = $event); });
    i0.ɵɵtext(1, " Optional ");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngModel", ctx_r0.question.meta.optional);
} }
function OptionControlComponent_mat_form_field_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "mat-form-field");
    i0.ɵɵelement(1, "input", 3);
    i0.ɵɵelementEnd();
} }
class OptionControlComponent {
    constructor() { }
}
OptionControlComponent.ɵfac = function OptionControlComponent_Factory(t) { return new (t || OptionControlComponent)(); };
OptionControlComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: OptionControlComponent, selectors: [["optional-control"]], inputs: { question: "question", canEdit: "canEdit" }, decls: 2, vars: 2, consts: [["color", "primary", 3, "ngModel", "ngModelChange", 4, "ngIf"], [4, "ngIf"], ["color", "primary", 3, "ngModel", "ngModelChange"], ["matInput", "", "placeholder", "Optional Checkbox Message", "fuzzyMetaProperty", "optionalText"]], template: function OptionControlComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, OptionControlComponent_mat_checkbox_0_Template, 2, 1, "mat-checkbox", 0);
        i0.ɵɵtemplate(1, OptionControlComponent_mat_form_field_1_Template, 2, 0, "mat-form-field", 1);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.canEdit);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.question.meta.optional);
    } }, dependencies: [i1$2.NgIf, i3.NgControlStatus, i3.NgModel, i4$1.MatFormField, i5.MatInput, i3$3.MatCheckbox, FuzzyMetaPropertyDirective], styles: ["[_nghost-%COMP%]{display:block;margin-top:25px;height:60px;display:flex;align-items:center}mat-form-field[_ngcontent-%COMP%]{margin-left:18px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OptionControlComponent, [{
        type: Component,
        args: [{ selector: 'optional-control', template: `
		<mat-checkbox color="primary" [(ngModel)]="question.meta.optional" *ngIf="canEdit">
			Optional
		</mat-checkbox>

		<mat-form-field *ngIf="question.meta.optional">				
			<input matInput placeholder="Optional Checkbox Message" fuzzyMetaProperty="optionalText" />				
		</mat-form-field>
	`, styles: [":host{display:block;margin-top:25px;height:60px;display:flex;align-items:center}mat-form-field{margin-left:18px}\n"] }]
    }], function () { return []; }, { question: [{
            type: Input
        }], canEdit: [{
            type: Input
        }] }); })();

function QuestionEditorDateComponent_div_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2)(1, "mat-form-field");
    i0.ɵɵelement(2, "input", 3);
    i0.ɵɵelementStart(3, "mat-hint", 4);
    i0.ɵɵtext(4, "Type something like \"-1 month\"");
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(5, "mat-form-field");
    i0.ɵɵelement(6, "input", 5);
    i0.ɵɵelementStart(7, "mat-hint", 4);
    i0.ɵɵtext(8, "Type something like \"today\"");
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("formGroup", ctx_r0.form);
} }
class QuestionEditorDateComponent {
    constructor(changeDetectorRef, question, editorConfig) {
        this.changeDetectorRef = changeDetectorRef;
        this.question = question;
        this.editorConfig = editorConfig;
        this.destroy$ = new Subject();
        this.form = new UntypedFormGroup({
            minDate: new UntypedFormControl(question.meta.minDate, (c) => this.validateMinDate(c)),
            maxDate: new UntypedFormControl(question.meta.maxDate, (c) => this.validateMaxDate(c))
        });
        this.form.controls.minDate.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(status => {
            if (status === 'VALID') {
                const value = this.getParsedParts(this.form.controls.minDate.value.toLowerCase().trim());
                question.meta.minDate = value ? `${value.modifier} ${value.span}`.trim() : '';
            }
            else {
                question.meta.minDate = '';
            }
        });
        this.form.controls.maxDate.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(status => {
            if (status === 'VALID') {
                const value = this.getParsedParts(this.form.controls.maxDate.value.toLowerCase().trim());
                question.meta.maxDate = value ? `${value.modifier} ${value.span}`.trim() : '';
            }
            else {
                question.meta.maxDate = '';
            }
        });
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        // Now let's also unsubscribe from the subject itself:
        this.destroy$.unsubscribe();
    }
    validateMinDate(c) {
        if (c.value && c.value.trim().length) {
            const parsed = this.parseReadableDate(c.value.toLowerCase());
            if (!parsed) {
                return {
                    invalid: true
                };
            }
        }
        return null;
    }
    validateMaxDate(c) {
        if (c.value && c.value.trim().length) {
            const parsed = this.parseReadableDate(c.value.toLowerCase());
            if (!parsed) {
                return {
                    invalid: true
                };
            }
        }
        return null;
    }
    getParsedParts(value) {
        if (value.trim() === 'today') {
            return {
                modifier: '',
                span: 'today'
            };
        }
        let parts = value.match(/([\d-+]+)\s?(day|days|week|weeks|month|months|year|years)/);
        if (!parts) {
            return null;
        }
        return {
            modifier: parseInt(parts[1]),
            span: parts[2]
        };
    }
    parseReadableDate(value) {
        const parts = this.getParsedParts(value);
        if (!parts) {
            return false;
        }
        return true;
    }
}
QuestionEditorDateComponent.ɵfac = function QuestionEditorDateComponent_Factory(t) { return new (t || QuestionEditorDateComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(QUESTION), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG)); };
QuestionEditorDateComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionEditorDateComponent, selectors: [["question-editor-date"]], decls: 2, vars: 3, consts: [[3, "formGroup", 4, "ngIf"], [3, "question", "canEdit"], [3, "formGroup"], ["matInput", "", "placeholder", "Minimum Date", "formControlName", "minDate"], ["align", "start"], ["matInput", "", "placeholder", "Maximum Date", "formControlName", "maxDate"]], template: function QuestionEditorDateComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, QuestionEditorDateComponent_div_0_Template, 9, 1, "div", 0);
        i0.ɵɵelement(1, "optional-control", 1);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.editorConfig.permissions.questions.edit);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("question", ctx.question)("canEdit", ctx.editorConfig.permissions.questions.edit);
    } }, dependencies: [i1$2.NgIf, i3.DefaultValueAccessor, i3.NgControlStatus, i3.NgControlStatusGroup, i3.FormGroupDirective, i3.FormControlName, i4$1.MatFormField, i4$1.MatHint, i5.MatInput, OptionControlComponent], styles: ["mat-form-field[_ngcontent-%COMP%] + mat-form-field[_ngcontent-%COMP%]{margin-left:16px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorDateComponent, [{
        type: Component,
        args: [{ selector: 'question-editor-date', template: "<div [formGroup]=\"form\"\n\t *ngIf=\"editorConfig.permissions.questions.edit\">\n\t<mat-form-field>\n\t\t<input matInput\n\t\t\t   placeholder=\"Minimum Date\"\n\t\t\t   formControlName=\"minDate\" />\n\t\t<mat-hint align=\"start\">Type something like &quot;-1 month&quot;</mat-hint>\n\t</mat-form-field>\n\n\t<mat-form-field>\n\t\t<input matInput\n\t\t\t   placeholder=\"Maximum Date\"\n\t\t\t   formControlName=\"maxDate\" />\n\t\t<mat-hint align=\"start\">Type something like &quot;today&quot;</mat-hint>\n\t</mat-form-field>\n\n</div>\n\n<optional-control [question]=\"question\"\n\t\t\t\t  [canEdit]=\"editorConfig.permissions.questions.edit\"></optional-control>\n", styles: ["mat-form-field+mat-form-field{margin-left:16px}\n"] }]
    }], function () { return [{ type: i0.ChangeDetectorRef }, { type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }]; }, null); })();

class QuestionEditorTextComponent {
    constructor(changeDetectorRef, question) {
        this.changeDetectorRef = changeDetectorRef;
        this.question = question;
    }
}
QuestionEditorTextComponent.ɵfac = function QuestionEditorTextComponent_Factory(t) { return new (t || QuestionEditorTextComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(QUESTION)); };
QuestionEditorTextComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionEditorTextComponent, selectors: [["question-editor-text"]], decls: 0, vars: 0, template: function QuestionEditorTextComponent_Template(rf, ctx) { }, encapsulation: 2 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorTextComponent, [{
        type: Component,
        args: [{ selector: 'question-editor-text', template: "" }]
    }], function () { return [{ type: i0.ChangeDetectorRef }, { type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }]; }, null); })();

class QuestionEditorTextareaComponent {
    constructor(changeDetectorRef, question) {
        this.changeDetectorRef = changeDetectorRef;
        this.question = question;
    }
}
QuestionEditorTextareaComponent.ɵfac = function QuestionEditorTextareaComponent_Factory(t) { return new (t || QuestionEditorTextareaComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(QUESTION)); };
QuestionEditorTextareaComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionEditorTextareaComponent, selectors: [["question-editor-textarea"]], decls: 0, vars: 0, template: function QuestionEditorTextareaComponent_Template(rf, ctx) { }, encapsulation: 2 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorTextareaComponent, [{
        type: Component,
        args: [{ selector: 'question-editor-textarea', template: "" }]
    }], function () { return [{ type: i0.ChangeDetectorRef }, { type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }]; }, null); })();

function QuestionEditorNumberComponent_div_0_Template(rf, ctx) { if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 2)(1, "mat-form-field")(2, "input", 3);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorNumberComponent_div_0_Template_input_ngModelChange_2_listener($event) { i0.ɵɵrestoreView(_r3); const ctx_r2 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r2.question.meta.minValue = $event); });
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(3, "mat-form-field")(4, "input", 4);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorNumberComponent_div_0_Template_input_ngModelChange_4_listener($event) { i0.ɵɵrestoreView(_r3); const ctx_r4 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r4.question.meta.maxValue = $event); });
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngModel", ctx_r0.question.meta.minValue);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngModel", ctx_r0.question.meta.maxValue);
} }
function QuestionEditorNumberComponent_div_1_table_5_tr_10_Template(rf, ctx) { if (rf & 1) {
    const _r9 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "tr")(1, "td", 14)(2, "input", 15);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorNumberComponent_div_1_table_5_tr_10_Template_input_ngModelChange_2_listener($event) { const restoredCtx = i0.ɵɵrestoreView(_r9); const score_r7 = restoredCtx.$implicit; return i0.ɵɵresetView(score_r7.range = $event); });
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "span", 16);
    i0.ɵɵtext(4, "+");
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(5, "td", 17)(6, "input", 18);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorNumberComponent_div_1_table_5_tr_10_Template_input_ngModelChange_6_listener($event) { const restoredCtx = i0.ɵɵrestoreView(_r9); const score_r7 = restoredCtx.$implicit; return i0.ɵɵresetView(score_r7.value = $event); });
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(7, "td", 12)(8, "mat-icon", 19);
    i0.ɵɵlistener("click", function QuestionEditorNumberComponent_div_1_table_5_tr_10_Template_mat_icon_click_8_listener() { const restoredCtx = i0.ɵɵrestoreView(_r9); const score_r7 = restoredCtx.$implicit; const ctx_r11 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r11.question.meta.scoreRange.splice(ctx_r11.question.meta.scoreRange.indexOf(score_r7), 1)); });
    i0.ɵɵtext(9, "delete");
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const score_r7 = ctx.$implicit;
    const ctx_r6 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("min", ctx_r6.question.meta.minValue)("max", ctx_r6.question.meta.maxValue)("ngModel", score_r7.range);
    i0.ɵɵadvance(4);
    i0.ɵɵproperty("ngModel", score_r7.value);
} }
function QuestionEditorNumberComponent_div_1_table_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "table", 9)(1, "thead")(2, "tr")(3, "th", 10);
    i0.ɵɵtext(4, "Range");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "th", 11);
    i0.ɵɵtext(6, "Value");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(7, "th", 12);
    i0.ɵɵtext(8, "Remove");
    i0.ɵɵelementEnd()()();
    i0.ɵɵelementStart(9, "tbody");
    i0.ɵɵtemplate(10, QuestionEditorNumberComponent_div_1_table_5_tr_10_Template, 10, 4, "tr", 13);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r5 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(10);
    i0.ɵɵproperty("ngForOf", ctx_r5.question.meta.scoreRange);
} }
function QuestionEditorNumberComponent_div_1_Template(rf, ctx) { if (rf & 1) {
    const _r13 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 5)(1, "h5", 6);
    i0.ɵɵtext(2, "Assign Scores ");
    i0.ɵɵelementStart(3, "mat-icon", 7);
    i0.ɵɵlistener("click", function QuestionEditorNumberComponent_div_1_Template_mat_icon_click_3_listener() { i0.ɵɵrestoreView(_r13); const ctx_r12 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r12.addNewScoreRangeItem()); });
    i0.ɵɵtext(4, "playlist_add");
    i0.ɵɵelementEnd()();
    i0.ɵɵtemplate(5, QuestionEditorNumberComponent_div_1_table_5_Template, 11, 1, "table", 8);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("offsetTopParent", ctx_r1.el.nativeElement)("offsetTopDoCheck", ctx_r1);
    i0.ɵɵadvance(5);
    i0.ɵɵproperty("ngIf", ctx_r1.question.meta.scoreRange.length);
} }
class QuestionEditorNumberComponent {
    constructor(el, changeDetectorRef, question, editorConfig, survey) {
        this.el = el;
        this.changeDetectorRef = changeDetectorRef;
        this.question = question;
        this.editorConfig = editorConfig;
        this.survey = survey;
        if (!question.meta.scoreRange) {
            question.meta.scoreRange = [];
        }
    }
    // needed for the offsetTopParent directive
    ngDoCheck() { }
    /**
     * Add an item to the score configuration
     */
    addNewScoreRangeItem() {
        const range = '';
        const value = '';
        this.question.meta.scoreRange.push({ range, value });
    }
}
QuestionEditorNumberComponent.ɵfac = function QuestionEditorNumberComponent_Factory(t) { return new (t || QuestionEditorNumberComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(QUESTION), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG), i0.ɵɵdirectiveInject(Survey)); };
QuestionEditorNumberComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionEditorNumberComponent, selectors: [["question-editor-number"]], decls: 2, vars: 2, consts: [["class", "min-max", 4, "ngIf"], ["class", "listset-options", 3, "offsetTopParent", "offsetTopDoCheck", 4, "ngIf"], [1, "min-max"], ["matInput", "", "placeholder", "Min", "type", "number", 3, "ngModel", "ngModelChange"], ["matInput", "", "placeholder", "Max", "type", "number", 3, "ngModel", "ngModelChange"], [1, "listset-options", 3, "offsetTopParent", "offsetTopDoCheck"], [1, "listset-options-title"], [3, "click"], ["class", "simple-table", "cellspacing", "0", "cellpadding", "0", 4, "ngIf"], ["cellspacing", "0", "cellpadding", "0", 1, "simple-table"], [1, "cell-score"], [1, "cell-value"], [1, "cell-remove"], [4, "ngFor", "ngForOf"], [1, "cell-score", "cell-numeric"], ["type", "number", "matInput", "", "scoreInput", "", "required", "", 1, "inline-input", 3, "min", "max", "ngModel", "ngModelChange"], [1, "range-plus"], [1, "cell-value", "cell-numeric"], ["type", "number", "scoreInput", "", "matInput", "", "required", "", 1, "inline-input", 3, "ngModel", "ngModelChange"], [1, "remove-icon", 3, "click"]], template: function QuestionEditorNumberComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, QuestionEditorNumberComponent_div_0_Template, 5, 2, "div", 0);
        i0.ɵɵtemplate(1, QuestionEditorNumberComponent_div_1_Template, 6, 3, "div", 1);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.editorConfig.permissions.questions.edit);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.editorConfig.permissions.recommendations.edit);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i3.DefaultValueAccessor, i3.NumberValueAccessor, i3.NgControlStatus, i3.RequiredValidator, i3.MinValidator, i3.MaxValidator, i3.NgModel, i4$1.MatFormField, i5.MatInput, i6$1.MatIcon, OffsetTopParentDirective, ScoreInputDirective], styles: ["[_nghost-%COMP%]{display:flex}.remove-icon[_ngcontent-%COMP%]{cursor:pointer}.cell-score[_ngcontent-%COMP%]{text-align:left}.cell-score[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{width:45px;text-align:center}.listset-options[_ngcontent-%COMP%]{width:260px;margin-left:35px}.listset-options-title[_ngcontent-%COMP%]   mat-icon[_ngcontent-%COMP%]{cursor:pointer}.min-max[_ngcontent-%COMP%]{display:flex}.min-max[_ngcontent-%COMP%]   mat-form-field[_ngcontent-%COMP%]{width:80px;margin-right:16px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorNumberComponent, [{
        type: Component,
        args: [{ selector: 'question-editor-number', template: "<div class=\"min-max\"\n\t *ngIf=\"editorConfig.permissions.questions.edit\">\n\n\t<mat-form-field>\n\t\t<input matInput\n\t\t\t   placeholder=\"Min\"\n\t\t\t   [(ngModel)]=\"question.meta.minValue\"\n\t\t\t   type=\"number\" />\n\t</mat-form-field>\n\n\t<mat-form-field>\n\t\t<input matInput\n\t\t\t   placeholder=\"Max\"\n\t\t\t   [(ngModel)]=\"question.meta.maxValue\"\n\t\t\t   type=\"number\" />\n\t</mat-form-field>\n\n</div>\n\n<div *ngIf=\"editorConfig.permissions.recommendations.edit\"\n\t class=\"listset-options\"\n\t [offsetTopParent]=\"el.nativeElement\"\n\t [offsetTopDoCheck]=\"this\">\n\n\t<h5 class=\"listset-options-title\">Assign Scores <mat-icon (click)=\"addNewScoreRangeItem()\">playlist_add</mat-icon>\n\t</h5>\n\n\t<table *ngIf=\"question.meta.scoreRange.length\"\n\t\t   class=\"simple-table\"\n\t\t   cellspacing=\"0\"\n\t\t   cellpadding=\"0\">\n\t\t<thead>\n\t\t\t<tr>\n\t\t\t\t<th class=\"cell-score\">Range</th>\n\t\t\t\t<th class=\"cell-value\">Value</th>\n\t\t\t\t<th class=\"cell-remove\">Remove</th>\n\t\t\t</tr>\n\t\t</thead>\n\t\t<tbody>\n\n\t\t\t<tr *ngFor=\"let score of question.meta.scoreRange\">\n\t\t\t\t<td class=\"cell-score cell-numeric\"><input type=\"number\"\n\t\t\t\t\t\t   class=\"inline-input\"\n\t\t\t\t\t\t   matInput\n\t\t\t\t\t\t   scoreInput\n\t\t\t\t\t\t   required\n\t\t\t\t\t\t   [min]=\"question.meta.minValue\"\n\t\t\t\t\t\t   [max]=\"question.meta.maxValue\"\n\t\t\t\t\t\t   [(ngModel)]=\"score.range\" />\n\t\t\t\t\t<span class='range-plus'>+</span>\n\t\t\t\t</td>\n\t\t\t\t<td class=\"cell-value cell-numeric\">\n\t\t\t\t\t<input type=\"number\"\n\t\t\t\t\t\t   class=\"inline-input\"\n\t\t\t\t\t\t   scoreInput\n\t\t\t\t\t\t   matInput\n\t\t\t\t\t\t   required\n\t\t\t\t\t\t   [(ngModel)]=\"score.value\" />\n\t\t\t\t</td>\n\t\t\t\t<td class=\"cell-remove\">\n\t\t\t\t\t<mat-icon class=\"remove-icon\"\n\t\t\t\t\t\t\t  (click)=\"question.meta.scoreRange.splice(question.meta.scoreRange.indexOf(score),1)\">delete</mat-icon>\n\t\t\t\t</td>\n\n\t\t\t</tr>\n\n\t\t</tbody>\n\t</table>\n\n</div>\n", styles: [":host{display:flex}.remove-icon{cursor:pointer}.cell-score{text-align:left}.cell-score input{width:45px;text-align:center}.listset-options{width:260px;margin-left:35px}.listset-options-title mat-icon{cursor:pointer}.min-max{display:flex}.min-max mat-form-field{width:80px;margin-right:16px}\n"] }]
    }], function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }, { type: Survey }]; }, null); })();

/**
 * Represents the values saved on the response for this type of question ( we don't want to save 0 as a value )
 */
class BooleanLabelValues {
}
BooleanLabelValues.YES = 1;
BooleanLabelValues.NO = 2;
/**
 * An enum containing the different sets of labels representing a YES/NO/UNCERTAIN type of question
 */
var BooleanLabelSets;
(function (BooleanLabelSets) {
    BooleanLabelSets[BooleanLabelSets["TRUE_FALSE"] = 0] = "TRUE_FALSE";
    BooleanLabelSets[BooleanLabelSets["YES_NO"] = 1] = "YES_NO";
})(BooleanLabelSets || (BooleanLabelSets = {}));
/**
 * Return a localized Boolean label
 *
 * @param value - One of the BooleanLabelValues
 * @param set   - The BooleanLabelSets enum to return labels for
 */
const transformBooleanLabel = (value, set) => {
    /**
       * Array containing the Localized strings for the different ways of expressing TRUE / FALSE
       * index of the objects must line up with the BooleanLabelSets enum
      */
    const BooleanLabelSetStrings = [
        {
            1: $localize `:common.words|Common Words - True@@commonWordsTrue:True`,
            2: $localize `:common.words|Common Words - False@@commonWordsFalse:False`
        },
        {
            1: $localize `:common.words|Common Words - Yes@@commonWordsYes:Yes`,
            2: $localize `:common.words|Common Words - No@@commonWordsNo:No`
        }
    ];
    return BooleanLabelSetStrings[set][value];
};

class BooleanLabelPipe {
    transform(value) {
        value = parseInt(value);
        // hardcoded type right now. If we need to provide one differently we can explore that
        // @todo
        const labelType = BooleanLabelSets.YES_NO;
        const label = transformBooleanLabel(value, labelType);
        return label;
    }
}
BooleanLabelPipe.ɵfac = function BooleanLabelPipe_Factory(t) { return new (t || BooleanLabelPipe)(); };
BooleanLabelPipe.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "booleanLabel", type: BooleanLabelPipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BooleanLabelPipe, [{
        type: Pipe,
        args: [{
                name: 'booleanLabel'
            }]
    }], null, null); })();

const _c0$c = ["autosize"];
function QuestionEditorBooleanComponent_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 1)(2, "h5", 2);
    i0.ɵɵtext(3, "Assign Scores");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "table", 3)(5, "thead")(6, "tr")(7, "th", 4);
    i0.ɵɵtext(8, "Answer");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(9, "th", 5);
    i0.ɵɵtext(10, "Value");
    i0.ɵɵelementEnd()()();
    i0.ɵɵelementStart(11, "tbody")(12, "tr")(13, "td", 4);
    i0.ɵɵtext(14);
    i0.ɵɵpipe(15, "booleanLabel");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(16, "td", 6)(17, "input", 7);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorBooleanComponent_ng_container_0_Template_input_ngModelChange_17_listener($event) { i0.ɵɵrestoreView(_r3); const ctx_r2 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r2.question.meta.scoreBoolean1 = $event); });
    i0.ɵɵelementEnd()()();
    i0.ɵɵelementStart(18, "tr")(19, "td", 4);
    i0.ɵɵtext(20);
    i0.ɵɵpipe(21, "booleanLabel");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(22, "td", 6)(23, "input", 7);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorBooleanComponent_ng_container_0_Template_input_ngModelChange_23_listener($event) { i0.ɵɵrestoreView(_r3); const ctx_r4 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r4.question.meta.scoreBoolean2 = $event); });
    i0.ɵɵelementEnd()()()()()();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("offsetTopParent", ctx_r0.el.nativeElement)("offsetTopDoCheck", ctx_r0);
    i0.ɵɵadvance(13);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(15, 10, 1));
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("scoreInput", ctx_r0.question)("scoreInputConditional", 1)("ngModel", ctx_r0.question.meta.scoreBoolean1);
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(21, 12, 2));
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("scoreInput", ctx_r0.question)("scoreInputConditional", 2)("ngModel", ctx_r0.question.meta.scoreBoolean2);
} }
function QuestionEditorBooleanComponent_ng_container_1_div_1_Template(rf, ctx) { if (rf & 1) {
    const _r8 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 12)(1, "label");
    i0.ɵɵtext(2, "Preferred Response");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "mat-radio-group", 13);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorBooleanComponent_ng_container_1_div_1_Template_mat_radio_group_ngModelChange_3_listener($event) { i0.ɵɵrestoreView(_r8); const ctx_r7 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r7.question.meta.preferredResponse = $event); });
    i0.ɵɵelementStart(4, "mat-radio-button", 14);
    i0.ɵɵtext(5);
    i0.ɵɵpipe(6, "booleanLabel");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(7, "mat-radio-button", 14);
    i0.ɵɵtext(8);
    i0.ɵɵpipe(9, "booleanLabel");
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const ctx_r5 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngModel", ctx_r5.question.meta.preferredResponse);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("value", 1);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(6, 5, 1));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("value", 2);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(9, 7, 2));
} }
function QuestionEditorBooleanComponent_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, QuestionEditorBooleanComponent_ng_container_1_div_1_Template, 10, 9, "div", 8);
    i0.ɵɵelementStart(2, "div", 9)(3, "mat-form-field")(4, "mat-label");
    i0.ɵɵtext(5, "Feedback when responses do not match:");
    i0.ɵɵelementEnd();
    i0.ɵɵelement(6, "textarea", 10, 11);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.editorConfig.permissions.questions.edit);
} }
class QuestionEditorBooleanComponent {
    get isSurveyStyleCompare() {
        return this.survey.intent === SurveyIntent.COMPARE;
    }
    get isSurveyStyleCollect() {
        return this.survey.intent === SurveyIntent.COLLECT;
    }
    constructor(el, survey, changeDetectorRef, question, editorConfig) {
        this.el = el;
        this.survey = survey;
        this.changeDetectorRef = changeDetectorRef;
        this.question = question;
        this.editorConfig = editorConfig;
        this.SurveyIntent = SurveyIntent;
        this.surveyChange = survey.events.subscribe(e => {
            this.changeDetectorRef.detectChanges();
        });
    }
    ngOnDestroy() {
        this.surveyChange.unsubscribe();
    }
    // needed for the offsetTopParent directive
    ngDoCheck() { }
}
QuestionEditorBooleanComponent.ɵfac = function QuestionEditorBooleanComponent_Factory(t) { return new (t || QuestionEditorBooleanComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(Survey), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(QUESTION), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG)); };
QuestionEditorBooleanComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionEditorBooleanComponent, selectors: [["boolean"]], viewQuery: function QuestionEditorBooleanComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0$c, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.autosize = _t.first);
    } }, hostVars: 4, hostBindings: function QuestionEditorBooleanComponent_HostBindings(rf, ctx) { if (rf & 2) {
        i0.ɵɵclassProp("survey-style-compare", ctx.isSurveyStyleCompare)("survey-style-collect", ctx.isSurveyStyleCollect);
    } }, decls: 2, vars: 2, consts: [[4, "ngIf"], [1, "listset-options", 3, "offsetTopParent", "offsetTopDoCheck"], [1, "listset-options-title"], ["cellspacing", "0", "cellpadding", "0", 1, "simple-table"], [1, "cell-text"], [1, "cell-value"], [1, "cell-value", "cell-numeric"], ["type", "number", "matInput", "", 1, "inline-input", 3, "scoreInput", "scoreInputConditional", "ngModel", "ngModelChange"], ["id", "comparePreferredResponse", 4, "ngIf"], ["id", "compareFeedbackMismatch"], ["matInput", "", "fuzzyMetaProperty", "feedbackMismatch", "cdkTextareaAutosize", "", "cdkAutosizeMinRows", "1", "cdkAutosizeMaxRows", "5"], ["autosize", "cdkTextareaAutosize"], ["id", "comparePreferredResponse"], [3, "ngModel", "ngModelChange"], [3, "value"]], template: function QuestionEditorBooleanComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, QuestionEditorBooleanComponent_ng_container_0_Template, 24, 14, "ng-container", 0);
        i0.ɵɵtemplate(1, QuestionEditorBooleanComponent_ng_container_1_Template, 8, 1, "ng-container", 0);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.editorConfig.recommendations && ctx.editorConfig.permissions.recommendations.edit);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.survey.intent === ctx.SurveyIntent.COMPARE);
    } }, dependencies: [i1$2.NgIf, i3.DefaultValueAccessor, i3.NumberValueAccessor, i3.NgControlStatus, i3.NgModel, i3$2.MatRadioGroup, i3$2.MatRadioButton, i4$1.MatFormField, i4$1.MatLabel, i5.MatInput, i7.CdkTextareaAutosize, OffsetTopParentDirective, ScoreInputDirective, FuzzyMetaPropertyDirective, BooleanLabelPipe], styles: [".survey-style-collect[_nghost-%COMP%]{display:flex}.listset-options[_ngcontent-%COMP%]{width:170px}.listset-options[_ngcontent-%COMP%]:first-child{margin-left:220px}.listset-options-title[_ngcontent-%COMP%]{margin-top:18px}#comparePreferredResponse[_ngcontent-%COMP%]{display:flex;align-items:center;margin-top:24px}#comparePreferredResponse[_ngcontent-%COMP%]   label[_ngcontent-%COMP%]{font-weight:700;margin-right:30px;cursor:default}#comparePreferredResponse[_ngcontent-%COMP%]   mat-radio-group[_ngcontent-%COMP%]   mat-radio-button[_ngcontent-%COMP%]{margin-right:15px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorBooleanComponent, [{
        type: Component,
        args: [{ selector: 'boolean', template: "<ng-container *ngIf=\"editorConfig.recommendations && editorConfig.permissions.recommendations.edit\">\n\n\t<div class=\"listset-options\"\n\t\t [offsetTopParent]=\"el.nativeElement\"\n\t\t [offsetTopDoCheck]=\"this\">\n\n\t\t<h5 class=\"listset-options-title\">Assign Scores</h5>\n\n\t\t<table class=\"simple-table\"\n\t\t\t   cellspacing=\"0\"\n\t\t\t   cellpadding=\"0\">\n\t\t\t<thead>\n\t\t\t\t<tr>\n\t\t\t\t\t<th class=\"cell-text\">Answer</th>\n\t\t\t\t\t<th class=\"cell-value\">Value</th>\n\t\t\t\t</tr>\n\t\t\t</thead>\n\t\t\t<tbody>\n\n\t\t\t\t<tr>\n\t\t\t\t\t<td class=\"cell-text\">{{ 1 | booleanLabel }}</td>\n\t\t\t\t\t<td class=\"cell-value cell-numeric\">\n\t\t\t\t\t\t<input type=\"number\"\n\t\t\t\t\t\t\t   class=\"inline-input\"\n\t\t\t\t\t\t\t   matInput\n\t\t\t\t\t\t\t   [scoreInput]=\"question\"\n\t\t\t\t\t\t\t   [scoreInputConditional]=\"1\"\n\t\t\t\t\t\t\t   [(ngModel)]=\"question.meta.scoreBoolean1\" />\n\t\t\t\t\t</td>\n\t\t\t\t</tr>\n\n\t\t\t\t<tr>\n\t\t\t\t\t<td class=\"cell-text\">{{ 2 | booleanLabel }}</td>\n\t\t\t\t\t<td class=\"cell-value cell-numeric\">\n\t\t\t\t\t\t<input type=\"number\"\n\t\t\t\t\t\t\t   class=\"inline-input\"\n\t\t\t\t\t\t\t   matInput\n\t\t\t\t\t\t\t   [scoreInput]=\"question\"\n\t\t\t\t\t\t\t   [scoreInputConditional]=\"2\"\n\t\t\t\t\t\t\t   [(ngModel)]=\"question.meta.scoreBoolean2\" />\n\t\t\t\t\t</td>\n\t\t\t\t</tr>\n\t\t\t</tbody>\n\t\t</table>\n\n\t</div>\n\n</ng-container>\n\n<ng-container *ngIf=\"survey.intent === SurveyIntent.COMPARE\">\n\t<div id=\"comparePreferredResponse\"\n\t\t *ngIf=\"editorConfig.permissions.questions.edit\">\n\t\t<label>Preferred Response</label>\n\t\t<mat-radio-group [(ngModel)]=\"question.meta.preferredResponse\">\n\t\t\t<mat-radio-button [value]=\"1\">{{ 1 | booleanLabel }}</mat-radio-button>\n\t\t\t<mat-radio-button [value]=\"2\">{{ 2 | booleanLabel }}</mat-radio-button>\n\t\t</mat-radio-group>\n\t</div>\n\n\t<div id=\"compareFeedbackMismatch\">\n\t\t<mat-form-field>\n\t\t\t<mat-label>Feedback when responses do not match:</mat-label>\n\t\t\t<textarea matInput\n\t\t\t\t\t  fuzzyMetaProperty=\"feedbackMismatch\"\n\t\t\t\t\t  cdkTextareaAutosize\n\t\t\t\t\t  cdkAutosizeMinRows=\"1\"\n\t\t\t\t\t  cdkAutosizeMaxRows=\"5\"\n\t\t\t\t\t  #autosize=\"cdkTextareaAutosize\"></textarea>\n\t\t</mat-form-field>\n\t</div>\n\n</ng-container>\n", styles: [":host.survey-style-collect{display:flex}.listset-options{width:170px}.listset-options:first-child{margin-left:220px}.listset-options-title{margin-top:18px}#comparePreferredResponse{display:flex;align-items:center;margin-top:24px}#comparePreferredResponse label{font-weight:700;margin-right:30px;cursor:default}#comparePreferredResponse mat-radio-group mat-radio-button{margin-right:15px}\n"] }]
    }], function () { return [{ type: i0.ElementRef }, { type: Survey }, { type: i0.ChangeDetectorRef }, { type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }]; }, { autosize: [{
            type: ViewChild,
            args: ['autosize', { static: false }]
        }], isSurveyStyleCompare: [{
            type: HostBinding,
            args: ['class.survey-style-compare']
        }], isSurveyStyleCollect: [{
            type: HostBinding,
            args: ['class.survey-style-collect']
        }] }); })();

const QuestionEditorComponentMap = new Map([
    [QuestionType.YES_NO_UNCERTAIN, QuestionEditorYesNoUncertain],
    [QuestionType.PICKLIST, QuestionEditorPicklistComponent],
    [QuestionType.CHECKLIST, QuestionEditorChecklistComponent],
    [QuestionType.RADIOSET, QuestionEditorRadiosetComponent],
    [QuestionType.DATE, QuestionEditorDateComponent],
    [QuestionType.TEXT, QuestionEditorTextComponent],
    [QuestionType.TEXTAREA, QuestionEditorTextareaComponent],
    [QuestionType.SCALE, QuestionEditorScaleComponent],
    [QuestionType.NUMBER, QuestionEditorNumberComponent],
    [QuestionType.BOOLEAN, QuestionEditorBooleanComponent]
]);

class VersionPromptComponent {
    constructor(ref) {
        this.ref = ref;
    }
}
VersionPromptComponent.ɵfac = function VersionPromptComponent_Factory(t) { return new (t || VersionPromptComponent)(i0.ɵɵdirectiveInject(i1$3.MatDialogRef)); };
VersionPromptComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: VersionPromptComponent, selectors: [["ng-component"]], decls: 7, vars: 0, consts: [[1, "buttons-confirm"], ["mat-flat-button", "", "color", "primary", 3, "click"], ["mat-flat-button", "", "color", "error", 3, "click"]], template: function VersionPromptComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "p");
        i0.ɵɵtext(1, "You are making an important change to this survey. Should you be creating a new version, or is the change minor?");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(2, "div", 0)(3, "button", 1);
        i0.ɵɵlistener("click", function VersionPromptComponent_Template_button_click_3_listener() { return ctx.ref.close(true); });
        i0.ɵɵtext(4, "Create New Version");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(5, "button", 2);
        i0.ɵɵlistener("click", function VersionPromptComponent_Template_button_click_5_listener() { return ctx.ref.close(false); });
        i0.ɵɵtext(6, "Keep Current Version");
        i0.ɵɵelementEnd()();
    } }, dependencies: [i2.MatButton], styles: [".buttons-confirm[_ngcontent-%COMP%]   button[_ngcontent-%COMP%] + button[_ngcontent-%COMP%]{margin-left:18px}button.mat-error[_ngcontent-%COMP%]{display:inline-block}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(VersionPromptComponent, [{
        type: Component,
        args: [{ template: `
	<p>You are making an important change to this survey. Should you be creating a new version, or is the change minor?</p>
	<div class='buttons-confirm'>
		<button mat-flat-button color="primary" (click)="ref.close(true)">Create New Version</button>
		<button mat-flat-button color="error" (click)="ref.close(false)">Keep Current Version</button>
	</div>
	`, styles: [".buttons-confirm button+button{margin-left:18px}button.mat-error{display:inline-block}\n"] }]
    }], function () { return [{ type: i1$3.MatDialogRef }]; }, null); })();

var VersionPromptStatus;
(function (VersionPromptStatus) {
    VersionPromptStatus[VersionPromptStatus["CURRENT_VERSION"] = 0] = "CURRENT_VERSION";
    VersionPromptStatus[VersionPromptStatus["NEW_VERSION"] = 1] = "NEW_VERSION";
})(VersionPromptStatus || (VersionPromptStatus = {}));
;
/**
 * This class is responsible for displaying the prompt for whether a change should start a new version
 * The survey data is posted to the wp post edit page in an input
 * We will also append a form input to indicate whether there is a new version or not
 * The main form that's posting is <form id="post"></form>
 *
 * It may be desirable to show the state ( Current / New ) somewhere on the page with the ability to change it
 */
class VersionPromptService {
    get isNewVersion() {
        return this.status === VersionPromptStatus.NEW_VERSION;
    }
    get isCurrentVersion() {
        return this.status === VersionPromptStatus.CURRENT_VERSION;
    }
    constructor(dialog, editorConfig) {
        this.dialog = dialog;
        this.editorConfig = editorConfig;
        this.status = VersionPromptStatus.CURRENT_VERSION;
        this.containingForm = document.getElementById('post');
        this.versionElement = document.createElement('input');
        this.versionElement.type = 'hidden';
        this.versionElement.value = '1';
        this.versionElement.name = 'alpha_survey_new_version';
    }
    prompt() {
        if (!this.editorConfig.supports.versioning) {
            return;
        }
        if (!this.promptedOnce) {
            this.dialog.open(VersionPromptComponent, {
                disableClose: true
            }).afterClosed().subscribe(status => {
                if (status) {
                    this.setNewVersion();
                }
                else {
                    this.setCurrentVersion();
                }
            });
            this.promptedOnce = true;
        }
    }
    setNewVersion() {
        this.status = VersionPromptStatus.NEW_VERSION;
        this.containingForm.appendChild(this.versionElement);
    }
    setCurrentVersion() {
        this.status = VersionPromptStatus.CURRENT_VERSION;
        if (this.versionElement.parentElement) {
            this.containingForm.removeChild(this.versionElement);
        }
    }
}
VersionPromptService.ɵfac = function VersionPromptService_Factory(t) { return new (t || VersionPromptService)(i0.ɵɵinject(i1$3.MatDialog), i0.ɵɵinject(SURVEY_EDITOR_CONFIG)); };
VersionPromptService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: VersionPromptService, factory: VersionPromptService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(VersionPromptService, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], function () { return [{ type: i1$3.MatDialog }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }]; }, null); })();

const _c0$b = ["portalHost"];
function QuestionEditorComponent_mat_form_field_1_mat_option_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "mat-option", 6);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const type_r5 = ctx.$implicit;
    i0.ɵɵproperty("value", type_r5.type);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", type_r5.viewValue, " ");
} }
function QuestionEditorComponent_mat_form_field_1_Template(rf, ctx) { if (rf & 1) {
    const _r7 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-form-field")(1, "mat-label");
    i0.ɵɵtext(2, "Question Type");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "mat-select", 4);
    i0.ɵɵlistener("valueChange", function QuestionEditorComponent_mat_form_field_1_Template_mat_select_valueChange_3_listener($event) { i0.ɵɵrestoreView(_r7); const ctx_r6 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r6.onQuestionTypeChange($event)); });
    i0.ɵɵtemplate(4, QuestionEditorComponent_mat_form_field_1_mat_option_4_Template, 2, 2, "mat-option", 5);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("value", ctx_r0.question.questionType);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", ctx_r0.questionTypes);
} }
function QuestionEditorComponent_ng_container_2_div_1_Template(rf, ctx) { if (rf & 1) {
    const _r11 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div")(1, "mat-form-field")(2, "input", 8);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorComponent_ng_container_2_div_1_Template_input_ngModelChange_2_listener($event) { const restoredCtx = i0.ɵɵrestoreView(_r11); const field_r9 = restoredCtx.$implicit; const ctx_r10 = i0.ɵɵnextContext(2); return i0.ɵɵresetView((ctx_r10.question.meta[field_r9.field] = $event)); });
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const field_r9 = ctx.$implicit;
    const ctx_r8 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("placeholder", field_r9.placeholder)("ngModel", ctx_r8.question.meta[field_r9.field]);
} }
function QuestionEditorComponent_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, QuestionEditorComponent_ng_container_2_div_1_Template, 3, 2, "div", 7);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", ctx_r1.editorConfig.additionalQuestionFields);
} }
function QuestionEditorComponent_div_5_div_3_Template(rf, ctx) { if (rf & 1) {
    const _r15 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 15)(1, "mat-checkbox", 16);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorComponent_div_5_div_3_Template_mat_checkbox_ngModelChange_1_listener($event) { i0.ɵɵrestoreView(_r15); const ctx_r14 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r14.question.optional = $event); });
    i0.ɵɵtext(2, "Optional ");
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r12 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngModel", ctx_r12.question.optional);
} }
function QuestionEditorComponent_div_5_ng_container_5_input_2_Template(rf, ctx) { if (rf & 1) {
    const _r18 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "input", 18);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorComponent_div_5_ng_container_5_input_2_Template_input_ngModelChange_0_listener($event) { i0.ɵɵrestoreView(_r18); const ctx_r17 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r17.question.meta.customOrder = $event); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r16 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("ngModel", ctx_r16.question.meta.customOrder);
} }
function QuestionEditorComponent_div_5_ng_container_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵprojection(1);
    i0.ɵɵtemplate(2, QuestionEditorComponent_div_5_ng_container_5_input_2_Template, 1, 1, "input", 17);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r13 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ctx_r13.survey.presentation === ctx_r13.SurveyPresentation.PSYCHOMETRIC && ctx_r13.editorConfig.permissions.questions.edit);
} }
function QuestionEditorComponent_div_5_Template(rf, ctx) { if (rf & 1) {
    const _r20 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 9)(1, "div", 10);
    i0.ɵɵelement(2, "span", 11);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(3, QuestionEditorComponent_div_5_div_3_Template, 3, 1, "div", 12);
    i0.ɵɵelementStart(4, "div", 13);
    i0.ɵɵtemplate(5, QuestionEditorComponent_div_5_ng_container_5_Template, 3, 1, "ng-container", 1);
    i0.ɵɵelementStart(6, "button", 14);
    i0.ɵɵlistener("click", function QuestionEditorComponent_div_5_Template_button_click_6_listener($event) { i0.ɵɵrestoreView(_r20); const ctx_r19 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r19.removeQuestion($event)); });
    i0.ɵɵtext(7, "Remove Question");
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("innerHTML", ctx_r3.question.prefix, i0.ɵɵsanitizeHtml);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r3.editorConfig.permissions.questions.edit);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", !ctx_r3.question.conditional);
} }
const _c1$2 = [[["", 8, "add-conditional-button"]]];
const _c2$2 = [".add-conditional-button"];
class QuestionEditorComponent {
    constructor(cd, injector, componentFactoryResolver, appRef, el, versionService, editorConfig, survey) {
        this.cd = cd;
        this.injector = injector;
        this.componentFactoryResolver = componentFactoryResolver;
        this.appRef = appRef;
        this.el = el;
        this.versionService = versionService;
        this.editorConfig = editorConfig;
        this.survey = survey;
        this.SurveyPresentation = SurveyPresentation;
        this.remove = new EventEmitter();
    }
    ngOnChanges(c) {
        if (c.isOpen && this.componentRef) {
            this.componentRef.instance.changeDetectorRef.markForCheck();
        }
    }
    ngAfterViewInit() {
        const ynuPipe = new YesNoUncertainLabelPipe();
        const booleanPipe = new BooleanLabelPipe();
        this.questionTypes = [
            {
                viewValue: [ynuPipe.transform(1), ynuPipe.transform(2), ynuPipe.transform(3)].join(' / '),
                type: QuestionType.YES_NO_UNCERTAIN
            },
            {
                viewValue: [booleanPipe.transform(1), booleanPipe.transform(2)].join(' / '),
                type: QuestionType.BOOLEAN
            },
            //{
            //	viewValue: 'Scale',
            //	type: QuestionType.SCALE
            //},
            {
                viewValue: 'Text',
                type: QuestionType.TEXT
            },
            {
                viewValue: 'Number',
                type: QuestionType.NUMBER
            },
            {
                viewValue: 'Date',
                type: QuestionType.DATE
            },
            {
                viewValue: 'Picklist',
                type: QuestionType.PICKLIST
            },
            {
                viewValue: 'Checklist',
                type: QuestionType.CHECKLIST
            },
            {
                viewValue: 'Radioset',
                type: QuestionType.RADIOSET
            },
            {
                viewValue: 'Textarea',
                type: QuestionType.TEXTAREA
            },
        ];
        this.portalHost = new DomPortalHost(this.portalHostContainer.nativeElement, this.componentFactoryResolver, this.appRef, this.injector);
        this.createComponentPortal();
        this.cd.markForCheck();
        this.cd.detectChanges();
    }
    createComponentPortal() {
        const component = QuestionEditorComponentMap.get(this.question.questionType);
        if (!component) {
            //console.error( 'No Component registered for: ' + this.question.questionType );
            return;
        }
        if (this.componentRef) {
            this.portalHost.detach();
        }
        const injector = new PortalInjector(this.injector, new WeakMap([
            [QUESTION, this.question]
        ]));
        this.portal = new ComponentPortal(component, null, injector);
        this.componentRef = this.portalHost.attach(this.portal);
    }
    onQuestionTypeChange(qt) {
        this.question.questionType = qt;
        this.createComponentPortal();
        this.versionService.prompt();
    }
    removeQuestion(event) {
        event.preventDefault();
        this.remove.emit(this.question);
    }
}
QuestionEditorComponent.ɵfac = function QuestionEditorComponent_Factory(t) { return new (t || QuestionEditorComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.Injector), i0.ɵɵdirectiveInject(i0.ComponentFactoryResolver), i0.ɵɵdirectiveInject(i0.ApplicationRef), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(VersionPromptService), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG), i0.ɵɵdirectiveInject(Survey)); };
QuestionEditorComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionEditorComponent, selectors: [["question-editor"]], viewQuery: function QuestionEditorComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0$b, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.portalHostContainer = _t.first);
    } }, inputs: { isOpen: "isOpen", question: "question" }, outputs: { remove: "remove" }, features: [i0.ɵɵNgOnChangesFeature], ngContentSelectors: _c2$2, decls: 6, vars: 3, consts: [[1, "survey-item-content"], [4, "ngIf"], ["portalHost", ""], ["class", "survey-item-footer", 4, "ngIf"], [3, "value", "valueChange"], [3, "value", 4, "ngFor", "ngForOf"], [3, "value"], [4, "ngFor", "ngForOf"], ["matInput", "", 3, "placeholder", "ngModel", "ngModelChange"], [1, "survey-item-footer"], [1, "survey-item-footer-index"], [1, "title-prefix", 3, "innerHTML"], ["class", "survey-item-optional", 4, "ngIf"], [1, "align-right"], ["mat-flat-button", "", "color", "accent", "version-prompt", "", 3, "click"], [1, "survey-item-optional"], ["color", "primary", 3, "ngModel", "ngModelChange"], ["class", "custom-order-input", "placeholder", "Custom Order", 3, "ngModel", "ngModelChange", 4, "ngIf"], ["placeholder", "Custom Order", 1, "custom-order-input", 3, "ngModel", "ngModelChange"]], template: function QuestionEditorComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef(_c1$2);
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵtemplate(1, QuestionEditorComponent_mat_form_field_1_Template, 5, 2, "mat-form-field", 1);
        i0.ɵɵtemplate(2, QuestionEditorComponent_ng_container_2_Template, 2, 1, "ng-container", 1);
        i0.ɵɵelement(3, "div", null, 2);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(5, QuestionEditorComponent_div_5_Template, 8, 3, "div", 3);
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.editorConfig.permissions.questions.edit);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.editorConfig.additionalQuestionFields && ctx.editorConfig.permissions.questions.edit);
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("ngIf", ctx.editorConfig.permissions.questions.remove);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i3.DefaultValueAccessor, i3.NgControlStatus, i3.NgModel, i2.MatButton, i4$1.MatFormField, i4$1.MatLabel, i4$2.MatSelect, i5$1.MatOption, i5.MatInput, i3$3.MatCheckbox], styles: ["[_nghost-%COMP%]{display:block}[_nghost-%COMP%]     #compareFeedbackMismatch{margin-top:24px}[_nghost-%COMP%]     #compareFeedbackMismatch mat-form-field{width:100%}.survey-item-content[_ngcontent-%COMP%]{padding:20px;position:relative}.survey-item-optional[_ngcontent-%COMP%]{margin:12px 0}.custom-order-input[_ngcontent-%COMP%]{margin:0 0 0 auto;border:none;border-left:1px solid #ccc;width:105px;text-align:center}  .add-conditional-button.alignright+.custom-order-input{margin-left:0}", ".survey-item-footer[_ngcontent-%COMP%]{border-top:1px solid #ccc;display:flex;align-items:center;height:50px}.survey-item-footer-index[_ngcontent-%COMP%]{padding:0 10px;display:flex;align-items:center;justify-content:center;align-self:stretch;border-right:1px solid #ccc}.survey-item-footer[_ngcontent-%COMP%]   .survey-item-optional[_ngcontent-%COMP%]{margin-left:12px;margin-right:12px}.survey-item-footer[_ngcontent-%COMP%]   .align-right[_ngcontent-%COMP%]{margin-left:auto}.survey-item-footer[_ngcontent-%COMP%]   .align-right[_ngcontent-%COMP%]     button{margin-right:8px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorComponent, [{
        type: Component,
        args: [{ selector: 'question-editor', template: "<div class=\"survey-item-content\">\n\n\t<mat-form-field *ngIf=\"editorConfig.permissions.questions.edit\">\n\t\t<mat-label>Question Type</mat-label>\n\t\t<mat-select [value]=\"question.questionType\"\n\t\t\t\t\t(valueChange)=\"onQuestionTypeChange($event)\">\n\t\t\t<mat-option *ngFor=\"let type of questionTypes\"\n\t\t\t\t\t\t[value]=\"type.type\">\n\t\t\t\t{{type.viewValue}}\n\t\t\t</mat-option>\n\n\n\t\t</mat-select>\n\t</mat-form-field>\n\n\t<!-- Additional Static Fields-->\n\t<ng-container *ngIf=\"editorConfig.additionalQuestionFields && editorConfig.permissions.questions.edit\">\n\n\t\t<div *ngFor=\"let field of editorConfig.additionalQuestionFields\">\n\t\t\t<mat-form-field>\n\t\t\t\t<input matInput\n\t\t\t\t\t   [placeholder]=\"field.placeholder\"\n\t\t\t\t\t   [(ngModel)]=\"question.meta[field.field]\" />\n\t\t\t</mat-form-field>\n\t\t</div>\n\n\t</ng-container>\n\n\t<!-- Question Type Editor Output -->\n\t<div #portalHost></div>\n\n</div>\n\n<div class=\"survey-item-footer\"\n\t *ngIf=\"editorConfig.permissions.questions.remove\">\n\n\t<div class=\"survey-item-footer-index\">\n\t\t<span class=\"title-prefix\"\n\t\t\t  [innerHTML]=\"$any(question).prefix\"></span>\n\t</div>\n\t<div *ngIf=\"editorConfig.permissions.questions.edit\"\n\t\t class=\"survey-item-optional\">\n\t\t<mat-checkbox color=\"primary\"\n\t\t\t\t\t  [(ngModel)]=\"question.optional\">Optional\n\t\t</mat-checkbox>\n\t</div>\n\n\t<div class=\"align-right\">\n\n\n\t\t<ng-container *ngIf=\"!question.conditional\">\n\n\t\t\t<ng-content select=\".add-conditional-button\"></ng-content>\n\n\t\t\t<input *ngIf=\"survey.presentation === SurveyPresentation.PSYCHOMETRIC && editorConfig.permissions.questions.edit\"\n\t\t\t\t   class=\"custom-order-input\"\n\t\t\t\t   [(ngModel)]=\"question.meta.customOrder\"\n\t\t\t\t   placeholder=\"Custom Order\" />\n\n\t\t</ng-container>\n\n\t\t<button mat-flat-button\n\t\t\t\tcolor=\"accent\"\n\t\t\t\tversion-prompt\n\t\t\t\t(click)=\"removeQuestion( $event )\">Remove Question</button>\n\n\t</div>\n</div>\n", styles: [":host{display:block}:host ::ng-deep #compareFeedbackMismatch{margin-top:24px}:host ::ng-deep #compareFeedbackMismatch mat-form-field{width:100%}.survey-item-content{padding:20px;position:relative}.survey-item-optional{margin:12px 0}.custom-order-input{margin:0 0 0 auto;border:none;border-left:1px solid #ccc;width:105px;text-align:center}::ng-deep .add-conditional-button.alignright+.custom-order-input{margin-left:0}\n", ".survey-item-footer{border-top:1px solid #ccc;display:flex;align-items:center;height:50px}.survey-item-footer-index{padding:0 10px;display:flex;align-items:center;justify-content:center;align-self:stretch;border-right:1px solid #ccc}.survey-item-footer .survey-item-optional{margin-left:12px;margin-right:12px}.survey-item-footer .align-right{margin-left:auto}.survey-item-footer .align-right ::ng-deep button{margin-right:8px}\n"] }]
    }], function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.Injector }, { type: i0.ComponentFactoryResolver }, { type: i0.ApplicationRef }, { type: i0.ElementRef }, { type: VersionPromptService }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }, { type: Survey }]; }, { portalHostContainer: [{
            type: ViewChild,
            args: ['portalHost']
        }], isOpen: [{
            type: Input
        }], question: [{
            type: Input
        }], remove: [{
            type: Output
        }] }); })();

class VersionPromptDirective {
    constructor(service, el) {
        this.changeSubscription = fromEvent(el.nativeElement, 'change').subscribe(e => service.prompt());
        if (el.nativeElement.nodeName === 'BUTTON') {
            this.clickSubscription = fromEvent(el.nativeElement, 'click').subscribe(e => service.prompt());
        }
    }
    ngOnDestroy() {
        this.changeSubscription.unsubscribe();
        if (this.clickSubscription) {
            this.clickSubscription.unsubscribe;
        }
    }
}
VersionPromptDirective.ɵfac = function VersionPromptDirective_Factory(t) { return new (t || VersionPromptDirective)(i0.ɵɵdirectiveInject(VersionPromptService), i0.ɵɵdirectiveInject(i0.ElementRef)); };
VersionPromptDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: VersionPromptDirective, selectors: [["", "version-prompt", ""]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(VersionPromptDirective, [{
        type: Directive,
        args: [{
                selector: '[version-prompt]'
            }]
    }], function () { return [{ type: VersionPromptService }, { type: i0.ElementRef }]; }, null); })();

function SurveyGroupComponent_h3_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "h3", 4);
    i0.ɵɵtext(1, "Conditional Questions");
    i0.ɵɵelementEnd();
} }
function SurveyGroupComponent_survey_item_2_Template(rf, ctx) { if (rf & 1) {
    const _r6 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "survey-item", 5);
    i0.ɵɵlistener("remove", function SurveyGroupComponent_survey_item_2_Template_survey_item_remove_0_listener($event) { i0.ɵɵrestoreView(_r6); const ctx_r5 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r5.removeItem($event)); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const item_r3 = ctx.$implicit;
    const i_r4 = ctx.index;
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("index", i_r4 + 1)("item", item_r3)("conditionalQuestion", ctx_r1.conditionalQuestion)("prefix", ctx_r1.prefix)("depth", ctx_r1.depth + 1);
} }
function SurveyGroupComponent_div_3_ng_container_4_mat_checkbox_1_Template(rf, ctx) { if (rf & 1) {
    const _r15 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-checkbox", 14);
    i0.ɵɵlistener("ngModelChange", function SurveyGroupComponent_div_3_ng_container_4_mat_checkbox_1_Template_mat_checkbox_ngModelChange_0_listener($event) { i0.ɵɵrestoreView(_r15); const ctx_r14 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r14.group.meta.enumerate = $event); });
    i0.ɵɵtext(1, " Enumerate ");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r12 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("ngModel", ctx_r12.group.meta.enumerate);
} }
function SurveyGroupComponent_div_3_ng_container_4_mat_checkbox_4_Template(rf, ctx) { if (rf & 1) {
    const _r17 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-checkbox", 14);
    i0.ɵɵlistener("ngModelChange", function SurveyGroupComponent_div_3_ng_container_4_mat_checkbox_4_Template_mat_checkbox_ngModelChange_0_listener($event) { i0.ɵɵrestoreView(_r17); const ctx_r16 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r16.group.meta.showOnGuestOverview = $event); });
    i0.ɵɵtext(1, " Show on Guest Overview ");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r13 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("ngModel", ctx_r13.group.meta.showOnGuestOverview);
} }
function SurveyGroupComponent_div_3_ng_container_4_Template(rf, ctx) { if (rf & 1) {
    const _r19 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SurveyGroupComponent_div_3_ng_container_4_mat_checkbox_1_Template, 2, 1, "mat-checkbox", 13);
    i0.ɵɵelementStart(2, "mat-checkbox", 14);
    i0.ɵɵlistener("ngModelChange", function SurveyGroupComponent_div_3_ng_container_4_Template_mat_checkbox_ngModelChange_2_listener($event) { i0.ɵɵrestoreView(_r19); const ctx_r18 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r18.group.meta.ownPage = $event); });
    i0.ɵɵtext(3, " Display on own page ");
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(4, SurveyGroupComponent_div_3_ng_container_4_mat_checkbox_4_Template, 2, 1, "mat-checkbox", 13);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r7 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r7.depth === 1 && ctx_r7.survey.presentation === ctx_r7.SurveyPresentation.NORMAL);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngModel", ctx_r7.group.meta.ownPage);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ctx_r7.depth === 1 && ctx_r7.survey.intent === ctx_r7.SurveyIntent.COMPARE);
} }
function SurveyGroupComponent_div_3_ng_container_5_div_1_Template(rf, ctx) { if (rf & 1) {
    const _r23 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 16)(1, "mat-form-field")(2, "input", 17);
    i0.ɵɵlistener("ngModelChange", function SurveyGroupComponent_div_3_ng_container_5_div_1_Template_input_ngModelChange_2_listener($event) { const restoredCtx = i0.ɵɵrestoreView(_r23); const field_r21 = restoredCtx.$implicit; const ctx_r22 = i0.ɵɵnextContext(3); return i0.ɵɵresetView((ctx_r22.group.meta[field_r21.field] = $event)); });
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const field_r21 = ctx.$implicit;
    const ctx_r20 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("placeholder", field_r21.placeholder)("ngModel", ctx_r20.group.meta[field_r21.field]);
} }
function SurveyGroupComponent_div_3_ng_container_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SurveyGroupComponent_div_3_ng_container_5_div_1_Template, 3, 2, "div", 15);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r8 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", ctx_r8.editorConfig.additionalGroupFields);
} }
function SurveyGroupComponent_div_3_button_7_Template(rf, ctx) { if (rf & 1) {
    const _r25 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 18);
    i0.ɵɵlistener("click", function SurveyGroupComponent_div_3_button_7_Template_button_click_0_listener($event) { i0.ɵɵrestoreView(_r25); const ctx_r24 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r24.addQuestion($event)); });
    i0.ɵɵtext(1, "Add Question");
    i0.ɵɵelementEnd();
} }
function SurveyGroupComponent_div_3_button_8_Template(rf, ctx) { if (rf & 1) {
    const _r27 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 18);
    i0.ɵɵlistener("click", function SurveyGroupComponent_div_3_button_8_Template_button_click_0_listener($event) { i0.ɵɵrestoreView(_r27); const ctx_r26 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r26.addGroup($event)); });
    i0.ɵɵtext(1, "Add Group");
    i0.ɵɵelementEnd();
} }
function SurveyGroupComponent_div_3_button_9_Template(rf, ctx) { if (rf & 1) {
    const _r29 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 19);
    i0.ɵɵlistener("click", function SurveyGroupComponent_div_3_button_9_Template_button_click_0_listener($event) { i0.ɵɵrestoreView(_r29); const ctx_r28 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r28.removeGroup($event)); });
    i0.ɵɵtext(1, "Remove Group");
    i0.ɵɵelementEnd();
} }
function SurveyGroupComponent_div_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 6)(1, "div", 7)(2, "span", 8);
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd()();
    i0.ɵɵtemplate(4, SurveyGroupComponent_div_3_ng_container_4_Template, 5, 3, "ng-container", 9);
    i0.ɵɵtemplate(5, SurveyGroupComponent_div_3_ng_container_5_Template, 2, 1, "ng-container", 9);
    i0.ɵɵelementStart(6, "div", 10);
    i0.ɵɵtemplate(7, SurveyGroupComponent_div_3_button_7_Template, 2, 0, "button", 11);
    i0.ɵɵtemplate(8, SurveyGroupComponent_div_3_button_8_Template, 2, 0, "button", 11);
    i0.ɵɵtemplate(9, SurveyGroupComponent_div_3_button_9_Template, 2, 0, "button", 12);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1("", ctx_r2.group.prefix, ".");
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r2.editorConfig.permissions.groups.edit);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r2.editorConfig.additionalGroupFields && ctx_r2.editorConfig.permissions.questions.edit);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ctx_r2.editorConfig.permissions.questions.add);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r2.editorConfig.permissions.groups.add && (!ctx_r2.editorConfig.maxDepth || ctx_r2.depth < ctx_r2.editorConfig.maxDepth));
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r2.editorConfig.permissions.groups.remove);
} }
class SurveyGroupComponent {
    get isGuestOverviewGroup() {
        return this.group.meta.showOnGuestOverview;
    }
    constructor(cd, survey, editorConfig) {
        this.cd = cd;
        this.survey = survey;
        this.editorConfig = editorConfig;
        this.remove = new EventEmitter();
        this.SurveyIntent = SurveyIntent;
        this.SurveyPresentation = SurveyPresentation;
        this.surveyChange = survey.events.subscribe(e => {
            this.cd.detectChanges();
        });
    }
    ngOnDestroy() {
        this.surveyChange.unsubscribe();
    }
    addQuestion(event) {
        event.preventDefault();
        const question = new Question();
        question.id = this.survey.meta.insert_id++;
        question.expanded = true;
        /**
         * If an 'onAdd' callback is specified, call it before adding the question to the group
         */
        if (this.editorConfig.questions?.onAdd && window[this.editorConfig.questions.onAdd]) {
            const cb = window[this.editorConfig.questions.onAdd];
            try {
                cb(question, this.group, this.survey);
            }
            catch (error) {
                console.error(error);
            }
        }
        this.group.addQuestion(question);
        this.cd.markForCheck();
        this.cd.detectChanges();
    }
    addGroup(event) {
        event.preventDefault();
        const group = new QuestionGroup();
        group.id = this.survey.meta.insert_id++;
        /**
         * If an 'onAdd' callback is specified, call it before adding the group to the group
         */
        if (this.editorConfig.groups?.onAdd && window[this.editorConfig.groups.onAdd]) {
            const cb = window[this.editorConfig.groups.onAdd];
            try {
                cb(group, this.group, this.survey);
            }
            catch (error) {
                console.error(error);
            }
        }
        group.expanded = true;
        this.group.addGroup(group);
        this.cd.markForCheck();
        this.cd.detectChanges();
    }
    removeGroup(event) {
        event.preventDefault();
        this.remove.emit(this.group);
    }
    removeItem(item) {
        this.group.items.splice(this.group.items.indexOf(item), 1);
        this.cd.markForCheck();
        this.cd.detectChanges();
    }
}
SurveyGroupComponent.ɵfac = function SurveyGroupComponent_Factory(t) { return new (t || SurveyGroupComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(Survey), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG)); };
SurveyGroupComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: SurveyGroupComponent, selectors: [["survey-group"]], hostVars: 2, hostBindings: function SurveyGroupComponent_HostBindings(rf, ctx) { if (rf & 2) {
        i0.ɵɵclassProp("guest-overview-group", ctx.isGuestOverviewGroup);
    } }, inputs: { group: "group", depth: "depth", prefix: "prefix", conditionalQuestion: "conditionalQuestion" }, outputs: { remove: "remove" }, decls: 4, vars: 5, consts: [["class", "conditional-title", 4, "ngIf"], ["cdkDropList", "", "cdkDropListLockAxis", "y", 1, "survey-item-content", "drag-list", 3, "cdkDropListDisabled", "cdkDropListData"], ["cdkDrag", "", "cdkDragBoundary", ".drag-list", 3, "index", "item", "conditionalQuestion", "prefix", "depth", "remove", 4, "ngFor", "ngForOf"], ["class", "survey-item-footer", 4, "ngIf"], [1, "conditional-title"], ["cdkDrag", "", "cdkDragBoundary", ".drag-list", 3, "index", "item", "conditionalQuestion", "prefix", "depth", "remove"], [1, "survey-item-footer"], [1, "survey-item-footer-index"], [1, "title-prefix"], [4, "ngIf"], [1, "align-right"], ["mat-flat-button", "", "color", "primary", "version-prompt", "", 3, "click", 4, "ngIf"], ["mat-flat-button", "", "color", "accent", "version-prompt", "", 3, "click", 4, "ngIf"], ["color", "primary", 3, "ngModel", "ngModelChange", 4, "ngIf"], ["color", "primary", 3, "ngModel", "ngModelChange"], ["class", "group-additional-field", 4, "ngFor", "ngForOf"], [1, "group-additional-field"], ["matInput", "", 3, "placeholder", "ngModel", "ngModelChange"], ["mat-flat-button", "", "color", "primary", "version-prompt", "", 3, "click"], ["mat-flat-button", "", "color", "accent", "version-prompt", "", 3, "click"]], template: function SurveyGroupComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, SurveyGroupComponent_h3_0_Template, 2, 0, "h3", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵtemplate(2, SurveyGroupComponent_survey_item_2_Template, 1, 5, "survey-item", 2);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(3, SurveyGroupComponent_div_3_Template, 10, 6, "div", 3);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.group.conditional);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("cdkDropListDisabled", !ctx.editorConfig.permissions.questions.edit)("cdkDropListData", ctx.group.items);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngForOf", ctx.group.items);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", !ctx.group.conditional && (ctx.editorConfig.permissions.questions.add || ctx.editorConfig.permissions.questions.remove || ctx.editorConfig.permissions.questions.edit));
    } }, styles: ["[_nghost-%COMP%]{display:block}.guest-overview-group[_nghost-%COMP%]     .custom-order-input{display:none}.survey-item-content[_ngcontent-%COMP%]{padding:0 20px 20px}.survey-item-top[_ngcontent-%COMP%]{padding:20px 20px 0}.group-additional-field[_ngcontent-%COMP%]{margin-left:20px}.group-additional-field[_ngcontent-%COMP%]   mat-form-field[_ngcontent-%COMP%]{margin-top:5px}.group-additional-field[_ngcontent-%COMP%]   mat-form-field[_ngcontent-%COMP%]     .mat-mdc-form-field-subscript-wrapper{display:none}.conditional-title[_ngcontent-%COMP%]{margin-left:20px}mat-checkbox[_ngcontent-%COMP%] + mat-checkbox[_ngcontent-%COMP%], .survey-item-footer-index[_ngcontent-%COMP%] + mat-checkbox[_ngcontent-%COMP%]{margin-left:16px}", ".survey-item-footer[_ngcontent-%COMP%]{border-top:1px solid #ccc;display:flex;align-items:center;height:50px}.survey-item-footer-index[_ngcontent-%COMP%]{padding:0 10px;display:flex;align-items:center;justify-content:center;align-self:stretch;border-right:1px solid #ccc}.survey-item-footer[_ngcontent-%COMP%]   .survey-item-optional[_ngcontent-%COMP%]{margin-left:12px;margin-right:12px}.survey-item-footer[_ngcontent-%COMP%]   .align-right[_ngcontent-%COMP%]{margin-left:auto}.survey-item-footer[_ngcontent-%COMP%]   .align-right[_ngcontent-%COMP%]     button{margin-right:8px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurveyGroupComponent, [{
        type: Component,
        args: [{ selector: 'survey-group', template: "<h3 *ngIf=\"group.conditional\"\n\tclass=\"conditional-title\">Conditional Questions</h3>\n\n<div class=\"survey-item-content drag-list\"\n\t cdkDropList\n\t cdkDropListLockAxis=\"y\"\n\t [cdkDropListDisabled]=\"!editorConfig.permissions.questions.edit\"\n\t [cdkDropListData]=\"group.items\">\n\t<survey-item *ngFor=\"let item of group.items;index as i;\"\n\t\t\t\t cdkDrag\n\t\t\t\t cdkDragBoundary='.drag-list'\n\t\t\t\t [index]=\"i +1\"\n\t\t\t\t [item]=\"item\"\n\t\t\t\t [conditionalQuestion]=\"conditionalQuestion\"\n\t\t\t\t [prefix]=\"prefix\"\n\t\t\t\t [depth]=\"depth+1\"\n\t\t\t\t (remove)=\"removeItem( $event )\"></survey-item>\n\n</div>\n\n<div *ngIf=\"!group.conditional && (editorConfig.permissions.questions.add || editorConfig.permissions.questions.remove || editorConfig.permissions.questions.edit )\"\n\t class=\"survey-item-footer\">\n\n\t<div class=\"survey-item-footer-index\">\n\t\t<span class=\"title-prefix\">{{$any(group).prefix}}.</span>\n\t</div>\n\n\t<ng-container *ngIf=\"editorConfig.permissions.groups.edit\">\n\n\t\t<mat-checkbox color=\"primary\"\n\t\t\t\t\t  *ngIf=\"depth === 1 && survey.presentation === SurveyPresentation.NORMAL\"\n\t\t\t\t\t  [(ngModel)]=\"group.meta.enumerate\">\n\t\t\tEnumerate\n\t\t</mat-checkbox>\n\n\t\t<mat-checkbox color=\"primary\"\n\t\t\t\t\t  [(ngModel)]=\"group.meta.ownPage\">\n\t\t\tDisplay on own page\n\t\t</mat-checkbox>\n\n\t\t<mat-checkbox color=\"primary\"\n\t\t\t\t\t  *ngIf=\"depth === 1 && survey.intent === SurveyIntent.COMPARE\"\n\t\t\t\t\t  [(ngModel)]=\"group.meta.showOnGuestOverview\">\n\t\t\tShow on Guest Overview\n\t\t</mat-checkbox>\n\n\t</ng-container>\n\n\t<!-- Additional Static Fields-->\n\t<ng-container *ngIf=\"editorConfig.additionalGroupFields && editorConfig.permissions.questions.edit\">\n\n\t\t<div class=\"group-additional-field\"\n\t\t\t *ngFor=\"let field of editorConfig.additionalGroupFields\">\n\t\t\t<mat-form-field>\n\t\t\t\t<input matInput\n\t\t\t\t\t   [placeholder]=\"field.placeholder\"\n\t\t\t\t\t   [(ngModel)]=\"group.meta[field.field]\" />\n\t\t\t</mat-form-field>\n\t\t</div>\n\n\t</ng-container>\n\n\t<div class=\"align-right\">\n\n\t\t<button *ngIf=\"editorConfig.permissions.questions.add\"\n\t\t\t\tmat-flat-button\n\t\t\t\tcolor=\"primary\"\n\t\t\t\tversion-prompt\n\t\t\t\t(click)=\"addQuestion( $event )\">Add Question</button>\n\n\t\t<button *ngIf=\"editorConfig.permissions.groups.add && ( !editorConfig.maxDepth || depth < editorConfig.maxDepth )\"\n\t\t\t\tmat-flat-button\n\t\t\t\tcolor=\"primary\"\n\t\t\t\tversion-prompt\n\t\t\t\t(click)=\"addGroup( $event )\">Add Group</button>\n\n\t\t<button *ngIf=\"editorConfig.permissions.groups.remove\"\n\t\t\t\tmat-flat-button\n\t\t\t\tcolor=\"accent\"\n\t\t\t\tversion-prompt\n\t\t\t\t(click)=\"removeGroup($event)\">Remove Group</button>\n\n\n\t</div>\n</div>\n", styles: [":host{display:block}:host.guest-overview-group ::ng-deep .custom-order-input{display:none}.survey-item-content{padding:0 20px 20px}.survey-item-top{padding:20px 20px 0}.group-additional-field{margin-left:20px}.group-additional-field mat-form-field{margin-top:5px}.group-additional-field mat-form-field ::ng-deep .mat-mdc-form-field-subscript-wrapper{display:none}.conditional-title{margin-left:20px}mat-checkbox+mat-checkbox,.survey-item-footer-index+mat-checkbox{margin-left:16px}\n", ".survey-item-footer{border-top:1px solid #ccc;display:flex;align-items:center;height:50px}.survey-item-footer-index{padding:0 10px;display:flex;align-items:center;justify-content:center;align-self:stretch;border-right:1px solid #ccc}.survey-item-footer .survey-item-optional{margin-left:12px;margin-right:12px}.survey-item-footer .align-right{margin-left:auto}.survey-item-footer .align-right ::ng-deep button{margin-right:8px}\n"] }]
    }], function () { return [{ type: i0.ChangeDetectorRef }, { type: Survey }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }]; }, { group: [{
            type: Input
        }], depth: [{
            type: Input
        }], prefix: [{
            type: Input
        }], conditionalQuestion: [{
            type: Input
        }], remove: [{
            type: Output
        }], isGuestOverviewGroup: [{
            type: HostBinding,
            args: ['class.guest-overview-group']
        }] }); })();

function ConditionalResponseComponent_div_2_Template(rf, ctx) { if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div")(1, "mat-form-field")(2, "mat-label");
    i0.ɵɵtext(3, "Select");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "mat-select", 2);
    i0.ɵɵlistener("ngModelChange", function ConditionalResponseComponent_div_2_Template_mat_select_ngModelChange_4_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r3 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r3.question.meta.conditionalOn = $event); });
    i0.ɵɵelementStart(5, "mat-option", 3);
    i0.ɵɵtext(6, "Any");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(7, "mat-option", 4);
    i0.ɵɵtext(8);
    i0.ɵɵpipe(9, "ynuLabel");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(10, "mat-option", 4);
    i0.ɵɵtext(11);
    i0.ɵɵpipe(12, "ynuLabel");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(13, "mat-option", 4);
    i0.ɵɵtext(14);
    i0.ɵɵpipe(15, "ynuLabel");
    i0.ɵɵelementEnd()()()();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(4);
    i0.ɵɵproperty("ngModel", ctx_r0.question.meta.conditionalOn);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("value", 1);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(9, 7, 1));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("value", 2);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(12, 9, 2));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("value", 3);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(15, 11, 3));
} }
function ConditionalResponseComponent_div_3_Template(rf, ctx) { if (rf & 1) {
    const _r6 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div")(1, "mat-form-field")(2, "mat-label");
    i0.ɵɵtext(3, "Select");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "mat-select", 2);
    i0.ɵɵlistener("ngModelChange", function ConditionalResponseComponent_div_3_Template_mat_select_ngModelChange_4_listener($event) { i0.ɵɵrestoreView(_r6); const ctx_r5 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r5.question.meta.conditionalOn = $event); });
    i0.ɵɵelementStart(5, "mat-option", 3);
    i0.ɵɵtext(6, "Any");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(7, "mat-option", 4);
    i0.ɵɵtext(8);
    i0.ɵɵpipe(9, "booleanLabel");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(10, "mat-option", 4);
    i0.ɵɵtext(11);
    i0.ɵɵpipe(12, "booleanLabel");
    i0.ɵɵelementEnd()()()();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(4);
    i0.ɵɵproperty("ngModel", ctx_r1.question.meta.conditionalOn);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("value", 1);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(9, 5, 1));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("value", 2);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(12, 7, 2));
} }
function ConditionalResponseComponent_div_4_mat_option_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "mat-option", 4);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const option_r8 = ctx.$implicit;
    i0.ɵɵproperty("value", option_r8.id);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(option_r8.label);
} }
function ConditionalResponseComponent_div_4_Template(rf, ctx) { if (rf & 1) {
    const _r10 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div")(1, "mat-form-field")(2, "mat-label");
    i0.ɵɵtext(3, "Select");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "mat-select", 2);
    i0.ɵɵlistener("ngModelChange", function ConditionalResponseComponent_div_4_Template_mat_select_ngModelChange_4_listener($event) { i0.ɵɵrestoreView(_r10); const ctx_r9 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r9.question.meta.conditionalOn = $event); });
    i0.ɵɵelementStart(5, "mat-option", 3);
    i0.ɵɵtext(6, "Any");
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(7, ConditionalResponseComponent_div_4_mat_option_7_Template, 2, 2, "mat-option", 5);
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(4);
    i0.ɵɵproperty("ngModel", ctx_r2.question.meta.conditionalOn);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngForOf", ctx_r2.conditionalQuestion.meta.listValues);
} }
/**
 * All of the rendering of conditional response options is handled by this component
 * Since some of the components (YNU) aren't backed by a defined form model, the rendering of the options is handled by the various components
 * Therefore we need to have cases for all the options in our template here
 */
class ConditionalResponseComponent {
    constructor() {
        this.QuestionType = QuestionType;
    }
}
ConditionalResponseComponent.ɵfac = function ConditionalResponseComponent_Factory(t) { return new (t || ConditionalResponseComponent)(); };
ConditionalResponseComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: ConditionalResponseComponent, selectors: [["conditional-response-select"]], inputs: { question: "question", conditionalQuestion: "conditionalQuestion" }, decls: 5, vars: 3, consts: [[1, "conditional-response-label"], [4, "ngIf"], [3, "ngModel", "ngModelChange"], ["value", "*"], [3, "value"], [3, "value", 4, "ngFor", "ngForOf"]], template: function ConditionalResponseComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "mat-label", 0);
        i0.ɵɵtext(1, "Show question when response is:");
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(2, ConditionalResponseComponent_div_2_Template, 16, 13, "div", 1);
        i0.ɵɵtemplate(3, ConditionalResponseComponent_div_3_Template, 13, 9, "div", 1);
        i0.ɵɵtemplate(4, ConditionalResponseComponent_div_4_Template, 8, 2, "div", 1);
    } if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", ctx.conditionalQuestion.questionType === ctx.QuestionType.YES_NO_UNCERTAIN);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.conditionalQuestion.questionType === ctx.QuestionType.BOOLEAN);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.conditionalQuestion.questionType === ctx.QuestionType.PICKLIST || ctx.conditionalQuestion.questionType === ctx.QuestionType.CHECKLIST || ctx.conditionalQuestion.questionType === ctx.QuestionType.RADIOSET);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i3.NgControlStatus, i3.NgModel, i4$1.MatFormField, i4$1.MatLabel, i4$2.MatSelect, i5$1.MatOption, YesNoUncertainLabelPipe, BooleanLabelPipe], styles: ["[_nghost-%COMP%]{display:flex;align-items:baseline}.conditional-response-label[_ngcontent-%COMP%]{font-weight:700;margin-right:12px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ConditionalResponseComponent, [{
        type: Component,
        args: [{ selector: 'conditional-response-select', template: "<mat-label class=\"conditional-response-label\">Show question when response is:</mat-label>\n\n<!-- Yes/No/Uncertain Options -->\n<div *ngIf=\"conditionalQuestion.questionType === QuestionType.YES_NO_UNCERTAIN\">\n\t<mat-form-field>\n\t\t<mat-label>Select</mat-label>\n\t\t<mat-select [(ngModel)]=\"question.meta.conditionalOn\">\n\t\t\t<mat-option value=\"*\">Any</mat-option>\n\t\t\t<mat-option [value]=\"1\">{{ 1 | ynuLabel }}</mat-option>\n\t\t\t<mat-option [value]=\"2\">{{ 2 | ynuLabel }}</mat-option>\n\t\t\t<mat-option [value]=\"3\">{{ 3 | ynuLabel }}</mat-option>\n\t\t</mat-select>\n\t</mat-form-field>\n</div>\n\n<!-- Boolean Options -->\n<div *ngIf=\"conditionalQuestion.questionType === QuestionType.BOOLEAN\">\n\t<mat-form-field>\n\t\t<mat-label>Select</mat-label>\n\t\t<mat-select [(ngModel)]=\"question.meta.conditionalOn\">\n\t\t\t<mat-option value=\"*\">Any</mat-option>\n\t\t\t<mat-option [value]=\"1\">{{ 1 | booleanLabel }}</mat-option>\n\t\t\t<mat-option [value]=\"2\">{{ 2 | booleanLabel }}</mat-option>\n\t\t</mat-select>\n\t</mat-form-field>\n</div>\n\n<!-- List Options -->\n<div *ngIf=\"conditionalQuestion.questionType === QuestionType.PICKLIST \n\t\t\t|| conditionalQuestion.questionType === QuestionType.CHECKLIST\n\t\t\t|| conditionalQuestion.questionType === QuestionType.RADIOSET\n\t\t\">\n\n\t<mat-form-field>\n\t\t<mat-label>Select</mat-label>\n\t\t<mat-select [(ngModel)]=\"question.meta.conditionalOn\">\n\t\t\t<mat-option value=\"*\">Any</mat-option>\n\t\t\t<mat-option *ngFor=\"let option of conditionalQuestion.meta.listValues\"\n\t\t\t\t\t\t[value]=\"option.id\">{{ option.label }}</mat-option>\n\t\t</mat-select>\n\t</mat-form-field>\n</div>\n", styles: [":host{display:flex;align-items:baseline}.conditional-response-label{font-weight:700;margin-right:12px}\n"] }]
    }], null, { question: [{
            type: Input
        }], conditionalQuestion: [{
            type: Input
        }] }); })();

const _c0$a = ["fuzzyTranslation"];
function SurveyItemComponent_span_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "span", 14);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("innerHTML", ctx_r0.itemPrefix, i0.ɵɵsanitizeHtml);
} }
function SurveyItemComponent_mat_icon_5_Template(rf, ctx) { if (rf & 1) {
    const _r10 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-icon", 15);
    i0.ɵɵlistener("click", function SurveyItemComponent_mat_icon_5_Template_mat_icon_click_0_listener() { i0.ɵɵrestoreView(_r10); const ctx_r9 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r9.onClickFuzzy()); });
    i0.ɵɵtext(1, "translate");
    i0.ɵɵelementEnd();
} }
function SurveyItemComponent_mat_icon_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "mat-icon", 16);
    i0.ɵɵtext(1, "open_with");
    i0.ɵɵelementEnd();
} }
function SurveyItemComponent_conditional_response_select_10_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "conditional-response-select", 17);
} if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵproperty("question", ctx_r3.item)("conditionalQuestion", ctx_r3.conditionalQuestion);
} }
function SurveyItemComponent_survey_group_11_Template(rf, ctx) { if (rf & 1) {
    const _r12 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "survey-group", 18);
    i0.ɵɵlistener("remove", function SurveyItemComponent_survey_group_11_Template_survey_group_remove_0_listener($event) { i0.ɵɵrestoreView(_r12); const ctx_r11 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r11.removeItem($event)); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵproperty("group", ctx_r4.item)("prefix", ctx_r4.itemPrefix)("depth", ctx_r4.depth);
} }
function SurveyItemComponent_question_editor_12_button_1_Template(rf, ctx) { if (rf & 1) {
    const _r15 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 21);
    i0.ɵɵlistener("click", function SurveyItemComponent_question_editor_12_button_1_Template_button_click_0_listener($event) { i0.ɵɵrestoreView(_r15); const ctx_r14 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r14.addConditionalQuestion($event)); });
    i0.ɵɵtext(1, "Add Conditional Question");
    i0.ɵɵelementEnd();
} }
function SurveyItemComponent_question_editor_12_Template(rf, ctx) { if (rf & 1) {
    const _r17 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "question-editor", 19);
    i0.ɵɵlistener("remove", function SurveyItemComponent_question_editor_12_Template_question_editor_remove_0_listener($event) { i0.ɵɵrestoreView(_r17); const ctx_r16 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r16.removeItem($event)); });
    i0.ɵɵtemplate(1, SurveyItemComponent_question_editor_12_button_1_Template, 2, 0, "button", 20);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r5 = i0.ɵɵnextContext();
    i0.ɵɵclassProp("has-conditional", ctx_r5.item.conditionalQuestions == null ? null : ctx_r5.item.conditionalQuestions.items.length);
    i0.ɵɵproperty("question", ctx_r5.item)("isOpen", ctx_r5.expanded);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r5.canHaveConditional(ctx_r5.item));
} }
function SurveyItemComponent_ng_container_13_Template(rf, ctx) { if (rf & 1) {
    const _r19 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "survey-group", 22);
    i0.ɵɵlistener("remove", function SurveyItemComponent_ng_container_13_Template_survey_group_remove_1_listener($event) { i0.ɵɵrestoreView(_r19); const ctx_r18 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r18.removeItem($event)); });
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r6 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("group", ctx_r6.item.conditionalQuestions)("conditionalQuestion", ctx_r6.item)("depth", ctx_r6.depth);
} }
function SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_ng_container_1_ng_container_4_ng_container_1_div_1_p_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p")(1, "span");
    i0.ɵɵtext(2, "Old:");
    i0.ɵɵelementEnd();
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const pickOptionChange_r34 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1("", pickOptionChange_r34.value.current, " ");
} }
function SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_ng_container_1_ng_container_4_ng_container_1_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 28)(1, "p")(2, "span");
    i0.ɵɵtext(3, "New:");
    i0.ɵɵelementEnd();
    i0.ɵɵtext(4);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(5, SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_ng_container_1_ng_container_4_ng_container_1_div_1_p_5_Template, 4, 1, "p", 12);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const pickOptionChange_r34 = ctx.$implicit;
    i0.ɵɵadvance(4);
    i0.ɵɵtextInterpolate(pickOptionChange_r34.value.new);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", pickOptionChange_r34.value.current);
} }
function SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_ng_container_1_ng_container_4_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_ng_container_1_ng_container_4_ng_container_1_div_1_Template, 6, 2, "div", 27);
    i0.ɵɵpipe(2, "keyvalue");
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const pickOption_r32 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", i0.ɵɵpipeBind1(2, 1, pickOption_r32.value));
} }
function SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_ng_container_1_ng_container_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_ng_container_1_ng_container_4_ng_container_1_Template, 3, 3, "ng-container", 25);
    i0.ɵɵpipe(2, "keyvalue");
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const metaChange_r28 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", i0.ɵɵpipeBind1(2, 1, metaChange_r28.value));
} }
function SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_ng_container_1_ng_container_5_p_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p")(1, "span");
    i0.ɵɵtext(2, "Old:");
    i0.ɵɵelementEnd();
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const metaChange_r28 = i0.ɵɵnextContext(2).$implicit;
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate(metaChange_r28.value.current);
} }
function SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_ng_container_1_ng_container_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 28)(2, "p")(3, "span");
    i0.ɵɵtext(4, "New:");
    i0.ɵɵelementEnd();
    i0.ɵɵtext(5);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(6, SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_ng_container_1_ng_container_5_p_6_Template, 4, 1, "p", 12);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const metaChange_r28 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(5);
    i0.ɵɵtextInterpolate(metaChange_r28.value.new);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", metaChange_r28.value.current);
} }
function SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "h4");
    i0.ɵɵtext(2);
    i0.ɵɵpipe(3, "titlecase");
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(4, SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_ng_container_1_ng_container_4_Template, 3, 3, "ng-container", 12);
    i0.ɵɵtemplate(5, SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_ng_container_1_ng_container_5_Template, 7, 2, "ng-container", 12);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const metaChange_r28 = ctx.$implicit;
    const ctx_r27 = i0.ɵɵnextContext(5);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(3, 3, metaChange_r28.key));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ctx_r27.isListset(metaChange_r28.key));
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", !ctx_r27.isListset(metaChange_r28.key));
} }
function SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_ng_container_1_Template, 6, 5, "ng-container", 25);
    i0.ɵɵpipe(2, "keyvalue");
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const change_r24 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", i0.ɵɵpipeBind1(2, 1, change_r24.value));
} }
function SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_2_p_9_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p")(1, "span");
    i0.ɵɵtext(2, "Old:");
    i0.ɵɵelementEnd();
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const change_r24 = i0.ɵɵnextContext(2).$implicit;
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate(change_r24.value.current);
} }
function SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "h4");
    i0.ɵɵtext(2);
    i0.ɵɵpipe(3, "titlecase");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "div", 28)(5, "p")(6, "span");
    i0.ɵɵtext(7, "New:");
    i0.ɵɵelementEnd();
    i0.ɵɵtext(8);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(9, SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_2_p_9_Template, 4, 1, "p", 12);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const change_r24 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(3, 3, change_r24.key));
    i0.ɵɵadvance(6);
    i0.ɵɵtextInterpolate(change_r24.value.new);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", change_r24.value.current);
} }
function SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_1_Template, 3, 3, "ng-container", 12);
    i0.ɵɵtemplate(2, SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_ng_container_2_Template, 10, 5, "ng-container", 12);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const change_r24 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", change_r24.key === "meta");
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", change_r24.key !== "meta");
} }
function SurveyItemComponent_ng_template_14_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    const _r46 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "h3");
    i0.ɵɵtext(2, "Source Translation Changed");
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(3, SurveyItemComponent_ng_template_14_ng_container_1_ng_container_3_Template, 3, 2, "ng-container", 25);
    i0.ɵɵpipe(4, "keyvalue");
    i0.ɵɵelementStart(5, "button", 26);
    i0.ɵɵlistener("click", function SurveyItemComponent_ng_template_14_ng_container_1_Template_button_click_5_listener($event) { i0.ɵɵrestoreView(_r46); const ctx_r45 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r45.onUnFuzzyClick($event)); });
    i0.ɵɵtext(6, "This does not need an updated translation.");
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const item_r20 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngForOf", i0.ɵɵpipeBind1(4, 1, item_r20.fuzzy));
} }
function SurveyItemComponent_ng_template_14_ng_container_2_div_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const fuzzy_r49 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate2(" ", fuzzy_r49.prefix, " ", fuzzy_r49.title, " ");
} }
function SurveyItemComponent_ng_template_14_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "h3");
    i0.ɵɵi18n(2, 29);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(3, SurveyItemComponent_ng_template_14_ng_container_2_div_3_Template, 2, 2, "div", 25);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const item_r20 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(2);
    i0.ɵɵi18nExp(item_r20.fuzzyItems.length)(item_r20.fuzzyItems.length);
    i0.ɵɵi18nApply(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", item_r20.fuzzyItems);
} }
function SurveyItemComponent_ng_template_14_Template(rf, ctx) { if (rf & 1) {
    const _r52 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 23);
    i0.ɵɵtemplate(1, SurveyItemComponent_ng_template_14_ng_container_1_Template, 7, 3, "ng-container", 12);
    i0.ɵɵtemplate(2, SurveyItemComponent_ng_template_14_ng_container_2_Template, 4, 3, "ng-container", 12);
    i0.ɵɵelementStart(3, "mat-icon", 24);
    i0.ɵɵlistener("click", function SurveyItemComponent_ng_template_14_Template_mat_icon_click_3_listener() { i0.ɵɵrestoreView(_r52); const ctx_r51 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r51.dialogRef.close()); });
    i0.ɵɵtext(4, "close");
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const item_r20 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", item_r20.fuzzy);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", item_r20.fuzzyItems == null ? null : item_r20.fuzzyItems.length);
} }
const _c3$1 = function () { return { updateOn: "submit" }; };
class SurveyItemComponent {
    constructor(el, dragRef, changeDetectorRef, dialog, survey, editorConfig) {
        this.el = el;
        this.dragRef = dragRef;
        this.changeDetectorRef = changeDetectorRef;
        this.dialog = dialog;
        this.survey = survey;
        this.editorConfig = editorConfig;
        this.remove = new EventEmitter();
        this.SurveyItemType = SurveyItemType;
        if (this.dragRef) {
            this._subscription = dragRef.dropContainer.dropped.subscribe((e) => {
                if (e.item === this.dragRef) {
                    moveItemInArray(e.container.data, e.previousIndex, e.currentIndex);
                }
            });
        }
    }
    get isDraggable() {
        return this.dragRef === null;
    }
    ngOnChanges(changes) {
        if (changes.depth && changes.depth.currentValue) {
            this.el.nativeElement.setAttribute('depth', changes.depth.currentValue);
            this.el.nativeElement.removeAttribute('depth-even');
            this.el.nativeElement.removeAttribute('depth-odd');
            if (changes.depth.currentValue % 2 === 0) {
                this.el.nativeElement.setAttribute('depth-even', '');
            }
            else {
                this.el.nativeElement.setAttribute('depth-odd', '');
            }
        }
        this.setItemPrefix();
    }
    ngOnInit() {
        if (this.item.expanded) {
            this.expanded = true;
        }
        if (this.item.type === SurveyItemType.QUESTION) {
            this.itemPlaceholder = 'Question';
        }
        else {
            this.itemPlaceholder = 'Group';
        }
    }
    ngAfterViewInit() {
        if (this.dragRef) {
            this.dragRef._handles = this.dragHandles;
        }
    }
    ngOnDestroy() {
        if (this._subscription) {
            this._subscription.unsubscribe();
        }
    }
    /**
     * Check if a question can add a conditional question to it
     * @param item
     */
    canHaveConditional(item) {
        return this.editorConfig.supports.conditionalQuestions && ConditionalQuestionTypes.includes(item.questionType);
    }
    /**
     * Add a conditional question
     */
    addConditionalQuestion(event) {
        event.preventDefault();
        this.item.addConditionalQuestion({ id: this.survey.meta.insert_id++ });
        this.survey.triggerRefresh();
    }
    /**
     * on blur update the model and possibly clear the fuzzy marker
     */
    onTitleBlur() {
        const newValue = event.target.value;
        if (newValue !== this.item.title && this.item.fuzzy) {
            delete this.item.fuzzy.title;
            validateFuzzyObject(this.item);
        }
        this.item.title = newValue;
    }
    /**
     * Open the dialog showing information about the fuzzy content within
     */
    onClickFuzzy() {
        this.dialogRef = this.dialog.open(this.fuzzyTranslation, {
            autoFocus: false,
            data: this.item
        });
    }
    /**
     * Mark as unfuzzy
     */
    onUnFuzzyClick(event) {
        event.preventDefault();
        this.item.fuzzy = null;
        if (!this.item.fuzzyItems?.length) {
            this.dialogRef.close();
        }
        this.changeDetectorRef.markForCheck();
        // trigger change detection on question editors
        this.survey.triggerRefresh();
    }
    /**
     * Set the prefix before the label
     */
    setItemPrefix() {
        let prefix = this.prefix || '';
        if (this.item.type === SurveyItemType.QUESTION) {
            prefix += prefix ? ' | Q' + this.index : '' + this.index;
        }
        else {
            prefix += prefix ? '.' + this.index : '' + this.index;
        }
        this.itemPrefix = prefix;
        this.item.prefix = prefix;
    }
    toggleExpanded() {
        this.expanded = !this.expanded;
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
    }
    removeItem(item) {
        this.remove.emit(item);
    }
    isListset(key) {
        return 'listValues' === key;
    }
}
SurveyItemComponent.ɵfac = function SurveyItemComponent_Factory(t) { return new (t || SurveyItemComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i6$2.CdkDrag, 11), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1$3.MatDialog), i0.ɵɵdirectiveInject(Survey), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG)); };
SurveyItemComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: SurveyItemComponent, selectors: [["survey-item"]], viewQuery: function SurveyItemComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0$a, 7);
        i0.ɵɵviewQuery(CdkDragHandle, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.fuzzyTranslation = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.dragHandles = _t);
    } }, hostVars: 4, hostBindings: function SurveyItemComponent_HostBindings(rf, ctx) { if (rf & 2) {
        i0.ɵɵclassProp("expanded", ctx.expanded)("cdk-drag-disabled", ctx.isDraggable);
    } }, inputs: { item: "item", depth: "depth", index: "index", prefix: "prefix", conditionalQuestion: "conditionalQuestion" }, outputs: { remove: "remove" }, features: [i0.ɵɵNgOnChangesFeature], decls: 16, vars: 14, consts: function () { let i18n_1; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @suppress {msgDescriptions}
         */
        const MSG_EXTERNAL_2267068358377175263$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_EDITOR_SRC_COMPONENTS_ITEM_SURVEY_ITEM_TS___2 = goog.getMsg("{VAR_PLURAL, plural, =1 {One nested change} other {{INTERPOLATION} nested changes\n\t\t\t\t}}");
        i18n_1 = MSG_EXTERNAL_2267068358377175263$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_EDITOR_SRC_COMPONENTS_ITEM_SURVEY_ITEM_TS___2;
    }
    else {
        i18n_1 = $localize `:␟2aa8037f6c191014a3d2b12ebc9a0f8040f9a9f8␟2267068358377175263:{VAR_PLURAL, plural, =1 {One nested change} other {{INTERPOLATION} nested changes
				}}`;
    } i18n_1 = i0.ɵɵi18nPostprocess(i18n_1, { "VAR_PLURAL": "\uFFFD0\uFFFD", "INTERPOLATION": "\uFFFD1\uFFFD" }); return [[1, "survey-item-header"], [1, "survey-item-header-title"], ["class", "title-prefix", 3, "innerHTML", 4, "ngIf"], ["type", "text", "version-prompt", "", "required", "", 1, "survey-item-title", 3, "placeholder", "ngModel", "ngModelOptions", "readonly", "ngModelChange", "blur"], [1, "survey-item-header-actions"], ["matTooltip", "This element's source translation has changed", 3, "click", 4, "ngIf"], ["cdkDragHandle", "", 4, "ngIf"], [3, "click"], [1, "survey-item-container", 3, "hidden"], [3, "question", "conditionalQuestion", 4, "ngIf"], [3, "group", "prefix", "depth", "remove", 4, "ngIf"], [3, "question", "isOpen", "has-conditional", "remove", 4, "ngIf"], [4, "ngIf"], ["fuzzyTranslation", ""], [1, "title-prefix", 3, "innerHTML"], ["matTooltip", "This element's source translation has changed", 3, "click"], ["cdkDragHandle", ""], [3, "question", "conditionalQuestion"], [3, "group", "prefix", "depth", "remove"], [3, "question", "isOpen", "remove"], ["version-prompt", "", "mat-flat-button", "", "color", "primary", "class", "add-conditional-button", 3, "click", 4, "ngIf"], ["version-prompt", "", "mat-flat-button", "", "color", "primary", 1, "add-conditional-button", 3, "click"], [3, "group", "conditionalQuestion", "depth", "remove"], [1, "fuzzy-dialog"], ["matDialogClose", "", 1, "close-fuzzy", 3, "click"], [4, "ngFor", "ngForOf"], ["mat-flat-button", "", "color", "primary", 3, "click"], ["class", "fuzzy-item", 4, "ngFor", "ngForOf"], [1, "fuzzy-item"], i18n_1]; }, template: function SurveyItemComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0)(1, "div", 1);
        i0.ɵɵtemplate(2, SurveyItemComponent_span_2_Template, 1, 1, "span", 2);
        i0.ɵɵelementStart(3, "input", 3);
        i0.ɵɵlistener("ngModelChange", function SurveyItemComponent_Template_input_ngModelChange_3_listener($event) { return ctx.item.title = $event; })("blur", function SurveyItemComponent_Template_input_blur_3_listener() { return ctx.onTitleBlur(); });
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(4, "div", 4);
        i0.ɵɵtemplate(5, SurveyItemComponent_mat_icon_5_Template, 2, 0, "mat-icon", 5);
        i0.ɵɵtemplate(6, SurveyItemComponent_mat_icon_6_Template, 2, 0, "mat-icon", 6);
        i0.ɵɵelementStart(7, "mat-icon", 7);
        i0.ɵɵlistener("click", function SurveyItemComponent_Template_mat_icon_click_7_listener() { return ctx.toggleExpanded(); });
        i0.ɵɵtext(8);
        i0.ɵɵelementEnd()()();
        i0.ɵɵelementStart(9, "div", 8);
        i0.ɵɵtemplate(10, SurveyItemComponent_conditional_response_select_10_Template, 1, 2, "conditional-response-select", 9);
        i0.ɵɵtemplate(11, SurveyItemComponent_survey_group_11_Template, 1, 3, "survey-group", 10);
        i0.ɵɵtemplate(12, SurveyItemComponent_question_editor_12_Template, 2, 5, "question-editor", 11);
        i0.ɵɵtemplate(13, SurveyItemComponent_ng_container_13_Template, 2, 3, "ng-container", 12);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(14, SurveyItemComponent_ng_template_14_Template, 5, 2, "ng-template", null, 13, i0.ɵɵtemplateRefExtractor);
    } if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", ctx.editorConfig.maxDepth !== 1);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("placeholder", ctx.itemPlaceholder)("ngModel", ctx.item.title)("ngModelOptions", i0.ɵɵpureFunction0(13, _c3$1))("readonly", !ctx.expanded || ctx.editorConfig.permissions.readonly);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", ctx.item.fuzzy || (ctx.item.fuzzyItems == null ? null : ctx.item.fuzzyItems.length));
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.editorConfig.permissions.questions.edit);
        i0.ɵɵadvance(2);
        i0.ɵɵtextInterpolate(ctx.expanded ? "keyboard_arrow_up" : "keyboard_arrow_down");
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("hidden", !ctx.expanded);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.conditionalQuestion && ctx.editorConfig.permissions.questions.add);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.item.type === ctx.SurveyItemType.QUESTION_GROUP);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.item.type === ctx.SurveyItemType.QUESTION);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.item.conditionalQuestions == null ? null : ctx.item.conditionalQuestions.items.length);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i3.DefaultValueAccessor, i3.NgControlStatus, i3.RequiredValidator, i3.NgModel, i2.MatButton, i6$1.MatIcon, i8.MatTooltip, i6$2.CdkDragHandle, QuestionEditorComponent, VersionPromptDirective, SurveyGroupComponent, ConditionalResponseComponent, i1$2.TitleCasePipe, i1$2.KeyValuePipe], styles: ["[_nghost-%COMP%]{display:block;margin:25px 0;border:1px solid #ccc}[depth-even][_nghost-%COMP%]{background-color:#f1f1f1}[depth-odd][_nghost-%COMP%]{background-color:#fff}.disabled[_nghost-%COMP%]{background-color:#d5d8da}.cdk-drag-disabled[_nghost-%COMP%]   .cdk-drag-handle[_ngcontent-%COMP%]{display:none}question-editor.has-conditional[_ngcontent-%COMP%]{border-bottom:1px solid #ccc}.survey-item-header[_ngcontent-%COMP%]{display:flex;justify-content:space-between;min-height:50px}.survey-item-header-title[_ngcontent-%COMP%]{display:flex;align-items:center;flex-grow:1;padding:10px}.survey-item-header-title[_ngcontent-%COMP%]   .title-prefix[_ngcontent-%COMP%]{margin-right:8px;display:inline-block;min-width:-moz-fit-content;min-width:fit-content}.survey-item-header-title[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{transition:all .2s ease-out;width:100%}.survey-item-header-title[_ngcontent-%COMP%]   input[readonly][_ngcontent-%COMP%]{background-color:transparent;border-color:transparent;box-shadow:none}.survey-item-header-title[_ngcontent-%COMP%]   input[readonly][_ngcontent-%COMP%]:focus{outline:none;box-shadow:none}.expanded[_nghost-%COMP%] > .survey-item-header[_ngcontent-%COMP%]{border-bottom:1px solid #ccc}.survey-item-header-actions[_ngcontent-%COMP%]{display:flex;align-items:center}.survey-item-header-actions[_ngcontent-%COMP%] > *[_ngcontent-%COMP%]{border-left:1px solid #ccc;width:49px;height:49px;cursor:pointer;display:flex;align-items:center;justify-content:center}.survey-item-header-actions[_ngcontent-%COMP%] > *[cdkdraghandle][_ngcontent-%COMP%]{cursor:default}.survey-item-container[_ngcontent-%COMP%]{overflow:hidden}conditional-response-select[_ngcontent-%COMP%]{padding:20px}.add-conditional-button.alignright[_ngcontent-%COMP%]{margin:0 0 0 auto;border-left:1px solid #ccc}", ".fuzzy-dialog[_ngcontent-%COMP%]{min-width:320px;position:relative}.fuzzy-dialog[_ngcontent-%COMP%]   h3[_ngcontent-%COMP%]{margin-top:0}.fuzzy-dialog[_ngcontent-%COMP%]   h4[_ngcontent-%COMP%]{margin:0 0 -5px;font-size:14px;border-bottom:1px solid #ccc}.fuzzy-dialog[_ngcontent-%COMP%]   button[_ngcontent-%COMP%]{margin:12px 0 18px}.fuzzy-dialog[_ngcontent-%COMP%]   p[_ngcontent-%COMP%]{display:flex}.fuzzy-dialog[_ngcontent-%COMP%]   p[_ngcontent-%COMP%]   span[_ngcontent-%COMP%]{min-width:60px;display:inline-block}.fuzzy-dialog[_ngcontent-%COMP%]   .fuzzy-item[_ngcontent-%COMP%]{margin:15px 0;max-width:700px}.fuzzy-dialog[_ngcontent-%COMP%]   .fuzzy-item[_ngcontent-%COMP%]   p[_ngcontent-%COMP%]{margin:0 0 6px}.fuzzy-dialog[_ngcontent-%COMP%]   .close-fuzzy[_ngcontent-%COMP%]{cursor:pointer;position:absolute;top:-17px;right:-13px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurveyItemComponent, [{
        type: Component,
        args: [{ selector: 'survey-item', template: "<div class=\"survey-item-header\">\n\n\t<div class=\"survey-item-header-title\">\n\n\t\t<span *ngIf=\"editorConfig.maxDepth !== 1\"\n\t\t\t  class=\"title-prefix\"\n\t\t\t  [innerHTML]=\"itemPrefix\"></span>\n\n\t\t<input type=\"text\"\n\t\t\t   class=\"survey-item-title\"\n\t\t\t   version-prompt\n\t\t\t   required\n\t\t\t   [placeholder]=\"itemPlaceholder\"\n\t\t\t   [(ngModel)]=\"item.title\"\n\t\t\t   [ngModelOptions]=\"{updateOn:'submit'}\"\n\t\t\t   (blur)=\"onTitleBlur()\"\n\t\t\t   [readonly]=\"!expanded || editorConfig.permissions.readonly\" />\n\n\t</div>\n\n\t<div class=\"survey-item-header-actions\">\n\n\t\t<mat-icon *ngIf=\"item.fuzzy || item.fuzzyItems?.length\"\n\t\t\t\t  matTooltip=\"This element's source translation has changed\"\n\t\t\t\t  (click)=\"onClickFuzzy()\">translate</mat-icon>\n\n\t\t<mat-icon *ngIf=\"editorConfig.permissions.questions.edit\"\n\t\t\t\t  cdkDragHandle>open_with</mat-icon>\n\n\t\t<mat-icon (click)=\"toggleExpanded()\">{{expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}}</mat-icon>\n\n\t</div>\n</div>\n\n<div class=\"survey-item-container\"\n\t [hidden]=\"!expanded\">\n\n\n\t<conditional-response-select *ngIf=\"conditionalQuestion && editorConfig.permissions.questions.add\"\n\t\t\t\t\t\t\t\t [question]=\"item\"\n\t\t\t\t\t\t\t\t [conditionalQuestion]=\"conditionalQuestion\"></conditional-response-select>\n\n\t<survey-group *ngIf=\"item.type === SurveyItemType.QUESTION_GROUP\"\n\t\t\t\t  [group]=\"item\"\n\t\t\t\t  [prefix]=\"itemPrefix\"\n\t\t\t\t  [depth]=\"depth\"\n\t\t\t\t  (remove)=\"removeItem( $event )\"></survey-group>\n\n\t<question-editor *ngIf=\"item.type === SurveyItemType.QUESTION\"\n\t\t\t\t\t [question]=\"item\"\n\t\t\t\t\t [isOpen]=\"expanded\"\n\t\t\t\t\t [class.has-conditional]=\"item.conditionalQuestions?.items.length\"\n\t\t\t\t\t (remove)=\"removeItem( $event )\">\n\n\t\t<button *ngIf=\"canHaveConditional(item)\"\n\t\t\t\tversion-prompt\n\t\t\t\tmat-flat-button\n\t\t\t\tcolor=\"primary\"\n\t\t\t\tclass=\"add-conditional-button\"\n\t\t\t\t(click)=\"addConditionalQuestion($event)\">Add Conditional Question</button>\n\n\t</question-editor>\n\n\t<ng-container *ngIf=\"item.conditionalQuestions?.items.length\">\n\n\t\t<survey-group [group]=\"item.conditionalQuestions\"\n\t\t\t\t\t  [conditionalQuestion]=\"item\"\n\t\t\t\t\t  [depth]=\"depth\"\n\t\t\t\t\t  (remove)=\"removeItem( $event )\"></survey-group>\n\t</ng-container>\n\n\n\n\n</div>\n\n<ng-template #fuzzyTranslation\n\t\t\t let-item>\n\n\t<div class=\"fuzzy-dialog\">\n\n\t\t<!-- If the item itself has any of it's properties fuzzy -->\n\t\t<ng-container *ngIf=\"item.fuzzy\">\n\t\t\t<h3>Source Translation Changed</h3>\n\n\t\t\t<ng-container *ngFor=\"let change of item.fuzzy |keyvalue\">\n\n\t\t\t\t<!-- handle display of changes to meta content -->\n\t\t\t\t<ng-container *ngIf=\"change.key === 'meta'\">\n\n\t\t\t\t\t<ng-container *ngFor=\"let metaChange of change.value |keyvalue\">\n\n\t\t\t\t\t\t<h4>{{metaChange.key | titlecase }}</h4>\n\n\t\t\t\t\t\t<!-- listset value changes-->\n\t\t\t\t\t\t<ng-container *ngIf=\"isListset( metaChange.key )\">\n\n\t\t\t\t\t\t\t<ng-container *ngFor=\"let pickOption of metaChange.value |keyvalue\">\n\n\t\t\t\t\t\t\t\t<div *ngFor=\"let pickOptionChange of pickOption.value |keyvalue\"\n\t\t\t\t\t\t\t\t\t class=\"fuzzy-item\">\n\n\t\t\t\t\t\t\t\t\t<p><span>New:</span>{{pickOptionChange.value.new}}</p>\n\t\t\t\t\t\t\t\t\t<p *ngIf=\"pickOptionChange.value.current\">\n\t\t\t\t\t\t\t\t\t\t<span>Old:</span>{{pickOptionChange.value.current}}\n\t\t\t\t\t\t\t\t\t</p>\n\n\t\t\t\t\t\t\t\t</div>\n\n\t\t\t\t\t\t\t</ng-container>\n\n\t\t\t\t\t\t</ng-container>\n\n\t\t\t\t\t\t<!-- Other meta value changes-->\n\t\t\t\t\t\t<ng-container *ngIf=\"!isListset( metaChange.key )\">\n\t\t\t\t\t\t\t<div class=\"fuzzy-item\">\n\t\t\t\t\t\t\t\t<p><span>New:</span>{{metaChange.value.new}}</p>\n\t\t\t\t\t\t\t\t<p *ngIf=\"metaChange.value.current\"><span>Old:</span>{{metaChange.value.current}}</p>\n\t\t\t\t\t\t\t</div>\n\t\t\t\t\t\t</ng-container>\n\n\n\n\t\t\t\t\t</ng-container>\n\n\n\t\t\t\t</ng-container>\n\n\t\t\t\t<!-- handle display of other standard change properties -->\n\t\t\t\t<ng-container *ngIf=\"change.key !== 'meta'\">\n\n\t\t\t\t\t<h4>{{change.key | titlecase }}</h4>\n\n\t\t\t\t\t<div class=\"fuzzy-item\">\n\t\t\t\t\t\t<p><span>New:</span>{{change.value.new}}</p>\n\t\t\t\t\t\t<p *ngIf=\"change.value.current\"><span>Old:</span>{{change.value.current}}</p>\n\t\t\t\t\t</div>\n\n\t\t\t\t</ng-container>\n\n\t\t\t</ng-container>\n\n\t\t\t<!-- button to dismiss fuzzy notification -->\n\t\t\t<button mat-flat-button\n\t\t\t\t\tcolor=\"primary\"\n\t\t\t\t\t(click)=\"onUnFuzzyClick($event)\">This does not need an updated translation.</button>\n\n\t\t</ng-container>\n\n\t\t<!-- Show information about nested fuzzy items-->\n\t\t<ng-container *ngIf=\"item.fuzzyItems?.length\">\n\n\t\t\t<h3>{item.fuzzyItems.length, plural, =1 {One nested change} other {{{item.fuzzyItems.length}} nested changes\n\t\t\t\t} }</h3>\n\n\t\t\t<div *ngFor=\"let fuzzy of item.fuzzyItems\">\n\t\t\t\t{{ $any(fuzzy).prefix }} {{fuzzy.title}}\n\t\t\t</div>\n\t\t</ng-container>\n\n\n\t\t<mat-icon class=\"close-fuzzy\"\n\t\t\t\t  matDialogClose\n\t\t\t\t  (click)=\"dialogRef.close()\">close</mat-icon>\n\t</div>\n</ng-template>\n", styles: [":host{display:block;margin:25px 0;border:1px solid #ccc}:host[depth-even]{background-color:#f1f1f1}:host[depth-odd]{background-color:#fff}:host.disabled{background-color:#d5d8da}:host.cdk-drag-disabled .cdk-drag-handle{display:none}question-editor.has-conditional{border-bottom:1px solid #ccc}.survey-item-header{display:flex;justify-content:space-between;min-height:50px}.survey-item-header-title{display:flex;align-items:center;flex-grow:1;padding:10px}.survey-item-header-title .title-prefix{margin-right:8px;display:inline-block;min-width:-moz-fit-content;min-width:fit-content}.survey-item-header-title input{transition:all .2s ease-out;width:100%}.survey-item-header-title input[readonly]{background-color:transparent;border-color:transparent;box-shadow:none}.survey-item-header-title input[readonly]:focus{outline:none;box-shadow:none}:host.expanded>.survey-item-header{border-bottom:1px solid #ccc}.survey-item-header-actions{display:flex;align-items:center}.survey-item-header-actions>*{border-left:1px solid #ccc;width:49px;height:49px;cursor:pointer;display:flex;align-items:center;justify-content:center}.survey-item-header-actions>*[cdkdraghandle]{cursor:default}.survey-item-container{overflow:hidden}conditional-response-select{padding:20px}.add-conditional-button.alignright{margin:0 0 0 auto;border-left:1px solid #ccc}\n", ".fuzzy-dialog{min-width:320px;position:relative}.fuzzy-dialog h3{margin-top:0}.fuzzy-dialog h4{margin:0 0 -5px;font-size:14px;border-bottom:1px solid #ccc}.fuzzy-dialog button{margin:12px 0 18px}.fuzzy-dialog p{display:flex}.fuzzy-dialog p span{min-width:60px;display:inline-block}.fuzzy-dialog .fuzzy-item{margin:15px 0;max-width:700px}.fuzzy-dialog .fuzzy-item p{margin:0 0 6px}.fuzzy-dialog .close-fuzzy{cursor:pointer;position:absolute;top:-17px;right:-13px}\n"] }]
    }], function () { return [{ type: i0.ElementRef }, { type: i6$2.CdkDrag, decorators: [{
                type: Host
            }, {
                type: Self
            }, {
                type: Optional
            }] }, { type: i0.ChangeDetectorRef }, { type: i1$3.MatDialog }, { type: Survey }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }]; }, { expanded: [{
            type: HostBinding,
            args: ['class.expanded']
        }], dragHandles: [{
            type: ViewChildren,
            args: [CdkDragHandle]
        }], fuzzyTranslation: [{
            type: ViewChild,
            args: ['fuzzyTranslation', { static: true }]
        }], item: [{
            type: Input
        }], depth: [{
            type: Input
        }], index: [{
            type: Input
        }], prefix: [{
            type: Input
        }], conditionalQuestion: [{
            type: Input
        }], remove: [{
            type: Output
        }], isDraggable: [{
            type: HostBinding,
            args: ['class.cdk-drag-disabled']
        }] }); })();

class VersionWarningComponent {
    constructor(ref) {
        this.ref = ref;
    }
}
VersionWarningComponent.ɵfac = function VersionWarningComponent_Factory(t) { return new (t || VersionWarningComponent)(i0.ɵɵdirectiveInject(i1$3.MatDialogRef)); };
VersionWarningComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: VersionWarningComponent, selectors: [["ng-component"]], decls: 9, vars: 0, consts: [[1, "buttons-confirm"], ["mat-flat-button", "", "color", "primary", 3, "click"], ["mat-flat-button", "", "color", "error", 3, "click"]], template: function VersionWarningComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "h1");
        i0.ɵɵtext(1, "Heads up!");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(2, "p");
        i0.ɵɵtext(3, "You appear to be making edits to an earlier version of the survey. Should you be editing the latest version?");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(4, "div", 0)(5, "button", 1);
        i0.ɵɵlistener("click", function VersionWarningComponent_Template_button_click_5_listener() { return ctx.ref.close(true); });
        i0.ɵɵtext(6, "Go to Latest Version");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(7, "button", 2);
        i0.ɵɵlistener("click", function VersionWarningComponent_Template_button_click_7_listener() { return ctx.ref.close(false); });
        i0.ɵɵtext(8, "Keep Current Version");
        i0.ɵɵelementEnd()();
    } }, dependencies: [i2.MatButton], styles: [".buttons-confirm[_ngcontent-%COMP%]   button[_ngcontent-%COMP%] + button[_ngcontent-%COMP%]{margin-left:18px}button.mat-error[_ngcontent-%COMP%]{display:inline-block}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(VersionWarningComponent, [{
        type: Component,
        args: [{ template: `
	<h1>Heads up!</h1>
	<p>You appear to be making edits to an earlier version of the survey. Should you be editing the latest version?</p>
	<div class='buttons-confirm'>
		<button mat-flat-button color="primary" (click)="ref.close(true)">Go to Latest Version</button>
		<button mat-flat-button color="error" (click)="ref.close(false)">Keep Current Version</button>
	</div>
	`, styles: [".buttons-confirm button+button{margin-left:18px}button.mat-error{display:inline-block}\n"] }]
    }], function () { return [{ type: i1$3.MatDialogRef }]; }, null); })();

function QuestionsTabComponent_ng_container_0_p_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p", 5);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1("*Note that question groups can be nested up to ", ctx_r3.editorConfig.maxDepth, " levels deep.");
} }
function QuestionsTabComponent_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "p");
    i0.ɵɵtext(2, "Survey questions can be grouped into categories or created as a single list.");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "p");
    i0.ɵɵtext(4, "If you would like to create a grouped list use the add group button and add your questions inside your new group.");
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(5, QuestionsTabComponent_ng_container_0_p_5_Template, 2, 1, "p", 4);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(5);
    i0.ɵɵproperty("ngIf", ctx_r0.editorConfig.maxDepth);
} }
function QuestionsTabComponent_div_1_button_1_Template(rf, ctx) { if (rf & 1) {
    const _r7 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 9);
    i0.ɵɵlistener("click", function QuestionsTabComponent_div_1_button_1_Template_button_click_0_listener($event) { i0.ɵɵrestoreView(_r7); const ctx_r6 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r6.onAddQuestionClick($event)); });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("disabled", ctx_r4.editorConfig.maxItems <= ctx_r4.survey.items.length);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(ctx_r4.editorConfig.i18n.questions.addLabel || "Add Question");
} }
function QuestionsTabComponent_div_1_button_2_Template(rf, ctx) { if (rf & 1) {
    const _r9 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 10);
    i0.ɵɵlistener("click", function QuestionsTabComponent_div_1_button_2_Template_button_click_0_listener($event) { i0.ɵɵrestoreView(_r9); const ctx_r8 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r8.onAddGroupClick($event)); });
    i0.ɵɵtext(1, "Add Group");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r5 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("disabled", ctx_r5.editorConfig.maxItems <= ctx_r5.survey.items.length);
} }
function QuestionsTabComponent_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 6);
    i0.ɵɵtemplate(1, QuestionsTabComponent_div_1_button_1_Template, 2, 2, "button", 7);
    i0.ɵɵtemplate(2, QuestionsTabComponent_div_1_button_2_Template, 2, 1, "button", 8);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.editorConfig.permissions.questions.add);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.editorConfig.permissions.groups.add);
} }
function QuestionsTabComponent_survey_item_3_Template(rf, ctx) { if (rf & 1) {
    const _r13 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "survey-item", 11);
    i0.ɵɵlistener("remove", function QuestionsTabComponent_survey_item_3_Template_survey_item_remove_0_listener($event) { i0.ɵɵrestoreView(_r13); const ctx_r12 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r12.onRemoveItem($event)); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const item_r10 = ctx.$implicit;
    const i_r11 = ctx.index;
    i0.ɵɵproperty("index", i_r11 + 1)("item", item_r10)("depth", 1);
} }
class QuestionsTabComponent {
    constructor(cd, editorConfig) {
        this.cd = cd;
        this.editorConfig = editorConfig;
    }
    /**
     *
     * @param event
     */
    onAddQuestionClick(event) {
        event.preventDefault();
        this.addQuestion();
    }
    /**
     *
     * @param event
     */
    onAddGroupClick(event) {
        event.preventDefault();
        this.addGroup();
    }
    /**
     * Add a top level question
     */
    addQuestion() {
        const question = new Question();
        question.expanded = true;
        /**
         * If an 'onAdd' callback is specified, call it before adding the question to the survey
         */
        if (this.editorConfig.questions?.onAdd && window[this.editorConfig.questions.onAdd]) {
            const cb = window[this.editorConfig.questions.onAdd];
            try {
                cb(question, null, this.survey);
            }
            catch (error) {
                console.error(error);
            }
        }
        this.survey.addQuestion(question);
        this.cd.markForCheck();
        this.cd.detectChanges();
        return true;
    }
    /**
     * Add a top level group
     */
    addGroup() {
        const group = new QuestionGroup();
        group.expanded = true;
        /**
         * If an 'onAdd' callback is specified, call it before adding the group to the survey
         */
        if (this.editorConfig.groups?.onAdd && window[this.editorConfig.groups.onAdd]) {
            const cb = window[this.editorConfig.groups.onAdd];
            try {
                cb(group, null, this.survey);
            }
            catch (error) {
                console.error(error);
            }
        }
        this.survey.addGroup(group);
        this.cd.markForCheck();
        this.cd.detectChanges();
        return true;
    }
    /**
     * Remove a top level item
     */
    onRemoveItem(item) {
        this.survey.items.splice(this.survey.items.indexOf(item), 1);
        this.cd.markForCheck();
    }
}
QuestionsTabComponent.ɵfac = function QuestionsTabComponent_Factory(t) { return new (t || QuestionsTabComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG)); };
QuestionsTabComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionsTabComponent, selectors: [["questions-tab"]], inputs: { survey: "survey" }, decls: 4, vars: 5, consts: [[4, "ngIf"], ["id", "topControls", 4, "ngIf"], ["id", "questionContainer", "cdkDropList", "", "cdkDropListLockAxis", "y", 3, "cdkDropListData", "cdkDropListDisabled"], ["cdkDrag", "", "cdkDragBoundary", "#questionContainer", 3, "index", "item", "depth", "remove", 4, "ngFor", "ngForOf"], ["class", "note", 4, "ngIf"], [1, "note"], ["id", "topControls"], ["version-prompt", "", "mat-flat-button", "", "version-prompt", "", "color", "primary", 3, "disabled", "click", 4, "ngIf"], ["version-prompt", "", "mat-flat-button", "", "color", "primary", 3, "disabled", "click", 4, "ngIf"], ["version-prompt", "", "mat-flat-button", "", "version-prompt", "", "color", "primary", 3, "disabled", "click"], ["version-prompt", "", "mat-flat-button", "", "color", "primary", 3, "disabled", "click"], ["cdkDrag", "", "cdkDragBoundary", "#questionContainer", 3, "index", "item", "depth", "remove"]], template: function QuestionsTabComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, QuestionsTabComponent_ng_container_0_Template, 6, 1, "ng-container", 0);
        i0.ɵɵtemplate(1, QuestionsTabComponent_div_1_Template, 3, 2, "div", 1);
        i0.ɵɵelementStart(2, "div", 2);
        i0.ɵɵtemplate(3, QuestionsTabComponent_survey_item_3_Template, 1, 3, "survey-item", 3);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.editorConfig.maxDepth > 1);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.editorConfig.permissions.questions.add || ctx.editorConfig.permissions.groups.add);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("cdkDropListData", ctx.survey.items)("cdkDropListDisabled", !ctx.editorConfig.permissions.questions.edit);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngForOf", ctx.survey.items);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i2.MatButton, i6$2.CdkDropList, i6$2.CdkDrag, VersionPromptDirective, SurveyItemComponent], styles: ["[_nghost-%COMP%]{display:block}#topControls[_ngcontent-%COMP%]{margin:25px 0}#topControls[_ngcontent-%COMP%]   button[_ngcontent-%COMP%] + button[_ngcontent-%COMP%]{margin-left:24px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionsTabComponent, [{
        type: Component,
        args: [{ selector: 'questions-tab', template: "<ng-container *ngIf=\"editorConfig.maxDepth > 1\">\n\t<p>Survey questions can be grouped into categories or created as a single list.</p>\n\t<p>If you would like to create a grouped list use the add group button and add your questions inside your new group.</p>\n\t<p *ngIf=\"editorConfig.maxDepth\"\n\t   class=\"note\">*Note that question groups can be nested up to {{editorConfig.maxDepth}} levels deep.</p>\n</ng-container>\n\n<div id=\"topControls\"\n\t *ngIf=\"editorConfig.permissions.questions.add || editorConfig.permissions.groups.add\">\n\n\t<button *ngIf=\"editorConfig.permissions.questions.add\"\n\t\t\tversion-prompt\n\t\t\tmat-flat-button\n\t\t\tversion-prompt\n\t\t\t[disabled]=\"editorConfig.maxItems <= survey.items.length\"\n\t\t\tcolor=\"primary\"\n\t\t\t(click)=\"onAddQuestionClick( $event )\">{{ (editorConfig.i18n.questions.addLabel || 'Add Question' ) }}</button>\n\n\t<button *ngIf=\"editorConfig.permissions.groups.add\"\n\t\t\tversion-prompt\n\t\t\tmat-flat-button\n\t\t\t[disabled]=\"editorConfig.maxItems <= survey.items.length\"\n\t\t\tcolor=\"primary\"\n\t\t\t(click)=\"onAddGroupClick( $event )\">Add Group</button>\n\n</div>\n\n\n<div id=\"questionContainer\"\n\t cdkDropList\n\t cdkDropListLockAxis='y'\n\t [cdkDropListData]=\"survey.items\"\n\t [cdkDropListDisabled]=\"!editorConfig.permissions.questions.edit\">\n\n\n\t<survey-item *ngFor=\"let item of survey.items;index as i;\"\n\t\t\t\t cdkDrag\n\t\t\t\t [index]=\"i+1\"\n\t\t\t\t cdkDragBoundary='#questionContainer'\n\t\t\t\t [item]=\"item\"\n\t\t\t\t [depth]=\"1\"\n\t\t\t\t (remove)=\"onRemoveItem($event)\"></survey-item>\n\n</div>\n", styles: [":host{display:block}#topControls{margin:25px 0}#topControls button+button{margin-left:24px}\n"] }]
    }], function () { return [{ type: i0.ChangeDetectorRef }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }]; }, { survey: [{
            type: Input
        }] }); })();

class RecommendationItem {
    constructor(data = {}) {
        if (data.text) {
            this.text = data.text;
        }
        if (data.scoreRange) {
            this.scoreRange = data.scoreRange;
        }
        if (data.object) {
            this.object = data.object;
        }
    }
}

/**
 * Within the editor we don't use the Reactive Forms Module, we use the traditional Forms Module that uses [(ngModel)]
 * As such, the input elements contains attributes that determine that validity requirements. These are checked when the form is submitted
 * When there are hidden invalid elements the browser will throw an error saying that an element is not focusable. The browser is trying to focus on the input element with the error.
 * This service class is used to register containers witin the survey editor that can be expanded/hidden.
 * When the form submit button is clicked, we find all invalid inputs that are not visible and make their containers expanded.
 * This allows the native form validation to focus on the error element
 */
class FormValidityService {
    constructor() {
        this.visibilityContainers = [];
        // this editor is tightly bound to the wordpress post editor page.
        // This area would need a bit of work if #post and #publish elements were not available
        this.form = document.getElementById('post');
        if (this.form) {
            this.registerFormSubmitButtonListener();
        }
    }
    /**
     * Attach a listener to the form submit button
     * This will trigger before the form tries to perform validation natively
     */
    registerFormSubmitButtonListener() {
        const but = document.getElementById('publish');
        if (but) {
            but.addEventListener('click', e => this.runVisibilityCheck(e));
        }
    }
    /**
     * Register a container that implements the expanded property
     */
    registerControlVisibilityContainer(container) {
        this.visibilityContainers.push(container);
    }
    /**
     * Before form submits, find any containers that have hidden invalid inputs and make them visible
     */
    runVisibilityCheck(event) {
        // get any invalid inputs in the form
        const invalidInputs = Array.from(this.form.querySelectorAll('*:invalid'));
        // find all the registered containers that contain any of the hidden invalid inputs and expand them
        this.visibilityContainers.filter(container => invalidInputs.filter(input => container.el.nativeElement.contains(input)).length > 0).forEach(c => c.expanded = true);
    }
}
FormValidityService.ɵfac = function FormValidityService_Factory(t) { return new (t || FormValidityService)(); };
FormValidityService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: FormValidityService, factory: FormValidityService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FormValidityService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return []; }, null); })();

const _c0$9 = ["fuzzyTranslation"];
function RecommendationSurveyItemComponent_mat_icon_5_Template(rf, ctx) { if (rf & 1) {
    const _r5 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-icon", 7);
    i0.ɵɵlistener("click", function RecommendationSurveyItemComponent_mat_icon_5_Template_mat_icon_click_0_listener() { i0.ɵɵrestoreView(_r5); const ctx_r4 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r4.toggleExpanded()); });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(ctx_r0.expanded ? "keyboard_arrow_up" : "keyboard_arrow_down");
} }
function RecommendationSurveyItemComponent_div_6_recommendation_group_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "recommendation-group", 16);
} if (rf & 2) {
    const ctx_r6 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("group", ctx_r6.item)("prefix", ctx_r6.itemPrefix)("depth", ctx_r6.depth);
} }
function RecommendationSurveyItemComponent_div_6_div_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 17)(1, "span", 18);
    i0.ɵɵtext(2);
    i0.ɵɵelementStart(3, "i");
    i0.ɵɵtext(4);
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const ctx_r7 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1("", ctx_r7.itemPrefix, ". Recommendations for ");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(ctx_r7.item.title);
} }
function RecommendationSurveyItemComponent_div_6_button_4_Template(rf, ctx) { if (rf & 1) {
    const _r13 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 19);
    i0.ɵɵlistener("click", function RecommendationSurveyItemComponent_div_6_button_4_Template_button_click_0_listener($event) { i0.ɵɵrestoreView(_r13); const ctx_r12 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r12.addRecommendation($event)); });
    i0.ɵɵtext(1, "Add Recommendation");
    i0.ɵɵelementEnd();
} }
function RecommendationSurveyItemComponent_div_6_recommendations_table_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "recommendations-table", 20);
} if (rf & 2) {
    const ctx_r9 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("recommendations", ctx_r9.item.recommendations)("maximumScore", ctx_r9.item.maximumScore);
} }
function RecommendationSurveyItemComponent_div_6_div_6_button_7_Template(rf, ctx) { if (rf & 1) {
    const _r16 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 19);
    i0.ɵɵlistener("click", function RecommendationSurveyItemComponent_div_6_div_6_button_7_Template_button_click_0_listener($event) { i0.ɵɵrestoreView(_r16); const ctx_r15 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r15.addRecommendation($event)); });
    i0.ɵɵtext(1, "Add Recommendation");
    i0.ɵɵelementEnd();
} }
function RecommendationSurveyItemComponent_div_6_div_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 21)(1, "div", 22)(2, "span", 23);
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "span", 24);
    i0.ɵɵtext(5);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(6, "div", 25);
    i0.ɵɵtemplate(7, RecommendationSurveyItemComponent_div_6_div_6_button_7_Template, 2, 0, "button", 26);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r10 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1("", ctx_r10.itemPrefix, ".");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1("Max Value ", ctx_r10.item.maximumScore, "");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ctx_r10.item.recommendations.length);
} }
function RecommendationSurveyItemComponent_div_6_div_7_recommendation_survey_item_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "recommendation-survey-item", 29);
} if (rf & 2) {
    const item_r18 = ctx.$implicit;
    const i_r19 = ctx.index;
    const ctx_r17 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("item", item_r18)("index", i_r19 + 1)("depth", ctx_r17.depth);
} }
function RecommendationSurveyItemComponent_div_6_div_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 27)(1, "h3");
    i0.ɵɵtext(2, "Conditional Questions");
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(3, RecommendationSurveyItemComponent_div_6_div_7_recommendation_survey_item_3_Template, 1, 3, "recommendation-survey-item", 28);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r11 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngForOf", ctx_r11.item.conditionalQuestions.items);
} }
function RecommendationSurveyItemComponent_div_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 8);
    i0.ɵɵtemplate(1, RecommendationSurveyItemComponent_div_6_recommendation_group_1_Template, 1, 3, "recommendation-group", 9);
    i0.ɵɵelementStart(2, "div", 10);
    i0.ɵɵtemplate(3, RecommendationSurveyItemComponent_div_6_div_3_Template, 5, 2, "div", 11);
    i0.ɵɵtemplate(4, RecommendationSurveyItemComponent_div_6_button_4_Template, 2, 0, "button", 12);
    i0.ɵɵtemplate(5, RecommendationSurveyItemComponent_div_6_recommendations_table_5_Template, 1, 2, "recommendations-table", 13);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(6, RecommendationSurveyItemComponent_div_6_div_6_Template, 8, 3, "div", 14);
    i0.ɵɵtemplate(7, RecommendationSurveyItemComponent_div_6_div_7_Template, 4, 1, "div", 15);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("hidden", !ctx_r1.expanded);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.item.type === ctx_r1.SurveyItemType.QUESTION_GROUP);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ctx_r1.item.type === ctx_r1.SurveyItemType.QUESTION_GROUP);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", !ctx_r1.item.recommendations.length && ctx_r1.editorConfig.permissions.recommendations.edit);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.item.recommendations.length);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.editorConfig.permissions.recommendations.edit);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.item.type === ctx_r1.SurveyItemType.QUESTION && (ctx_r1.item.conditionalQuestions == null ? null : ctx_r1.item.conditionalQuestions.items.length));
} }
function RecommendationSurveyItemComponent_ng_template_7_Template(rf, ctx) { }
class RecommendationSurveyItemComponent {
    get IsQuestionType() {
        return this.item.type === SurveyItemType.QUESTION;
    }
    get IsGroupType() {
        return this.item.type === SurveyItemType.QUESTION_GROUP;
    }
    get disabled() {
        // if it's a question and there are no recommendations and the user doesn't have edit permissions ( i.e. this is a translation ), then don't allow expansion
        if (this.item.type === SurveyItemType.QUESTION && this.item.recommendations.length === 0 && !this.editorConfig.permissions.recommendations.edit) {
            return true;
        }
        // if no score can be found for this question/group then there's no recommendations to be made
        return !this.item.maximumScore;
    }
    constructor(el, changeDetectorRef, dialog, survey, editorConfig, formValidity) {
        this.el = el;
        this.changeDetectorRef = changeDetectorRef;
        this.dialog = dialog;
        this.survey = survey;
        this.editorConfig = editorConfig;
        this.SurveyItemType = SurveyItemType;
        formValidity.registerControlVisibilityContainer(this);
    }
    ngOnChanges(changes) {
        if (changes.depth && changes.depth.currentValue) {
            this.el.nativeElement.setAttribute('depth', changes.depth.currentValue);
            this.el.nativeElement.removeAttribute('depth-even');
            this.el.nativeElement.removeAttribute('depth-odd');
            if (changes.depth.currentValue % 2 === 0) {
                this.el.nativeElement.setAttribute('depth-even', '');
            }
            else {
                this.el.nativeElement.setAttribute('depth-odd', '');
            }
        }
        this.setItemPrefix();
    }
    ngOnInit() {
        if (this.item.type === SurveyItemType.QUESTION) {
            this.itemPlaceholder = 'Question';
        }
        else {
            this.itemPlaceholder = 'Group';
        }
    }
    /**
     * Add a new recommendation item
     */
    addRecommendation(event) {
        event.preventDefault();
        if (!this.item.meta.insertId) {
            this.item.meta.insertId = 0;
        }
        const recommendation = new RecommendationItem();
        recommendation.id = this.item.meta.insertId++;
        this.item.recommendations.push(recommendation);
        this.changeDetectorRef.detectChanges();
        const newItem = Array.from(this.el.nativeElement.querySelectorAll('.cell-text-recommendation')).pop();
        if (newItem) {
            newItem.focus();
        }
    }
    /**
     * Set the prefix before the label
     */
    setItemPrefix() {
        let prefix = this.prefix || '';
        if (this.item.type === SurveyItemType.QUESTION) {
            prefix += prefix ? ' | Q' + this.index : '' + this.index;
        }
        else {
            prefix += prefix ? '.' + this.index : '' + this.index;
        }
        this.itemPrefix = prefix;
        this.item['prefix'] = prefix;
    }
    toggleExpanded() {
        this.expanded = !this.expanded;
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
    }
    isListset(key) {
        return 'listValues' === key;
    }
}
RecommendationSurveyItemComponent.ɵfac = function RecommendationSurveyItemComponent_Factory(t) { return new (t || RecommendationSurveyItemComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1$3.MatDialog), i0.ɵɵdirectiveInject(Survey), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG), i0.ɵɵdirectiveInject(FormValidityService)); };
RecommendationSurveyItemComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: RecommendationSurveyItemComponent, selectors: [["recommendation-survey-item"]], viewQuery: function RecommendationSurveyItemComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0$9, 7);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.fuzzyTranslation = _t.first);
    } }, hostVars: 8, hostBindings: function RecommendationSurveyItemComponent_HostBindings(rf, ctx) { if (rf & 2) {
        i0.ɵɵclassProp("type-question", ctx.IsQuestionType)("type-group", ctx.IsGroupType)("expanded", ctx.expanded)("disabled", ctx.disabled);
    } }, inputs: { item: "item", depth: "depth", index: "index", prefix: "prefix" }, features: [i0.ɵɵNgOnChangesFeature], decls: 9, vars: 4, consts: [[1, "survey-item-header"], [1, "survey-item-header-title"], [1, "title-prefix"], [1, "survey-item-header-actions"], [3, "click", 4, "ngIf"], ["class", "survey-item-container", 3, "hidden", 4, "ngIf"], ["fuzzyTranslation", ""], [3, "click"], [1, "survey-item-container", 3, "hidden"], [3, "group", "prefix", "depth", 4, "ngIf"], [1, "recommendations-container"], ["class", "group-recommendations-label", 4, "ngIf"], ["mat-flat-button", "", "color", "primary", "class", "add-recommendation-button", 3, "click", 4, "ngIf"], [3, "recommendations", "maximumScore", 4, "ngIf"], ["class", "survey-item-footer", 4, "ngIf"], ["class", "conditional-questions", 4, "ngIf"], [3, "group", "prefix", "depth"], [1, "group-recommendations-label"], [1, "prefix"], ["mat-flat-button", "", "color", "primary", 1, "add-recommendation-button", 3, "click"], [3, "recommendations", "maximumScore"], [1, "survey-item-footer"], [1, "footer-text"], [1, "footer-text-prefix"], [1, "footer-text-label"], [1, "align-right"], ["class", "add-recommendation-button", "mat-flat-button", "", "color", "primary", 3, "click", 4, "ngIf"], [1, "conditional-questions"], [3, "item", "index", "depth", 4, "ngFor", "ngForOf"], [3, "item", "index", "depth"]], template: function RecommendationSurveyItemComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0)(1, "div", 1)(2, "span", 2);
        i0.ɵɵtext(3);
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(4, "div", 3);
        i0.ɵɵtemplate(5, RecommendationSurveyItemComponent_mat_icon_5_Template, 2, 1, "mat-icon", 4);
        i0.ɵɵelementEnd()();
        i0.ɵɵtemplate(6, RecommendationSurveyItemComponent_div_6_Template, 8, 7, "div", 5);
        i0.ɵɵtemplate(7, RecommendationSurveyItemComponent_ng_template_7_Template, 0, 0, "ng-template", null, 6, i0.ɵɵtemplateRefExtractor);
    } if (rf & 2) {
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate2("", ctx.itemPrefix, ". ", ctx.item.title, "");
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", !ctx.disabled);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.item.maximumScore);
    } }, styles: [".recommendation-list[_ngcontent-%COMP%]{padding:20px}.recommendations-container[_ngcontent-%COMP%]{padding:0 20px 20px}.recommendations-container[_ngcontent-%COMP%] > button[_ngcontent-%COMP%]:first-child{margin-top:20px}.group-recommendations-label[_ngcontent-%COMP%]{font-weight:700}.group-recommendations-label[_ngcontent-%COMP%] + button[_ngcontent-%COMP%]{margin-top:12px}.conditional-questions[_ngcontent-%COMP%]{padding:0 20px;border-top:1px solid #ccc}.footer-text[_ngcontent-%COMP%]{display:flex}.footer-text-prefix[_ngcontent-%COMP%]{border-right:1px solid #ccc;padding:14px}.footer-text-label[_ngcontent-%COMP%]{padding:14px}", "[_nghost-%COMP%]{display:block;margin:25px 0;border:1px solid #ccc}[depth-even][_nghost-%COMP%]{background-color:#f1f1f1}[depth-odd][_nghost-%COMP%]{background-color:#fff}.disabled[_nghost-%COMP%]{background-color:#d5d8da}.cdk-drag-disabled[_nghost-%COMP%]   .cdk-drag-handle[_ngcontent-%COMP%]{display:none}question-editor.has-conditional[_ngcontent-%COMP%]{border-bottom:1px solid #ccc}.survey-item-header[_ngcontent-%COMP%]{display:flex;justify-content:space-between;min-height:50px}.survey-item-header-title[_ngcontent-%COMP%]{display:flex;align-items:center;flex-grow:1;padding:10px}.survey-item-header-title[_ngcontent-%COMP%]   .title-prefix[_ngcontent-%COMP%]{margin-right:8px;display:inline-block;min-width:-moz-fit-content;min-width:fit-content}.survey-item-header-title[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{transition:all .2s ease-out;width:100%}.survey-item-header-title[_ngcontent-%COMP%]   input[readonly][_ngcontent-%COMP%]{background-color:transparent;border-color:transparent;box-shadow:none}.survey-item-header-title[_ngcontent-%COMP%]   input[readonly][_ngcontent-%COMP%]:focus{outline:none;box-shadow:none}.expanded[_nghost-%COMP%] > .survey-item-header[_ngcontent-%COMP%]{border-bottom:1px solid #ccc}.survey-item-header-actions[_ngcontent-%COMP%]{display:flex;align-items:center}.survey-item-header-actions[_ngcontent-%COMP%] > *[_ngcontent-%COMP%]{border-left:1px solid #ccc;width:49px;height:49px;cursor:pointer;display:flex;align-items:center;justify-content:center}.survey-item-header-actions[_ngcontent-%COMP%] > *[cdkdraghandle][_ngcontent-%COMP%]{cursor:default}.survey-item-container[_ngcontent-%COMP%]{overflow:hidden}conditional-response-select[_ngcontent-%COMP%]{padding:20px}.add-conditional-button.alignright[_ngcontent-%COMP%]{margin:0 0 0 auto;border-left:1px solid #ccc}", ".survey-item-footer[_ngcontent-%COMP%]{border-top:1px solid #ccc;display:flex;align-items:center;height:50px}.survey-item-footer-index[_ngcontent-%COMP%]{padding:0 10px;display:flex;align-items:center;justify-content:center;align-self:stretch;border-right:1px solid #ccc}.survey-item-footer[_ngcontent-%COMP%]   .survey-item-optional[_ngcontent-%COMP%]{margin-left:12px;margin-right:12px}.survey-item-footer[_ngcontent-%COMP%]   .align-right[_ngcontent-%COMP%]{margin-left:auto}.survey-item-footer[_ngcontent-%COMP%]   .align-right[_ngcontent-%COMP%]     button{margin-right:8px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RecommendationSurveyItemComponent, [{
        type: Component,
        args: [{ selector: 'recommendation-survey-item', template: "<div class=\"survey-item-header\">\n\n\t<div class=\"survey-item-header-title\">\n\t\t<span class=\"title-prefix\">{{itemPrefix}}. {{item.title}}</span>\n\t</div>\n\n\t<div class=\"survey-item-header-actions\">\n\n\t\t<mat-icon *ngIf=\"!disabled\"\n\t\t\t\t  (click)=\"toggleExpanded()\">{{expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}}</mat-icon>\n\n\t</div>\n</div>\n\n<div *ngIf=\"item.maximumScore\"\n\t class=\"survey-item-container\"\n\t [hidden]=\"!expanded\">\n\n\n\t<recommendation-group *ngIf=\"item.type === SurveyItemType.QUESTION_GROUP\"\n\t\t\t\t\t\t  [group]=\"item\"\n\t\t\t\t\t\t  [prefix]=\"itemPrefix\"\n\t\t\t\t\t\t  [depth]=\"depth\"></recommendation-group>\n\n\n\t<div class=\"recommendations-container\">\n\n\t\t<div class=\"group-recommendations-label\"\n\t\t\t *ngIf=\"item.type === SurveyItemType.QUESTION_GROUP\">\n\n\t\t\t<span class=\"prefix\">{{itemPrefix}}. Recommendations for <i>{{item.title}}</i></span>\n\t\t</div>\n\n\t\t<button *ngIf=\"!item.recommendations.length && editorConfig.permissions.recommendations.edit\"\n\t\t\t\tmat-flat-button\n\t\t\t\tcolor=\"primary\"\n\t\t\t\tclass=\"add-recommendation-button\"\n\t\t\t\t(click)=\"addRecommendation($event)\">Add Recommendation</button>\n\n\t\t<recommendations-table *ngIf=\"item.recommendations.length\"\n\t\t\t\t\t\t\t   [recommendations]=\"item.recommendations\"\n\t\t\t\t\t\t\t   [maximumScore]=\"item.maximumScore\"></recommendations-table>\n\n\t</div>\n\n\t<div *ngIf=\"editorConfig.permissions.recommendations.edit\"\n\t\t class=\"survey-item-footer\">\n\n\t\t<div class=\"footer-text\">\n\n\t\t\t<span class=\"footer-text-prefix\">{{itemPrefix}}.</span>\n\n\t\t\t<span class=\"footer-text-label\">Max Value {{item.maximumScore}}</span>\n\n\t\t</div>\n\n\t\t<div class=\"align-right\">\n\t\t\t<button *ngIf=\"item.recommendations.length\"\n\t\t\t\t\tclass=\"add-recommendation-button\"\n\t\t\t\t\tmat-flat-button\n\t\t\t\t\tcolor=\"primary\"\n\t\t\t\t\t(click)=\"addRecommendation($event)\">Add Recommendation</button>\n\n\t\t</div>\n\n\n\t</div>\n\n\t<div *ngIf=\"item.type === SurveyItemType.QUESTION && $any(item).conditionalQuestions?.items.length\"\n\t\t class=\"conditional-questions\">\n\n\t\t<h3>Conditional Questions</h3>\n\t\t<recommendation-survey-item [item]=\"item\"\n\t\t\t\t\t\t\t\t\t*ngFor=\"let item of $any(item).conditionalQuestions.items;index as i;\"\n\t\t\t\t\t\t\t\t\t[index]=\"i+1\"\n\t\t\t\t\t\t\t\t\t[depth]=\"depth\"></recommendation-survey-item>\n\n\t</div>\n\n\n</div>\n\n<!-- @todo fuzzy translation -->\n<ng-template #fuzzyTranslation\n\t\t\t let-item>\n</ng-template>\n", styles: [".recommendation-list{padding:20px}.recommendations-container{padding:0 20px 20px}.recommendations-container>button:first-child{margin-top:20px}.group-recommendations-label{font-weight:700}.group-recommendations-label+button{margin-top:12px}.conditional-questions{padding:0 20px;border-top:1px solid #ccc}.footer-text{display:flex}.footer-text-prefix{border-right:1px solid #ccc;padding:14px}.footer-text-label{padding:14px}\n", ":host{display:block;margin:25px 0;border:1px solid #ccc}:host[depth-even]{background-color:#f1f1f1}:host[depth-odd]{background-color:#fff}:host.disabled{background-color:#d5d8da}:host.cdk-drag-disabled .cdk-drag-handle{display:none}question-editor.has-conditional{border-bottom:1px solid #ccc}.survey-item-header{display:flex;justify-content:space-between;min-height:50px}.survey-item-header-title{display:flex;align-items:center;flex-grow:1;padding:10px}.survey-item-header-title .title-prefix{margin-right:8px;display:inline-block;min-width:-moz-fit-content;min-width:fit-content}.survey-item-header-title input{transition:all .2s ease-out;width:100%}.survey-item-header-title input[readonly]{background-color:transparent;border-color:transparent;box-shadow:none}.survey-item-header-title input[readonly]:focus{outline:none;box-shadow:none}:host.expanded>.survey-item-header{border-bottom:1px solid #ccc}.survey-item-header-actions{display:flex;align-items:center}.survey-item-header-actions>*{border-left:1px solid #ccc;width:49px;height:49px;cursor:pointer;display:flex;align-items:center;justify-content:center}.survey-item-header-actions>*[cdkdraghandle]{cursor:default}.survey-item-container{overflow:hidden}conditional-response-select{padding:20px}.add-conditional-button.alignright{margin:0 0 0 auto;border-left:1px solid #ccc}\n", ".survey-item-footer{border-top:1px solid #ccc;display:flex;align-items:center;height:50px}.survey-item-footer-index{padding:0 10px;display:flex;align-items:center;justify-content:center;align-self:stretch;border-right:1px solid #ccc}.survey-item-footer .survey-item-optional{margin-left:12px;margin-right:12px}.survey-item-footer .align-right{margin-left:auto}.survey-item-footer .align-right ::ng-deep button{margin-right:8px}\n"] }]
    }], function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i1$3.MatDialog }, { type: Survey }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }, { type: FormValidityService }]; }, { IsQuestionType: [{
            type: HostBinding,
            args: ['class.type-question']
        }], IsGroupType: [{
            type: HostBinding,
            args: ['class.type-group']
        }], expanded: [{
            type: HostBinding,
            args: ['class.expanded']
        }], disabled: [{
            type: HostBinding,
            args: ['class.disabled']
        }], fuzzyTranslation: [{
            type: ViewChild,
            args: ['fuzzyTranslation', { static: true }]
        }], item: [{
            type: Input
        }], depth: [{
            type: Input
        }], index: [{
            type: Input
        }], prefix: [{
            type: Input
        }] }); })();

/**
 * Helper function to get an object property value allowing for dot notation for deep values
 */
const getPropValue = (object, propertyName) => {
    let parts = propertyName.split("."), length = parts.length, i, property = object || this;
    for (i = 0; i < length; i++) {
        property = property[parts[i]];
    }
    return property;
};
/** Helper function used to get the css class for a property name ( ignore dot notation parts ) */
const getPropCssClass = (propertyName) => {
    return propertyName.split('.').shift();
};
/**
 * Pipe to filter table columns to only include those with properties from the given object
 */
class FilterColumnsByPropertyPipe {
    transform(columns, object) {
        return columns.filter(c => getPropValue(object, c.property) !== undefined);
    }
}
FilterColumnsByPropertyPipe.ɵfac = function FilterColumnsByPropertyPipe_Factory(t) { return new (t || FilterColumnsByPropertyPipe)(); };
FilterColumnsByPropertyPipe.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "filterColumnsByProperty", type: FilterColumnsByPropertyPipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FilterColumnsByPropertyPipe, [{
        type: Pipe,
        args: [{
                name: 'filterColumnsByProperty'
            }]
    }], null, null); })();
/**
 * Pipe to return value from an object using dot notation
 */
class PropertyValueFromDotNotationPipe {
    transform(object, propertyName) {
        return getPropValue(object, propertyName);
    }
}
PropertyValueFromDotNotationPipe.ɵfac = function PropertyValueFromDotNotationPipe_Factory(t) { return new (t || PropertyValueFromDotNotationPipe)(); };
PropertyValueFromDotNotationPipe.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "propertyValueFromDotNotation", type: PropertyValueFromDotNotationPipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PropertyValueFromDotNotationPipe, [{
        type: Pipe,
        args: [{
                name: 'propertyValueFromDotNotation'
            }]
    }], null, null); })();
/**
 * Pipe to return css class name based off a dot notation
 */
class CssClassFromDotNotationPipe {
    transform(propertyName) {
        return getPropCssClass(propertyName);
    }
}
CssClassFromDotNotationPipe.ɵfac = function CssClassFromDotNotationPipe_Factory(t) { return new (t || CssClassFromDotNotationPipe)(); };
CssClassFromDotNotationPipe.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "cssClassFromDotNotation", type: CssClassFromDotNotationPipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CssClassFromDotNotationPipe, [{
        type: Pipe,
        args: [{
                name: 'cssClassFromDotNotation'
            }]
    }], null, null); })();
/**
 * Common Editor Pipes module
 */
class EditorPipesModule {
}
EditorPipesModule.ɵfac = function EditorPipesModule_Factory(t) { return new (t || EditorPipesModule)(); };
EditorPipesModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: EditorPipesModule });
EditorPipesModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({});
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(EditorPipesModule, [{
        type: NgModule,
        args: [{
                declarations: [
                    FilterColumnsByPropertyPipe,
                    CssClassFromDotNotationPipe,
                    PropertyValueFromDotNotationPipe
                ],
                exports: [
                    FilterColumnsByPropertyPipe,
                    CssClassFromDotNotationPipe,
                    PropertyValueFromDotNotationPipe
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(EditorPipesModule, { declarations: [FilterColumnsByPropertyPipe, CssClassFromDotNotationPipe, PropertyValueFromDotNotationPipe], exports: [FilterColumnsByPropertyPipe, CssClassFromDotNotationPipe, PropertyValueFromDotNotationPipe] }); })();

function RelatedObjectRowComponent_div_0_div_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "date");
    i0.ɵɵpipe(3, "propertyValueFromDotNotation");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const column_r1 = i0.ɵɵnextContext().$implicit;
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, i0.ɵɵpipeBind2(3, 4, ctx_r2.object, column_r1.property), column_r1.dateFormat ? column_r1.dateFormat : "mediumDate"), " ");
} }
function RelatedObjectRowComponent_div_0_div_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "div", 4);
    i0.ɵɵpipe(1, "propertyValueFromDotNotation");
} if (rf & 2) {
    const column_r1 = i0.ɵɵnextContext().$implicit;
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵproperty("innerHTML", i0.ɵɵpipeBind2(1, 1, ctx_r3.object, column_r1.property), i0.ɵɵsanitizeHtml);
} }
function RelatedObjectRowComponent_div_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵelementContainerStart(1, 1);
    i0.ɵɵtemplate(2, RelatedObjectRowComponent_div_0_div_2_Template, 4, 7, "div", 2);
    i0.ɵɵtemplate(3, RelatedObjectRowComponent_div_0_div_3_Template, 2, 4, "div", 3);
    i0.ɵɵelementContainerEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const column_r1 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngSwitch", column_r1.type);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngSwitchCase", "date");
} }
class RelatedObjectRowComponent {
}
RelatedObjectRowComponent.ɵfac = function RelatedObjectRowComponent_Factory(t) { return new (t || RelatedObjectRowComponent)(); };
RelatedObjectRowComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: RelatedObjectRowComponent, selectors: [["related-object-row"]], inputs: { object: "object", columns: "columns" }, decls: 1, vars: 1, consts: [[4, "ngFor", "ngForOf"], [3, "ngSwitch"], [4, "ngSwitchCase"], [3, "innerHTML", 4, "ngSwitchDefault"], [3, "innerHTML"]], template: function RelatedObjectRowComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, RelatedObjectRowComponent_div_0_Template, 4, 2, "div", 0);
    } if (rf & 2) {
        i0.ɵɵproperty("ngForOf", ctx.columns);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgSwitch, i1$2.NgSwitchCase, i1$2.NgSwitchDefault, i1$2.DatePipe, PropertyValueFromDotNotationPipe], styles: ["[_nghost-%COMP%]{display:flex;width:100%;box-sizing:border-box}[_nghost-%COMP%] > *[_ngcontent-%COMP%]{padding:8px 15px}[_nghost-%COMP%] > *[_ngcontent-%COMP%]:first-child{flex-grow:1}[_nghost-%COMP%] + -shadowcsshost{border-top:none}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RelatedObjectRowComponent, [{
        type: Component,
        args: [{ selector: 'related-object-row', template: "<div *ngFor=\"let column of columns\">\n\n\t<ng-container [ngSwitch]=\"column.type\">\n\n\t\t<div *ngSwitchCase=\"'date'\">\n\t\t\t{{object | propertyValueFromDotNotation:column.property | date:column.dateFormat ? column.dateFormat : 'mediumDate' }}\n\t\t</div>\n\n\t\t<div *ngSwitchDefault\n\t\t\t [innerHTML]=\"object | propertyValueFromDotNotation:column.property\"></div>\n\n\t</ng-container>\n\n</div>\n", styles: [":host{display:flex;width:100%;box-sizing:border-box}:host>*{padding:8px 15px}:host>*:first-child{flex-grow:1}:host+:host{border-top:none}\n"] }]
    }], null, { object: [{
            type: Input
        }], columns: [{
            type: Input
        }] }); })();

function ContentSearchComponent_mat_progress_spinner_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "mat-progress-spinner", 5);
} if (rf & 2) {
    i0.ɵɵproperty("diameter", 16);
} }
function ContentSearchComponent_div_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 6);
    i0.ɵɵtext(1, "No results were found for your search.");
    i0.ɵɵelementEnd();
} }
function ContentSearchComponent_ng_container_6_related_object_row_3_Template(rf, ctx) { if (rf & 1) {
    const _r6 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "related-object-row", 11);
    i0.ɵɵlistener("click", function ContentSearchComponent_ng_container_6_related_object_row_3_Template_related_object_row_click_0_listener() { const restoredCtx = i0.ɵɵrestoreView(_r6); const result_r4 = restoredCtx.$implicit; const ctx_r5 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r5.selectObject(result_r4)); });
    i0.ɵɵpipe(1, "filterColumnsByProperty");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const result_r4 = ctx.$implicit;
    const ctx_r3 = i0.ɵɵnextContext(2);
    i0.ɵɵclassProp("selected", result_r4 === ctx_r3.selectedItem);
    i0.ɵɵproperty("columns", i0.ɵɵpipeBind2(1, 4, ctx_r3.editorConfig.recommendations.object.autocomplete.tableColumns, result_r4))("object", result_r4);
} }
function ContentSearchComponent_ng_container_6_Template(rf, ctx) { if (rf & 1) {
    const _r8 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 7)(2, "div", 8);
    i0.ɵɵtemplate(3, ContentSearchComponent_ng_container_6_related_object_row_3_Template, 2, 7, "related-object-row", 9);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(4, "button", 10);
    i0.ɵɵlistener("click", function ContentSearchComponent_ng_container_6_Template_button_click_4_listener() { i0.ɵɵrestoreView(_r8); const ctx_r7 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r7.confirmSelection()); });
    i0.ɵɵtext(5);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngForOf", ctx_r2.results);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("disabled", !ctx_r2.selectedItem);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1("Select ", ctx_r2.editorConfig.recommendations.object.i18n.singularName, "");
} }
class ContentSearchComponent {
    constructor(http, cd, editorConfig) {
        this.http = http;
        this.cd = cd;
        this.editorConfig = editorConfig;
        this.choose = new EventEmitter();
        this.destroy$ = new Subject();
        this.formControl = new UntypedFormControl('');
        const beforeLoad = () => {
            this.isLoading = true;
            this.results = null;
            document.activeElement.blur();
            this.cd.markForCheck();
        };
        this.formControl.valueChanges.pipe(filter((value) => value.length > 3), debounceTime(300), tap(() => beforeLoad()), switchMap(value => this.http.get(this.editorConfig.recommendations.object.endpoints.list, { params: { search: value } })), tap({
            next: () => {
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.formControl.setErrors({
                    not_found: true
                });
                this.cd.markForCheck();
            }
        }), retry(), takeUntil(this.destroy$)).subscribe(results => {
            this.results = results;
            this.cd.markForCheck();
        });
    }
    /**
     * Set an item to be the currently selected in the list
     */
    selectObject(item) {
        this.selectedItem = item;
    }
    /**
     * Confirm selection
     */
    confirmSelection() {
        this.choose.emit(this.selectedItem);
    }
}
ContentSearchComponent.ɵfac = function ContentSearchComponent_Factory(t) { return new (t || ContentSearchComponent)(i0.ɵɵdirectiveInject(i1$4.HttpClient), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG)); };
ContentSearchComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: ContentSearchComponent, selectors: [["content-search"]], outputs: { choose: "choose" }, decls: 7, vars: 5, consts: [[1, "search-input"], [3, "placeholder", "formControl"], ["mode", "indeterminate", "color", "primary", "matSuffix", "", 3, "diameter", 4, "ngIf"], ["id", "noResultsError", "class", "mat-error", 4, "ngIf"], [4, "ngIf"], ["mode", "indeterminate", "color", "primary", "matSuffix", "", 3, "diameter"], ["id", "noResultsError", 1, "mat-error"], [1, "results-container"], [1, "results-list"], [3, "columns", "object", "selected", "click", 4, "ngFor", "ngForOf"], ["id", "confirmSelectionBtn", "mat-flat-button", "", "color", "primary", 3, "disabled", "click"], [3, "columns", "object", "click"]], template: function ContentSearchComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0)(1, "mat-icon");
        i0.ɵɵtext(2, "search");
        i0.ɵɵelementEnd();
        i0.ɵɵelement(3, "input", 1);
        i0.ɵɵtemplate(4, ContentSearchComponent_mat_progress_spinner_4_Template, 1, 1, "mat-progress-spinner", 2);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(5, ContentSearchComponent_div_5_Template, 2, 0, "div", 3);
        i0.ɵɵtemplate(6, ContentSearchComponent_ng_container_6_Template, 6, 3, "ng-container", 4);
    } if (rf & 2) {
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("placeholder", ctx.editorConfig.recommendations.object.i18n.searchPlaceholder)("formControl", ctx.formControl);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.isLoading);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", (ctx.results == null ? null : ctx.results.length) === 0);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.results == null ? null : ctx.results.length);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i3.DefaultValueAccessor, i3.NgControlStatus, i3.FormControlDirective, i2.MatButton, i4$1.MatSuffix, i6$1.MatIcon, i7$1.MatProgressSpinner, RelatedObjectRowComponent, FilterColumnsByPropertyPipe], styles: ["[_nghost-%COMP%]{display:block}.search-input[_ngcontent-%COMP%]{width:100%;position:relative;display:flex;align-items:center;border:1px solid #ccc;padding:0 8px;box-sizing:border-box;margin-top:18px}.search-input[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{width:100%;border:none;margin:0;padding:8px}.search-input[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]:focus{outline:none}#noResultsError[_ngcontent-%COMP%], #confirmSelectionBtn[_ngcontent-%COMP%]{margin-top:18px}.results-container[_ngcontent-%COMP%]{border:1px solid #ccc;border-top:none;overflow:auto;max-height:242px;margin-bottom:18px}related-object-row[_ngcontent-%COMP%]{cursor:default}related-object-row.selected[_ngcontent-%COMP%]{background-color:#0000003b}"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ContentSearchComponent, [{
        type: Component,
        args: [{ selector: 'content-search', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"search-input\">\n\t<mat-icon>search</mat-icon>\n\t<input [placeholder]=\"editorConfig.recommendations.object.i18n.searchPlaceholder\"\n\t\t   [formControl]=\"formControl\" />\n\n\t<mat-progress-spinner *ngIf=\"isLoading\"\n\t\t\t\t\t\t  mode=\"indeterminate\"\n\t\t\t\t\t\t  color=\"primary\"\n\t\t\t\t\t\t  [diameter]=\"16\"\n\t\t\t\t\t\t  matSuffix></mat-progress-spinner>\n\n</div>\n\n<div *ngIf=\"results?.length === 0\"\n\t id=\"noResultsError\"\n\t class=\"mat-error\">No results were found for your search.</div>\n\n<ng-container *ngIf=\"results?.length\">\n\t<div class=\"results-container\">\n\n\t\t<div class=\"results-list\">\n\t\t\t<related-object-row *ngFor=\"let result of results\"\n\t\t\t\t\t\t\t\t[columns]=\"editorConfig.recommendations.object.autocomplete.tableColumns | filterColumnsByProperty:result\"\n\t\t\t\t\t\t\t\t[object]=\"result\"\n\t\t\t\t\t\t\t\t[class.selected]=\"result === selectedItem\"\n\t\t\t\t\t\t\t\t(click)=\"selectObject( result )\">\n\t\t\t</related-object-row>\n\n\t\t</div>\n\n\t</div>\n\n\t<button id=\"confirmSelectionBtn\"\n\t\t\tmat-flat-button\n\t\t\tcolor=\"primary\"\n\t\t\t[disabled]=\"!selectedItem\"\n\t\t\t(click)=\"confirmSelection()\">Select {{editorConfig.recommendations.object.i18n.singularName}}</button>\n\n</ng-container>\n", styles: [":host{display:block}.search-input{width:100%;position:relative;display:flex;align-items:center;border:1px solid #ccc;padding:0 8px;box-sizing:border-box;margin-top:18px}.search-input input{width:100%;border:none;margin:0;padding:8px}.search-input input:focus{outline:none}#noResultsError,#confirmSelectionBtn{margin-top:18px}.results-container{border:1px solid #ccc;border-top:none;overflow:auto;max-height:242px;margin-bottom:18px}related-object-row{cursor:default}related-object-row.selected{background-color:#0000003b}\n"] }]
    }], function () { return [{ type: i1$4.HttpClient }, { type: i0.ChangeDetectorRef }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }]; }, { choose: [{
            type: Output
        }] }); })();

function ManageRelatedContentComponent_mat_progress_spinner_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "mat-progress-spinner", 5);
} if (rf & 2) {
    i0.ɵɵproperty("diameter", 16);
} }
function ManageRelatedContentComponent_p_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "p", 6);
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("innerHTML", ctx_r1.error.message, i0.ɵɵsanitizeHtml);
} }
function ManageRelatedContentComponent_ng_container_4_Template(rf, ctx) { if (rf & 1) {
    const _r5 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "p");
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "content-search", 7);
    i0.ɵɵlistener("choose", function ManageRelatedContentComponent_ng_container_4_Template_content_search_choose_3_listener($event) { i0.ɵɵrestoreView(_r5); const ctx_r4 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r4.acceptObject($event)); });
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate2("Use the predictive search field below to find and assign the ", ctx_r2.editorConfig.recommendations.object.i18n.singularName, " you wish to associate with this recommendation. If you are unable to find a particular ", ctx_r2.editorConfig.recommendations.object.i18n.singularName, " please ensure it's been enabled for use as a recommendation.");
} }
function ManageRelatedContentComponent_div_5_Template(rf, ctx) { if (rf & 1) {
    const _r7 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 8);
    i0.ɵɵelement(1, "related-object-row", 9);
    i0.ɵɵpipe(2, "filterColumnsByProperty");
    i0.ɵɵelementStart(3, "button", 10);
    i0.ɵɵlistener("click", function ManageRelatedContentComponent_div_5_Template_button_click_3_listener() { i0.ɵɵrestoreView(_r7); const ctx_r6 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r6.changeState("search")); });
    i0.ɵɵtext(4, "Edit Recommendation");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "button", 11);
    i0.ɵɵlistener("click", function ManageRelatedContentComponent_div_5_Template_button_click_5_listener() { i0.ɵɵrestoreView(_r7); const ctx_r8 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r8.close.emit()); });
    i0.ɵɵtext(6, "Close");
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("object", ctx_r3.object)("columns", i0.ɵɵpipeBind2(2, 2, ctx_r3.editorConfig.recommendations.object.autocomplete.tableColumns, ctx_r3.object));
} }
class ManageRelatedContentComponent {
    constructor(editorConfig, el, cd, http) {
        this.editorConfig = editorConfig;
        this.el = el;
        this.cd = cd;
        this.http = http;
        this.selectedContent = new EventEmitter();
        this.close = new EventEmitter();
    }
    ngOnChanges(c) {
        const object = c.object?.currentValue;
        // if there's a value and it's not missing a translation. ( missing translation means you have to search for a new one )
        if (object && !object.missing_translation) {
            if (this.editorConfig.recommendations.object.endpoints.detail) {
                this.loadDetail(object);
            }
            else {
                this.state = 'overview';
            }
        }
        else {
            this.state = 'search';
        }
    }
    /**
     * Load more information about the object based off an url provided for 'detail' in the endpoints configuration
     */
    loadDetail(object) {
        // do some variable replacement on the detail endpoint based off of object properties
        const url = this.editorConfig.recommendations.object.endpoints.detail.replace(/\{(\w+)\}/g, (match, param) => {
            return object[param];
        });
        this.isLoading = true;
        this.http.get(url).subscribe(result => {
            this.isLoading = false;
            this.state = 'overview';
            this.object = result;
        }, error => {
            this.isLoading = false;
            this.state = 'search';
            this.error = error;
            if (error.error?.code) {
                this.error = error.error;
            }
        });
    }
    changeState(state) {
        this.state = state;
        if (state === 'search') {
            this.cd.detectChanges();
            const inp = this.el.nativeElement.querySelector('.search-input input');
            if (inp) {
                inp.focus();
            }
        }
    }
    /** Emit selected content event */
    acceptObject(object) {
        this.selectedContent.emit(object);
    }
}
ManageRelatedContentComponent.ɵfac = function ManageRelatedContentComponent_Factory(t) { return new (t || ManageRelatedContentComponent)(i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1$4.HttpClient)); };
ManageRelatedContentComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: ManageRelatedContentComponent, selectors: [["manage-related-content"]], inputs: { object: "object" }, outputs: { selectedContent: "selectedContent", close: "close" }, features: [i0.ɵɵNgOnChangesFeature], decls: 8, vars: 5, consts: [["mode", "indeterminate", "color", "primary", "matSuffix", "", 3, "diameter", 4, "ngIf"], ["class", "error-msg", 3, "innerHTML", 4, "ngIf"], [4, "ngIf"], ["id", "overview", 4, "ngIf"], [1, "close-dialog", 3, "click"], ["mode", "indeterminate", "color", "primary", "matSuffix", "", 3, "diameter"], [1, "error-msg", 3, "innerHTML"], [3, "choose"], ["id", "overview"], [3, "object", "columns"], ["mat-flat-button", "", "color", "primary", 3, "click"], ["mat-flat-button", "", "color", "accent", 3, "click"]], template: function ManageRelatedContentComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "h2");
        i0.ɵɵtext(1);
        i0.ɵɵtemplate(2, ManageRelatedContentComponent_mat_progress_spinner_2_Template, 1, 1, "mat-progress-spinner", 0);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(3, ManageRelatedContentComponent_p_3_Template, 1, 1, "p", 1);
        i0.ɵɵtemplate(4, ManageRelatedContentComponent_ng_container_4_Template, 4, 2, "ng-container", 2);
        i0.ɵɵtemplate(5, ManageRelatedContentComponent_div_5_Template, 7, 5, "div", 3);
        i0.ɵɵelementStart(6, "mat-icon", 4);
        i0.ɵɵlistener("click", function ManageRelatedContentComponent_Template_mat_icon_click_6_listener() { return ctx.close.emit(); });
        i0.ɵɵtext(7, "close");
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate1("Recommended ", ctx.editorConfig.recommendations.object.i18n.singularName, " ");
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.isLoading);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.error);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.state === "search");
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.state === "overview");
    } }, dependencies: [i1$2.NgIf, i2.MatButton, i4$1.MatSuffix, i6$1.MatIcon, i7$1.MatProgressSpinner, RelatedObjectRowComponent, ContentSearchComponent, FilterColumnsByPropertyPipe], styles: ["[_nghost-%COMP%]{display:block;position:relative}h2[_ngcontent-%COMP%]{font-size:24px;margin:0}h2[_ngcontent-%COMP%]   mat-progress-spinner[_ngcontent-%COMP%]{margin-top:18px}.close-dialog[_ngcontent-%COMP%]{position:absolute;top:-16px;right:-10px;cursor:pointer}#overview[_ngcontent-%COMP%]{display:flex;justify-content:space-between;flex-flow:row wrap;margin-top:28px}related-object-row[_ngcontent-%COMP%]{border:1px solid #ccc;margin-bottom:18px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ManageRelatedContentComponent, [{
        type: Component,
        args: [{ selector: 'manage-related-content', template: "<h2>Recommended {{editorConfig.recommendations.object.i18n.singularName}}\n\t<mat-progress-spinner *ngIf=\"isLoading\"\n\t\t\t\t\t\t  mode=\"indeterminate\"\n\t\t\t\t\t\t  color=\"primary\"\n\t\t\t\t\t\t  [diameter]=\"16\"\n\t\t\t\t\t\t  matSuffix></mat-progress-spinner>\n</h2>\n\n<p *ngIf=\"error\"\n   class=\"error-msg\"\n   [innerHTML]=\"error.message\"></p>\n\n<ng-container *ngIf=\"state === 'search'\">\n\t<p>Use the predictive search field below to find and assign the {{editorConfig.recommendations.object.i18n.singularName}} you wish to associate with this recommendation. If you are unable to find a particular {{editorConfig.recommendations.object.i18n.singularName}} please ensure it's been enabled for use as a recommendation.</p>\n\n\t<content-search (choose)=\"acceptObject($event)\"></content-search>\n</ng-container>\n\n<div *ngIf=\"state === 'overview'\"\n\t id=\"overview\">\n\n\t<related-object-row [object]=\"object\"\n\t\t\t\t\t\t[columns]=\"editorConfig.recommendations.object.autocomplete.tableColumns | filterColumnsByProperty:object\"></related-object-row>\n\n\t<button mat-flat-button\n\t\t\tcolor=\"primary\"\n\t\t\t(click)=\"changeState('search')\">Edit Recommendation</button>\n\t<button mat-flat-button\n\t\t\tcolor=\"accent\"\n\t\t\t(click)=\"close.emit()\">Close</button>\n</div>\n\n<mat-icon class=\"close-dialog\"\n\t\t  (click)=\"close.emit()\">close</mat-icon>\n", styles: [":host{display:block;position:relative}h2{font-size:24px;margin:0}h2 mat-progress-spinner{margin-top:18px}.close-dialog{position:absolute;top:-16px;right:-10px;cursor:pointer}#overview{display:flex;justify-content:space-between;flex-flow:row wrap;margin-top:28px}related-object-row{border:1px solid #ccc;margin-bottom:18px}\n"] }]
    }], function () { return [{ type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i1$4.HttpClient }]; }, { selectedContent: [{
            type: Output
        }], close: [{
            type: Output
        }], object: [{
            type: Input
        }] }); })();

const _c0$8 = ["contentModal"];
function RecommendationsTableComponent_th_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "th", 9);
    i0.ɵɵtext(1, "Range");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵpropertyInterpolate1("matTooltip", "Enter a value between 0 and ", ctx_r0.maximumScore, ". The recommendation will be shown if the user chose a response with a score that is equal to or greater than the value you enter.");
} }
function RecommendationsTableComponent_th_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "th", 10);
    i0.ɵɵtext(1, "Reorder");
    i0.ɵɵelementEnd();
} }
function RecommendationsTableComponent_th_9_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "th", 11);
    i0.ɵɵtext(1, "Remove");
    i0.ɵɵelementEnd();
} }
function RecommendationsTableComponent_tr_11_1_ng_template_0_Template(rf, ctx) { }
function RecommendationsTableComponent_tr_11_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵtemplate(0, RecommendationsTableComponent_tr_11_1_ng_template_0_Template, 0, 0, "ng-template");
} }
function RecommendationsTableComponent_tr_11_td_4_Template(rf, ctx) { if (rf & 1) {
    const _r17 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "td", 18)(1, "input", 19);
    i0.ɵɵlistener("ngModelChange", function RecommendationsTableComponent_tr_11_td_4_Template_input_ngModelChange_1_listener($event) { i0.ɵɵrestoreView(_r17); const recommendation_r6 = i0.ɵɵnextContext().$implicit; return i0.ɵɵresetView(recommendation_r6.scoreRange = $event); });
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(2, "span", 20);
    i0.ɵɵtext(3, "+");
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const recommendation_r6 = i0.ɵɵnextContext().$implicit;
    const ctx_r8 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngModel", recommendation_r6.scoreRange)("max", ctx_r8.maximumScore)("min", 0);
} }
function RecommendationsTableComponent_tr_11_mat_icon_6_Template(rf, ctx) { if (rf & 1) {
    const _r21 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-icon", 21);
    i0.ɵɵlistener("click", function RecommendationsTableComponent_tr_11_mat_icon_6_Template_mat_icon_click_0_listener() { i0.ɵɵrestoreView(_r21); const recommendation_r6 = i0.ɵɵnextContext().$implicit; const ctx_r19 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r19.openChooseRelatedContent(recommendation_r6)); });
    i0.ɵɵtext(1, "playlist_add_check");
    i0.ɵɵelementEnd();
} }
function RecommendationsTableComponent_tr_11_mat_icon_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "mat-icon");
    i0.ɵɵtext(1, "playlist_add_check");
    i0.ɵɵelementEnd();
} }
function RecommendationsTableComponent_tr_11_mat_icon_8_Template(rf, ctx) { if (rf & 1) {
    const _r24 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-icon", 21);
    i0.ɵɵlistener("click", function RecommendationsTableComponent_tr_11_mat_icon_8_Template_mat_icon_click_0_listener() { i0.ɵɵrestoreView(_r24); const recommendation_r6 = i0.ɵɵnextContext().$implicit; const ctx_r22 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r22.openChooseRelatedContent(recommendation_r6)); });
    i0.ɵɵtext(1, "playlist_add");
    i0.ɵɵelementEnd();
} }
function RecommendationsTableComponent_tr_11_td_9_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "td", 10)(1, "mat-icon", 22);
    i0.ɵɵtext(2, "open_with");
    i0.ɵɵelementEnd()();
} }
function RecommendationsTableComponent_tr_11_td_10_Template(rf, ctx) { if (rf & 1) {
    const _r27 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "td", 11)(1, "mat-icon", 23);
    i0.ɵɵlistener("click", function RecommendationsTableComponent_tr_11_td_10_Template_mat_icon_click_1_listener() { i0.ɵɵrestoreView(_r27); const recommendation_r6 = i0.ɵɵnextContext().$implicit; const ctx_r25 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r25.removeItem(recommendation_r6)); });
    i0.ɵɵtext(2, "delete");
    i0.ɵɵelementEnd()();
} }
function RecommendationsTableComponent_tr_11_Template(rf, ctx) { if (rf & 1) {
    const _r29 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "tr", 12);
    i0.ɵɵtemplate(1, RecommendationsTableComponent_tr_11_1_Template, 1, 0, null, 13);
    i0.ɵɵelementStart(2, "td", 1)(3, "input", 14);
    i0.ɵɵlistener("ngModelChange", function RecommendationsTableComponent_tr_11_Template_input_ngModelChange_3_listener($event) { const restoredCtx = i0.ɵɵrestoreView(_r29); const recommendation_r6 = restoredCtx.$implicit; return i0.ɵɵresetView(recommendation_r6.text = $event); });
    i0.ɵɵelementEnd()();
    i0.ɵɵtemplate(4, RecommendationsTableComponent_tr_11_td_4_Template, 4, 3, "td", 15);
    i0.ɵɵelementStart(5, "td", 3);
    i0.ɵɵtemplate(6, RecommendationsTableComponent_tr_11_mat_icon_6_Template, 2, 0, "mat-icon", 16);
    i0.ɵɵtemplate(7, RecommendationsTableComponent_tr_11_mat_icon_7_Template, 2, 0, "mat-icon", 17);
    i0.ɵɵtemplate(8, RecommendationsTableComponent_tr_11_mat_icon_8_Template, 2, 0, "mat-icon", 16);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(9, RecommendationsTableComponent_tr_11_td_9_Template, 3, 0, "td", 4);
    i0.ɵɵtemplate(10, RecommendationsTableComponent_tr_11_td_10_Template, 3, 0, "td", 5);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const recommendation_r6 = ctx.$implicit;
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("readOnly", ctx_r3.editorConfig.permissions.readonly)("ngModel", recommendation_r6.text);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r3.editorConfig.permissions.recommendations.edit);
    i0.ɵɵadvance(1);
    i0.ɵɵclassProp("can-open", ctx_r3.canOpen(recommendation_r6));
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", recommendation_r6.object && !recommendation_r6.object.missing_translation && ctx_r3.canOpen(recommendation_r6));
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", recommendation_r6.object && !ctx_r3.canOpen(recommendation_r6));
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", !recommendation_r6.object || recommendation_r6.object.missing_translation);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r3.editorConfig.permissions.recommendations.edit);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r3.editorConfig.permissions.recommendations.edit);
} }
function RecommendationsTableComponent_ng_template_12_Template(rf, ctx) { if (rf & 1) {
    const _r32 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "manage-related-content", 24);
    i0.ɵɵlistener("close", function RecommendationsTableComponent_ng_template_12_Template_manage_related_content_close_0_listener() { i0.ɵɵrestoreView(_r32); const ctx_r31 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r31.cancelDialog()); })("selectedContent", function RecommendationsTableComponent_ng_template_12_Template_manage_related_content_selectedContent_0_listener($event) { i0.ɵɵrestoreView(_r32); const ctx_r33 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r33.onSelectContent($event)); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const data_r30 = ctx.$implicit;
    i0.ɵɵproperty("object", data_r30);
} }
class RecommendationsTableComponent {
    constructor(dialog, cd, editorConfig) {
        this.dialog = dialog;
        this.cd = cd;
        this.editorConfig = editorConfig;
    }
    removeItem(item) {
        this.recommendations.splice(this.recommendations.indexOf(item), 1);
    }
    onDropped(event) {
        moveItemInArray(this.recommendations, event.previousIndex, event.currentIndex);
    }
    /**
     * Open the choose related content modal
     */
    openChooseRelatedContent(item) {
        this.currentEditItem = item;
        this.contentDialog = this.dialog.open(this.contentModal, {
            data: item.object,
            autoFocus: typeof (item.object) === 'undefined',
            maxWidth: '720px',
            minWidth: '340px',
            disableClose: true
        });
    }
    /**
     * Object selected
     */
    onSelectContent(object) {
        // filter everything but id/blog_id
        object = Object.assign({}, ...['id', 'blog_id'].map(prop => ({ [prop]: object[prop] })));
        // if this is a replacement for a missing translation, mark it as such
        if (this.currentEditItem.object?.missing_translation) {
            object.alt_translation = true;
        }
        this.currentEditItem.object = object;
        this.contentDialog.close();
        this.cd.markForCheck();
    }
    cancelDialog() {
        this.currentEditItem = null;
        this.contentDialog.close();
    }
    /** Helper */
    canOpen(recommendation) {
        if (this.editorConfig.permissions.readonly) {
            return false;
        }
        // if the user has permissions to always edit recommendations then return true
        if (this.editorConfig.permissions.recommendations.edit) {
            return true;
        }
        // if there's no recommended object always allow open
        if (!recommendation.object) {
            return true;
        }
        // if this is marked as an alt translation ( to be used in place of the missing translation )
        if (recommendation.object.alt_translation) {
            return true;
        }
        // if this is marked as missing a translation it can be opened to pick an alt
        if (recommendation.object.missing_translation) {
            return true;
        }
        return false;
    }
}
RecommendationsTableComponent.ɵfac = function RecommendationsTableComponent_Factory(t) { return new (t || RecommendationsTableComponent)(i0.ɵɵdirectiveInject(i1$3.MatDialog), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG)); };
RecommendationsTableComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: RecommendationsTableComponent, selectors: [["recommendations-table"]], viewQuery: function RecommendationsTableComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0$8, 7);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.contentModal = _t.first);
    } }, inputs: { recommendations: "recommendations", maximumScore: "maximumScore" }, decls: 14, vars: 7, consts: [["cellpadding", "0", "cellspacing", "0", 1, "simple-table"], [1, "cell-text"], ["class", "cell-range", 3, "matTooltip", 4, "ngIf"], [1, "cell-object"], ["class", "cell-reorder", 4, "ngIf"], ["class", "cell-remove", 4, "ngIf"], ["cdkDropList", "", "cdkDropListLockAxis", "y", 3, "cdkDropListData", "cdkDropListDisabled", "cdkDropListDropped"], ["cdkDrag", "", "cdkDragBoundary", "tbody", 4, "ngFor", "ngForOf"], ["contentModal", ""], [1, "cell-range", 3, "matTooltip"], [1, "cell-reorder"], [1, "cell-remove"], ["cdkDrag", "", "cdkDragBoundary", "tbody"], [4, "cdkDragPreview"], ["type", "text", "autocomplete", "off", "required", "", 1, "cell-text-recommendation", "inline-input", 3, "readOnly", "ngModel", "ngModelChange"], ["class", "cell-range cell-numeric", 4, "ngIf"], [3, "click", 4, "ngIf"], [4, "ngIf"], [1, "cell-range", "cell-numeric"], ["type", "number", "required", "", 1, "inline-input", 3, "ngModel", "max", "min", "ngModelChange"], [1, "range-plus"], [3, "click"], ["cdkDragHandle", "", 1, "reorder-icon"], [1, "remove-icon", 3, "click"], [3, "object", "close", "selectedContent"]], template: function RecommendationsTableComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "table", 0)(1, "thead")(2, "tr")(3, "th", 1);
        i0.ɵɵtext(4, "Copy");
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(5, RecommendationsTableComponent_th_5_Template, 2, 1, "th", 2);
        i0.ɵɵelementStart(6, "th", 3);
        i0.ɵɵtext(7);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(8, RecommendationsTableComponent_th_8_Template, 2, 0, "th", 4);
        i0.ɵɵtemplate(9, RecommendationsTableComponent_th_9_Template, 2, 0, "th", 5);
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(10, "tbody", 6);
        i0.ɵɵlistener("cdkDropListDropped", function RecommendationsTableComponent_Template_tbody_cdkDropListDropped_10_listener($event) { return ctx.onDropped($event); });
        i0.ɵɵtemplate(11, RecommendationsTableComponent_tr_11_Template, 11, 10, "tr", 7);
        i0.ɵɵelementEnd()();
        i0.ɵɵtemplate(12, RecommendationsTableComponent_ng_template_12_Template, 1, 1, "ng-template", null, 8, i0.ɵɵtemplateRefExtractor);
    } if (rf & 2) {
        i0.ɵɵadvance(5);
        i0.ɵɵproperty("ngIf", ctx.editorConfig.permissions.recommendations.edit);
        i0.ɵɵadvance(2);
        i0.ɵɵtextInterpolate(ctx.editorConfig.recommendations.object.i18n.singularName);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.editorConfig.permissions.recommendations.edit);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.editorConfig.permissions.recommendations.edit);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("cdkDropListData", ctx.recommendations)("cdkDropListDisabled", !ctx.editorConfig.permissions.recommendations.edit);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngForOf", ctx.recommendations);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i3.DefaultValueAccessor, i3.NumberValueAccessor, i3.NgControlStatus, i3.RequiredValidator, i3.MinValidator, i3.MaxValidator, i3.NgModel, i6$1.MatIcon, i8.MatTooltip, i6$2.CdkDropList, i6$2.CdkDrag, i6$2.CdkDragHandle, i6$2.CdkDragPreview, ManageRelatedContentComponent], styles: [".cell-object[_ngcontent-%COMP%]{width:80px}.cell-object.can-open[_ngcontent-%COMP%]   mat-icon[_ngcontent-%COMP%]{cursor:pointer}.cell-text[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{width:100%}.cell-reorder[_ngcontent-%COMP%], .cell-remove[_ngcontent-%COMP%]{width:80px}.cell-remove[_ngcontent-%COMP%]   mat-icon[_ngcontent-%COMP%]{cursor:pointer}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RecommendationsTableComponent, [{
        type: Component,
        args: [{ selector: 'recommendations-table', template: "<table class=\"simple-table\"\n\t   cellpadding=\"0\"\n\t   cellspacing=\"0\">\n\t<thead>\n\t\t<tr>\n\t\t\t<th class=\"cell-text\">Copy</th>\n\n\t\t\t<th *ngIf=\"editorConfig.permissions.recommendations.edit\"\n\t\t\t\tclass=\"cell-range\"\n\t\t\t\tmatTooltip=\"Enter a value between 0 and {{maximumScore}}. The recommendation will be shown if the user chose a response with a score that is equal to or greater than the value you enter.\">Range</th>\n\n\t\t\t<th class=\"cell-object\">{{editorConfig.recommendations.object.i18n.singularName}}</th>\n\n\t\t\t<th *ngIf=\"editorConfig.permissions.recommendations.edit\"\n\t\t\t\tclass=\"cell-reorder\">Reorder</th>\n\n\t\t\t<th *ngIf=\"editorConfig.permissions.recommendations.edit\"\n\t\t\t\tclass=\"cell-remove\">Remove</th>\n\t\t</tr>\n\t</thead>\n\n\t<tbody cdkDropList\n\t\t   cdkDropListLockAxis=\"y\"\n\t\t   [cdkDropListData]=\"recommendations\"\n\t\t   [cdkDropListDisabled]=\"!editorConfig.permissions.recommendations.edit\"\n\t\t   (cdkDropListDropped)=\"onDropped($event)\">\n\n\t\t<tr *ngFor=\"let recommendation of recommendations;\"\n\t\t\tcdkDrag\n\t\t\tcdkDragBoundary='tbody'>\n\n\t\t\t<ng-template *cdkDragPreview></ng-template>\n\n\t\t\t<td class=\"cell-text\">\n\t\t\t\t<input class=\"cell-text-recommendation inline-input\"\n\t\t\t\t\t   type=\"text\"\n\t\t\t\t\t   autocomplete=\"off\"\n\t\t\t\t\t   required\n\t\t\t\t\t   [readOnly]=\"editorConfig.permissions.readonly\"\n\t\t\t\t\t   [(ngModel)]=\"recommendation.text\" />\n\n\t\t\t</td>\n\t\t\t<td *ngIf=\"editorConfig.permissions.recommendations.edit\"\n\t\t\t\tclass=\"cell-range cell-numeric\"><input type=\"number\"\n\t\t\t\t\t   class=\"inline-input\"\n\t\t\t\t\t   required\n\t\t\t\t\t   [(ngModel)]=\"recommendation.scoreRange\"\n\t\t\t\t\t   [max]=\"maximumScore\"\n\t\t\t\t\t   [min]=\"0\" /><span class=\"range-plus\">+</span></td>\n\n\t\t\t<td class=\"cell-object\"\n\t\t\t\t[class.can-open]=\"canOpen(recommendation)\">\n\n\t\t\t\t<!-- If there is a selection, and the editor has permission to add, or it's a \"custom\" recommendation which is a multi-lingual tie-in and can be edited -->\n\t\t\t\t<mat-icon *ngIf=\"recommendation.object && ! recommendation.object.missing_translation && canOpen(recommendation)\"\n\t\t\t\t\t\t  (click)=\"openChooseRelatedContent(recommendation)\">playlist_add_check</mat-icon>\n\n\t\t\t\t<!-- There is a selection, it's not custom, -->\n\t\t\t\t<mat-icon *ngIf=\"recommendation.object && !canOpen(recommendation)\">playlist_add_check</mat-icon>\n\n\t\t\t\t<mat-icon *ngIf=\"!recommendation.object || recommendation.object.missing_translation\"\n\t\t\t\t\t\t  (click)=\"openChooseRelatedContent(recommendation)\">playlist_add</mat-icon>\n\n\t\t\t</td>\n\n\t\t\t<td *ngIf=\"editorConfig.permissions.recommendations.edit\"\n\t\t\t\tclass=\"cell-reorder\">\n\t\t\t\t<mat-icon cdkDragHandle\n\t\t\t\t\t\t  class=\"reorder-icon\">open_with</mat-icon>\n\t\t\t</td>\n\n\t\t\t<td *ngIf=\"editorConfig.permissions.recommendations.edit\"\n\t\t\t\tclass=\"cell-remove\">\n\t\t\t\t<mat-icon class=\"remove-icon\"\n\t\t\t\t\t\t  (click)=\"removeItem(recommendation)\">delete</mat-icon>\n\t\t\t</td>\n\n\t\t</tr>\n\n\t</tbody>\n</table>\n\n<ng-template #contentModal\n\t\t\t let-data>\n\t<manage-related-content [object]=\"data\"\n\t\t\t\t\t\t\t(close)=\"cancelDialog()\"\n\t\t\t\t\t\t\t(selectedContent)=\"onSelectContent($event)\"></manage-related-content>\n</ng-template>\n", styles: [".cell-object{width:80px}.cell-object.can-open mat-icon{cursor:pointer}.cell-text input{width:100%}.cell-reorder,.cell-remove{width:80px}.cell-remove mat-icon{cursor:pointer}\n"] }]
    }], function () { return [{ type: i1$3.MatDialog }, { type: i0.ChangeDetectorRef }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }]; }, { recommendations: [{
            type: Input
        }], maximumScore: [{
            type: Input
        }], contentModal: [{
            type: ViewChild,
            args: ['contentModal', { static: true }]
        }] }); })();

function RecommendationsTabComponent_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "p");
    i0.ɵɵtext(2, "You haven't assigned scores to any of your questions yet. You'll need to do this before you can make any recommendations.");
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} }
function RecommendationsTabComponent_ng_container_1_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelement(1, "div", 8);
    i0.ɵɵelementStart(2, "p");
    i0.ɵɵtext(3, "Here you will configure recommendations for individual questions or groups. If a question or group is greyed out it means that no score values have been assigned to that item and no recommendations can be made.");
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("innerHTML", ctx_r2.editorConfig.recommendations.i18n.tabHeaderText, i0.ɵɵsanitizeHtml);
} }
function RecommendationsTabComponent_ng_container_1_recommendation_survey_item_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "recommendation-survey-item", 9);
} if (rf & 2) {
    const item_r6 = ctx.$implicit;
    const i_r7 = ctx.index;
    i0.ɵɵproperty("index", i_r7 + 1)("item", item_r6)("depth", 1);
} }
function RecommendationsTabComponent_ng_container_1_recommendations_table_10_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "recommendations-table", 10);
} if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("recommendations", ctx_r4.survey.recommendations)("maximumScore", ctx_r4.survey.maximumScore);
} }
function RecommendationsTabComponent_ng_container_1_button_11_Template(rf, ctx) { if (rf & 1) {
    const _r9 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 11);
    i0.ɵɵlistener("click", function RecommendationsTabComponent_ng_container_1_button_11_Template_button_click_0_listener($event) { i0.ɵɵrestoreView(_r9); const ctx_r8 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r8.addRecommendation($event)); });
    i0.ɵɵtext(1, "Add Recommendation");
    i0.ɵɵelementEnd();
} }
function RecommendationsTabComponent_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    const _r11 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, RecommendationsTabComponent_ng_container_1_ng_container_1_Template, 4, 1, "ng-container", 0);
    i0.ɵɵelementStart(2, "div", 1);
    i0.ɵɵtemplate(3, RecommendationsTabComponent_ng_container_1_recommendation_survey_item_3_Template, 1, 3, "recommendation-survey-item", 2);
    i0.ɵɵelementStart(4, "div", 3);
    i0.ɵɵelement(5, "h1", 4);
    i0.ɵɵelementStart(6, "p")(7, "strong");
    i0.ɵɵtext(8);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(9, "textarea", 5);
    i0.ɵɵlistener("ngModelChange", function RecommendationsTabComponent_ng_container_1_Template_textarea_ngModelChange_9_listener($event) { i0.ɵɵrestoreView(_r11); const ctx_r10 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r10.survey.meta.recommendationsIntro = $event); });
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(10, RecommendationsTabComponent_ng_container_1_recommendations_table_10_Template, 1, 2, "recommendations-table", 6);
    i0.ɵɵtemplate(11, RecommendationsTabComponent_ng_container_1_button_11_Template, 2, 0, "button", 7);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", !ctx_r1.editorConfig.permissions.readonly);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngForOf", ctx_r1.survey.items);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("innerHTML", ctx_r1.editorConfig.i18n.recommendations.surveyRecommendationsLabel, i0.ɵɵsanitizeHtml);
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1("Maximum Score: ", ctx_r1.survey.maximumScore, "");
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("placeholder", ctx_r1.editorConfig.i18n.recommendations.surveyRecommendationsPlaceholder)("ngModel", ctx_r1.survey.meta.recommendationsIntro)("readOnly", ctx_r1.editorConfig.permissions.readonly)("cdkTextareaAutosize", true)("cdkAutosizeMinRows", 1)("cdkAutosizeMaxRows", 5);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.survey.recommendations.length);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.editorConfig.permissions.recommendations.edit);
} }
class RecommendationsTabComponent {
    constructor(cd, el, editorConfig) {
        this.cd = cd;
        this.el = el;
        this.editorConfig = editorConfig;
    }
    /**
     * Add a new recommendation item
     */
    addRecommendation(event) {
        event.preventDefault();
        const recommendation = new RecommendationItem();
        recommendation.id = this.survey.meta.insert_id++;
        this.survey.recommendations.push(recommendation);
        this.cd.detectChanges();
        const newItem = Array.from(this.el.nativeElement.querySelectorAll('.cell-text-recommendation')).pop();
        if (newItem) {
            newItem.focus();
        }
    }
}
RecommendationsTabComponent.ɵfac = function RecommendationsTabComponent_Factory(t) { return new (t || RecommendationsTabComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG)); };
RecommendationsTabComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: RecommendationsTabComponent, selectors: [["recommendations-tab"]], inputs: { survey: "survey" }, decls: 2, vars: 2, consts: [[4, "ngIf"], ["id", "recommendationContainer"], [3, "index", "item", "depth", 4, "ngFor", "ngForOf"], ["id", "surveyRecommendations"], ["id", "surveyRecommendationsTitle", 3, "innerHTML"], ["id", "surveyRecommendationsText", "type", "text", 1, "recommendation-intro-input", 3, "placeholder", "ngModel", "readOnly", "cdkTextareaAutosize", "cdkAutosizeMinRows", "cdkAutosizeMaxRows", "ngModelChange"], [3, "recommendations", "maximumScore", 4, "ngIf"], ["mat-flat-button", "", "color", "primary", 3, "click", 4, "ngIf"], [3, "innerHTML"], [3, "index", "item", "depth"], [3, "recommendations", "maximumScore"], ["mat-flat-button", "", "color", "primary", 3, "click"]], template: function RecommendationsTabComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, RecommendationsTabComponent_ng_container_0_Template, 3, 0, "ng-container", 0);
        i0.ɵɵtemplate(1, RecommendationsTabComponent_ng_container_1_Template, 12, 12, "ng-container", 0);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", !ctx.survey.maximumScore);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.survey.maximumScore);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i3.DefaultValueAccessor, i3.NgControlStatus, i3.NgModel, i2.MatButton, i7.CdkTextareaAutosize, RecommendationSurveyItemComponent, RecommendationsTableComponent], styles: ["#surveyRecommendations[_ngcontent-%COMP%]{padding-right:5px}#surveyRecommendationsTitle[_ngcontent-%COMP%]{font-size:20px;margin-bottom:8px}#surveyRecommendationsText[_ngcontent-%COMP%]{width:calc(100% - 20px);padding:8px 10px;margin-bottom:12px;box-sizing:content-box;box-shadow:inset 0 1px 2px #00000012;border-color:#ddd}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RecommendationsTabComponent, [{
        type: Component,
        args: [{ selector: 'recommendations-tab', template: "<ng-container *ngIf=\"!survey.maximumScore\">\n\t<p>You haven't assigned scores to any of your questions yet. You'll need to do this before you can make any recommendations.</p>\n</ng-container>\n\n<ng-container *ngIf=\"survey.maximumScore\">\n\n\t<ng-container *ngIf=\"!editorConfig.permissions.readonly\">\n\t\t<div [innerHTML]=\"editorConfig.recommendations.i18n.tabHeaderText\"></div>\n\t\t<p>Here you will configure recommendations for individual questions or groups. If a question or group is greyed out it means that no score values have been assigned to that item and no recommendations can be made.</p>\n\t</ng-container>\n\n\t<div id=\"recommendationContainer\">\n\n\t\t<recommendation-survey-item *ngFor=\"let item of survey.items;index as i;\"\n\t\t\t\t\t\t\t\t\t[index]=\"i+1\"\n\t\t\t\t\t\t\t\t\t[item]=\"item\"\n\t\t\t\t\t\t\t\t\t[depth]=\"1\"></recommendation-survey-item>\n\n\t\t<div id=\"surveyRecommendations\">\n\n\t\t\t<h1 id=\"surveyRecommendationsTitle\"\n\t\t\t\t[innerHTML]=\"editorConfig.i18n.recommendations.surveyRecommendationsLabel\"></h1>\n\n\t\t\t<p><strong>Maximum Score: {{survey.maximumScore}}</strong></p>\n\n\t\t\t<textarea id=\"surveyRecommendationsText\"\n\t\t\t\t\t  type=\"text\"\n\t\t\t\t\t  [placeholder]=\"editorConfig.i18n.recommendations.surveyRecommendationsPlaceholder\"\n\t\t\t\t\t  class=\"recommendation-intro-input\"\n\t\t\t\t\t  [(ngModel)]=\"survey.meta.recommendationsIntro\"\n\t\t\t\t\t  [readOnly]=\"editorConfig.permissions.readonly\"\n\t\t\t\t\t  [cdkTextareaAutosize]=\"true\"\n\t\t\t\t\t  [cdkAutosizeMinRows]=\"1\"\n\t\t\t\t\t  [cdkAutosizeMaxRows]=\"5\"></textarea>\n\n\t\t\t<recommendations-table *ngIf=\"survey.recommendations.length\"\n\t\t\t\t\t\t\t\t   [recommendations]=\"survey.recommendations\"\n\t\t\t\t\t\t\t\t   [maximumScore]=\"survey.maximumScore\"></recommendations-table>\n\n\t\t\t<button *ngIf=\"editorConfig.permissions.recommendations.edit\"\n\t\t\t\t\tmat-flat-button\n\t\t\t\t\tcolor=\"primary\"\n\t\t\t\t\t(click)=\"addRecommendation($event)\">Add Recommendation</button>\n\n\t\t</div>\n\n\t</div>\n</ng-container>\n", styles: ["#surveyRecommendations{padding-right:5px}#surveyRecommendationsTitle{font-size:20px;margin-bottom:8px}#surveyRecommendationsText{width:calc(100% - 20px);padding:8px 10px;margin-bottom:12px;box-sizing:content-box;box-shadow:inset 0 1px 2px #00000012;border-color:#ddd}\n"] }]
    }], function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }]; }, { survey: [{
            type: Input
        }] }); })();

function SurveyEditorComponent_div_0_Template(rf, ctx) { if (rf & 1) {
    const _r6 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 5)(1, "input", 6);
    i0.ɵɵlistener("ngModelChange", function SurveyEditorComponent_div_0_Template_input_ngModelChange_1_listener($event) { i0.ɵɵrestoreView(_r6); const ctx_r5 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r5.survey.title = $event); });
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngModel", ctx_r0.survey.title)("readonly", ctx_r0.editorConfig.permissions.readonly);
} }
function SurveyEditorComponent_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "div", 7);
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("innerHTML", ctx_r1.editorConfig.i18n.staticIntroText, i0.ɵɵsanitizeHtml);
} }
function SurveyEditorComponent_mat_form_field_2_Template(rf, ctx) { if (rf & 1) {
    const _r8 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-form-field", 8)(1, "mat-label");
    i0.ɵɵtext(2, "What is the intent of your survey?");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "mat-select", 9);
    i0.ɵɵlistener("ngModelChange", function SurveyEditorComponent_mat_form_field_2_Template_mat_select_ngModelChange_3_listener($event) { i0.ɵɵrestoreView(_r8); const ctx_r7 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r7.survey.intent = $event); });
    i0.ɵɵelementStart(4, "mat-option", 10);
    i0.ɵɵtext(5, "Compare results with other people");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(6, "mat-option", 10);
    i0.ɵɵtext(7, "Collect information");
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngModel", ctx_r2.survey.intent);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("value", ctx_r2.SurveyIntent.COMPARE);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("value", ctx_r2.SurveyIntent.COLLECT);
} }
function SurveyEditorComponent_mat_form_field_4_Template(rf, ctx) { if (rf & 1) {
    const _r10 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-form-field", 11)(1, "mat-label");
    i0.ɵɵtext(2, "How would you like to present your survey?");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "mat-select", 9);
    i0.ɵɵlistener("ngModelChange", function SurveyEditorComponent_mat_form_field_4_Template_mat_select_ngModelChange_3_listener($event) { i0.ɵɵrestoreView(_r10); const ctx_r9 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r9.survey.presentation = $event); });
    i0.ɵɵelementStart(4, "mat-option", 10);
    i0.ɵɵtext(5, "Standard - Present Questions as configured");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(6, "mat-option", 10);
    i0.ɵɵtext(7, "Psychometric - Randomize Questions");
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngModel", ctx_r3.survey.presentation);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("value", ctx_r3.SurveyPresentation.NORMAL);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("value", ctx_r3.SurveyPresentation.PSYCHOMETRIC);
} }
function SurveyEditorComponent_div_5_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "mat-tab-group", 14)(2, "mat-tab", 15);
    i0.ɵɵelement(3, "questions-tab", 16);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "mat-tab", 17);
    i0.ɵɵelement(5, "recommendations-tab", 16);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r11 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("selectedIndex", 0);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("label", ctx_r11.editorConfig.i18n.questions.tabLabel);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("survey", ctx_r11.survey);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("survey", ctx_r11.survey);
} }
function SurveyEditorComponent_div_5_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelement(1, "questions-tab", 16);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r12 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("survey", ctx_r12.survey);
} }
function SurveyEditorComponent_div_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 12);
    i0.ɵɵtemplate(1, SurveyEditorComponent_div_5_ng_container_1_Template, 6, 4, "ng-container", 13);
    i0.ɵɵtemplate(2, SurveyEditorComponent_div_5_ng_container_2_Template, 2, 1, "ng-container", 13);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r4.editorConfig.recommendations);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", !ctx_r4.editorConfig.recommendations);
} }
/**
 * Check the [survey] attribute on the component and use it to provide the Survey
 */
function editSurveyFactory(el) {
    const surveyData = JSON.parse(el.nativeElement.getAttribute('survey') || '{}');
    el.nativeElement.removeAttribute('survey');
    //const surveyData = require( '../../../../../../test/fixtures/sample-survey.json' );
    //console.info( surveyData )
    return new Survey(surveyData);
}
/**
 * Check the config attribute to provide the injectable editor settings
 */
function editorConfigFactory(el) {
    const config = JSON.parse(el.nativeElement.getAttribute('config') || '{}');
    el.nativeElement.removeAttribute('config');
    return new SurveyEditorConfig(config);
}
/**
 * The editor component
 */
class SurveyEditorComponent {
    get survey() {
        return this._survey;
    }
    set survey(val) { }
    // constructor
    constructor(survey, el, editorConfig, dialog) {
        this.editorConfig = editorConfig;
        this.dialog = dialog;
        // mappings for template
        this.SurveyIntent = SurveyIntent;
        this.SurveyPresentation = SurveyPresentation;
        this._survey = survey;
        const latest_version_url = el.nativeElement.getAttribute('latest_version_url');
        if (null !== latest_version_url && latest_version_url !== '') {
            this.openVersionWarning(latest_version_url);
        }
    }
    openVersionWarning(latest_version_url) {
        this.dialog.open(VersionWarningComponent).afterClosed().subscribe((result) => {
            if (result === true) {
                location.href = latest_version_url;
            }
        });
    }
}
SurveyEditorComponent.ɵfac = function SurveyEditorComponent_Factory(t) { return new (t || SurveyEditorComponent)(i0.ɵɵdirectiveInject(Survey), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG), i0.ɵɵdirectiveInject(i1$3.MatDialog)); };
SurveyEditorComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: SurveyEditorComponent, selectors: [["survey-editor-component"]], inputs: { survey: "survey" }, features: [i0.ɵɵProvidersFeature([
            {
                provide: Survey,
                useFactory: editSurveyFactory,
                deps: [ElementRef]
            },
            {
                provide: SURVEY_EDITOR_CONFIG,
                useFactory: editorConfigFactory,
                deps: [ElementRef]
            },
            VersionPromptService
        ])], decls: 6, vars: 5, consts: [["id", "surveyTitleDiv", 4, "ngIf"], [3, "innerHTML", 4, "ngIf"], ["id", "surveyIntent", 4, "ngIf"], ["id", "surveyPresentation", 4, "ngIf"], ["class", "survey-content-container", 4, "ngIf"], ["id", "surveyTitleDiv"], ["id", "surveyTitle", "type", "text", "name", "post_title", 3, "ngModel", "readonly", "ngModelChange"], [3, "innerHTML"], ["id", "surveyIntent"], [3, "ngModel", "ngModelChange"], [3, "value"], ["id", "surveyPresentation"], [1, "survey-content-container"], [4, "ngIf"], ["animationDuration", "0ms", "mat-stretch-tabs", "false", "mat-align-tabs", "start", "dynamicHeight", "", 3, "selectedIndex"], [3, "label"], [3, "survey"], ["label", "Recommendations"]], template: function SurveyEditorComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, SurveyEditorComponent_div_0_Template, 2, 2, "div", 0);
        i0.ɵɵtemplate(1, SurveyEditorComponent_div_1_Template, 1, 1, "div", 1);
        i0.ɵɵtemplate(2, SurveyEditorComponent_mat_form_field_2_Template, 8, 3, "mat-form-field", 2);
        i0.ɵɵelementStart(3, "div");
        i0.ɵɵtemplate(4, SurveyEditorComponent_mat_form_field_4_Template, 8, 3, "mat-form-field", 3);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(5, SurveyEditorComponent_div_5_Template, 3, 2, "div", 4);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.editorConfig.supports.title);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.editorConfig.i18n.staticIntroText);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.editorConfig.permissions.survey.edit);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", ctx.editorConfig.permissions.survey.edit && ctx.survey.intent !== undefined);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.survey.intent !== undefined && ctx.survey.presentation !== undefined);
    } }, dependencies: [i1$2.NgIf, i3.DefaultValueAccessor, i3.NgControlStatus, i3.NgModel, i4$1.MatFormField, i4$1.MatLabel, i4$2.MatSelect, i5$1.MatOption, i8$1.MatTab, i8$1.MatTabGroup, QuestionsTabComponent, RecommendationsTabComponent], styles: ["[_nghost-%COMP%]{display:block}[_nghost-%COMP%]     mat-tab-group{margin-top:20px}[_nghost-%COMP%]     mat-tab-group mat-tab-header{font-size:20px;border-bottom:1px solid rgba(0,0,0,.12)}[_nghost-%COMP%]     mat-tab-group mat-tab-body{padding-top:20px}#surveyTitleDiv[_ngcontent-%COMP%]{margin-bottom:25px}#surveyTitle[_ngcontent-%COMP%]{padding:3px 8px;font-size:1.7em;line-height:100%;height:1.7em;width:100%;outline:none;background-color:#fff;box-shadow:inset 0 1px 2px #00000012;border-color:#ddd}#surveyIntent[_ngcontent-%COMP%], #surveyPresentation[_ngcontent-%COMP%]{width:100%;max-width:360px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurveyEditorComponent, [{
        type: Component,
        args: [{ selector: 'survey-editor-component', providers: [
                    {
                        provide: Survey,
                        useFactory: editSurveyFactory,
                        deps: [ElementRef]
                    },
                    {
                        provide: SURVEY_EDITOR_CONFIG,
                        useFactory: editorConfigFactory,
                        deps: [ElementRef]
                    },
                    VersionPromptService
                ], template: "<div *ngIf=\"editorConfig.supports.title\"\n\t id=\"surveyTitleDiv\">\n\t<input id=\"surveyTitle\"\n\t\t   type=\"text\"\n\t\t   [(ngModel)]=\"survey.title\"\n\t\t   name=\"post_title\"\n\t\t   [readonly]=\"editorConfig.permissions.readonly\" />\n</div>\n\n<div *ngIf=\"editorConfig.i18n.staticIntroText\"\n\t [innerHTML]=\"editorConfig.i18n.staticIntroText\"></div>\n\n<mat-form-field id=\"surveyIntent\"\n\t\t\t\t*ngIf=\"editorConfig.permissions.survey.edit\">\n\t<mat-label>What is the intent of your survey?</mat-label>\n\t<mat-select [(ngModel)]=\"survey.intent\">\n\t\t<mat-option [value]=\"SurveyIntent.COMPARE\">Compare results with other people</mat-option>\n\t\t<mat-option [value]=\"SurveyIntent.COLLECT\">Collect information</mat-option>\n\t</mat-select>\n</mat-form-field>\n\n<div>\n\t<mat-form-field id=\"surveyPresentation\"\n\t\t\t\t\t*ngIf=\"editorConfig.permissions.survey.edit && survey.intent !== undefined\">\n\t\t<mat-label>How would you like to present your survey?</mat-label>\n\t\t<mat-select [(ngModel)]=\"survey.presentation\">\n\t\t\t<mat-option [value]=\"SurveyPresentation.NORMAL\">Standard - Present Questions as configured</mat-option>\n\t\t\t<mat-option [value]=\"SurveyPresentation.PSYCHOMETRIC\">Psychometric - Randomize Questions</mat-option>\n\t\t</mat-select>\n\t</mat-form-field>\n</div>\n\n<div class=\"survey-content-container\"\n\t *ngIf=\"survey.intent !== undefined && survey.presentation !== undefined\">\n\n\t<ng-container *ngIf=\"editorConfig.recommendations\">\n\t\t<mat-tab-group animationDuration=\"0ms\"\n\t\t\t\t\t   mat-stretch-tabs=\"false\"\n\t\t\t\t\t   mat-align-tabs=\"start\"\n\t\t\t\t\t   [selectedIndex]=\"0\"\n\t\t\t\t\t   dynamicHeight>\n\n\t\t\t<mat-tab [label]=\"editorConfig.i18n.questions.tabLabel\">\n\t\t\t\t<questions-tab [survey]=\"survey\"></questions-tab>\n\t\t\t</mat-tab>\n\n\t\t\t<mat-tab label=\"Recommendations\">\n\t\t\t\t<recommendations-tab [survey]=\"survey\"></recommendations-tab>\n\t\t\t</mat-tab>\n\n\t\t</mat-tab-group>\n\t</ng-container>\n\n\t<ng-container *ngIf=\"!editorConfig.recommendations\">\n\t\t<questions-tab [survey]=\"survey\"></questions-tab>\n\t</ng-container>\n\n</div>\n", styles: [":host{display:block}:host ::ng-deep mat-tab-group{margin-top:20px}:host ::ng-deep mat-tab-group mat-tab-header{font-size:20px;border-bottom:1px solid rgba(0,0,0,.12)}:host ::ng-deep mat-tab-group mat-tab-body{padding-top:20px}#surveyTitleDiv{margin-bottom:25px}#surveyTitle{padding:3px 8px;font-size:1.7em;line-height:100%;height:1.7em;width:100%;outline:none;background-color:#fff;box-shadow:inset 0 1px 2px #00000012;border-color:#ddd}#surveyIntent,#surveyPresentation{width:100%;max-width:360px}\n"] }]
    }], function () { return [{ type: Survey }, { type: i0.ElementRef }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }, { type: i1$3.MatDialog }]; }, { survey: [{
            type: Input
        }] }); })();

const NG_MATERIALS$2 = [
    MatRadioModule,
    MatButtonModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatTabsModule,
    DragDropModule,
    PortalModule,
    MatProgressSpinnerModule,
    MatSnackBarModule
];
const globalRippleConfig = {
    disabled: true,
    animation: {
        enterDuration: 300,
        exitDuration: 0
    }
};
class EditorMaterialModule {
}
EditorMaterialModule.ɵfac = function EditorMaterialModule_Factory(t) { return new (t || EditorMaterialModule)(); };
EditorMaterialModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: EditorMaterialModule });
EditorMaterialModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [
        { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig }
    ], imports: [MatRadioModule,
        MatButtonModule,
        MatSelectModule,
        MatInputModule,
        MatIconModule,
        MatFormFieldModule,
        MatCheckboxModule,
        MatTooltipModule,
        MatTabsModule,
        DragDropModule,
        PortalModule,
        MatProgressSpinnerModule,
        MatSnackBarModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(EditorMaterialModule, [{
        type: NgModule,
        args: [{
                exports: NG_MATERIALS$2,
                providers: [
                    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig }
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(EditorMaterialModule, { exports: [MatRadioModule,
        MatButtonModule,
        MatSelectModule,
        MatInputModule,
        MatIconModule,
        MatFormFieldModule,
        MatCheckboxModule,
        MatTooltipModule,
        MatTabsModule,
        DragDropModule,
        PortalModule,
        MatProgressSpinnerModule,
        MatSnackBarModule] }); })();

class FuzzyModule {
}
FuzzyModule.ɵfac = function FuzzyModule_Factory(t) { return new (t || FuzzyModule)(); };
FuzzyModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: FuzzyModule });
FuzzyModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FuzzyModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                ],
                declarations: [
                    FuzzyMetaPropertyDirective
                ],
                exports: [
                    FuzzyMetaPropertyDirective
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(FuzzyModule, { declarations: [FuzzyMetaPropertyDirective], imports: [CommonModule], exports: [FuzzyMetaPropertyDirective] }); })();

class OptionControlModule {
}
OptionControlModule.ɵfac = function OptionControlModule_Factory(t) { return new (t || OptionControlModule)(); };
OptionControlModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: OptionControlModule });
OptionControlModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        FormsModule,
        EditorMaterialModule,
        FuzzyModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OptionControlModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    EditorMaterialModule,
                    FuzzyModule
                ],
                declarations: [
                    OptionControlComponent
                ],
                exports: [
                    OptionControlComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(OptionControlModule, { declarations: [OptionControlComponent], imports: [CommonModule,
        FormsModule,
        EditorMaterialModule,
        FuzzyModule], exports: [OptionControlComponent] }); })();

class QuestionEditorDateModule {
}
QuestionEditorDateModule.ɵfac = function QuestionEditorDateModule_Factory(t) { return new (t || QuestionEditorDateModule)(); };
QuestionEditorDateModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionEditorDateModule });
QuestionEditorDateModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        ReactiveFormsModule,
        EditorMaterialModule,
        OptionControlModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorDateModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    ReactiveFormsModule,
                    EditorMaterialModule,
                    OptionControlModule
                ],
                declarations: [
                    QuestionEditorDateComponent
                ],
                exports: [
                    QuestionEditorDateComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionEditorDateModule, { declarations: [QuestionEditorDateComponent], imports: [CommonModule,
        ReactiveFormsModule,
        EditorMaterialModule,
        OptionControlModule], exports: [QuestionEditorDateComponent] }); })();

class QuestionEditorTextModule {
}
QuestionEditorTextModule.ɵfac = function QuestionEditorTextModule_Factory(t) { return new (t || QuestionEditorTextModule)(); };
QuestionEditorTextModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionEditorTextModule });
QuestionEditorTextModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({});
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorTextModule, [{
        type: NgModule,
        args: [{
                imports: [],
                declarations: [
                    QuestionEditorTextComponent
                ],
                exports: [
                    QuestionEditorTextComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionEditorTextModule, { declarations: [QuestionEditorTextComponent], exports: [QuestionEditorTextComponent] }); })();

const _c0$7 = ["fuzzyTranslation"];
function QuestionEditorListsetComponent_div_1_Template(rf, ctx) { if (rf & 1) {
    const _r6 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 5)(1, "button", 6);
    i0.ɵɵlistener("click", function QuestionEditorListsetComponent_div_1_Template_button_click_1_listener($event) { i0.ɵɵrestoreView(_r6); const ctx_r5 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r5.onClickAdd($event)); });
    i0.ɵɵtext(2, "Add Option");
    i0.ɵɵelementEnd()();
} }
function QuestionEditorListsetComponent_div_2_th_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "th", 16);
    i0.ɵɵtext(1, "Value");
    i0.ɵɵelementEnd();
} }
function QuestionEditorListsetComponent_div_2_th_9_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "th", 17);
    i0.ɵɵtext(1, "Reorder");
    i0.ɵɵelementEnd();
} }
function QuestionEditorListsetComponent_div_2_th_10_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "th", 18);
    i0.ɵɵtext(1, "Remove");
    i0.ɵɵelementEnd();
} }
function QuestionEditorListsetComponent_div_2_tr_12_1_ng_template_0_Template(rf, ctx) { }
function QuestionEditorListsetComponent_div_2_tr_12_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵtemplate(0, QuestionEditorListsetComponent_div_2_tr_12_1_ng_template_0_Template, 0, 0, "ng-template");
} }
function QuestionEditorListsetComponent_div_2_tr_12_td_4_Template(rf, ctx) { if (rf & 1) {
    const _r19 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "td", 23)(1, "input", 24);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorListsetComponent_div_2_tr_12_td_4_Template_input_ngModelChange_1_listener($event) { i0.ɵɵrestoreView(_r19); const option_r11 = i0.ɵɵnextContext().$implicit; return i0.ɵɵresetView(option_r11.scoreValue = $event); });
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const option_r11 = i0.ɵɵnextContext().$implicit;
    const ctx_r13 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("scoreInput", ctx_r13.question)("scoreInputConditional", option_r11.id)("ngModel", option_r11.scoreValue);
} }
function QuestionEditorListsetComponent_div_2_tr_12_td_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "td", 17)(1, "mat-icon", 25);
    i0.ɵɵtext(2, "open_with");
    i0.ɵɵelementEnd()();
} }
function QuestionEditorListsetComponent_div_2_tr_12_td_6_Template(rf, ctx) { if (rf & 1) {
    const _r23 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "td", 18)(1, "mat-icon", 26);
    i0.ɵɵlistener("click", function QuestionEditorListsetComponent_div_2_tr_12_td_6_Template_mat_icon_click_1_listener() { i0.ɵɵrestoreView(_r23); const option_r11 = i0.ɵɵnextContext().$implicit; const ctx_r21 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r21.removeItem(option_r11)); });
    i0.ɵɵtext(2, "delete");
    i0.ɵɵelementEnd()();
} }
function QuestionEditorListsetComponent_div_2_tr_12_Template(rf, ctx) { if (rf & 1) {
    const _r25 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "tr", 19);
    i0.ɵɵtemplate(1, QuestionEditorListsetComponent_div_2_tr_12_1_Template, 1, 0, null, 20);
    i0.ɵɵelementStart(2, "td", 10)(3, "input", 21);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorListsetComponent_div_2_tr_12_Template_input_ngModelChange_3_listener($event) { const restoredCtx = i0.ɵɵrestoreView(_r25); const option_r11 = restoredCtx.$implicit; return i0.ɵɵresetView(option_r11.label = $event); });
    i0.ɵɵelementEnd()();
    i0.ɵɵtemplate(4, QuestionEditorListsetComponent_div_2_tr_12_td_4_Template, 2, 3, "td", 22);
    i0.ɵɵtemplate(5, QuestionEditorListsetComponent_div_2_tr_12_td_5_Template, 3, 0, "td", 12);
    i0.ɵɵtemplate(6, QuestionEditorListsetComponent_div_2_tr_12_td_6_Template, 3, 0, "td", 13);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const option_r11 = ctx.$implicit;
    const ctx_r10 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("readonly", ctx_r10.editorConfig.permissions.readonly)("ngModel", option_r11.label);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r10.editorConfig.recommendations && ctx_r10.editorConfig.permissions.questions[ctx_r10.questionType].edit);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r10.editorConfig.permissions.questions[ctx_r10.questionType].edit);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r10.editorConfig.permissions.questions[ctx_r10.questionType].remove);
} }
function QuestionEditorListsetComponent_div_2_Template(rf, ctx) { if (rf & 1) {
    const _r27 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 7)(1, "h5", 8);
    i0.ɵɵtext(2, "Options");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "table", 9)(4, "thead")(5, "tr")(6, "th", 10);
    i0.ɵɵtext(7, "Text");
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(8, QuestionEditorListsetComponent_div_2_th_8_Template, 2, 0, "th", 11);
    i0.ɵɵtemplate(9, QuestionEditorListsetComponent_div_2_th_9_Template, 2, 0, "th", 12);
    i0.ɵɵtemplate(10, QuestionEditorListsetComponent_div_2_th_10_Template, 2, 0, "th", 13);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(11, "tbody", 14);
    i0.ɵɵlistener("cdkDropListDropped", function QuestionEditorListsetComponent_div_2_Template_tbody_cdkDropListDropped_11_listener($event) { i0.ɵɵrestoreView(_r27); const ctx_r26 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r26.onDropped($event)); });
    i0.ɵɵtemplate(12, QuestionEditorListsetComponent_div_2_tr_12_Template, 7, 5, "tr", 15);
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("offsetTopParent", ctx_r1.el.nativeElement)("offsetTopDoCheck", ctx_r1);
    i0.ɵɵadvance(8);
    i0.ɵɵproperty("ngIf", ctx_r1.editorConfig.recommendations && ctx_r1.editorConfig.permissions.questions[ctx_r1.questionType].edit);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.editorConfig.permissions.questions[ctx_r1.questionType].edit);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.editorConfig.permissions.questions[ctx_r1.questionType].remove);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("cdkDropListData", ctx_r1.question.meta.listValues)("cdkDropListDisabled", !ctx_r1.editorConfig.permissions.questions[ctx_r1.questionType].edit);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", ctx_r1.question.meta.listValues);
} }
function QuestionEditorListsetComponent_ng_container_3_div_1_mat_option_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "mat-option", 33);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const option_r31 = ctx.$implicit;
    i0.ɵɵproperty("value", option_r31.label);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", option_r31.label, " ");
} }
function QuestionEditorListsetComponent_ng_container_3_div_1_Template(rf, ctx) { if (rf & 1) {
    const _r33 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 29)(1, "mat-form-field")(2, "mat-label");
    i0.ɵɵtext(3, "Preferred Response");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "mat-select", 30);
    i0.ɵɵlistener("ngModelChange", function QuestionEditorListsetComponent_ng_container_3_div_1_Template_mat_select_ngModelChange_4_listener($event) { i0.ɵɵrestoreView(_r33); const ctx_r32 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r32.question.meta.preferredResponse = $event); });
    i0.ɵɵelementStart(5, "mat-option", 31);
    i0.ɵɵtext(6, "Select");
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(7, QuestionEditorListsetComponent_ng_container_3_div_1_mat_option_7_Template, 2, 2, "mat-option", 32);
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const ctx_r28 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(4);
    i0.ɵɵproperty("ngModel", ctx_r28.question.meta.preferredResponse);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngForOf", ctx_r28.question.meta.listValues);
} }
function QuestionEditorListsetComponent_ng_container_3_div_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 34)(1, "mat-form-field")(2, "mat-label");
    i0.ɵɵtext(3, "Feedback when responses do not match:");
    i0.ɵɵelementEnd();
    i0.ɵɵelement(4, "textarea", 35, 36);
    i0.ɵɵelementEnd()();
} }
function QuestionEditorListsetComponent_ng_container_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, QuestionEditorListsetComponent_ng_container_3_div_1_Template, 8, 2, "div", 27);
    i0.ɵɵtemplate(2, QuestionEditorListsetComponent_ng_container_3_div_2_Template, 6, 0, "div", 28);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r2.editorConfig.permissions.questions.edit);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r2.editorConfig.permissions.questions[ctx_r2.questionType].edit);
} }
function QuestionEditorListsetComponent_ng_template_4_ng_container_3_p_10_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p")(1, "span");
    i0.ɵɵtext(2, "Old:");
    i0.ɵɵelementEnd();
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const change_r37 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate(change_r37.value.current);
} }
function QuestionEditorListsetComponent_ng_template_4_ng_container_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0)(1);
    i0.ɵɵelementStart(2, "h4");
    i0.ɵɵtext(3);
    i0.ɵɵpipe(4, "titlecase");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "div", 40)(6, "p")(7, "span");
    i0.ɵɵtext(8, "New:");
    i0.ɵɵelementEnd();
    i0.ɵɵtext(9);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(10, QuestionEditorListsetComponent_ng_template_4_ng_container_3_p_10_Template, 4, 1, "p", 3);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd()();
} if (rf & 2) {
    const change_r37 = ctx.$implicit;
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(4, 3, change_r37.key));
    i0.ɵɵadvance(6);
    i0.ɵɵtextInterpolate(change_r37.value.new);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", change_r37.value.current);
} }
function QuestionEditorListsetComponent_ng_template_4_Template(rf, ctx) { if (rf & 1) {
    const _r41 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 37)(1, "h3");
    i0.ɵɵtext(2, "Source Translation Changed");
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(3, QuestionEditorListsetComponent_ng_template_4_ng_container_3_Template, 11, 5, "ng-container", 38);
    i0.ɵɵpipe(4, "keyvalue");
    i0.ɵɵelementStart(5, "button", 6);
    i0.ɵɵlistener("click", function QuestionEditorListsetComponent_ng_template_4_Template_button_click_5_listener() { const restoredCtx = i0.ɵɵrestoreView(_r41); const data_r35 = restoredCtx.$implicit; const ctx_r40 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r40.onUnFuzzyClick(data_r35.option)); });
    i0.ɵɵtext(6, "This does not need an updated translation.");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(7, "mat-icon", 39);
    i0.ɵɵlistener("click", function QuestionEditorListsetComponent_ng_template_4_Template_mat_icon_click_7_listener() { i0.ɵɵrestoreView(_r41); const ctx_r42 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r42.dialogRef.close()); });
    i0.ɵɵtext(8, "close");
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const data_r35 = ctx.$implicit;
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngForOf", i0.ɵɵpipeBind1(4, 1, data_r35.fuzzy));
} }
/**
 * Generates a listset editor
 *
 */
class QuestionEditorListsetComponent {
    constructor(question, editorConfig, parentComponent, survey, changeDetectorRef, dialog, el) {
        this.question = question;
        this.editorConfig = editorConfig;
        this.parentComponent = parentComponent;
        this.survey = survey;
        this.changeDetectorRef = changeDetectorRef;
        this.dialog = dialog;
        this.el = el;
        this.SurveyIntent = SurveyIntent;
        this.surveyChange = survey.events.subscribe(e => {
            this.changeDetectorRef.detectChanges();
        });
    }
    // needed for the offsetTopParent directive
    ngDoCheck() { }
    ngOnInit() {
        if (!this.question.meta.insertId) {
            this.question.meta.insertId = 0;
        }
        if (!this.question.meta.listValues) {
            this.question.meta.listValues = [];
        }
    }
    ngOnDestroy() {
        this.surveyChange.unsubscribe();
    }
    onClickAdd(event) {
        event.preventDefault();
        this.addItem();
    }
    addItem() {
        this.question.meta.listValues.push({
            label: this.newLabel,
            id: this.question.meta.insertId++
        });
        this.newLabel = '';
        this.changeDetectorRef.detectChanges();
        // focus the new option text
        const newTextxInput = this.el.nativeElement.querySelector('tbody tr:last-child td.cell-text input');
        if (newTextxInput) {
            newTextxInput.focus();
        }
    }
    removeItem(option) {
        this.question.meta.listValues = this.question.meta.listValues.filter(opt => opt !== option);
        this.changeDetectorRef.markForCheck();
    }
    onDropped(event) {
        moveItemInArray(this.question.meta.listValues, event.previousIndex, event.currentIndex);
    }
    /**
     * Open the dialog showing information about the fuzzy content within
     */
    onClickFuzzy(option) {
        event.preventDefault();
        const fuzzy = this.question.fuzzy?.meta.listValues[option.id];
        this.dialogRef = this.dialog.open(this.fuzzyTranslation, {
            autoFocus: false,
            data: { fuzzy, option }
        });
    }
    /**
     * Mark as unfuzzy
     */
    onUnFuzzyClick(option) {
        event.preventDefault();
        delete this.question.fuzzy.meta.listValues[option.id];
        validateFuzzyObject(this.question);
        this.dialogRef.close();
        this.changeDetectorRef.markForCheck();
        this.parentComponent.changeDetectorRef.markForCheck();
    }
}
QuestionEditorListsetComponent.ɵfac = function QuestionEditorListsetComponent_Factory(t) { return new (t || QuestionEditorListsetComponent)(i0.ɵɵdirectiveInject(QUESTION), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG), i0.ɵɵdirectiveInject(SurveyItemComponent), i0.ɵɵdirectiveInject(Survey), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1$3.MatDialog), i0.ɵɵdirectiveInject(i0.ElementRef)); };
QuestionEditorListsetComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionEditorListsetComponent, selectors: [["listset-editor"]], viewQuery: function QuestionEditorListsetComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0$7, 7);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.fuzzyTranslation = _t.first);
    } }, inputs: { questionType: "questionType" }, decls: 6, vars: 3, consts: [[1, "listset-manage"], ["class", "listset-add-new", 4, "ngIf"], ["class", "listset-options", 3, "offsetTopParent", "offsetTopDoCheck", 4, "ngIf"], [4, "ngIf"], ["fuzzyTranslation", ""], [1, "listset-add-new"], ["mat-flat-button", "", "color", "primary", 3, "click"], [1, "listset-options", 3, "offsetTopParent", "offsetTopDoCheck"], [1, "listset-options-title"], ["cellspacing", "0", "cellpadding", "0", 1, "list-table", "simple-table"], [1, "cell-text"], ["class", "cell-value", 4, "ngIf"], ["class", "cell-reorder", 4, "ngIf"], ["class", "cell-remove", 4, "ngIf"], ["cdkDropList", "", "cdkDropListLockAxis", "y", 3, "cdkDropListData", "cdkDropListDisabled", "cdkDropListDropped"], ["cdkDrag", "", "cdkDragBoundary", "tbody", 4, "ngFor", "ngForOf"], [1, "cell-value"], [1, "cell-reorder"], [1, "cell-remove"], ["cdkDrag", "", "cdkDragBoundary", "tbody"], [4, "cdkDragPreview"], ["type", "text", "matInput", "", "required", "", 1, "inline-input", 3, "readonly", "ngModel", "ngModelChange"], ["class", "cell-value cell-numeric", 4, "ngIf"], [1, "cell-value", "cell-numeric"], ["type", "number", "matInput", "", 1, "inline-input", 3, "scoreInput", "scoreInputConditional", "ngModel", "ngModelChange"], ["cdkDragHandle", "", 1, "reorder-icon"], [1, "remove-icon", 3, "click"], ["id", "comparePreferredResponse", 4, "ngIf"], ["id", "compareFeedbackMismatch", 4, "ngIf"], ["id", "comparePreferredResponse"], [3, "ngModel", "ngModelChange"], ["value", ""], [3, "value", 4, "ngFor", "ngForOf"], [3, "value"], ["id", "compareFeedbackMismatch"], ["matInput", "", "fuzzyMetaProperty", "feedbackMismatch", "cdkTextareaAutosize", "", "cdkAutosizeMinRows", "1", "cdkAutosizeMaxRows", "5"], ["autosize", "cdkTextareaAutosize"], [1, "fuzzy-dialog"], [4, "ngFor", "ngForOf"], ["matDialogClose", "", 1, "close-fuzzy", 3, "click"], [1, "fuzzy-item"]], template: function QuestionEditorListsetComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵtemplate(1, QuestionEditorListsetComponent_div_1_Template, 3, 0, "div", 1);
        i0.ɵɵtemplate(2, QuestionEditorListsetComponent_div_2_Template, 13, 8, "div", 2);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(3, QuestionEditorListsetComponent_ng_container_3_Template, 3, 2, "ng-container", 3);
        i0.ɵɵtemplate(4, QuestionEditorListsetComponent_ng_template_4_Template, 9, 3, "ng-template", null, 4, i0.ɵɵtemplateRefExtractor);
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.editorConfig.permissions.questions[ctx.questionType].add);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.question.meta.listValues.length);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.survey.intent === ctx.SurveyIntent.COMPARE);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i3.DefaultValueAccessor, i3.NumberValueAccessor, i3.NgControlStatus, i3.RequiredValidator, i3.NgModel, i2.MatButton, i4$1.MatFormField, i4$1.MatLabel, i4$2.MatSelect, i5$1.MatOption, i5.MatInput, i7.CdkTextareaAutosize, i6$1.MatIcon, i6$2.CdkDropList, i6$2.CdkDrag, i6$2.CdkDragHandle, i6$2.CdkDragPreview, OffsetTopParentDirective, ScoreInputDirective, FuzzyMetaPropertyDirective, i1$2.TitleCasePipe, i1$2.KeyValuePipe], styles: ["[_nghost-%COMP%]{display:block}td.cell-text[_ngcontent-%COMP%]{padding:0}.cell-text[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{margin:0 5px;width:100%;padding:10px 3px;box-sizing:border-box}.cell-reorder[_ngcontent-%COMP%], .cell-remove[_ngcontent-%COMP%]{width:80px}.remove-icon[_ngcontent-%COMP%]{cursor:pointer}.listset-add-new[_ngcontent-%COMP%]{width:180px}.listset-manage[_ngcontent-%COMP%]{display:flex}.listset-options[_ngcontent-%COMP%]{flex:1}.listset-options[_ngcontent-%COMP%]:not(:first-child){margin:0 0 0 55px}", ".fuzzy-dialog[_ngcontent-%COMP%]{min-width:320px;position:relative}.fuzzy-dialog[_ngcontent-%COMP%]   h3[_ngcontent-%COMP%]{margin-top:0}.fuzzy-dialog[_ngcontent-%COMP%]   h4[_ngcontent-%COMP%]{margin:0 0 -5px;font-size:14px;border-bottom:1px solid #ccc}.fuzzy-dialog[_ngcontent-%COMP%]   button[_ngcontent-%COMP%]{margin:12px 0 18px}.fuzzy-dialog[_ngcontent-%COMP%]   p[_ngcontent-%COMP%]{display:flex}.fuzzy-dialog[_ngcontent-%COMP%]   p[_ngcontent-%COMP%]   span[_ngcontent-%COMP%]{min-width:60px;display:inline-block}.fuzzy-dialog[_ngcontent-%COMP%]   .fuzzy-item[_ngcontent-%COMP%]{margin:15px 0;max-width:700px}.fuzzy-dialog[_ngcontent-%COMP%]   .fuzzy-item[_ngcontent-%COMP%]   p[_ngcontent-%COMP%]{margin:0 0 6px}.fuzzy-dialog[_ngcontent-%COMP%]   .close-fuzzy[_ngcontent-%COMP%]{cursor:pointer;position:absolute;top:-17px;right:-13px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorListsetComponent, [{
        type: Component,
        args: [{ selector: 'listset-editor', template: "<div class=\"listset-manage\">\n\t<div *ngIf=\"editorConfig.permissions.questions[questionType].add\"\n\t\t class=\"listset-add-new\">\n\t\t<button mat-flat-button\n\t\t\t\tcolor=\"primary\"\n\t\t\t\t(click)=\"onClickAdd( $event )\">Add Option</button>\n\t</div>\n\n\t<div *ngIf=\"question.meta.listValues.length\"\n\t\t [offsetTopParent]=\"el.nativeElement\"\n\t\t [offsetTopDoCheck]=\"this\"\n\t\t class=\"listset-options\">\n\n\t\t<h5 class=\"listset-options-title\">Options</h5>\n\n\t\t<table class=\"list-table simple-table\"\n\t\t\t   cellspacing=\"0\"\n\t\t\t   cellpadding=\"0\">\n\t\t\t<thead>\n\t\t\t\t<tr>\n\t\t\t\t\t<th class=\"cell-text\">Text</th>\n\t\t\t\t\t<th *ngIf=\"editorConfig.recommendations && editorConfig.permissions.questions[questionType].edit\"\n\t\t\t\t\t\tclass=\"cell-value\">Value</th>\n\t\t\t\t\t<th class=\"cell-reorder\"\n\t\t\t\t\t\t*ngIf=\"editorConfig.permissions.questions[questionType].edit\">Reorder</th>\n\t\t\t\t\t<th *ngIf=\"editorConfig.permissions.questions[questionType].remove\"\n\t\t\t\t\t\tclass=\"cell-remove\">Remove</th>\n\t\t\t\t</tr>\n\t\t\t</thead>\n\t\t\t<tbody cdkDropList\n\t\t\t\t   cdkDropListLockAxis=\"y\"\n\t\t\t\t   [cdkDropListData]=\"question.meta.listValues\"\n\t\t\t\t   [cdkDropListDisabled]=\"!editorConfig.permissions.questions[questionType].edit\"\n\t\t\t\t   (cdkDropListDropped)=\"onDropped($event)\">\n\n\t\t\t\t<tr *ngFor=\"let option of question.meta.listValues\"\n\t\t\t\t\tcdkDrag\n\t\t\t\t\tcdkDragBoundary='tbody'>\n\n\t\t\t\t\t<ng-template *cdkDragPreview></ng-template>\n\n\t\t\t\t\t<td class=\"cell-text\">\n\t\t\t\t\t\t<input type=\"text\"\n\t\t\t\t\t\t\t   class=\"inline-input\"\n\t\t\t\t\t\t\t   matInput\n\t\t\t\t\t\t\t   required\n\t\t\t\t\t\t\t   [readonly]=\"editorConfig.permissions.readonly\"\n\t\t\t\t\t\t\t   [(ngModel)]=\"option.label\" />\n\t\t\t\t\t</td>\n\t\t\t\t\t<td *ngIf=\"editorConfig.recommendations && editorConfig.permissions.questions[questionType].edit\"\n\t\t\t\t\t\tclass=\"cell-value cell-numeric\">\n\t\t\t\t\t\t<input type=\"number\"\n\t\t\t\t\t\t\t   class=\"inline-input\"\n\t\t\t\t\t\t\t   matInput\n\t\t\t\t\t\t\t   [scoreInput]=\"question\"\n\t\t\t\t\t\t\t   [scoreInputConditional]=\"option.id\"\n\t\t\t\t\t\t\t   [(ngModel)]=\"option.scoreValue\" />\n\t\t\t\t\t</td>\n\t\t\t\t\t<td *ngIf=\"editorConfig.permissions.questions[questionType].edit\"\n\t\t\t\t\t\tclass=\"cell-reorder\">\n\t\t\t\t\t\t<mat-icon cdkDragHandle\n\t\t\t\t\t\t\t\t  class=\"reorder-icon\">open_with</mat-icon>\n\t\t\t\t\t</td>\n\t\t\t\t\t<td *ngIf=\"editorConfig.permissions.questions[questionType].remove\"\n\t\t\t\t\t\tclass=\"cell-remove\">\n\t\t\t\t\t\t<mat-icon class=\"remove-icon\"\n\t\t\t\t\t\t\t\t  (click)=\"removeItem(option)\">delete</mat-icon>\n\n\t\t\t\t\t\t<!--  @todo maybe re-implement the fuzzy notification\n\t\t\t\t\t\t\t\n\t\t\t\t\t\t\t<button mat-button\n\t\t\t\t\t\t\t\tmatSuffix\n\t\t\t\t\t\t\t\tmat-icon-button\n\t\t\t\t\t\t\t\tmatTooltip=\"This element's source text has changed\"\n\t\t\t\t\t\t\t\t(click)=\"onClickFuzzy(option)\">\n\t\t\t\t\t\t\t<mat-icon>translate</mat-icon>\n\t\t\t\t\t\t</button>-->\n\t\t\t\t\t</td>\n\t\t\t\t</tr>\n\t\t\t</tbody>\n\t\t</table>\n\n\t</div>\n\n</div>\n\n<ng-container *ngIf=\"survey.intent === SurveyIntent.COMPARE\">\n\t<div id=\"comparePreferredResponse\"\n\t\t *ngIf=\"editorConfig.permissions.questions.edit\">\n\t\t<mat-form-field>\n\t\t\t<mat-label>Preferred Response</mat-label>\n\t\t\t<mat-select [(ngModel)]=\"question.meta.preferredResponse\">\n\t\t\t\t<mat-option value=''>Select</mat-option>\n\t\t\t\t<mat-option *ngFor=\"let option of question.meta.listValues\"\n\t\t\t\t\t\t\t[value]=\"option.label\">\n\t\t\t\t\t{{option.label}}\n\t\t\t\t</mat-option>\n\t\t\t</mat-select>\n\t\t</mat-form-field>\n\n\t</div>\n\n\t<div id=\"compareFeedbackMismatch\"\n\t\t *ngIf=\"editorConfig.permissions.questions[questionType].edit\">\n\t\t<mat-form-field>\n\t\t\t<mat-label>Feedback when responses do not match:</mat-label>\n\t\t\t<textarea matInput\n\t\t\t\t\t  fuzzyMetaProperty=\"feedbackMismatch\"\n\t\t\t\t\t  cdkTextareaAutosize\n\t\t\t\t\t  cdkAutosizeMinRows=\"1\"\n\t\t\t\t\t  cdkAutosizeMaxRows=\"5\"\n\t\t\t\t\t  #autosize=\"cdkTextareaAutosize\"></textarea>\n\t\t</mat-form-field>\n\t</div>\n\n</ng-container>\n\n<!-- Fuzzy Translations Dialog-->\n<ng-template #fuzzyTranslation\n\t\t\t let-data>\n\n\t<div class=\"fuzzy-dialog\">\n\n\t\t<!-- If the item itself has any of it's properties fuzzy -->\n\n\t\t<h3>Source Translation Changed</h3>\n\n\t\t<ng-container *ngFor=\"let change of data.fuzzy |keyvalue\">\n\n\t\t\t<!-- handle display of other standard change properties -->\n\t\t\t<ng-container>\n\n\t\t\t\t<h4>{{change.key | titlecase }}</h4>\n\n\t\t\t\t<div class=\"fuzzy-item\">\n\t\t\t\t\t<p><span>New:</span>{{change.value.new}}</p>\n\t\t\t\t\t<p *ngIf=\"change.value.current\"><span>Old:</span>{{change.value.current}}</p>\n\t\t\t\t</div>\n\n\t\t\t</ng-container>\n\n\t\t</ng-container>\n\n\t\t<!-- button to dismiss fuzzy notification -->\n\t\t<button mat-flat-button\n\t\t\t\tcolor=\"primary\"\n\t\t\t\t(click)=\"onUnFuzzyClick(data.option)\">This does not need an updated translation.</button>\n\n\n\t\t<mat-icon class=\"close-fuzzy\"\n\t\t\t\t  matDialogClose\n\t\t\t\t  (click)=\"dialogRef.close()\">close</mat-icon>\n\t</div>\n</ng-template>\n", styles: [":host{display:block}td.cell-text{padding:0}.cell-text input{margin:0 5px;width:100%;padding:10px 3px;box-sizing:border-box}.cell-reorder,.cell-remove{width:80px}.remove-icon{cursor:pointer}.listset-add-new{width:180px}.listset-manage{display:flex}.listset-options{flex:1}.listset-options:not(:first-child){margin:0 0 0 55px}\n", ".fuzzy-dialog{min-width:320px;position:relative}.fuzzy-dialog h3{margin-top:0}.fuzzy-dialog h4{margin:0 0 -5px;font-size:14px;border-bottom:1px solid #ccc}.fuzzy-dialog button{margin:12px 0 18px}.fuzzy-dialog p{display:flex}.fuzzy-dialog p span{min-width:60px;display:inline-block}.fuzzy-dialog .fuzzy-item{margin:15px 0;max-width:700px}.fuzzy-dialog .fuzzy-item p{margin:0 0 6px}.fuzzy-dialog .close-fuzzy{cursor:pointer;position:absolute;top:-17px;right:-13px}\n"] }]
    }], function () { return [{ type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }, { type: SurveyItemComponent }, { type: Survey }, { type: i0.ChangeDetectorRef }, { type: i1$3.MatDialog }, { type: i0.ElementRef }]; }, { questionType: [{
            type: Input
        }], fuzzyTranslation: [{
            type: ViewChild,
            args: ['fuzzyTranslation', { static: true }]
        }] }); })();

class QuestionEditorListsetModule {
}
QuestionEditorListsetModule.ɵfac = function QuestionEditorListsetModule_Factory(t) { return new (t || QuestionEditorListsetModule)(); };
QuestionEditorListsetModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionEditorListsetModule });
QuestionEditorListsetModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        FormsModule,
        EditorMaterialModule,
        EditorLayoutDirectivesModule,
        ScoreInputDirectiveModule,
        FuzzyModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorListsetModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    EditorMaterialModule,
                    EditorLayoutDirectivesModule,
                    ScoreInputDirectiveModule,
                    FuzzyModule
                ],
                declarations: [
                    QuestionEditorListsetComponent
                ],
                exports: [
                    QuestionEditorListsetComponent,
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionEditorListsetModule, { declarations: [QuestionEditorListsetComponent], imports: [CommonModule,
        FormsModule,
        EditorMaterialModule,
        EditorLayoutDirectivesModule,
        ScoreInputDirectiveModule,
        FuzzyModule], exports: [QuestionEditorListsetComponent] }); })();

class QuestionEditorPicklistModule {
}
QuestionEditorPicklistModule.ɵfac = function QuestionEditorPicklistModule_Factory(t) { return new (t || QuestionEditorPicklistModule)(); };
QuestionEditorPicklistModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionEditorPicklistModule });
QuestionEditorPicklistModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [QuestionEditorListsetModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorPicklistModule, [{
        type: NgModule,
        args: [{
                imports: [
                    QuestionEditorListsetModule
                ],
                declarations: [
                    QuestionEditorPicklistComponent
                ],
                exports: [
                    QuestionEditorPicklistComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionEditorPicklistModule, { declarations: [QuestionEditorPicklistComponent], imports: [QuestionEditorListsetModule], exports: [QuestionEditorPicklistComponent] }); })();
i0.ɵɵsetComponentScope(QuestionEditorPicklistComponent, [QuestionEditorListsetComponent], []);

class QuestionEditorChecklistModule {
}
QuestionEditorChecklistModule.ɵfac = function QuestionEditorChecklistModule_Factory(t) { return new (t || QuestionEditorChecklistModule)(); };
QuestionEditorChecklistModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionEditorChecklistModule });
QuestionEditorChecklistModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [QuestionEditorListsetModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorChecklistModule, [{
        type: NgModule,
        args: [{
                imports: [
                    QuestionEditorListsetModule
                ],
                declarations: [
                    QuestionEditorChecklistComponent
                ],
                exports: [
                    QuestionEditorChecklistComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionEditorChecklistModule, { declarations: [QuestionEditorChecklistComponent], imports: [QuestionEditorListsetModule], exports: [QuestionEditorChecklistComponent] }); })();
i0.ɵɵsetComponentScope(QuestionEditorChecklistComponent, [QuestionEditorListsetComponent], []);

class QuestionEditorRadiosetModule {
}
QuestionEditorRadiosetModule.ɵfac = function QuestionEditorRadiosetModule_Factory(t) { return new (t || QuestionEditorRadiosetModule)(); };
QuestionEditorRadiosetModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionEditorRadiosetModule });
QuestionEditorRadiosetModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [QuestionEditorListsetModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorRadiosetModule, [{
        type: NgModule,
        args: [{
                imports: [
                    QuestionEditorListsetModule
                ],
                declarations: [
                    QuestionEditorRadiosetComponent
                ],
                exports: [
                    QuestionEditorRadiosetComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionEditorRadiosetModule, { declarations: [QuestionEditorRadiosetComponent], imports: [QuestionEditorListsetModule], exports: [QuestionEditorRadiosetComponent] }); })();
i0.ɵɵsetComponentScope(QuestionEditorRadiosetComponent, [QuestionEditorListsetComponent], []);

class QuestionEditorScaleModule {
}
QuestionEditorScaleModule.ɵfac = function QuestionEditorScaleModule_Factory(t) { return new (t || QuestionEditorScaleModule)(); };
QuestionEditorScaleModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionEditorScaleModule });
QuestionEditorScaleModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({});
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorScaleModule, [{
        type: NgModule,
        args: [{
                imports: [],
                declarations: [
                    QuestionEditorScaleComponent
                ],
                exports: [
                    QuestionEditorScaleComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionEditorScaleModule, { declarations: [QuestionEditorScaleComponent], exports: [QuestionEditorScaleComponent] }); })();

const NG_MATERIALS$1 = [
    MatRadioModule,
    MatCheckboxModule,
    MatButtonModule,
    MatSelectModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogModule,
    PortalModule,
    A11yModule,
    MatTooltipModule
];
class SurveyMaterialModule {
}
SurveyMaterialModule.ɵfac = function SurveyMaterialModule_Factory(t) { return new (t || SurveyMaterialModule)(); };
SurveyMaterialModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: SurveyMaterialModule });
SurveyMaterialModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [MatRadioModule,
        MatCheckboxModule,
        MatButtonModule,
        MatSelectModule,
        MatInputModule,
        MatFormFieldModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatDialogModule,
        PortalModule,
        A11yModule,
        MatTooltipModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurveyMaterialModule, [{
        type: NgModule,
        args: [{
                exports: NG_MATERIALS$1
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SurveyMaterialModule, { exports: [MatRadioModule,
        MatCheckboxModule,
        MatButtonModule,
        MatSelectModule,
        MatInputModule,
        MatFormFieldModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatDialogModule,
        PortalModule,
        A11yModule,
        MatTooltipModule] }); })();

const ALPHA_SURVEYS_OPTIONS = new InjectionToken('question-title-options');

class QuestionTitleDirective {
    constructor(el, options) {
        this.el = el;
        this.options = options;
    }
    ngOnInit() {
        let suffix = '';
        if (this.options?.showRequiredAsterisks && !this.questionTitle.optional) {
            suffix = ' *';
        }
        else if (this.options?.showOptionalLabel && this.questionTitle.optional) {
            suffix = ' ' + $localize `:alpha.surveys|Form Helper Labels@@formLabelOptional:(Optional)`;
        }
        this.el.nativeElement.innerHTML = `${this.questionTitle.title}${suffix}`;
    }
}
QuestionTitleDirective.ɵfac = function QuestionTitleDirective_Factory(t) { return new (t || QuestionTitleDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(ALPHA_SURVEYS_OPTIONS)); };
QuestionTitleDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: QuestionTitleDirective, selectors: [["", "questionTitle", ""]], inputs: { questionTitle: "questionTitle" } });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionTitleDirective, [{
        type: Directive,
        args: [{
                selector: '[questionTitle]'
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                type: Inject,
                args: [ALPHA_SURVEYS_OPTIONS]
            }] }]; }, { questionTitle: [{
            type: Input
        }] }); })();

class QuestionTitlePipe {
    constructor(options) {
        this.options = options;
    }
    transform(question) {
        let suffix = '';
        if (this.options?.showRequiredAsterisks && !question.optional) {
            suffix = ' *';
        }
        else if (this.options?.showOptionalLabel && question.optional) {
            suffix = ' ' + $localize `:alpha.surveys|Form Helper Labels@@formLabelOptional:(Optional)`;
        }
        return `${question.title}${suffix}`;
    }
}
QuestionTitlePipe.ɵfac = function QuestionTitlePipe_Factory(t) { return new (t || QuestionTitlePipe)(i0.ɵɵdirectiveInject(ALPHA_SURVEYS_OPTIONS, 16)); };
QuestionTitlePipe.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "questionTitle", type: QuestionTitlePipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionTitlePipe, [{
        type: Pipe,
        args: [{
                name: 'questionTitle'
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [ALPHA_SURVEYS_OPTIONS]
            }] }]; }, null); })();

class QuestionTitleModule {
}
QuestionTitleModule.ɵfac = function QuestionTitleModule_Factory(t) { return new (t || QuestionTitleModule)(); };
QuestionTitleModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionTitleModule });
QuestionTitleModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({});
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionTitleModule, [{
        type: NgModule,
        args: [{
                declarations: [
                    QuestionTitleDirective,
                    QuestionTitlePipe
                ],
                exports: [
                    QuestionTitleDirective,
                    QuestionTitlePipe
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionTitleModule, { declarations: [QuestionTitleDirective,
        QuestionTitlePipe], exports: [QuestionTitleDirective,
        QuestionTitlePipe] }); })();

class YesNoUncertainLabelDirective {
    constructor(el) {
        this.el = el;
        this.ynuPipe = new YesNoUncertainLabelPipe();
    }
    ngOnInit() {
        this.el.nativeElement.innerHTML = this.ynuPipe.transform(this.ynuLabelValue);
    }
}
YesNoUncertainLabelDirective.ɵfac = function YesNoUncertainLabelDirective_Factory(t) { return new (t || YesNoUncertainLabelDirective)(i0.ɵɵdirectiveInject(i0.ElementRef)); };
YesNoUncertainLabelDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: YesNoUncertainLabelDirective, selectors: [["", "ynuLabelValue", ""]], inputs: { ynuLabelValue: "ynuLabelValue" } });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(YesNoUncertainLabelDirective, [{
        type: Directive,
        args: [{
                selector: '[ynuLabelValue]'
            }]
    }], function () { return [{ type: i0.ElementRef }]; }, { ynuLabelValue: [{
            type: Input
        }] }); })();

function QuestionYNUComponent_p_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p", 4);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ctx_r0.question.error, "\n");
} }
class QuestionYNUComponent {
    onKeyDown(e) {
        this.keyManager.onKeydown(e);
    }
    constructor(question, fm, el) {
        this.question = question;
        this.fm = fm;
        this.el = el;
        /**
         * We're allowing the default tab behaviour to move us through the form, however when shift-tabbing back into this component the focus gets messed up
         * This is a hacky way to set the focus index when re-entering the component
         */
        this.subscription = fm.monitor(el, true).subscribe(origin => {
            if (!origin) {
                this.focusedOut = true;
            }
            else if (origin && this.focusedOut) {
                this.focusedOut = false;
                setTimeout(() => {
                    const focused = el.nativeElement.querySelector('.cdk-focused');
                    if (focused) {
                        const curFocusIndex = Array.from(focused.parentElement.children).indexOf(focused);
                        this.keyManager.updateActiveItem(curFocusIndex);
                    }
                });
            }
        });
    }
    focus() {
        this.keyManager.setFirstItemActive();
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.fm.stopMonitoring(this.el);
    }
    ngAfterViewInit() {
        // @note the set focus origin seems to be buggy, without the call to setFocusOrigin there's an error where the origin is a string 'program' but is expected to be an object
        this.keyManager = new FocusKeyManager(this.options).withHorizontalOrientation('ltr').withWrap(true).setFocusOrigin({});
        this.keyManager.updateActiveItem(0);
    }
    onChange(e) {
        if (e instanceof MatRadioChange) {
            const isFirst = typeof (this.question.response) === 'undefined';
            this.question.response = e.value;
            e.source._elementRef.nativeElement.parentElement.dispatchEvent(new QuestionChangeEvent({ firstChange: isFirst, question: this.question }));
        }
    }
}
QuestionYNUComponent.ɵfac = function QuestionYNUComponent_Factory(t) { return new (t || QuestionYNUComponent)(i0.ɵɵdirectiveInject(QUESTION), i0.ɵɵdirectiveInject(i1$1.FocusMonitor), i0.ɵɵdirectiveInject(i0.ElementRef)); };
QuestionYNUComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionYNUComponent, selectors: [["question-ynu"]], viewQuery: function QuestionYNUComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(MatRadioButton, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.options = _t);
    } }, hostBindings: function QuestionYNUComponent_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("keydown", function QuestionYNUComponent_keydown_HostBindingHandler($event) { return ctx.onKeyDown($event); });
    } }, inputs: { disabled: "disabled" }, decls: 12, vars: 16, consts: [[1, "question-title", 3, "questionTitle"], ["class", "question-error", 4, "ngIf"], [3, "value", "disabled", "change"], [3, "value"], [1, "question-error"]], template: function QuestionYNUComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelement(0, "div", 0);
        i0.ɵɵtemplate(1, QuestionYNUComponent_p_1_Template, 2, 1, "p", 1);
        i0.ɵɵelementStart(2, "mat-radio-group", 2);
        i0.ɵɵlistener("change", function QuestionYNUComponent_Template_mat_radio_group_change_2_listener($event) { return ctx.onChange($event); });
        i0.ɵɵelementStart(3, "mat-radio-button", 3);
        i0.ɵɵtext(4);
        i0.ɵɵpipe(5, "ynuLabel");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(6, "mat-radio-button", 3);
        i0.ɵɵtext(7);
        i0.ɵɵpipe(8, "ynuLabel");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(9, "mat-radio-button", 3);
        i0.ɵɵtext(10);
        i0.ɵɵpipe(11, "ynuLabel");
        i0.ɵɵelementEnd()();
    } if (rf & 2) {
        i0.ɵɵproperty("questionTitle", ctx.question);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.question.error);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("value", ctx.question.response)("disabled", ctx.disabled);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("value", 1);
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(5, 10, 1));
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("value", 2);
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(8, 12, 2));
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("value", 3);
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(11, 14, 3));
    } }, dependencies: [i1$2.NgIf, i3$2.MatRadioGroup, i3$2.MatRadioButton, QuestionTitleDirective, YesNoUncertainLabelPipe], styles: [".mat-radio-button[_ngcontent-%COMP%] ~ .mat-radio-button[_ngcontent-%COMP%]{margin-left:16px}.question-title[_ngcontent-%COMP%]{margin-bottom:12px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionYNUComponent, [{
        type: Component,
        args: [{ selector: 'question-ynu', template: "<div class=\"question-title\"\n\t [questionTitle]=\"question\"></div>\n<p *ngIf=\"question.error\"\n   class=\"question-error\">\n\t{{question.error}}\n</p>\n<mat-radio-group [value]=\"question.response\"\n\t\t\t\t (change)=\"onChange( $event )\"\n\t\t\t\t [disabled]=\"disabled\">\n\n\t<mat-radio-button [value]=\"1\">{{ 1 | ynuLabel }}</mat-radio-button>\n\t<mat-radio-button [value]=\"2\">{{ 2 | ynuLabel }}</mat-radio-button>\n\t<mat-radio-button [value]=\"3\">{{ 3 | ynuLabel }}</mat-radio-button>\n\n</mat-radio-group>\n", styles: [".mat-radio-button~.mat-radio-button{margin-left:16px}.question-title{margin-bottom:12px}\n"] }]
    }], function () { return [{ type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }, { type: i1$1.FocusMonitor }, { type: i0.ElementRef }]; }, { onKeyDown: [{
            type: HostListener,
            args: ['keydown', ['$event']]
        }], disabled: [{
            type: Input
        }], options: [{
            type: ViewChildren,
            args: [MatRadioButton]
        }] }); })();

class YesNoUncertainQuestionModule {
}
YesNoUncertainQuestionModule.ɵfac = function YesNoUncertainQuestionModule_Factory(t) { return new (t || YesNoUncertainQuestionModule)(); };
YesNoUncertainQuestionModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: YesNoUncertainQuestionModule });
YesNoUncertainQuestionModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        SurveyMaterialModule,
        QuestionTitleModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(YesNoUncertainQuestionModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    SurveyMaterialModule,
                    QuestionTitleModule
                ],
                declarations: [
                    QuestionYNUComponent,
                    YesNoUncertainLabelPipe,
                    YesNoUncertainLabelDirective,
                ],
                exports: [
                    YesNoUncertainLabelPipe,
                    YesNoUncertainLabelDirective,
                    QuestionYNUComponent,
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(YesNoUncertainQuestionModule, { declarations: [QuestionYNUComponent,
        YesNoUncertainLabelPipe,
        YesNoUncertainLabelDirective], imports: [CommonModule,
        SurveyMaterialModule,
        QuestionTitleModule], exports: [YesNoUncertainLabelPipe,
        YesNoUncertainLabelDirective,
        QuestionYNUComponent] }); })();

class QuestionEditorYNUModule {
}
QuestionEditorYNUModule.ɵfac = function QuestionEditorYNUModule_Factory(t) { return new (t || QuestionEditorYNUModule)(); };
QuestionEditorYNUModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionEditorYNUModule });
QuestionEditorYNUModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        FormsModule,
        EditorMaterialModule,
        YesNoUncertainQuestionModule,
        EditorLayoutDirectivesModule,
        ScoreInputDirectiveModule,
        FuzzyModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorYNUModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    EditorMaterialModule,
                    YesNoUncertainQuestionModule,
                    EditorLayoutDirectivesModule,
                    ScoreInputDirectiveModule,
                    FuzzyModule
                ],
                declarations: [
                    QuestionEditorYesNoUncertain
                ],
                exports: [
                    QuestionEditorYesNoUncertain
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionEditorYNUModule, { declarations: [QuestionEditorYesNoUncertain], imports: [CommonModule,
        FormsModule,
        EditorMaterialModule,
        YesNoUncertainQuestionModule,
        EditorLayoutDirectivesModule,
        ScoreInputDirectiveModule,
        FuzzyModule], exports: [QuestionEditorYesNoUncertain] }); })();

class QuestionEditorNumberModule {
}
QuestionEditorNumberModule.ɵfac = function QuestionEditorNumberModule_Factory(t) { return new (t || QuestionEditorNumberModule)(); };
QuestionEditorNumberModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionEditorNumberModule });
QuestionEditorNumberModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        FormsModule,
        EditorMaterialModule,
        EditorLayoutDirectivesModule,
        ScoreInputDirectiveModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorNumberModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    EditorMaterialModule,
                    EditorLayoutDirectivesModule,
                    ScoreInputDirectiveModule
                ],
                declarations: [
                    QuestionEditorNumberComponent
                ],
                exports: [
                    QuestionEditorNumberComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionEditorNumberModule, { declarations: [QuestionEditorNumberComponent], imports: [CommonModule,
        FormsModule,
        EditorMaterialModule,
        EditorLayoutDirectivesModule,
        ScoreInputDirectiveModule], exports: [QuestionEditorNumberComponent] }); })();

class BooleanLabelDirective {
    constructor(el) {
        this.el = el;
        this.booleanPipe = new BooleanLabelPipe();
    }
    ngOnInit() {
        this.el.nativeElement.innerHTML = this.booleanPipe.transform(this.booleanLabelValue);
    }
}
BooleanLabelDirective.ɵfac = function BooleanLabelDirective_Factory(t) { return new (t || BooleanLabelDirective)(i0.ɵɵdirectiveInject(i0.ElementRef)); };
BooleanLabelDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: BooleanLabelDirective, selectors: [["", "booleanLabelValue", ""]], inputs: { booleanLabelValue: "booleanLabelValue" } });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BooleanLabelDirective, [{
        type: Directive,
        args: [{
                selector: '[booleanLabelValue]'
            }]
    }], function () { return [{ type: i0.ElementRef }]; }, { booleanLabelValue: [{
            type: Input
        }] }); })();

function QuestionBooleanComponent_p_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p", 4);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ctx_r0.question.error, "\n");
} }
class QuestionBooleanComponent {
    onKeyDown(e) {
        this.keyManager.onKeydown(e);
    }
    constructor(question, fm, el) {
        this.question = question;
        this.fm = fm;
        this.el = el;
        /**
         * We're allowing the default tab behaviour to move us through the form, however when shift-tabbing back into this component the focus gets messed up
         * This is a hacky way to set the focus index when re-entering the component
         */
        this.subscription = fm.monitor(el, true).subscribe(origin => {
            if (!origin) {
                this.focusedOut = true;
            }
            else if (origin && this.focusedOut) {
                this.focusedOut = false;
                setTimeout(() => {
                    const focused = el.nativeElement.querySelector('.cdk-focused');
                    if (focused) {
                        const curFocusIndex = Array.from(focused.parentElement.children).indexOf(focused);
                        this.keyManager.updateActiveItem(curFocusIndex);
                    }
                });
            }
        });
    }
    focus() {
        this.keyManager.setFirstItemActive();
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.fm.stopMonitoring(this.el);
    }
    ngAfterViewInit() {
        // @note the set focus origin seems to be buggy, without the call to setFocusOrigin there's an error where the origin is a string 'program' but is expected to be an object
        this.keyManager = new FocusKeyManager(this.options).withHorizontalOrientation('ltr').withWrap(true).setFocusOrigin({});
        this.keyManager.updateActiveItem(0);
    }
    onChange(e) {
        if (e instanceof MatRadioChange) {
            const isFirst = typeof (this.question.response) === 'undefined';
            this.question.response = e.value;
            e.source._elementRef.nativeElement.parentElement.dispatchEvent(new QuestionChangeEvent({ firstChange: isFirst, question: this.question }));
        }
    }
}
QuestionBooleanComponent.ɵfac = function QuestionBooleanComponent_Factory(t) { return new (t || QuestionBooleanComponent)(i0.ɵɵdirectiveInject(QUESTION), i0.ɵɵdirectiveInject(i1$1.FocusMonitor), i0.ɵɵdirectiveInject(i0.ElementRef)); };
QuestionBooleanComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionBooleanComponent, selectors: [["question-boolean"]], viewQuery: function QuestionBooleanComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(MatRadioButton, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.options = _t);
    } }, hostBindings: function QuestionBooleanComponent_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("keydown", function QuestionBooleanComponent_keydown_HostBindingHandler($event) { return ctx.onKeyDown($event); });
    } }, inputs: { disabled: "disabled" }, decls: 9, vars: 12, consts: [[1, "question-title", 3, "questionTitle"], ["class", "question-error", 4, "ngIf"], [3, "value", "disabled", "change"], [3, "value"], [1, "question-error"]], template: function QuestionBooleanComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelement(0, "div", 0);
        i0.ɵɵtemplate(1, QuestionBooleanComponent_p_1_Template, 2, 1, "p", 1);
        i0.ɵɵelementStart(2, "mat-radio-group", 2);
        i0.ɵɵlistener("change", function QuestionBooleanComponent_Template_mat_radio_group_change_2_listener($event) { return ctx.onChange($event); });
        i0.ɵɵelementStart(3, "mat-radio-button", 3);
        i0.ɵɵtext(4);
        i0.ɵɵpipe(5, "booleanLabel");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(6, "mat-radio-button", 3);
        i0.ɵɵtext(7);
        i0.ɵɵpipe(8, "booleanLabel");
        i0.ɵɵelementEnd()();
    } if (rf & 2) {
        i0.ɵɵproperty("questionTitle", ctx.question);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.question.error && !ctx.disabled);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("value", ctx.question.response)("disabled", ctx.disabled);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("value", 1);
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(5, 8, 1));
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("value", 2);
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(8, 10, 2));
    } }, dependencies: [i1$2.NgIf, i3$2.MatRadioGroup, i3$2.MatRadioButton, QuestionTitleDirective, BooleanLabelPipe], styles: [".mat-radio-button[_ngcontent-%COMP%] ~ .mat-radio-button[_ngcontent-%COMP%]{margin-left:16px}.question-title[_ngcontent-%COMP%]{margin-bottom:.5rem}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionBooleanComponent, [{
        type: Component,
        args: [{ selector: 'question-boolean', template: "<div class=\"question-title\"\n\t [questionTitle]=\"question\"></div>\n<p *ngIf=\"question.error && ! disabled\"\n   class=\"question-error\">\n\t{{question.error}}\n</p>\n<mat-radio-group [value]=\"question.response\"\n\t\t\t\t (change)=\"onChange( $event )\"\n\t\t\t\t [disabled]=\"disabled\">\n\n\t<mat-radio-button [value]=\"1\">{{ 1 | booleanLabel }}</mat-radio-button>\n\t<mat-radio-button [value]=\"2\">{{ 2 | booleanLabel }}</mat-radio-button>\n\n</mat-radio-group>\n", styles: [".mat-radio-button~.mat-radio-button{margin-left:16px}.question-title{margin-bottom:.5rem}\n"] }]
    }], function () { return [{ type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }, { type: i1$1.FocusMonitor }, { type: i0.ElementRef }]; }, { onKeyDown: [{
            type: HostListener,
            args: ['keydown', ['$event']]
        }], disabled: [{
            type: Input
        }], options: [{
            type: ViewChildren,
            args: [MatRadioButton]
        }] }); })();

class BooleanQuestionModule {
}
BooleanQuestionModule.ɵfac = function BooleanQuestionModule_Factory(t) { return new (t || BooleanQuestionModule)(); };
BooleanQuestionModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: BooleanQuestionModule });
BooleanQuestionModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        SurveyMaterialModule,
        QuestionTitleModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BooleanQuestionModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    SurveyMaterialModule,
                    QuestionTitleModule
                ],
                declarations: [
                    QuestionBooleanComponent,
                    BooleanLabelPipe,
                    BooleanLabelDirective,
                ],
                exports: [
                    BooleanLabelPipe,
                    BooleanLabelDirective,
                    QuestionBooleanComponent,
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(BooleanQuestionModule, { declarations: [QuestionBooleanComponent,
        BooleanLabelPipe,
        BooleanLabelDirective], imports: [CommonModule,
        SurveyMaterialModule,
        QuestionTitleModule], exports: [BooleanLabelPipe,
        BooleanLabelDirective,
        QuestionBooleanComponent] }); })();

class QuestionEditorBooleanModule {
}
QuestionEditorBooleanModule.ɵfac = function QuestionEditorBooleanModule_Factory(t) { return new (t || QuestionEditorBooleanModule)(); };
QuestionEditorBooleanModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionEditorBooleanModule });
QuestionEditorBooleanModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        FormsModule,
        EditorMaterialModule,
        BooleanQuestionModule,
        EditorLayoutDirectivesModule,
        ScoreInputDirectiveModule,
        FuzzyModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorBooleanModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    EditorMaterialModule,
                    BooleanQuestionModule,
                    EditorLayoutDirectivesModule,
                    ScoreInputDirectiveModule,
                    FuzzyModule
                ],
                declarations: [
                    QuestionEditorBooleanComponent
                ],
                exports: [
                    QuestionEditorBooleanComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionEditorBooleanModule, { declarations: [QuestionEditorBooleanComponent], imports: [CommonModule,
        FormsModule,
        EditorMaterialModule,
        BooleanQuestionModule,
        EditorLayoutDirectivesModule,
        ScoreInputDirectiveModule,
        FuzzyModule], exports: [QuestionEditorBooleanComponent] }); })();

class QuestionEditorTextareaModule {
}
QuestionEditorTextareaModule.ɵfac = function QuestionEditorTextareaModule_Factory(t) { return new (t || QuestionEditorTextareaModule)(); };
QuestionEditorTextareaModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionEditorTextareaModule });
QuestionEditorTextareaModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({});
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorTextareaModule, [{
        type: NgModule,
        args: [{
                imports: [],
                declarations: [
                    QuestionEditorTextareaComponent
                ],
                exports: [
                    QuestionEditorTextareaComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionEditorTextareaModule, { declarations: [QuestionEditorTextareaComponent], exports: [QuestionEditorTextareaComponent] }); })();

class QuestionEditorModule {
}
QuestionEditorModule.ɵfac = function QuestionEditorModule_Factory(t) { return new (t || QuestionEditorModule)(); };
QuestionEditorModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionEditorModule });
QuestionEditorModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        FormsModule,
        EditorMaterialModule,
        QuestionEditorDateModule,
        QuestionEditorTextModule,
        QuestionEditorTextareaModule,
        QuestionEditorPicklistModule,
        QuestionEditorChecklistModule,
        QuestionEditorRadiosetModule,
        QuestionEditorScaleModule,
        QuestionEditorYNUModule,
        QuestionEditorNumberModule,
        QuestionEditorBooleanModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionEditorModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    EditorMaterialModule,
                    QuestionEditorDateModule,
                    QuestionEditorTextModule,
                    QuestionEditorTextareaModule,
                    QuestionEditorPicklistModule,
                    QuestionEditorChecklistModule,
                    QuestionEditorRadiosetModule,
                    QuestionEditorScaleModule,
                    QuestionEditorYNUModule,
                    QuestionEditorNumberModule,
                    QuestionEditorBooleanModule
                ],
                declarations: [
                    QuestionEditorComponent
                ],
                exports: [
                    QuestionEditorComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionEditorModule, { declarations: [QuestionEditorComponent], imports: [CommonModule,
        FormsModule,
        EditorMaterialModule,
        QuestionEditorDateModule,
        QuestionEditorTextModule,
        QuestionEditorTextareaModule,
        QuestionEditorPicklistModule,
        QuestionEditorChecklistModule,
        QuestionEditorRadiosetModule,
        QuestionEditorScaleModule,
        QuestionEditorYNUModule,
        QuestionEditorNumberModule,
        QuestionEditorBooleanModule], exports: [QuestionEditorComponent] }); })();

class VersionPromptModule {
}
VersionPromptModule.ɵfac = function VersionPromptModule_Factory(t) { return new (t || VersionPromptModule)(); };
VersionPromptModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: VersionPromptModule });
VersionPromptModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [EditorMaterialModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(VersionPromptModule, [{
        type: NgModule,
        args: [{
                imports: [
                    EditorMaterialModule
                ],
                declarations: [
                    VersionPromptDirective,
                    VersionPromptComponent,
                    VersionWarningComponent
                ],
                exports: [
                    VersionPromptDirective,
                    VersionPromptComponent,
                    VersionWarningComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(VersionPromptModule, { declarations: [VersionPromptDirective,
        VersionPromptComponent,
        VersionWarningComponent], imports: [EditorMaterialModule], exports: [VersionPromptDirective,
        VersionPromptComponent,
        VersionWarningComponent] }); })();

function RecommendationGroupComponent_recommendation_survey_item_9_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "recommendation-survey-item", 7);
} if (rf & 2) {
    const item_r1 = ctx.$implicit;
    const i_r2 = ctx.index;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("index", i_r2 + 1)("item", item_r1)("prefix", ctx_r0.prefix)("depth", ctx_r0.depth + 1);
} }
const _c0$6 = function () { return { updateOn: "submit" }; };
class RecommendationGroupComponent {
    constructor(cd, survey, editorConfig) {
        this.cd = cd;
        this.survey = survey;
        this.editorConfig = editorConfig;
        this.surveyChange = survey.events.subscribe(e => {
            this.cd.detectChanges();
        });
    }
    ngOnDestroy() {
        this.surveyChange.unsubscribe();
    }
    /**
     * on blur update the model and possibly clear the fuzzy marker
     */
    onIntroBlur() {
        const newValue = event.target.value;
        if (newValue !== this.group.meta.recommendationsIntro && this.group.fuzzy) {
            delete this.group.fuzzy.meta.recommendationsIntro;
            //	validateFuzzyObject( this.group );
        }
        this.group.meta.recommendationsIntro = newValue;
    }
}
RecommendationGroupComponent.ɵfac = function RecommendationGroupComponent_Factory(t) { return new (t || RecommendationGroupComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(Survey), i0.ɵɵdirectiveInject(SURVEY_EDITOR_CONFIG)); };
RecommendationGroupComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: RecommendationGroupComponent, selectors: [["recommendation-group"]], inputs: { group: "group", depth: "depth", prefix: "prefix" }, decls: 10, vars: 10, consts: [[1, "survey-item-top"], [1, "recommendation-intro"], [1, "label-text"], [1, "title-prefix", 3, "innerHTML"], ["type", "text", "placeholder", "This text will be displayed before all recommendations in this group", 1, "recommendation-intro-input", 3, "ngModel", "ngModelOptions", "readOnly", "cdkTextareaAutosize", "cdkAutosizeMinRows", "cdkAutosizeMaxRows", "ngModelChange", "blur"], [1, "survey-item-content"], [3, "index", "item", "prefix", "depth", 4, "ngFor", "ngForOf"], [3, "index", "item", "prefix", "depth"]], template: function RecommendationGroupComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0)(1, "div", 1)(2, "div", 2);
        i0.ɵɵelement(3, "span", 3);
        i0.ɵɵtext(4, ". Recommendation Introduction for ");
        i0.ɵɵelementStart(5, "i");
        i0.ɵɵtext(6);
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(7, "textarea", 4);
        i0.ɵɵlistener("ngModelChange", function RecommendationGroupComponent_Template_textarea_ngModelChange_7_listener($event) { return ctx.group.meta.recommendationsIntro = $event; })("blur", function RecommendationGroupComponent_Template_textarea_blur_7_listener() { return ctx.onIntroBlur(); });
        i0.ɵɵelementEnd()()();
        i0.ɵɵelementStart(8, "div", 5);
        i0.ɵɵtemplate(9, RecommendationGroupComponent_recommendation_survey_item_9_Template, 1, 4, "recommendation-survey-item", 6);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("innerHTML", ctx.prefix, i0.ɵɵsanitizeHtml);
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate(ctx.group.title);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngModel", ctx.group.meta.recommendationsIntro)("ngModelOptions", i0.ɵɵpureFunction0(9, _c0$6))("readOnly", ctx.editorConfig.permissions.readonly)("cdkTextareaAutosize", true)("cdkAutosizeMinRows", 1)("cdkAutosizeMaxRows", 5);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngForOf", ctx.group.items);
    } }, dependencies: [i1$2.NgForOf, i3.DefaultValueAccessor, i3.NgControlStatus, i3.NgModel, i7.CdkTextareaAutosize, RecommendationSurveyItemComponent], styles: [".recommendation-intro[_ngcontent-%COMP%]   .label-text[_ngcontent-%COMP%]{font-weight:700}.recommendation-intro-input[_ngcontent-%COMP%]{margin-top:8px;padding:8px 10px;width:calc(100% - 20px);box-sizing:content-box;box-shadow:inset 0 1px 2px #00000012;border-color:#ddd}", "[_nghost-%COMP%]{display:block}.guest-overview-group[_nghost-%COMP%]     .custom-order-input{display:none}.survey-item-content[_ngcontent-%COMP%]{padding:0 20px 20px}.survey-item-top[_ngcontent-%COMP%]{padding:20px 20px 0}.group-additional-field[_ngcontent-%COMP%]{margin-left:20px}.group-additional-field[_ngcontent-%COMP%]   mat-form-field[_ngcontent-%COMP%]{margin-top:5px}.group-additional-field[_ngcontent-%COMP%]   mat-form-field[_ngcontent-%COMP%]     .mat-mdc-form-field-subscript-wrapper{display:none}.conditional-title[_ngcontent-%COMP%]{margin-left:20px}mat-checkbox[_ngcontent-%COMP%] + mat-checkbox[_ngcontent-%COMP%], .survey-item-footer-index[_ngcontent-%COMP%] + mat-checkbox[_ngcontent-%COMP%]{margin-left:16px}", ".survey-item-footer[_ngcontent-%COMP%]{border-top:1px solid #ccc;display:flex;align-items:center;height:50px}.survey-item-footer-index[_ngcontent-%COMP%]{padding:0 10px;display:flex;align-items:center;justify-content:center;align-self:stretch;border-right:1px solid #ccc}.survey-item-footer[_ngcontent-%COMP%]   .survey-item-optional[_ngcontent-%COMP%]{margin-left:12px;margin-right:12px}.survey-item-footer[_ngcontent-%COMP%]   .align-right[_ngcontent-%COMP%]{margin-left:auto}.survey-item-footer[_ngcontent-%COMP%]   .align-right[_ngcontent-%COMP%]     button{margin-right:8px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RecommendationGroupComponent, [{
        type: Component,
        args: [{ selector: 'recommendation-group', template: "<div class=\"survey-item-top\">\n\t<div class=\"recommendation-intro\">\n\n\t\t<div class=\"label-text\">\n\t\t\t<span class=\"title-prefix\"\n\t\t\t\t  [innerHTML]=\"prefix\"></span>. Recommendation Introduction for <i>{{group.title}}</i>\n\t\t</div>\n\n\t\t<textarea type=\"text\"\n\t\t\t\t  class=\"recommendation-intro-input\"\n\t\t\t\t  placeholder=\"This text will be displayed before all recommendations in this group\"\n\t\t\t\t  [(ngModel)]=\"group.meta.recommendationsIntro\"\n\t\t\t\t  [ngModelOptions]=\"{updateOn:'submit'}\"\n\t\t\t\t  [readOnly]=\"editorConfig.permissions.readonly\"\n\t\t\t\t  [cdkTextareaAutosize]=\"true\"\n\t\t\t\t  [cdkAutosizeMinRows]=\"1\"\n\t\t\t\t  [cdkAutosizeMaxRows]=\"5\"\n\t\t\t\t  (blur)=\"onIntroBlur()\"></textarea>\n\n\n\n\t</div>\n\n</div>\n\n\n<div class=\"survey-item-content\">\n\t<recommendation-survey-item *ngFor=\"let item of group.items;index as i;\"\n\t\t\t\t\t\t\t\t[index]=\"i +1\"\n\t\t\t\t\t\t\t\t[item]=\"item\"\n\t\t\t\t\t\t\t\t[prefix]=\"prefix\"\n\t\t\t\t\t\t\t\t[depth]=\"depth+1\"></recommendation-survey-item>\n\n</div>\n", styles: [".recommendation-intro .label-text{font-weight:700}.recommendation-intro-input{margin-top:8px;padding:8px 10px;width:calc(100% - 20px);box-sizing:content-box;box-shadow:inset 0 1px 2px #00000012;border-color:#ddd}\n", ":host{display:block}:host.guest-overview-group ::ng-deep .custom-order-input{display:none}.survey-item-content{padding:0 20px 20px}.survey-item-top{padding:20px 20px 0}.group-additional-field{margin-left:20px}.group-additional-field mat-form-field{margin-top:5px}.group-additional-field mat-form-field ::ng-deep .mat-mdc-form-field-subscript-wrapper{display:none}.conditional-title{margin-left:20px}mat-checkbox+mat-checkbox,.survey-item-footer-index+mat-checkbox{margin-left:16px}\n", ".survey-item-footer{border-top:1px solid #ccc;display:flex;align-items:center;height:50px}.survey-item-footer-index{padding:0 10px;display:flex;align-items:center;justify-content:center;align-self:stretch;border-right:1px solid #ccc}.survey-item-footer .survey-item-optional{margin-left:12px;margin-right:12px}.survey-item-footer .align-right{margin-left:auto}.survey-item-footer .align-right ::ng-deep button{margin-right:8px}\n"] }]
    }], function () { return [{ type: i0.ChangeDetectorRef }, { type: Survey }, { type: SurveyEditorConfig, decorators: [{
                type: Inject,
                args: [SURVEY_EDITOR_CONFIG]
            }] }]; }, { group: [{
            type: Input
        }], depth: [{
            type: Input
        }], prefix: [{
            type: Input
        }] }); })();

class ManageRelatedContentModule {
}
ManageRelatedContentModule.ɵfac = function ManageRelatedContentModule_Factory(t) { return new (t || ManageRelatedContentModule)(); };
ManageRelatedContentModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: ManageRelatedContentModule });
ManageRelatedContentModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        HttpClientModule,
        ReactiveFormsModule,
        EditorMaterialModule,
        EditorPipesModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ManageRelatedContentModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    HttpClientModule,
                    ReactiveFormsModule,
                    EditorMaterialModule,
                    EditorPipesModule
                ],
                declarations: [
                    RelatedObjectRowComponent,
                    ManageRelatedContentComponent,
                    ContentSearchComponent
                ],
                exports: [
                    ManageRelatedContentComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(ManageRelatedContentModule, { declarations: [RelatedObjectRowComponent,
        ManageRelatedContentComponent,
        ContentSearchComponent], imports: [CommonModule,
        HttpClientModule,
        ReactiveFormsModule,
        EditorMaterialModule,
        EditorPipesModule], exports: [ManageRelatedContentComponent] }); })();

class RecommendationsModule {
}
RecommendationsModule.ɵfac = function RecommendationsModule_Factory(t) { return new (t || RecommendationsModule)(); };
RecommendationsModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: RecommendationsModule });
RecommendationsModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        FormsModule,
        EditorMaterialModule,
        ManageRelatedContentModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RecommendationsModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    EditorMaterialModule,
                    ManageRelatedContentModule
                ],
                declarations: [
                    RecommendationGroupComponent,
                    RecommendationSurveyItemComponent,
                    RecommendationsTableComponent,
                ],
                exports: [
                    RecommendationSurveyItemComponent,
                    RecommendationGroupComponent,
                    RecommendationsTableComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(RecommendationsModule, { declarations: [RecommendationGroupComponent,
        RecommendationSurveyItemComponent,
        RecommendationsTableComponent], imports: [CommonModule,
        FormsModule,
        EditorMaterialModule,
        ManageRelatedContentModule], exports: [RecommendationSurveyItemComponent,
        RecommendationGroupComponent,
        RecommendationsTableComponent] }); })();
i0.ɵɵsetComponentScope(RecommendationSurveyItemComponent, [i1$2.NgForOf, i1$2.NgIf, i2.MatButton, i6$1.MatIcon, RecommendationGroupComponent,
    RecommendationSurveyItemComponent,
    RecommendationsTableComponent], []);

class SurveyEditorModule {
}
SurveyEditorModule.ɵfac = function SurveyEditorModule_Factory(t) { return new (t || SurveyEditorModule)(); };
SurveyEditorModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: SurveyEditorModule });
SurveyEditorModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [
        VersionPromptService
    ], imports: [CommonModule,
        BrowserAnimationsModule,
        FormsModule,
        EditorMaterialModule,
        QuestionEditorModule,
        YesNoUncertainQuestionModule,
        BooleanQuestionModule,
        VersionPromptModule,
        RecommendationsModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurveyEditorModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    BrowserAnimationsModule,
                    FormsModule,
                    EditorMaterialModule,
                    QuestionEditorModule,
                    YesNoUncertainQuestionModule,
                    BooleanQuestionModule,
                    VersionPromptModule,
                    RecommendationsModule
                ],
                declarations: [
                    SurveyEditorComponent,
                    SurveyItemComponent,
                    SurveyGroupComponent,
                    ConditionalResponseComponent,
                    QuestionsTabComponent,
                    RecommendationsTabComponent
                ],
                exports: [
                    SurveyEditorComponent,
                    SurveyItemComponent,
                    SurveyGroupComponent,
                    ConditionalResponseComponent
                ],
                providers: [
                    VersionPromptService
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SurveyEditorModule, { declarations: [SurveyEditorComponent,
        SurveyItemComponent,
        SurveyGroupComponent,
        ConditionalResponseComponent,
        QuestionsTabComponent,
        RecommendationsTabComponent], imports: [CommonModule,
        BrowserAnimationsModule,
        FormsModule,
        EditorMaterialModule,
        QuestionEditorModule,
        YesNoUncertainQuestionModule,
        BooleanQuestionModule,
        VersionPromptModule,
        RecommendationsModule], exports: [SurveyEditorComponent,
        SurveyItemComponent,
        SurveyGroupComponent,
        ConditionalResponseComponent] }); })();
i0.ɵɵsetComponentScope(SurveyGroupComponent, [i1$2.NgForOf, i1$2.NgIf, i3.DefaultValueAccessor, i3.NgControlStatus, i3.NgModel, i2.MatButton, i4$1.MatFormField, i5.MatInput, i3$3.MatCheckbox, i6$2.CdkDropList, i6$2.CdkDrag, VersionPromptDirective, SurveyItemComponent], []);

class UserSurvey {
    get survey() {
        return this._survey;
    }
    set survey(v) {
        this._survey = v;
    }
    constructor(data = {}) {
        if (data.survey) {
            data.survey = new Survey(data.survey);
        }
        if (data.additional) {
            data.additional = new Survey(data.additional);
        }
        if (data.completed_on) {
            data.completed_on = parseISO(data.completed_on);
        }
        for (let prop in data) {
            this[prop] = data[prop];
        }
    }
    get pctComplete() {
        let requiredAnswered = 0;
        let optionalAnswered = 0;
        let totalRequired = 0;
        let totalOptional = 0;
        // determine what completion ints to increment for a question
        const count = (questionItem) => {
            const answered = typeof (questionItem.response) !== 'undefined';
            if (questionItem.optional) {
                totalOptional++;
                if (answered) {
                    optionalAnswered++;
                }
            }
            else {
                totalRequired++;
                if (answered) {
                    requiredAnswered++;
                }
            }
        };
        const questions = this.survey.getQuestions();
        for (let i = 0; i < questions.length; i++) {
            let question = questions[i];
            // don't process conditional questions directly, we'll look at the collection of conditional questions on an item instead
            if (question.conditional) {
                continue;
            }
            // increment count values
            count(question);
            // if no conditional questions, go to next
            if (!question.conditionalQuestions) {
                continue;
            }
            question.conditionalQuestions.items
                .filter(q => question.isConditionSatisfied(q))
                .forEach(q => count(q));
        }
        const totalQuestionsToAnswer = totalRequired + optionalAnswered;
        const totalQuestionsAnswered = requiredAnswered + optionalAnswered;
        //console.info( `Required: ${ totalRequired }, Optional: ${ totalOptional }` );
        //console.info( `Answered: ${ requiredAnswered }, Answered: ${ optionalAnswered }` );
        //console.info( `${ totalQuestionsAnswered }/${ totalQuestionsToAnswer }` );
        return totalQuestionsAnswered / totalQuestionsToAnswer;
    }
    getUserResponses() {
        // reference to all the groups in the survey
        const groups = this.survey.groups;
        // iterator to collect response from questions or groups
        const _getResponses = (item, responses, parentQuestion = undefined) => {
            if (item.type === SurveyItemType.QUESTION && typeof (item.response) !== 'undefined') {
                if (item.conditional && !(parentQuestion?.isConditionSatisfied(item))) {
                    return;
                }
                // get a reference to the group that contains this question
                const containingGroup = groups.find(g => g.items.includes(item));
                // the basic response we're going to add to the array
                const response = { id: item.id, response: item.response };
                // if the containing group has been configured to append additional data to each response, add it
                if (containingGroup?.meta.appendResponse) {
                    Object.assign(response, containingGroup.meta.appendResponse);
                }
                // push response onto the array
                responses.push(response);
                if (item.conditionalQuestions) {
                    _getResponses(item.conditionalQuestions, responses, item);
                }
            }
            else if (item.type === SurveyItemType.QUESTION_GROUP) {
                item.items.forEach(subItem => _getResponses(subItem, responses, parentQuestion));
            }
        };
        let responses = [];
        this.survey.items.forEach(item => _getResponses(item, responses));
        return responses;
    }
    resetUserResponses() {
        const _resetResponses = (item) => {
            if (item.type === SurveyItemType.QUESTION && typeof (item.response) !== 'undefined') {
                item.response = undefined;
                if (item.conditionalQuestions) {
                    _resetResponses(item.conditionalQuestions);
                }
            }
            else if (item.type === SurveyItemType.QUESTION_GROUP) {
                item.items.forEach(subItem => _resetResponses(subItem));
            }
        };
        this.survey.items.forEach(item => _resetResponses(item));
    }
    /**
     * Return object of stats for Matches, Mismatches, and Uncertain response
     */
    getStats() {
        const results = {};
        const scoreableQuestions = this.survey.getQuestions(true);
        scoreableQuestions.forEach(question => {
            const state = question.state;
            if (!results[state]) {
                results[state] = 0;
            }
            results[state]++;
        });
        const total = Object.values(results).reduce((partial_sum, a) => partial_sum + a, 0);
        return { results, total };
    }
}

class UserSurveyError {
    constructor(code, title, message) {
        this.code = code;
        this.title = title;
        this.message = message;
    }
}

function QuestionPicklistComponent_mat_option_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "mat-option", 2);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const option_r1 = ctx.$implicit;
    i0.ɵɵproperty("value", option_r1.id);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", option_r1.label, " ");
} }
class QuestionPicklistComponent {
    onKeyDown(e) {
        if (e.code === 'Tab') {
            this.onTabOut();
        }
    }
    constructor(question) {
        this.question = question;
        this.hasAnswered = typeof (question.response) !== 'undefined';
    }
    focus() {
        this.matSelect.focus();
    }
    onSelectFocus(e) {
        this.onFocusValue = this.matSelect.value;
    }
    onSelectBlur(e) {
        if (this.tabbingOut) {
            return;
        }
        if (this.onFocusValue !== this.matSelect.value) {
            this.dispatchChange();
        }
    }
    /**
     * When tabbing out of the component, dispatch change event if value different than on focus
     */
    onTabOut() {
        this.tabbingOut = true;
        if (!this.hasAnswered && this.question.response) {
            this.isFirstChange = true;
        }
        if (this.onFocusValue !== this.matSelect.value) {
            this.dispatchChange();
        }
        setTimeout(() => {
            this.tabbingOut = false;
        }, 5);
    }
    onOpenChange(opened) {
        /**
         * When closing the select, dispatch a change event if it's the first change or the value has changed
         */
        if (!opened) {
            if (!this.hasAnswered && this.question.response) {
                this.isFirstChange = true;
            }
            if (!this.hasAnswered || this.onOpenedValue !== this.matSelect.value) {
                this.dispatchChange();
            }
        }
        else {
            this.onOpenedValue = this.matSelect.value;
        }
    }
    dispatchChange() {
        const ev = new QuestionChangeEvent({ firstChange: this.isFirstChange, question: this.question });
        this.isFirstChange = false;
        this.hasAnswered = true;
        this.matSelect._elementRef.nativeElement.dispatchEvent(ev);
    }
}
QuestionPicklistComponent.ɵfac = function QuestionPicklistComponent_Factory(t) { return new (t || QuestionPicklistComponent)(i0.ɵɵdirectiveInject(QUESTION)); };
QuestionPicklistComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionPicklistComponent, selectors: [["question-picklist"]], viewQuery: function QuestionPicklistComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(MatSelect, 7);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.matSelect = _t.first);
    } }, hostBindings: function QuestionPicklistComponent_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("keydown", function QuestionPicklistComponent_keydown_HostBindingHandler($event) { return ctx.onKeyDown($event); });
    } }, inputs: { disabled: "disabled" }, decls: 6, vars: 6, consts: [[3, "disabled", "ngModel", "focus", "blur", "ngModelChange", "openedChange"], [3, "value", 4, "ngFor", "ngForOf"], [3, "value"]], template: function QuestionPicklistComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "mat-form-field")(1, "mat-label");
        i0.ɵɵtext(2);
        i0.ɵɵpipe(3, "questionTitle");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(4, "mat-select", 0);
        i0.ɵɵlistener("focus", function QuestionPicklistComponent_Template_mat_select_focus_4_listener($event) { return ctx.onSelectFocus($event); })("blur", function QuestionPicklistComponent_Template_mat_select_blur_4_listener($event) { return ctx.onSelectBlur($event); })("ngModelChange", function QuestionPicklistComponent_Template_mat_select_ngModelChange_4_listener($event) { return ctx.question.response = $event; })("openedChange", function QuestionPicklistComponent_Template_mat_select_openedChange_4_listener($event) { return ctx.onOpenChange($event); });
        i0.ɵɵtemplate(5, QuestionPicklistComponent_mat_option_5_Template, 2, 2, "mat-option", 1);
        i0.ɵɵelementEnd()();
    } if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(3, 4, ctx.question));
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("disabled", ctx.disabled)("ngModel", ctx.question.response);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngForOf", ctx.question.meta.listValues);
    } }, dependencies: [i1$2.NgForOf, i3.NgControlStatus, i3.NgModel, i4$1.MatFormField, i4$1.MatLabel, i4$2.MatSelect, i5$1.MatOption, QuestionTitlePipe], encapsulation: 2 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionPicklistComponent, [{
        type: Component,
        args: [{ selector: 'question-picklist', template: "<mat-form-field>\n\t<mat-label>{{question | questionTitle}}</mat-label>\n\t<mat-select [disabled]=\"disabled\"\n\t\t\t\t(focus)=\"onSelectFocus($event)\"\n\t\t\t\t(blur)=\"onSelectBlur($event)\"\n\t\t\t\t[(ngModel)]=\"question.response\"\n\t\t\t\t(openedChange)=\"onOpenChange($event)\">\n\t\t<mat-option *ngFor=\"let option of question.meta.listValues\"\n\t\t\t\t\t[value]=\"option.id\">\n\t\t\t{{option.label}}\n\t\t</mat-option>\n\t</mat-select>\n</mat-form-field>\n" }]
    }], function () { return [{ type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }]; }, { disabled: [{
            type: Input
        }], matSelect: [{
            type: ViewChild,
            args: [MatSelect, { static: true }]
        }], onKeyDown: [{
            type: HostListener,
            args: ['keydown', ['$event']]
        }] }); })();

function QuestionChecklistComponent_p_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p", 3);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ctx_r0.question.error, "\n");
} }
function QuestionChecklistComponent_mat_checkbox_2_Template(rf, ctx) { if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-checkbox", 4);
    i0.ɵɵlistener("change", function QuestionChecklistComponent_mat_checkbox_2_Template_mat_checkbox_change_0_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r3 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r3.onChange($event)); });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const option_r2 = ctx.$implicit;
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("value", option_r2.id)("checked", ctx_r1.isOptionChecked(option_r2.id))("disabled", ctx_r1.disabled);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", option_r2.label, "\n");
} }
class QuestionChecklistComponent {
    onKeyDown(e) {
        this.keyManager.onKeydown(e);
    }
    constructor(question, fm, el) {
        this.question = question;
        this.fm = fm;
        this.el = el;
        /**
         * We're allowing the default tab behaviour to move us through the form, however when shift-tabbing back into this component the focus gets messed up
         * This is a hacky way to set the focus index when re-entering the component
         */
        this.subscription = fm.monitor(el, true).subscribe(origin => {
            if (!origin) {
                this.focusedOut = true;
            }
            else if (origin && this.focusedOut) {
                this.focusedOut = false;
                setTimeout(() => {
                    const focused = el.nativeElement.querySelector('.cdk-focused');
                    if (focused) {
                        const curFocusIndex = Array.from(focused.parentElement.children).indexOf(focused);
                        this.keyManager.updateActiveItem(curFocusIndex);
                    }
                });
            }
        });
    }
    focus() {
        this.keyManager.setFirstItemActive();
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.fm.stopMonitoring(this.el);
    }
    ngAfterViewInit() {
        // @note the set focus origin seems to be buggy, without the call to setFocusOrigin there's an error where the origin is a string 'program' but is expected to be an object
        this.keyManager = new FocusKeyManager(this.options).withHorizontalOrientation('ltr').withWrap(true).setFocusOrigin({});
        this.keyManager.updateActiveItem(0);
    }
    isOptionChecked(id) {
        if (!Array.isArray(this.question.response)) {
            return false;
        }
        return this.question.response.includes(id);
    }
    onChange(e) {
        if (e instanceof MatCheckboxChange) {
            const isFirst = typeof (this.question.response) === 'undefined';
            const changedValue = e.source.value;
            if (!Array.isArray(this.question.response)) {
                this.question.response = [];
            }
            if (e.checked) {
                if (!this.question.response.includes(changedValue)) {
                    this.question.response = [...this.question.response, changedValue];
                }
            }
            else {
                this.question.response = this.question.response.filter(id => changedValue !== id);
            }
            e.source._elementRef.nativeElement.parentElement.dispatchEvent(new QuestionChangeEvent({ firstChange: isFirst, question: this.question }));
        }
    }
}
QuestionChecklistComponent.ɵfac = function QuestionChecklistComponent_Factory(t) { return new (t || QuestionChecklistComponent)(i0.ɵɵdirectiveInject(QUESTION), i0.ɵɵdirectiveInject(i1$1.FocusMonitor), i0.ɵɵdirectiveInject(i0.ElementRef)); };
QuestionChecklistComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionChecklistComponent, selectors: [["question-checklist"]], viewQuery: function QuestionChecklistComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(MatCheckbox, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.options = _t);
    } }, hostBindings: function QuestionChecklistComponent_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("keydown", function QuestionChecklistComponent_keydown_HostBindingHandler($event) { return ctx.onKeyDown($event); });
    } }, inputs: { disabled: "disabled" }, decls: 3, vars: 3, consts: [[1, "question-title", 3, "questionTitle"], ["class", "question-error", 4, "ngIf"], [3, "value", "checked", "disabled", "change", 4, "ngFor", "ngForOf"], [1, "question-error"], [3, "value", "checked", "disabled", "change"]], template: function QuestionChecklistComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelement(0, "div", 0);
        i0.ɵɵtemplate(1, QuestionChecklistComponent_p_1_Template, 2, 1, "p", 1);
        i0.ɵɵtemplate(2, QuestionChecklistComponent_mat_checkbox_2_Template, 2, 4, "mat-checkbox", 2);
    } if (rf & 2) {
        i0.ɵɵproperty("questionTitle", ctx.question);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.question.error && !ctx.disabled);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngForOf", ctx.question.meta.listValues);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i3$3.MatCheckbox, QuestionTitleDirective], styles: [".mat-checkbox[_ngcontent-%COMP%] ~ .mat-checkbox[_ngcontent-%COMP%]{margin-left:16px}.question-title[_ngcontent-%COMP%]{margin-bottom:12px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionChecklistComponent, [{
        type: Component,
        args: [{ selector: 'question-checklist', template: "<div class=\"question-title\"\n\t [questionTitle]=\"question\"></div>\n<p *ngIf=\"question.error && ! disabled\"\n   class=\"question-error\">\n\t{{question.error}}\n</p>\n<mat-checkbox *ngFor=\"let option of question.meta.listValues\"\n\t\t\t  [value]=\"option.id\"\n\t\t\t  (change)=\"onChange($event)\"\n\t\t\t  [checked]=\"isOptionChecked(option.id)\"\n\t\t\t  [disabled]=\"disabled\">\n\t{{option.label}}\n</mat-checkbox>\n", styles: [".mat-checkbox~.mat-checkbox{margin-left:16px}.question-title{margin-bottom:12px}\n"] }]
    }], function () { return [{ type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }, { type: i1$1.FocusMonitor }, { type: i0.ElementRef }]; }, { onKeyDown: [{
            type: HostListener,
            args: ['keydown', ['$event']]
        }], disabled: [{
            type: Input
        }], options: [{
            type: ViewChildren,
            args: [MatCheckbox]
        }] }); })();

function QuestionRadiosetComponent_p_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p", 4);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ctx_r0.question.error, "\n");
} }
function QuestionRadiosetComponent_mat_radio_button_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "mat-radio-button", 5);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const option_r2 = ctx.$implicit;
    i0.ɵɵproperty("value", option_r2.id);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(option_r2.label);
} }
class QuestionRadiosetComponent {
    onKeyDown(e) {
        this.keyManager.onKeydown(e);
    }
    constructor(question, fm, el) {
        this.question = question;
        this.fm = fm;
        this.el = el;
        /**
         * We're allowing the default tab behaviour to move us through the form, however when shift-tabbing back into this component the focus gets messed up
         * This is a hacky way to set the focus index when re-entering the component
         */
        this.subscription = fm.monitor(el, true).subscribe(origin => {
            if (!origin) {
                this.focusedOut = true;
            }
            else if (origin && this.focusedOut) {
                this.focusedOut = false;
                setTimeout(() => {
                    const focused = el.nativeElement.querySelector('.cdk-focused');
                    if (focused) {
                        const curFocusIndex = Array.from(focused.parentElement.children).indexOf(focused);
                        this.keyManager.updateActiveItem(curFocusIndex);
                    }
                });
            }
        });
    }
    focus() {
        this.keyManager.setFirstItemActive();
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.fm.stopMonitoring(this.el);
    }
    ngAfterViewInit() {
        // @note the set focus origin seems to be buggy, without the call to setFocusOrigin there's an error where the origin is a string 'program' but is expected to be an object
        this.keyManager = new FocusKeyManager(this.options).withHorizontalOrientation('ltr').withWrap(true).setFocusOrigin({});
        this.keyManager.updateActiveItem(0);
    }
    onChange(e) {
        if (e instanceof MatRadioChange) {
            const isFirst = typeof (this.question.response) === 'undefined';
            this.question.response = e.value;
            e.source._elementRef.nativeElement.parentElement.dispatchEvent(new QuestionChangeEvent({ firstChange: isFirst, question: this.question }));
        }
    }
}
QuestionRadiosetComponent.ɵfac = function QuestionRadiosetComponent_Factory(t) { return new (t || QuestionRadiosetComponent)(i0.ɵɵdirectiveInject(QUESTION), i0.ɵɵdirectiveInject(i1$1.FocusMonitor), i0.ɵɵdirectiveInject(i0.ElementRef)); };
QuestionRadiosetComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionRadiosetComponent, selectors: [["question-radioset"]], viewQuery: function QuestionRadiosetComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(MatRadioButton, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.options = _t);
    } }, hostBindings: function QuestionRadiosetComponent_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("keydown", function QuestionRadiosetComponent_keydown_HostBindingHandler($event) { return ctx.onKeyDown($event); });
    } }, inputs: { disabled: "disabled" }, decls: 4, vars: 5, consts: [[1, "question-title", 3, "questionTitle"], ["class", "question-error", 4, "ngIf"], [3, "value", "disabled", "change"], [3, "value", 4, "ngFor", "ngForOf"], [1, "question-error"], [3, "value"]], template: function QuestionRadiosetComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelement(0, "div", 0);
        i0.ɵɵtemplate(1, QuestionRadiosetComponent_p_1_Template, 2, 1, "p", 1);
        i0.ɵɵelementStart(2, "mat-radio-group", 2);
        i0.ɵɵlistener("change", function QuestionRadiosetComponent_Template_mat_radio_group_change_2_listener($event) { return ctx.onChange($event); });
        i0.ɵɵtemplate(3, QuestionRadiosetComponent_mat_radio_button_3_Template, 2, 2, "mat-radio-button", 3);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("questionTitle", ctx.question);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.question.error && !ctx.disabled);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("value", ctx.question.response)("disabled", ctx.disabled);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngForOf", ctx.question.meta.listValues);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i3$2.MatRadioGroup, i3$2.MatRadioButton, QuestionTitleDirective], styles: [".mat-radio-button[_ngcontent-%COMP%] ~ .mat-radio-button[_ngcontent-%COMP%]{margin-left:16px}.question-title[_ngcontent-%COMP%]{margin-bottom:12px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionRadiosetComponent, [{
        type: Component,
        args: [{ selector: 'question-radioset', template: "<div class=\"question-title\"\n\t [questionTitle]=\"question\"></div>\n<p *ngIf=\"question.error && ! disabled\"\n   class=\"question-error\">\n\t{{question.error}}\n</p>\n<mat-radio-group [value]=\"question.response\"\n\t\t\t\t (change)=\"onChange( $event )\"\n\t\t\t\t [disabled]=\"disabled\">\n\n\t<mat-radio-button *ngFor=\"let option of question.meta.listValues\"\n\t\t\t\t\t  [value]=\"option.id\">{{ option.label}}</mat-radio-button>\n\n</mat-radio-group>\n", styles: [".mat-radio-button~.mat-radio-button{margin-left:16px}.question-title{margin-bottom:12px}\n"] }]
    }], function () { return [{ type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }, { type: i1$1.FocusMonitor }, { type: i0.ElementRef }]; }, { onKeyDown: [{
            type: HostListener,
            args: ['keydown', ['$event']]
        }], disabled: [{
            type: Input
        }], options: [{
            type: ViewChildren,
            args: [MatRadioButton]
        }] }); })();

function QuestionDatePickerComponent_mat_form_field_0_mat_hint_8_Template(rf, ctx) { if (rf & 1) {
    const _r6 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-hint", 7)(1, "mat-checkbox", 8);
    i0.ɵɵlistener("change", function QuestionDatePickerComponent_mat_form_field_0_mat_hint_8_Template_mat_checkbox_change_1_listener($event) { i0.ɵɵrestoreView(_r6); const ctx_r5 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r5.onOptionalChange($event)); });
    i0.ɵɵelement(2, "span", 9);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("disabled", !ctx_r4.hasBeenEnabled && ctx_r4.question.response === undefined && !ctx_r4.survey.complete)("checked", ctx_r4.question.response === null);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("innerHTML", ctx_r4.question.meta.optionalText, i0.ɵɵsanitizeHtml);
} }
function QuestionDatePickerComponent_mat_form_field_0_Template(rf, ctx) { if (rf & 1) {
    const _r8 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-form-field")(1, "input", 2);
    i0.ɵɵlistener("change", function QuestionDatePickerComponent_mat_form_field_0_Template_input_change_1_listener($event) { i0.ɵɵrestoreView(_r8); const ctx_r7 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r7.onNativeChange($event)); })("dateChange", function QuestionDatePickerComponent_mat_form_field_0_Template_input_dateChange_1_listener($event) { i0.ɵɵrestoreView(_r8); const ctx_r9 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r9.onChange($event)); });
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(2, "mat-label");
    i0.ɵɵtext(3);
    i0.ɵɵpipe(4, "questionTitle");
    i0.ɵɵelementEnd();
    i0.ɵɵelement(5, "mat-datepicker-toggle", 3)(6, "mat-datepicker", 4, 5);
    i0.ɵɵtemplate(8, QuestionDatePickerComponent_mat_form_field_0_mat_hint_8_Template, 3, 3, "mat-hint", 6);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const _r3 = i0.ɵɵreference(7);
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("disabled", ctx_r0.disabled)("matDatepicker", _r3)("max", ctx_r0.maxDate)("min", ctx_r0.minDate)("value", ctx_r0.question.response);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(4, 10, ctx_r0.question));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("for", _r3);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("disabled", ctx_r0.disabled)("startAt", ctx_r0.maxDate);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ctx_r0.question.meta.optional && ctx_r0.question.meta.optionalText);
} }
function QuestionDatePickerComponent_ng_container_1_mat_hint_7_Template(rf, ctx) { if (rf & 1) {
    const _r13 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-hint", 7)(1, "mat-checkbox", 8);
    i0.ɵɵlistener("change", function QuestionDatePickerComponent_ng_container_1_mat_hint_7_Template_mat_checkbox_change_1_listener($event) { i0.ɵɵrestoreView(_r13); const ctx_r12 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r12.onOptionalChange($event)); });
    i0.ɵɵelement(2, "span", 9);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r11 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("disabled", !ctx_r11.hasBeenEnabled && ctx_r11.question.response === undefined && !ctx_r11.survey.complete)("checked", ctx_r11.question.response === null);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("innerHTML", ctx_r11.question.meta.optionalText, i0.ɵɵsanitizeHtml);
} }
function QuestionDatePickerComponent_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    const _r15 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelement(1, "div", 10);
    i0.ɵɵelementStart(2, "div", 11);
    i0.ɵɵelement(3, "mat-datepicker-toggle", 12);
    i0.ɵɵelementStart(4, "input", 13);
    i0.ɵɵlistener("change", function QuestionDatePickerComponent_ng_container_1_Template_input_change_4_listener($event) { i0.ɵɵrestoreView(_r15); const ctx_r14 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r14.onNativeChange($event)); })("dateChange", function QuestionDatePickerComponent_ng_container_1_Template_input_dateChange_4_listener($event) { i0.ɵɵrestoreView(_r15); const ctx_r16 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r16.onChange($event)); });
    i0.ɵɵelementEnd()();
    i0.ɵɵelement(5, "mat-datepicker", 4, 5);
    i0.ɵɵtemplate(7, QuestionDatePickerComponent_ng_container_1_mat_hint_7_Template, 3, 3, "mat-hint", 6);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const _r10 = i0.ɵɵreference(6);
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("questionTitle", ctx_r1.question);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("disableRipple", true)("for", _r10);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("disabled", ctx_r1.disabled)("matDatepicker", _r10)("max", ctx_r1.maxDate)("min", ctx_r1.minDate)("value", ctx_r1.question.response);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("disabled", ctx_r1.disabled)("startAt", ctx_r1.maxDate);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ctx_r1.question.meta.optional && ctx_r1.question.meta.optionalText);
} }
function QuestionDatePickerComponent_p_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p", 14);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ctx_r2.question.error, "\n");
} }
class QuestionDatePickerComponent {
    get disabled() {
        return this._disabled;
    }
    set disabled(val) {
        this._disabled = val;
        if (!val) {
            this._hasBeenEnabled = true;
        }
    }
    constructor(question, options, survey) {
        this.question = question;
        this.options = options;
        this.survey = survey;
        if (question.meta.minDate) {
            this.minDate = this.parseReadableDate(question.meta.minDate);
        }
        if (question.meta.maxDate) {
            this.maxDate = this.parseReadableDate(question.meta.maxDate);
        }
    }
    parseReadableDate(value) {
        const parts = this.getParsedParts(value);
        if (!parts) {
            return null;
        }
        const now = new Date();
        if (parts.span === 'today') {
            return now;
        }
        switch (parts.span) {
            case 'day': return addDays(now, parts.modifier);
            case 'week': return addWeeks(now, parts.modifier);
            case 'month': return addMonths(now, parts.modifier);
            case 'year': return addYears(now, parts.modifier);
        }
        return null;
    }
    getParsedParts(value) {
        if (value.trim() === 'today') {
            return {
                modifier: '',
                span: 'today'
            };
        }
        let parts = value.match(/([\d-+]+)\s?(day|days|week|weeks|month|months|year|years)/);
        if (!parts) {
            return null;
        }
        return {
            modifier: parseInt(parts[1]),
            span: parts[2].replace(/s\b/, '') // get rid of pluralization
        };
    }
    onNativeChange(e) {
        e.stopPropagation();
    }
    onChange(e) {
        const firstChange = !this.question.response;
        if (e.value) {
            this.question.response = e.value;
            // dispatch using the inputs parent element otherwise we get a callstack error
            e.targetElement.parentElement.dispatchEvent(new QuestionChangeEvent({ firstChange: firstChange, question: this.question }));
        }
    }
    get hasBeenEnabled() { return this._hasBeenEnabled; }
    onOptionalChange(e) {
        // for some reason a native change event bubbles into here... we don't want it
        if (!(e instanceof MatCheckboxChange)) {
            return;
        }
        this.disabled = e.checked;
        const firstChange = !this.question.response;
        if (e.checked) {
            this.question.response = null;
        }
        else {
            delete this.question.response;
        }
        e.source._inputElement.nativeElement.parentElement.dispatchEvent(new QuestionChangeEvent({ firstChange: firstChange, question: this.question }));
    }
}
QuestionDatePickerComponent.ɵfac = function QuestionDatePickerComponent_Factory(t) { return new (t || QuestionDatePickerComponent)(i0.ɵɵdirectiveInject(QUESTION), i0.ɵɵdirectiveInject(ALPHA_SURVEYS_OPTIONS), i0.ɵɵdirectiveInject(Survey)); };
QuestionDatePickerComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionDatePickerComponent, selectors: [["question-date-picker"]], decls: 3, vars: 3, consts: [[4, "ngIf"], ["class", "question-error", 4, "ngIf"], ["matInput", "", "autocomplete", "off", 3, "disabled", "matDatepicker", "max", "min", "value", "change", "dateChange"], ["matSuffix", "", 3, "for"], [3, "disabled", "startAt"], ["picker", ""], ["align", "start", 4, "ngIf"], ["align", "start"], [3, "disabled", "checked", "change"], [3, "innerHTML"], [1, "question-title", 3, "questionTitle"], [1, "datepicker-wrapper"], ["matPrefix", "", 3, "disableRipple", "for"], ["matInput", "", "readonly", "", "autocomplete", "off", 3, "disabled", "matDatepicker", "max", "min", "value", "change", "dateChange"], [1, "question-error"]], template: function QuestionDatePickerComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, QuestionDatePickerComponent_mat_form_field_0_Template, 9, 12, "mat-form-field", 0);
        i0.ɵɵtemplate(1, QuestionDatePickerComponent_ng_container_1_Template, 8, 11, "ng-container", 0);
        i0.ɵɵtemplate(2, QuestionDatePickerComponent_p_2_Template, 2, 1, "p", 1);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.options.theme !== "MyAlpha");
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.options.theme === "MyAlpha");
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.question.error && !ctx.disabled);
    } }, dependencies: [i1$2.NgIf, i3$3.MatCheckbox, i4$1.MatFormField, i4$1.MatLabel, i4$1.MatHint, i4$1.MatPrefix, i4$1.MatSuffix, i5.MatInput, i6$3.MatDatepicker, i6$3.MatDatepickerInput, i6$3.MatDatepickerToggle, QuestionTitleDirective, QuestionTitlePipe], styles: [".question-title[_ngcontent-%COMP%]{margin-bottom:12px}.datepicker-wrapper[_ngcontent-%COMP%]   .mat-datepicker-toggle[_ngcontent-%COMP%]{position:relative;z-index:10}.datepicker-wrapper[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{position:relative;left:-40px;padding-left:40px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionDatePickerComponent, [{
        type: Component,
        args: [{ selector: 'question-date-picker', template: "<mat-form-field *ngIf=\"options.theme !== 'MyAlpha'\">\n\t<input matInput\n\t\t   [disabled]=\"disabled\"\n\t\t   [matDatepicker]=\"picker\"\n\t\t   [max]=\"maxDate\"\n\t\t   [min]=\"minDate\"\n\t\t   [value]=\"question.response\"\n\t\t   (change)=\"onNativeChange($event)\"\n\t\t   (dateChange)=\"onChange($event)\"\n\t\t   autocomplete=\"off\">\n\n\t<mat-label>{{ question | questionTitle }}</mat-label>\n\t<mat-datepicker-toggle matSuffix\n\t\t\t\t\t\t   [for]=\"picker\"></mat-datepicker-toggle>\n\t<mat-datepicker #picker\n\t\t\t\t\t[disabled]=\"disabled\"\n\t\t\t\t\t[startAt]=\"maxDate\"></mat-datepicker>\n\n\t<mat-hint *ngIf=\"question.meta.optional && question.meta.optionalText\"\n\t\t\t  align=\"start\">\n\n\t\t<mat-checkbox (change)=\"onOptionalChange($event)\"\n\t\t\t\t\t  [disabled]=\"! hasBeenEnabled && question.response === undefined && ! survey.complete\"\n\t\t\t\t\t  [checked]=\"question.response === null\">\n\n\t\t\t<span [innerHTML]=\"question.meta.optionalText\"></span>\n\t\t</mat-checkbox>\n\n\t</mat-hint>\n\n</mat-form-field>\n\n\n<ng-container *ngIf=\"options.theme === 'MyAlpha'\">\n\t<div class=\"question-title\"\n\t\t [questionTitle]=\"question\">\n\t</div>\n\n\t<div class=\"datepicker-wrapper\">\n\t\t<mat-datepicker-toggle matPrefix\n\t\t\t\t\t\t\t   [disableRipple]=\"true\"\n\t\t\t\t\t\t\t   [for]=\"picker\"></mat-datepicker-toggle>\n\n\t\t<input matInput\n\t\t\t   readonly\n\t\t\t   [disabled]=\"disabled\"\n\t\t\t   [matDatepicker]=\"picker\"\n\t\t\t   [max]=\"maxDate\"\n\t\t\t   [min]=\"minDate\"\n\t\t\t   [value]=\"question.response\"\n\t\t\t   (change)=\"onNativeChange($event)\"\n\t\t\t   (dateChange)=\"onChange($event)\"\n\t\t\t   autocomplete=\"off\">\n\t</div>\n\t<mat-datepicker #picker\n\t\t\t\t\t[disabled]=\"disabled\"\n\t\t\t\t\t[startAt]=\"maxDate\"></mat-datepicker>\n\n\t<mat-hint *ngIf=\"question.meta.optional && question.meta.optionalText\"\n\t\t\t  align=\"start\">\n\n\t\t<mat-checkbox (change)=\"onOptionalChange($event)\"\n\t\t\t\t\t  [disabled]=\"! hasBeenEnabled && question.response === undefined && ! survey.complete\"\n\t\t\t\t\t  [checked]=\"question.response === null\">\n\n\t\t\t<span [innerHTML]=\"question.meta.optionalText\"></span>\n\t\t</mat-checkbox>\n\n\t</mat-hint>\n\n</ng-container>\n\n<p *ngIf=\"question.error && ! disabled\"\n   class=\"question-error\">\n\t{{question.error}}\n</p>\n", styles: [".question-title{margin-bottom:12px}.datepicker-wrapper .mat-datepicker-toggle{position:relative;z-index:10}.datepicker-wrapper input{position:relative;left:-40px;padding-left:40px}\n"] }]
    }], function () { return [{ type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [ALPHA_SURVEYS_OPTIONS]
            }] }, { type: Survey }]; }, null); })();

function QuestionTextComponent_mat_form_field_0_p_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p", 5);
    i0.ɵɵi18n(1, 6);
    i0.ɵɵpipe(2, "number");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(2);
    i0.ɵɵi18nExp(i0.ɵɵpipeBind1(2, 1, ctx_r4.maxChars));
    i0.ɵɵi18nApply(1);
} }
function QuestionTextComponent_mat_form_field_0_Template(rf, ctx) { if (rf & 1) {
    const _r6 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-form-field")(1, "input", 2, 3);
    i0.ɵɵlistener("change", function QuestionTextComponent_mat_form_field_0_Template_input_change_1_listener($event) { i0.ɵɵrestoreView(_r6); const ctx_r5 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r5.onChange($event)); })("ngModelChange", function QuestionTextComponent_mat_form_field_0_Template_input_ngModelChange_1_listener($event) { i0.ɵɵrestoreView(_r6); const ctx_r7 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r7.question.response = $event); });
    i0.ɵɵpipe(3, "questionTitle");
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(4, QuestionTextComponent_mat_form_field_0_p_4_Template, 3, 3, "p", 4);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const _r3 = i0.ɵɵreference(2);
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("maxlength", ctx_r0.maxChars)("disabled", ctx_r0.disabled)("placeholder", i0.ɵɵpipeBind1(3, 5, ctx_r0.question))("ngModel", ctx_r0.question.response);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngIf", _r3.value.length > ctx_r0.maxChars - 10);
} }
function QuestionTextComponent_ng_container_1_p_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p", 5);
    i0.ɵɵi18n(1, 9);
    i0.ɵɵpipe(2, "number");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r9 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(2);
    i0.ɵɵi18nExp(i0.ɵɵpipeBind1(2, 1, ctx_r9.maxChars));
    i0.ɵɵi18nApply(1);
} }
function QuestionTextComponent_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    const _r11 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelement(1, "div", 7);
    i0.ɵɵelementStart(2, "input", 8, 3);
    i0.ɵɵlistener("change", function QuestionTextComponent_ng_container_1_Template_input_change_2_listener($event) { i0.ɵɵrestoreView(_r11); const ctx_r10 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r10.onChange($event)); })("ngModelChange", function QuestionTextComponent_ng_container_1_Template_input_ngModelChange_2_listener($event) { i0.ɵɵrestoreView(_r11); const ctx_r12 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r12.question.response = $event); });
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(4, QuestionTextComponent_ng_container_1_p_4_Template, 3, 3, "p", 4);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const _r8 = i0.ɵɵreference(3);
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("questionTitle", ctx_r1.question);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("maxlength", ctx_r1.maxChars)("disabled", ctx_r1.disabled)("ngModel", ctx_r1.question.response);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", _r8.value.length > ctx_r1.maxChars - 10);
} }
function QuestionTextComponent_p_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p", 10);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ctx_r2.question.error, "\n");
} }
class QuestionTextComponent {
    constructor(question, options) {
        this.question = question;
        this.options = options;
        this.maxChars = 255;
        this.isFirstChange = true;
    }
    onChange(event) {
        this.question.response = this.question.response.trim();
        event.isFirstChange = this.isFirstChange;
        event.question = this.question;
        this.isFirstChange = false;
    }
}
QuestionTextComponent.ɵfac = function QuestionTextComponent_Factory(t) { return new (t || QuestionTextComponent)(i0.ɵɵdirectiveInject(QUESTION), i0.ɵɵdirectiveInject(ALPHA_SURVEYS_OPTIONS)); };
QuestionTextComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionTextComponent, selectors: [["question-text"]], inputs: { disabled: "disabled" }, decls: 3, vars: 3, consts: function () { let i18n_0; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Text input character limit
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_textInputCharLimit$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_QUESTIONS_TEXT_QUESTION_TEXT_COMPONENT_TS___1 = goog.getMsg("* Maximum {$num} characters", { "num": "\uFFFD0\uFFFD" }, { original_code: { "num": "{{maxChars | number // i18n: (ph=\"num\")}}" } });
        i18n_0 = MSG_EXTERNAL_textInputCharLimit$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_QUESTIONS_TEXT_QUESTION_TEXT_COMPONENT_TS___1;
    }
    else {
        i18n_0 = $localize `:alpha.surveys|Text input character limit@@textInputCharLimit␟67677763e994dac103492b02ca2a4c7c8ed0e984␟1433510879205213700:* Maximum ${"\uFFFD0\uFFFD"}:NUM: characters`;
    } let i18n_2; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Text input character limit
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_textInputCharLimit$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_QUESTIONS_TEXT_QUESTION_TEXT_COMPONENT_TS___3 = goog.getMsg("* Maximum {$num} characters", { "num": "\uFFFD0\uFFFD" }, { original_code: { "num": "{{maxChars | number // i18n: (ph=\"num\")}}" } });
        i18n_2 = MSG_EXTERNAL_textInputCharLimit$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_QUESTIONS_TEXT_QUESTION_TEXT_COMPONENT_TS___3;
    }
    else {
        i18n_2 = $localize `:alpha.surveys|Text input character limit@@textInputCharLimit␟67677763e994dac103492b02ca2a4c7c8ed0e984␟1433510879205213700:* Maximum ${"\uFFFD0\uFFFD"}:NUM: characters`;
    } return [[4, "ngIf"], ["class", "question-error", 4, "ngIf"], ["matInput", "", 3, "maxlength", "disabled", "placeholder", "ngModel", "change", "ngModelChange"], ["qTextInput", ""], ["class", "mya-caption", 4, "ngIf"], [1, "mya-caption"], i18n_0, [1, "question-title", 3, "questionTitle"], [3, "maxlength", "disabled", "ngModel", "change", "ngModelChange"], i18n_2, [1, "question-error"]]; }, template: function QuestionTextComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, QuestionTextComponent_mat_form_field_0_Template, 5, 7, "mat-form-field", 0);
        i0.ɵɵtemplate(1, QuestionTextComponent_ng_container_1_Template, 5, 5, "ng-container", 0);
        i0.ɵɵtemplate(2, QuestionTextComponent_p_2_Template, 2, 1, "p", 1);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.options.theme !== "MyAlpha");
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.options.theme === "MyAlpha");
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.question.error && !ctx.disabled);
    } }, dependencies: [i1$2.NgIf, i3.DefaultValueAccessor, i3.NgControlStatus, i3.MaxLengthValidator, i3.NgModel, QuestionTitleDirective, i4$1.MatFormField, i5.MatInput, i1$2.DecimalPipe, QuestionTitlePipe], styles: [".question-title[_ngcontent-%COMP%]{margin-bottom:12px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionTextComponent, [{
        type: Component,
        args: [{ selector: 'question-text', template: "<mat-form-field *ngIf=\"options.theme !== 'MyAlpha'\">\n\t<input #qTextInput\n\t\t   matInput\n\t\t   [maxlength]=\"maxChars\"\n\t\t   [disabled]=\"disabled\"\n\t\t   [placeholder]=\"question | questionTitle\"\n\t\t   (change)=\"onChange($event)\"\n\t\t   [(ngModel)]=\"question.response\" />\n\t<p class=\"mya-caption\"\n\t   *ngIf=\"qTextInput.value.length > ( maxChars - 10 )\"\n\t   i18n=\"alpha.surveys|Text input character limit@@textInputCharLimit\">* Maximum {{maxChars | number // i18n: (ph=\"num\")}} characters</p>\n</mat-form-field>\n\n<ng-container *ngIf=\"options.theme === 'MyAlpha'\">\n\t<div class=\"question-title\"\n\t\t [questionTitle]=\"question\"></div>\n\t<input #qTextInput\n\t\t   [maxlength]=\"maxChars\"\n\t\t   [disabled]=\"disabled\"\n\t\t   (change)=\"onChange($event)\"\n\t\t   [(ngModel)]=\"question.response\" />\n\t<p class=\"mya-caption\"\n\t   *ngIf=\"qTextInput.value.length > ( maxChars - 10 )\"\n\t   i18n=\"alpha.surveys|Text input character limit@@textInputCharLimit\">* Maximum {{maxChars | number // i18n: (ph=\"num\")}} characters</p>\n</ng-container>\n\n<p *ngIf=\"question.error && ! disabled\"\n   class=\"question-error\">\n\t{{question.error}}\n</p>\n", styles: [".question-title{margin-bottom:12px}\n"] }]
    }], function () { return [{ type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [ALPHA_SURVEYS_OPTIONS]
            }] }]; }, { disabled: [{
            type: Input
        }] }); })();

class QuestionScaleComponent {
    constructor(question) {
        this.question = question;
    }
}
QuestionScaleComponent.ɵfac = function QuestionScaleComponent_Factory(t) { return new (t || QuestionScaleComponent)(i0.ɵɵdirectiveInject(QUESTION)); };
QuestionScaleComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionScaleComponent, selectors: [["question-text"]], decls: 1, vars: 0, template: function QuestionScaleComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtext(0, "scale component todo\n");
    } }, encapsulation: 2 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionScaleComponent, [{
        type: Component,
        args: [{ selector: 'question-text', template: "scale component todo\n" }]
    }], function () { return [{ type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }]; }, null); })();

const _c0$5 = ["input"];
function QuestionNumberComponent_mat_form_field_0_mat_hint_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "mat-hint", 6)(1, "strong");
    i0.ɵɵi18n(2, 7);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(2);
    i0.ɵɵi18nExp(ctx_r4.question.meta.minValue);
    i0.ɵɵi18nApply(2);
} }
function QuestionNumberComponent_mat_form_field_0_mat_hint_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "mat-hint", 8)(1, "strong");
    i0.ɵɵi18n(2, 9);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r5 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(2);
    i0.ɵɵi18nExp(ctx_r5.question.meta.maxValue);
    i0.ɵɵi18nApply(2);
} }
function QuestionNumberComponent_mat_form_field_0_Template(rf, ctx) { if (rf & 1) {
    const _r7 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-form-field")(1, "input", 2, 3);
    i0.ɵɵlistener("blur", function QuestionNumberComponent_mat_form_field_0_Template_input_blur_1_listener($event) { i0.ɵɵrestoreView(_r7); const ctx_r6 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r6.onBlur($event)); })("focus", function QuestionNumberComponent_mat_form_field_0_Template_input_focus_1_listener($event) { i0.ɵɵrestoreView(_r7); const ctx_r8 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r8.onFocus($event)); })("keyup", function QuestionNumberComponent_mat_form_field_0_Template_input_keyup_1_listener($event) { i0.ɵɵrestoreView(_r7); const ctx_r9 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r9.onKeyUp($event)); })("keydown", function QuestionNumberComponent_mat_form_field_0_Template_input_keydown_1_listener($event) { i0.ɵɵrestoreView(_r7); const ctx_r10 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r10.onKeyDown($event)); })("change", function QuestionNumberComponent_mat_form_field_0_Template_input_change_1_listener($event) { i0.ɵɵrestoreView(_r7); const ctx_r11 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r11.onChange($event)); });
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "mat-label");
    i0.ɵɵtext(4);
    i0.ɵɵpipe(5, "questionTitle");
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(6, QuestionNumberComponent_mat_form_field_0_mat_hint_6_Template, 3, 1, "mat-hint", 4);
    i0.ɵɵtemplate(7, QuestionNumberComponent_mat_form_field_0_mat_hint_7_Template, 3, 1, "mat-hint", 5);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("disabled", ctx_r0.disabled)("min", ctx_r0.question.meta.minValue)("max", ctx_r0.question.meta.maxValue)("value", ctx_r0.question.response);
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(5, 7, ctx_r0.question));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ctx_r0.question.meta.minValue !== undefined);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r0.question.meta.maxValue !== undefined);
} }
function QuestionNumberComponent_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    const _r14 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelement(1, "div", 10);
    i0.ɵɵelementStart(2, "input", 11, 3);
    i0.ɵɵlistener("blur", function QuestionNumberComponent_ng_container_1_Template_input_blur_2_listener($event) { i0.ɵɵrestoreView(_r14); const ctx_r13 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r13.onBlur($event)); })("focus", function QuestionNumberComponent_ng_container_1_Template_input_focus_2_listener($event) { i0.ɵɵrestoreView(_r14); const ctx_r15 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r15.onFocus($event)); })("keyup", function QuestionNumberComponent_ng_container_1_Template_input_keyup_2_listener($event) { i0.ɵɵrestoreView(_r14); const ctx_r16 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r16.onKeyUp($event)); })("keydown", function QuestionNumberComponent_ng_container_1_Template_input_keydown_2_listener($event) { i0.ɵɵrestoreView(_r14); const ctx_r17 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r17.onKeyDown($event)); })("change", function QuestionNumberComponent_ng_container_1_Template_input_change_2_listener($event) { i0.ɵɵrestoreView(_r14); const ctx_r18 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r18.onChange($event)); });
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("questionTitle", ctx_r1.question);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("disabled", ctx_r1.disabled)("min", ctx_r1.question.meta.minValue)("max", ctx_r1.question.meta.maxValue)("value", ctx_r1.question.response);
} }
function QuestionNumberComponent_p_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p", 12);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ctx_r2.question.error, "\n");
} }
class QuestionNumberComponent {
    constructor(question, options) {
        this.question = question;
        this.options = options;
        this.isFirstChange = typeof (question.response) === 'undefined';
    }
    /**
     * Get the current value in the text input
     */
    get currentText() {
        return this.textInput.nativeElement.value;
    }
    /**
     * Listener for mouse down, set flag
     */
    onMouse(event) {
        this.isMouseDown = true;
    }
    /**
     * When the input loses focus, set the value on the question and dispatch a question change event
     */
    onBlur(e) {
        const newValue = this.currentText ? parseInt(this.currentText) : '';
        if (typeof (this.question.meta.maxValue) !== 'undefined' && newValue > this.question.meta.maxValue) {
            e.preventDefault();
            this.textInput.nativeElement.focus();
            return;
        }
        if (typeof (this.question.meta.minValue) !== 'undefined' && newValue < this.question.meta.minValue) {
            e.preventDefault();
            this.textInput.nativeElement.focus();
            return;
        }
        if (newValue !== this.onFocusValue) {
            this.question.response = newValue;
            // dispatch the event from the question component
            this.textInput.nativeElement.parentElement.dispatchEvent(new QuestionChangeEvent({ firstChange: this.isFirstChange, question: this.question }));
            this.isFirstChange = false;
        }
    }
    /**
     * On focus capture the current value of the question
     */
    onFocus(e) {
        this.onFocusValue = this.currentText ? parseInt(this.currentText) : '';
    }
    /**
     * Stop the default change event from bubbling up ( will trigger on arrows control, we don't want to save until they're done )
     */
    onChange(event) {
        /**
         * stop a change event from firing if the arrow key or the mouse is down
         * When the mouse is down the user might be using the native arrow controls to increment the value. This triggers a change event that we don't want
         * When the arrow keys are down the user is incrementing the value which triggers a change event that we don't want
         */
        if (this.preventChangeFlag || this.isMouseDown) {
            this.isMouseDown = false;
            event.stopPropagation();
        }
        // attach the question to the event
        event.question = this.question;
    }
    /**
     * When a key is released
     */
    onKeyUp(event) {
        // reset the prevent change flag
        this.preventChangeFlag = false;
    }
    /**
     * When a key is pressed
     */
    onKeyDown(event) {
        /**
         * certain keys being pressed should stop the change event from propagating
         * set the a flag to be read by the onChange handler
         */
        if (['ArrowUp', 'ArrowDown', 'Tab'].indexOf(event.key) >= 0) {
            this.preventChangeFlag = true;
            return;
        }
        // don't allow floats
        if (event.key === '.') {
            event.preventDefault();
            return;
        }
        const currentValue = this.textInput.nativeElement.value;
        // prevent entering values greater than the max
        const num = parseInt(event.key);
        if (typeof (this.question.meta.maxValue) !== 'undefined' && !isNaN(num) && this.question.meta.maxValue < parseInt((currentValue ?? '').toString() + num.toString())) {
            event.preventDefault();
        }
    }
}
QuestionNumberComponent.ɵfac = function QuestionNumberComponent_Factory(t) { return new (t || QuestionNumberComponent)(i0.ɵɵdirectiveInject(QUESTION), i0.ɵɵdirectiveInject(ALPHA_SURVEYS_OPTIONS)); };
QuestionNumberComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionNumberComponent, selectors: [["question-number"]], viewQuery: function QuestionNumberComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0$5, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.textInput = _t.first);
    } }, hostBindings: function QuestionNumberComponent_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("mousedown", function QuestionNumberComponent_mousedown_HostBindingHandler($event) { return ctx.onMouse($event); });
    } }, inputs: { disabled: "disabled" }, decls: 3, vars: 3, consts: function () { let i18n_1; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Min Amount
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_minAmount$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_QUESTIONS_NUMBER_NUMBER_COMPONENT_TS___2 = goog.getMsg("Min: {$value}", { "value": "\uFFFD0\uFFFD" }, { original_code: { "value": "{{question.meta.minValue// i18n: (ph=\"value\")\n\t\t\t  }}" } });
        i18n_1 = MSG_EXTERNAL_minAmount$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_QUESTIONS_NUMBER_NUMBER_COMPONENT_TS___2;
    }
    else {
        i18n_1 = $localize `:alpha.surveys|Min Amount@@minAmount␟fa4cb7fc9925f74c49e0aa6c42be24d10d13a3eb␟7483302102639004445:Min: ${"\uFFFD0\uFFFD"}:VALUE:`;
    } let i18n_3; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Max Amount
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_maxAmount$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_QUESTIONS_NUMBER_NUMBER_COMPONENT_TS___4 = goog.getMsg("Max: {$value}", { "value": "\uFFFD0\uFFFD" }, { original_code: { "value": "{{question.meta.maxValue// i18n: (ph=\"value\")\n\t\t\t  }}" } });
        i18n_3 = MSG_EXTERNAL_maxAmount$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_QUESTIONS_NUMBER_NUMBER_COMPONENT_TS___4;
    }
    else {
        i18n_3 = $localize `:alpha.surveys|Max Amount@@maxAmount␟bc2a5eba6373fba0fcf6ed5055c3800ff409751a␟6320457722426267290:Max: ${"\uFFFD0\uFFFD"}:VALUE:`;
    } return [[4, "ngIf"], ["class", "question-error", 4, "ngIf"], ["matInput", "", "type", "number", 3, "disabled", "min", "max", "value", "blur", "focus", "keyup", "keydown", "change"], ["input", ""], ["align", "start", 4, "ngIf"], ["align", "end", 4, "ngIf"], ["align", "start"], i18n_1, ["align", "end"], i18n_3, [1, "question-title", 3, "questionTitle"], ["type", "number", "pattern", "\\d*", 3, "disabled", "min", "max", "value", "blur", "focus", "keyup", "keydown", "change"], [1, "question-error"]]; }, template: function QuestionNumberComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, QuestionNumberComponent_mat_form_field_0_Template, 8, 9, "mat-form-field", 0);
        i0.ɵɵtemplate(1, QuestionNumberComponent_ng_container_1_Template, 4, 5, "ng-container", 0);
        i0.ɵɵtemplate(2, QuestionNumberComponent_p_2_Template, 2, 1, "p", 1);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.options.theme !== "MyAlpha");
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.options.theme === "MyAlpha");
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.question.error && !ctx.disabled);
    } }, dependencies: [i1$2.NgIf, i4$1.MatFormField, i4$1.MatLabel, i4$1.MatHint, i5.MatInput, QuestionTitleDirective, QuestionTitlePipe], styles: [".question-title[_ngcontent-%COMP%]{margin-bottom:12px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionNumberComponent, [{
        type: Component,
        args: [{ selector: 'question-number', template: "<mat-form-field *ngIf=\"options.theme !== 'MyAlpha'\">\n\t<input matInput\n\t\t   #input\n\t\t   [disabled]=\"disabled\"\n\t\t   [min]=\"question.meta.minValue\"\n\t\t   [max]=\"question.meta.maxValue\"\n\t\t   [value]=\"question.response\"\n\t\t   (blur)=\"onBlur($event)\"\n\t\t   (focus)=\"onFocus($event)\"\n\t\t   (keyup)=\"onKeyUp($event)\"\n\t\t   (keydown)=\"onKeyDown($event)\"\n\t\t   (change)=\"onChange($event)\"\n\t\t   type=\"number\" />\n\n\t<mat-label>{{ question | questionTitle }}</mat-label>\n\n\t<mat-hint *ngIf=\"question.meta.minValue !== undefined\"\n\t\t\t  align=\"start\"><strong i18n=\"alpha.surveys|Min Amount@@minAmount\">Min: {{question.meta.minValue// i18n: (ph=\"value\")\n\t\t\t  }}</strong> </mat-hint>\n\t<mat-hint *ngIf=\"question.meta.maxValue !== undefined\"\n\t\t\t  align=\"end\"><strong i18n=\"alpha.surveys|Max Amount@@maxAmount\">Max: {{question.meta.maxValue// i18n: (ph=\"value\")\n\t\t\t  }}</strong> </mat-hint>\n</mat-form-field>\n\n<ng-container *ngIf=\"options.theme === 'MyAlpha'\">\n\t<div class=\"question-title\"\n\t\t [questionTitle]=\"question\"></div>\n\t<input #input\n\t\t   type=\"number\"\n\t\t   pattern=\"\\d*\"\n\t\t   [disabled]=\"disabled\"\n\t\t   [min]=\"question.meta.minValue\"\n\t\t   [max]=\"question.meta.maxValue\"\n\t\t   (blur)=\"onBlur($event)\"\n\t\t   (focus)=\"onFocus($event)\"\n\t\t   (keyup)=\"onKeyUp($event)\"\n\t\t   (keydown)=\"onKeyDown($event)\"\n\t\t   (change)=\"onChange($event)\"\n\t\t   [value]=\"question.response\" />\n</ng-container>\n\n\n<p *ngIf=\"question.error && ! disabled\"\n   class=\"question-error\">\n\t{{question.error}}\n</p>\n", styles: [".question-title{margin-bottom:12px}\n"] }]
    }], function () { return [{ type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [ALPHA_SURVEYS_OPTIONS]
            }] }]; }, { disabled: [{
            type: Input
        }], textInput: [{
            type: ViewChild,
            args: ['input', { static: false }]
        }], onMouse: [{
            type: HostListener,
            args: ['mousedown', ['$event']]
        }] }); })();

function QuestionTextareaComponent_p_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p", 5);
    i0.ɵɵi18n(1, 6);
    i0.ɵɵpipe(2, "number");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵi18nExp(i0.ɵɵpipeBind1(2, 1, ctx_r1.maxChars));
    i0.ɵɵi18nApply(1);
} }
function QuestionTextareaComponent_p_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p", 7);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ctx_r2.question.error, "\n");
} }
class QuestionTextareaComponent {
    constructor(question) {
        this.question = question;
        this.maxChars = 2000;
        this.isFirstChange = true;
    }
    onChange(event) {
        this.question.response = this.question.response.trim();
        event.isFirstChange = this.isFirstChange;
        event.question = this.question;
        this.isFirstChange = false;
    }
}
QuestionTextareaComponent.ɵfac = function QuestionTextareaComponent_Factory(t) { return new (t || QuestionTextareaComponent)(i0.ɵɵdirectiveInject(QUESTION)); };
QuestionTextareaComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionTextareaComponent, selectors: [["question-textarea"]], inputs: { disabled: "disabled" }, decls: 5, vars: 6, consts: function () { let i18n_0; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Textarea character limit
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_textAreaCharLimit$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_QUESTIONS_TEXTAREA_QUESTION_TEXTAREA_COMPONENT_TS__1 = goog.getMsg("* Maximum {$num} characters", { "num": "\uFFFD0\uFFFD" }, { original_code: { "num": "{{maxChars | number // i18n: (ph=\"num\")}}" } });
        i18n_0 = MSG_EXTERNAL_textAreaCharLimit$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_QUESTIONS_TEXTAREA_QUESTION_TEXTAREA_COMPONENT_TS__1;
    }
    else {
        i18n_0 = $localize `:alpha.surveys|Textarea character limit@@textAreaCharLimit␟67677763e994dac103492b02ca2a4c7c8ed0e984␟1433510879205213700:* Maximum ${"\uFFFD0\uFFFD"}:NUM: characters`;
    } return [[1, "question-title", 3, "questionTitle"], [3, "disabled", "ngModel", "maxlength", "change", "ngModelChange"], ["qTextArea", ""], ["class", "mya-caption", 4, "ngIf"], ["class", "question-error", 4, "ngIf"], [1, "mya-caption"], i18n_0, [1, "question-error"]]; }, template: function QuestionTextareaComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelement(0, "div", 0);
        i0.ɵɵelementStart(1, "textarea", 1, 2);
        i0.ɵɵlistener("change", function QuestionTextareaComponent_Template_textarea_change_1_listener($event) { return ctx.onChange($event); })("ngModelChange", function QuestionTextareaComponent_Template_textarea_ngModelChange_1_listener($event) { return ctx.question.response = $event; });
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(3, QuestionTextareaComponent_p_3_Template, 3, 3, "p", 3);
        i0.ɵɵtemplate(4, QuestionTextareaComponent_p_4_Template, 2, 1, "p", 4);
    } if (rf & 2) {
        const _r0 = i0.ɵɵreference(2);
        i0.ɵɵproperty("questionTitle", ctx.question);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("disabled", ctx.disabled)("ngModel", ctx.question.response)("maxlength", ctx.maxChars);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", _r0.value.length > ctx.maxChars - 10);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.question.error && !ctx.disabled);
    } }, dependencies: [i1$2.NgIf, i3.DefaultValueAccessor, i3.NgControlStatus, i3.MaxLengthValidator, i3.NgModel, QuestionTitleDirective, i1$2.DecimalPipe], styles: [".question-title[_ngcontent-%COMP%]{margin-bottom:12px}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionTextareaComponent, [{
        type: Component,
        args: [{ selector: 'question-textarea', template: "<div class=\"question-title\"\n\t [questionTitle]=\"question\"></div>\n<textarea #qTextArea\n\t\t  [disabled]=\"disabled\"\n\t\t  (change)=\"onChange($event)\"\n\t\t  [(ngModel)]=\"question.response\"\n\t\t  [maxlength]=\"maxChars\"></textarea>\n<p class=\"mya-caption\"\n   *ngIf=\"qTextArea.value.length > ( maxChars - 10 )\"\n   i18n=\"alpha.surveys|Textarea character limit@@textAreaCharLimit\">* Maximum {{maxChars | number // i18n: (ph=\"num\")}} characters</p>\n<p *ngIf=\"question.error && ! disabled\"\n   class=\"question-error\">\n\t{{question.error}}\n</p>\n", styles: [".question-title{margin-bottom:12px}\n"] }]
    }], function () { return [{ type: Question, decorators: [{
                type: Inject,
                args: [QUESTION]
            }] }]; }, { disabled: [{
            type: Input
        }] }); })();

const QuestionComponentMap = new Map([
    [QuestionType.YES_NO_UNCERTAIN, QuestionYNUComponent],
    [QuestionType.PICKLIST, QuestionPicklistComponent],
    [QuestionType.CHECKLIST, QuestionChecklistComponent],
    [QuestionType.RADIOSET, QuestionRadiosetComponent],
    [QuestionType.DATE, QuestionDatePickerComponent],
    [QuestionType.TEXT, QuestionTextComponent],
    [QuestionType.SCALE, QuestionScaleComponent],
    [QuestionType.NUMBER, QuestionNumberComponent],
    [QuestionType.BOOLEAN, QuestionBooleanComponent],
    [QuestionType.TEXTAREA, QuestionTextareaComponent]
]);

const WP_SITE_URL = new InjectionToken('WP_SITE_URL');
// sessionStorage key for our unique session id
const SESSION_ID_KEY = 'userSurveySessionId';
// timer
const SESSION_PING_TIME = 30000;
class SurveyService {
    constructor(http, wpSiteUrl) {
        this.http = http;
        this.usersEndPoint = wpSiteUrl + 'wp-json/alpha-surveys/v1/survey/user';
        this.online$ = merge(fromEvent(window, 'offline').pipe(map(() => false)), fromEvent(window, 'online').pipe(map(() => true)), new Observable((sub) => {
            sub.next(navigator.onLine);
            sub.complete();
        }));
    }
    /**
     * Start pinging the server against a user survey to determine if current session is valid
     */
    initializeSession(userSurvey) {
        return timer(SESSION_PING_TIME, SESSION_PING_TIME).pipe(switchMap(() => this.http.post(`${this.usersEndPoint}/${userSurvey.id}/ping`, {}, {
            headers: this.addSessionHeader()
        })), map(r => !r.expired), tap(valid => userSurvey.locked = !valid), retry());
    }
    /**
     * Get or initialize a session id
     */
    getSessionToken() {
        let token = null;
        if (null === (token = sessionStorage.getItem(SESSION_ID_KEY))) {
            token = v4();
            sessionStorage.setItem(SESSION_ID_KEY, token);
        }
        return token;
    }
    /**
     * Get a user survey by id or surveyLink
     */
    getUserSurvey(id, by = "token", params = {}) {
        const options = {
            params: Object.assign({ by }, params),
            headers: this.addSessionHeader()
        };
        return this.http.get(`${this.usersEndPoint}/${id}`, options).pipe(map(result => new UserSurvey(result)));
    }
    /**
     * Get a user survey populated with recommendations
     */
    getUserSurveyRecommendations(id, by = "token") {
        return this.getUserSurvey(id, by, { recommendations: true });
    }
    /**
     * Update the responses of a user survey
     */
    updateUserSurveyResponses(survey) {
        return this.http.put(`${this.usersEndPoint}/${survey.id}/responses`, survey.getUserResponses(), { headers: this.addSessionHeader() });
    }
    /**
     * Mark a user survey as complete
     */
    completeUserSurvey(survey) {
        return this.http.post(`${this.usersEndPoint}/${survey.id}/complete`, survey, { headers: this.addSessionHeader(), observe: 'response' }).pipe(tap(result => {
            survey.complete = true;
            survey.completed_on = new Date();
        }));
    }
    /**
     * Reset the responses for a user survey
     */
    resetUserSurvey(survey) {
        return this.http.post(`${this.usersEndPoint}/${survey.id}/reset`, survey, { headers: this.addSessionHeader() }).pipe(tap(result => {
            survey.resetUserResponses();
        }));
    }
    /**
     * Add the user survey session header to an object
     */
    addSessionHeader(headers = null) {
        return Object.assign(headers ?? {}, {
            'user-survey-session': this.getSessionToken()
        });
    }
}
SurveyService.ɵfac = function SurveyService_Factory(t) { return new (t || SurveyService)(i0.ɵɵinject(i1$4.HttpClient), i0.ɵɵinject(WP_SITE_URL)); };
SurveyService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: SurveyService, factory: SurveyService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurveyService, [{
        type: Injectable
    }], function () { return [{ type: i1$4.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [WP_SITE_URL]
            }] }]; }, null); })();

const _c0$4 = ["completedModal"];
const _c1$1 = ["unavailableModal"];
const _c2$1 = ["notFoundModal"];
const _c3 = ["surveyLockedModal"];
const _c4 = ["offlineModal"];
function SurveyComponent_div_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 7);
    i0.ɵɵnamespaceSVG();
    i0.ɵɵelementStart(1, "svg", 8);
    i0.ɵɵelement(2, "path", 9);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("matTooltip", ctx_r0.connectionUnavailableMessage);
} }
function SurveyComponent_div_1_ng_container_1_ng_container_1_h2_1_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const i_r14 = i0.ɵɵnextContext(3).index;
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1("", i_r14, ". ");
} }
function SurveyComponent_div_1_ng_container_1_ng_container_1_h2_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "h2");
    i0.ɵɵtemplate(1, SurveyComponent_div_1_ng_container_1_ng_container_1_h2_1_ng_container_1_Template, 2, 1, "ng-container", 12);
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const group_r13 = i0.ɵɵnextContext(2).$implicit;
    const ctx_r16 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r16.currentPage > 0 && ctx_r16.theme !== "MyAlpha");
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1("", group_r13.title, " ");
} }
function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_1_button_1_Template(rf, ctx) { if (rf & 1) {
    const _r27 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 17);
    i0.ɵɵlistener("click", function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_1_button_1_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r27); const ctx_r26 = i0.ɵɵnextContext(6); return i0.ɵɵresetView(ctx_r26.gotoPreviousPage()); });
    i0.ɵɵi18n(1, 18);
    i0.ɵɵelementEnd();
} }
function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_1_button_2_Template(rf, ctx) { if (rf & 1) {
    const _r30 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 19);
    i0.ɵɵlistener("click", function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_1_button_2_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r30); const group_r13 = i0.ɵɵnextContext(4).$implicit; const ctx_r28 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r28.gotoNextPage(group_r13)); });
    i0.ɵɵi18n(1, 20);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const group_r13 = i0.ɵɵnextContext(4).$implicit;
    const ctx_r24 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("disabled", ctx_r24.linear && !group_r13.complete && ctx_r24.errorStyle === "form-disable");
} }
function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_1_button_4_Template(rf, ctx) { if (rf & 1) {
    const _r33 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 21);
    i0.ɵɵlistener("click", function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_1_button_4_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r33); const ctx_r32 = i0.ɵɵnextContext(6); return i0.ɵɵresetView(ctx_r32.onCompleteClick()); });
    i0.ɵɵi18n(1, 22);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const group_r13 = i0.ɵɵnextContext(4).$implicit;
    const ctx_r25 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("disabled", ctx_r25.submitting || ctx_r25.linear && !group_r13.complete && ctx_r25.errorStyle === "form-disable");
} }
function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_1_button_1_Template, 2, 0, "button", 14);
    i0.ɵɵtemplate(2, SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_1_button_2_Template, 2, 1, "button", 15);
    i0.ɵɵpipe(3, "surveyGroups");
    i0.ɵɵtemplate(4, SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_1_button_4_Template, 2, 1, "button", 16);
    i0.ɵɵpipe(5, "surveyGroups");
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const i_r14 = i0.ɵɵnextContext(3).index;
    const ctx_r21 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", i_r14 > 0);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r21.currentPage < i0.ɵɵpipeBind1(3, 3, ctx_r21.survey.items).length - 1);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", !ctx_r21.surveyWasCompleted && ctx_r21.currentPage === i0.ɵɵpipeBind1(5, 5, ctx_r21.survey.items).length - 1 && (!ctx_r21.userSurvey || !ctx_r21.userSurvey.complete));
} }
function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_2_lib_mrb_buttons_2_Template(rf, ctx) { if (rf & 1) {
    const _r40 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "lib-mrb-buttons", 27);
    i0.ɵɵlistener("buttonClick", function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_2_lib_mrb_buttons_2_Template_lib_mrb_buttons_buttonClick_0_listener() { i0.ɵɵrestoreView(_r40); const ctx_r39 = i0.ɵɵnextContext(6); return i0.ɵɵresetView(ctx_r39.gotoPreviousPage()); });
    i0.ɵɵi18n(1, 28);
    i0.ɵɵelementEnd();
} }
function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_2_lib_mrb_buttons_3_Template(rf, ctx) { if (rf & 1) {
    const _r43 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "lib-mrb-buttons", 29);
    i0.ɵɵlistener("buttonClick", function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_2_lib_mrb_buttons_3_Template_lib_mrb_buttons_buttonClick_0_listener() { i0.ɵɵrestoreView(_r43); const group_r13 = i0.ɵɵnextContext(4).$implicit; const ctx_r41 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r41.gotoNextPage(group_r13)); });
    i0.ɵɵi18n(1, 30);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const group_r13 = i0.ɵɵnextContext(4).$implicit;
    const ctx_r37 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("disabled", ctx_r37.linear && !group_r13.complete && ctx_r37.errorStyle === "form-disable");
} }
function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_2_lib_mrb_buttons_5_Template(rf, ctx) { if (rf & 1) {
    const _r46 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "lib-mrb-buttons", 31);
    i0.ɵɵlistener("buttonClick", function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_2_lib_mrb_buttons_5_Template_lib_mrb_buttons_buttonClick_0_listener() { i0.ɵɵrestoreView(_r46); const ctx_r45 = i0.ɵɵnextContext(6); return i0.ɵɵresetView(ctx_r45.onCompleteClick()); });
    i0.ɵɵi18n(1, 32);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const group_r13 = i0.ɵɵnextContext(4).$implicit;
    const ctx_r38 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("disabled", ctx_r38.submitting || ctx_r38.linear && !group_r13.complete && ctx_r38.errorStyle === "form-disable");
} }
function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 23);
    i0.ɵɵtemplate(2, SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_2_lib_mrb_buttons_2_Template, 2, 0, "lib-mrb-buttons", 24);
    i0.ɵɵtemplate(3, SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_2_lib_mrb_buttons_3_Template, 2, 1, "lib-mrb-buttons", 25);
    i0.ɵɵpipe(4, "surveyGroups");
    i0.ɵɵtemplate(5, SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_2_lib_mrb_buttons_5_Template, 2, 1, "lib-mrb-buttons", 26);
    i0.ɵɵpipe(6, "surveyGroups");
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const i_r14 = i0.ɵɵnextContext(3).index;
    const ctx_r22 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", i_r14 > 0);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r22.currentPage < i0.ɵɵpipeBind1(4, 3, ctx_r22.survey.items).length - 1);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", !ctx_r22.surveyWasCompleted && ctx_r22.currentPage === i0.ɵɵpipeBind1(6, 5, ctx_r22.survey.items).length - 1 && (!ctx_r22.userSurvey || !ctx_r22.userSurvey.complete));
} }
function SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_1_Template, 6, 7, "ng-container", 12);
    i0.ɵɵtemplate(2, SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_ng_container_2_Template, 7, 7, "ng-container", 12);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r17 = i0.ɵɵnextContext(4);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r17.options.theme == "MyAlpha");
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r17.options.theme == "MRB");
} }
const _c17 = function (a0) { return { orderCustom: a0 }; };
function SurveyComponent_div_1_ng_container_1_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    const _r50 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SurveyComponent_div_1_ng_container_1_ng_container_1_h2_1_Template, 3, 2, "h2", 12);
    i0.ɵɵpipe(2, "surveyGroups");
    i0.ɵɵelementStart(3, "question-list", 13);
    i0.ɵɵlistener("change", function SurveyComponent_div_1_ng_container_1_ng_container_1_Template_question_list_change_3_listener($event) { i0.ɵɵrestoreView(_r50); const ctx_r49 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r49.onQuestionChange($event)); })("complete", function SurveyComponent_div_1_ng_container_1_ng_container_1_Template_question_list_complete_3_listener() { i0.ɵɵrestoreView(_r50); const ctx_r51 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r51.onSectionComplete()); });
    i0.ɵɵpipe(4, "surveyQuestions");
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(5, SurveyComponent_div_1_ng_container_1_ng_container_1_ng_container_5_Template, 3, 2, "ng-container", 12);
    i0.ɵɵpipe(6, "surveyGroups");
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const group_r13 = i0.ɵɵnextContext().$implicit;
    const ctx_r15 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r15.currentPage === 0 || i0.ɵɵpipeBind1(2, 7, ctx_r15.survey.items).length > 2 && ctx_r15.survey.presentation !== ctx_r15.SurveyPresentation.PSYCHOMETRIC);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("questions", i0.ɵɵpipeBind2(4, 9, group_r13.items, i0.ɵɵpureFunction1(14, _c17, ctx_r15.survey.presentation === ctx_r15.SurveyPresentation.PSYCHOMETRIC && ctx_r15.currentPage > 0)))("linear", ctx_r15.linear)("disabled", ctx_r15.networkUnavailable || ctx_r15.criticalError)("enumerate", group_r13.enumerate)("survey", ctx_r15.survey);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(6, 12, ctx_r15.survey.items).length > 0);
} }
function SurveyComponent_div_1_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SurveyComponent_div_1_ng_container_1_ng_container_1_Template, 7, 16, "ng-container", 12);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const i_r14 = ctx.index;
    const ctx_r12 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r12.currentPage === i_r14);
} }
function SurveyComponent_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 10);
    i0.ɵɵtemplate(1, SurveyComponent_div_1_ng_container_1_Template, 2, 1, "ng-container", 11);
    i0.ɵɵpipe(2, "surveyGroups");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", i0.ɵɵpipeBind1(2, 1, ctx_r1.survey.items));
} }
function SurveyComponent_ng_template_2_Template(rf, ctx) { if (rf & 1) {
    const _r55 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "h2");
    i0.ɵɵi18n(1, 33);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(2, "p");
    i0.ɵɵi18n(3, 34);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "button", 35);
    i0.ɵɵlistener("click", function SurveyComponent_ng_template_2_Template_button_click_4_listener() { const restoredCtx = i0.ɵɵrestoreView(_r55); const dialogRef_r53 = restoredCtx.dialogRef; return i0.ɵɵresetView(dialogRef_r53.close()); });
    i0.ɵɵi18n(5, 36);
    i0.ɵɵelementEnd();
} }
function SurveyComponent_ng_template_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p");
    i0.ɵɵi18n(1, 37);
    i0.ɵɵelementEnd();
} }
function SurveyComponent_ng_template_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "p", 38);
} if (rf & 2) {
    const ctx_r7 = i0.ɵɵnextContext();
    i0.ɵɵproperty("innerHTML", ctx_r7.connectionUnavailableMessage, i0.ɵɵsanitizeHtml);
} }
function SurveyComponent_ng_template_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p");
    i0.ɵɵi18n(1, 39);
    i0.ɵɵelementEnd();
} }
function SurveyComponent_ng_template_10_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p");
    i0.ɵɵi18n(1, 40);
    i0.ɵɵelementEnd();
} }
class SurveyComponent {
    /**
     * Client can pass in a custom complete modal
     */
    set clientCompleteModal(tref) {
        if (tref) {
            this._completeModal = tref;
        }
    }
    /**
     * The default complete modal
     */
    set completeModal(tref) {
        this._completeModal = tref;
    }
    /** Getter for completeModal will return either the passed in content child or the default */
    get completeModal() {
        return this._completeModal;
    }
    constructor(service, cd, dialog, options) {
        this.service = service;
        this.cd = cd;
        this.dialog = dialog;
        this.options = options;
        this.currentPage = 0;
        /**
         * If linear is true then you can choose to disable the Next button (form-disable) if page is invalid, or display the errors inline on click ( on-submit )
         * If linear is false the errors will always be displayed inline but navigation to the next page will still occur
         */
        this.linear = true;
        this.errorStyle = 'form-disable';
        this.questionChange = new EventEmitter();
        this.surveyComplete = new EventEmitter();
        this.networkStatus = new EventEmitter();
        this.currentPageChange = new EventEmitter();
        this.connectionUnavailableMessage = $localize `:alpha.surveys|Survey Network Offline Error@@surveyOfflineError:It appears as though you are offline. The survey will be disabled until a network connection can be established.`;
        this.surveyWasCompleted = false;
        this.networkUnavailable = navigator.onLine;
        this.criticalError = false;
        this.submitting = false;
        this.SurveyPresentation = SurveyPresentation;
        this.destroy$ = new Subject();
        this.service.online$.pipe(takeUntil(this.destroy$)).subscribe(isOnline => this.onNetworkConnectionStatus(isOnline));
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        // Now let's also unsubscribe from the subject itself:
        this.destroy$.unsubscribe();
    }
    ngAfterContentInit() {
        if (this.userSurvey) {
            this.initializeUserSurvey(this.userSurvey);
        }
        this._contentInitialized = true;
    }
    ngOnChanges(c) {
        if (c.userLink && c.userLink.currentValue) {
            this.loadUserSurvey(c.userLink.currentValue);
        }
        if (c.userSurvey) {
            this.initializeUserSurvey(c.userSurvey.currentValue);
        }
        if (c.survey && typeof (c.survey.currentValue) === 'string') {
            const survey = new Survey(JSON.parse(c.survey.currentValue));
            this.survey = survey;
        }
    }
    /**
     * Initialize the user survey once we have it
     * Start listening for session id changes
     * Show the survey complete modal if needed
     */
    initializeUserSurvey(userSurvey) {
        this.survey = userSurvey.survey;
        if (userSurvey.locked) {
            this.showSurveySessionLockModal();
            return;
        }
        if (!this.sessionListener && this.enforceSession) {
            this.sessionListener = this.service.initializeSession(userSurvey).pipe(takeUntil(this.destroy$)).subscribe(session => this.onSessionStatus(session));
        }
        if (userSurvey.complete && this._contentInitialized) {
            this.showSurveyCompleteModal();
        }
        this.cd.detectChanges();
    }
    /**
     * Show the complete dialog template
     */
    showSurveyCompleteModal() {
        this.surveyWasCompleted = true;
        this.dialog.open(this._completeModal);
    }
    /**
     * Show the session locked dialog template
     */
    showSurveySessionLockModal() {
        this._lockedDialogRef = this.dialog.open(this.surveyLockedModal, {
            disableClose: true
        });
    }
    /**
     * Handle session status changes. Either true for a valid session, or false for a bad one.
     * We aren't actually doing anything on status change however. When the survey is loaded, and is locked it is essentially dead until refreshed
     */
    onSessionStatus(validSession) {
        if (!validSession && !this._lockedDialogRef) {
            this.showSurveySessionLockModal();
        }
    }
    /**
     * When navigator.onLine changes
     * @param online
     */
    onNetworkConnectionStatus(online) {
        this.networkUnavailable = !online;
        if (this.networkUnavailable) {
            this.offlineModalRef = this.dialog.open(this.offlineModal, { panelClass: 'simple-error-message' });
        }
        else if (this.offlineModalRef) {
            this.offlineModalRef.close();
        }
        this.networkStatus.emit(online);
        this.cd.markForCheck();
    }
    /**
     * Load the user survey
     */
    loadUserSurvey(userLink) {
        return this.service.getUserSurvey(userLink, 'token').subscribe(userSurvey => {
            this.userSurvey = userSurvey;
            this.initializeUserSurvey(this.userSurvey);
            this.cd.markForCheck();
        });
    }
    onQuestionChange(event) {
        // reset any error on the question
        event.question.error = null;
        if (this.userSurvey) {
            if (this.userSurvey.locked) {
                this.showSurveySessionLockModal();
                return;
            }
            //console.info( 'save ' + event.question.title + " === " + event.question.response );
            this.service.updateUserSurveyResponses(this.userSurvey).pipe(take(1), retryWhen(result => result.pipe(concatMap((result) => {
                // retry if timeout
                if (result.status === 408) {
                    return of(result);
                }
                // in other cases we throw an error down the pipe
                return throwError(result);
            }), 
            // max retries
            take(2), o => concat(o, throwError({ status: 500 }))))).subscribe(() => {
                //
            }, (error) => {
                this.criticalError = true;
                let template;
                if (error.status === 404) {
                    template = this.notFoundModal;
                }
                else {
                    template = this.unavailableModal;
                }
                this.dialog.open(template, { disableClose: true, panelClass: 'simple-error-message' });
                this.cd.markForCheck();
            });
        }
        this.questionChange.emit(event.question);
    }
    onSectionComplete() {
        if (this.survey.complete && (!this.userSurvey || !this.userSurvey.complete)) {
            //this.surveyComplete.emit( this.survey );
            // @moved this emit to the on complete click
        }
    }
    onCompleteClick() {
        // if the errorStyle is on-submit we validate
        const valid = this.survey.validate();
        if (!valid) {
            return;
        }
        if (this.userSurvey && !this.userSurvey.complete && this.survey.complete) {
            this.submitting = true;
            this.cd.detectChanges();
            this.service.completeUserSurvey(this.userSurvey).subscribe(response => {
                this.submitting = false;
                this.surveyComplete.emit({ response, survey: this.survey, userSurvey: this.userSurvey });
                this.surveyWasCompleted = true;
                this.cd.detectChanges();
            });
        }
    }
    gotoNextPage(group) {
        // if the survey is linear, we will validate fields upon user hits continue 
        const valid = group.validate();
        if (this.linear && !valid) {
            return;
        }
        this.currentPage++;
        window.scrollTo(0, 0);
        this.currentPageChange.emit(this.currentPage);
    }
    gotoPreviousPage() {
        this.currentPage--;
        window.scrollTo(0, 0);
        this.currentPageChange.emit(this.currentPage);
    }
}
SurveyComponent.ɵfac = function SurveyComponent_Factory(t) { return new (t || SurveyComponent)(i0.ɵɵdirectiveInject(SurveyService), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1$3.MatDialog), i0.ɵɵdirectiveInject(ALPHA_SURVEYS_OPTIONS)); };
SurveyComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: SurveyComponent, selectors: [["survey"]], contentQueries: function SurveyComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
        i0.ɵɵcontentQuery(dirIndex, _c0$4, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.clientCompleteModal = _t.first);
    } }, viewQuery: function SurveyComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0$4, 7);
        i0.ɵɵviewQuery(_c1$1, 7);
        i0.ɵɵviewQuery(_c2$1, 7);
        i0.ɵɵviewQuery(_c3, 7);
        i0.ɵɵviewQuery(_c4, 7);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.completeModal = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.unavailableModal = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.notFoundModal = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.surveyLockedModal = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.offlineModal = _t.first);
    } }, inputs: { survey: "survey", userLink: "userLink", userSurvey: "userSurvey", currentPage: "currentPage", linear: "linear", errorStyle: "errorStyle", theme: "theme", enforceSession: "enforceSession" }, outputs: { questionChange: "questionChange", surveyComplete: "surveyComplete", networkStatus: "networkStatus", currentPageChange: "currentPageChange" }, features: [i0.ɵɵNgOnChangesFeature], decls: 12, vars: 2, consts: function () { let i18n_5; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Common Words - Previous
         * @meaning common.words
         */
        const MSG_EXTERNAL_commonWordPrevious$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS_______6 = goog.getMsg("Previous");
        i18n_5 = MSG_EXTERNAL_commonWordPrevious$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS_______6;
    }
    else {
        i18n_5 = $localize `:common.words|Common Words - Previous@@commonWordPrevious␟71c7c63e78c3d335c8e95d93ab5c32107438f5c6␟3629690930210092522:Previous`;
    } let i18n_7; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Next Button
         * @meaning common.buttons
         */
        const MSG_EXTERNAL_NextButton$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS_______8 = goog.getMsg("Next");
        i18n_7 = MSG_EXTERNAL_NextButton$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS_______8;
    }
    else {
        i18n_7 = $localize `:common.buttons|Next Button@@NextButton␟6e81c236fbaff3ba85e8d8bbc6c49c5f553d014f␟4440399416965222281:Next`;
    } let i18n_9; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Common Words - Submit
         * @meaning common.words
         */
        const MSG_EXTERNAL_commonWordSubmit$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS_______10 = goog.getMsg("Submit");
        i18n_9 = MSG_EXTERNAL_commonWordSubmit$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS_______10;
    }
    else {
        i18n_9 = $localize `:common.words|Common Words - Submit@@commonWordSubmit␟89332d5fb52fa042bcbed29fdc4901c265cbfc13␟5818583321283807522:Submit`;
    } let i18n_11; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Common Words - Previous
         * @meaning common.words
         */
        const MSG_EXTERNAL_commonWordPrevious$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS_______12 = goog.getMsg("Previous ");
        i18n_11 = MSG_EXTERNAL_commonWordPrevious$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS_______12;
    }
    else {
        i18n_11 = $localize `:common.words|Common Words - Previous@@commonWordPrevious␟694ea0d3632432e913ca32f6b4db27875fc8f020␟6102520039549077768:Previous `;
    } let i18n_13; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Next Button
         * @meaning common.buttons
         */
        const MSG_EXTERNAL_NextButton$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS_______14 = goog.getMsg("Next ");
        i18n_13 = MSG_EXTERNAL_NextButton$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS_______14;
    }
    else {
        i18n_13 = $localize `:common.buttons|Next Button@@NextButton␟1d11f3c2c705e4bf691cf41edecbfd91978d996f␟7076873207044512946:Next `;
    } let i18n_15; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Common Words - Submit
         * @meaning common.words
         */
        const MSG_EXTERNAL_commonWordSubmit$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS_______16 = goog.getMsg("Submit ");
        i18n_15 = MSG_EXTERNAL_commonWordSubmit$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS_______16;
    }
    else {
        i18n_15 = $localize `:common.words|Common Words - Submit@@commonWordSubmit␟be2bd92f389e1ca86f088a10b3ad18f2719773d8␟2338801413839985845:Submit `;
    } let i18n_18; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Survey complete no access title
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_surveyCompleteNoAccessTitle$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS__19 = goog.getMsg("This survey has been completed.");
        i18n_18 = MSG_EXTERNAL_surveyCompleteNoAccessTitle$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS__19;
    }
    else {
        i18n_18 = $localize `:alpha.surveys|Survey complete no access title@@surveyCompleteNoAccessTitle␟5fb612c839b3a699593b7dc9bce641a544858f78␟7209846417628466495:This survey has been completed.`;
    } let i18n_20; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Survey complete with editability message
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_surveyCompleteCanEditMsg$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS__21 = goog.getMsg("You can review and update your answers. If you update your answers, please let your course administrator know so they can get an updated report.");
        i18n_20 = MSG_EXTERNAL_surveyCompleteCanEditMsg$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS__21;
    }
    else {
        i18n_20 = $localize `:alpha.surveys|Survey complete with editability message@@surveyCompleteCanEditMsg␟58afe7e3e872fc33e66d642b748dd83ba401b6f1␟4079897272637791596:You can review and update your answers. If you update your answers, please let your course administrator know so they can get an updated report.`;
    } let i18n_22; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Common Words - OK
         * @meaning common.words
         */
        const MSG_EXTERNAL_commonWordOK$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS__23 = goog.getMsg("OK");
        i18n_22 = MSG_EXTERNAL_commonWordOK$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS__23;
    }
    else {
        i18n_22 = $localize `:common.words|Common Words - OK@@commonWordOK␟990233843994de5e0e83ccb623621ae3cdc69a3f␟3497822989342587878:OK`;
    } let i18n_24; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Survey Technical Difficulties
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_surveyTechnicalIssue$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS__25 = goog.getMsg("We are experiencing technical difficulties. The survey will be disabled until the issue has been resolved. Please come back later to try again.");
        i18n_24 = MSG_EXTERNAL_surveyTechnicalIssue$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS__25;
    }
    else {
        i18n_24 = $localize `:alpha.surveys|Survey Technical Difficulties@@surveyTechnicalIssue␟4ad7c63b1002ae205f39d1a3967fa30d3930ee44␟637563133992360805:We are experiencing technical difficulties. The survey will be disabled until the issue has been resolved. Please come back later to try again.`;
    } let i18n_26; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Survey Not Found Error
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_surveyNotFoundError$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS__27 = goog.getMsg("This survey no longer exists. Please contact your course administrator for more information.");
        i18n_26 = MSG_EXTERNAL_surveyNotFoundError$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS__27;
    }
    else {
        i18n_26 = $localize `:alpha.surveys|Survey Not Found Error@@surveyNotFoundError␟5cf32d0ec19681bea596663be65858a16d6e220b␟8818882484058184009:This survey no longer exists. Please contact your course administrator for more information.`;
    } let i18n_28; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Survey Session Locked
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_surveySessionLocked$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS__29 = goog.getMsg("This survey is locked because it is active in another window.");
        i18n_28 = MSG_EXTERNAL_surveySessionLocked$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_SURVEY_SRC_COMPONENTS_SURVEY_SURVEY_COMPONENT_TS__29;
    }
    else {
        i18n_28 = $localize `:alpha.surveys|Survey Session Locked@@surveySessionLocked␟bc47fa0033c35f7b399cbc08b54e9fca005319e7␟2283582605770401150:This survey is locked because it is active in another window.`;
    } return [["id", "connectionNotice", 3, "matTooltip", 4, "ngIf"], ["cdkTrapFocus", "", "class", "spacing-lg", 4, "ngIf"], ["completedModal", ""], ["unavailableModal", ""], ["offlineModal", ""], ["notFoundModal", ""], ["surveyLockedModal", ""], ["id", "connectionNotice", 3, "matTooltip"], ["xmlns", "http://www.w3.org/2000/svg", "viewBox", "0 0 24 24", "fill", "orange", "width", "36px", "height", "36px"], ["d", "M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"], ["cdkTrapFocus", "", 1, "spacing-lg"], [4, "ngFor", "ngForOf"], [4, "ngIf"], [3, "questions", "linear", "disabled", "enumerate", "survey", "change", "complete"], ["mat-flat-button", "", "class", "prev", "color", "warn", 3, "click", 4, "ngIf"], ["mat-flat-button", "", "class", "next", "color", "warn", 3, "disabled", "click", 4, "ngIf"], ["mat-flat-button", "", "class", "submit", "color", "warn", 3, "disabled", "click", 4, "ngIf"], ["mat-flat-button", "", "color", "warn", 1, "prev", 3, "click"], i18n_5, ["mat-flat-button", "", "color", "warn", 1, "next", 3, "disabled", "click"], i18n_7, ["mat-flat-button", "", "color", "warn", 1, "submit", 3, "disabled", "click"], i18n_9, [1, "buttons"], ["variant", "secondary", "class", "prev", 3, "buttonClick", 4, "ngIf"], ["class", "next", 3, "disabled", "buttonClick", 4, "ngIf"], ["class", "submit", 3, "disabled", "buttonClick", 4, "ngIf"], ["variant", "secondary", 1, "prev", 3, "buttonClick"], i18n_11, [1, "next", 3, "disabled", "buttonClick"], i18n_13, [1, "submit", 3, "disabled", "buttonClick"], i18n_15, i18n_18, i18n_20, ["mat-flat-button", "", "color", "warn", 3, "click"], i18n_22, i18n_24, [3, "innerHTML"], i18n_26, i18n_28]; }, template: function SurveyComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, SurveyComponent_div_0_Template, 3, 1, "div", 0);
        i0.ɵɵtemplate(1, SurveyComponent_div_1_Template, 3, 3, "div", 1);
        i0.ɵɵtemplate(2, SurveyComponent_ng_template_2_Template, 6, 0, "ng-template", null, 2, i0.ɵɵtemplateRefExtractor);
        i0.ɵɵtemplate(4, SurveyComponent_ng_template_4_Template, 2, 0, "ng-template", null, 3, i0.ɵɵtemplateRefExtractor);
        i0.ɵɵtemplate(6, SurveyComponent_ng_template_6_Template, 1, 1, "ng-template", null, 4, i0.ɵɵtemplateRefExtractor);
        i0.ɵɵtemplate(8, SurveyComponent_ng_template_8_Template, 2, 0, "ng-template", null, 5, i0.ɵɵtemplateRefExtractor);
        i0.ɵɵtemplate(10, SurveyComponent_ng_template_10_Template, 2, 0, "ng-template", null, 6, i0.ɵɵtemplateRefExtractor);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.networkUnavailable && ctx.networkStatus.observers.length === 0);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.survey == null ? null : ctx.survey.items.length);
    } }, styles: ["[_nghost-%COMP%]{display:block}#connectionNotice[_ngcontent-%COMP%]{position:fixed;right:10px;top:10px;z-index:1000}#connectionNotice[_ngcontent-%COMP%]   svg[_ngcontent-%COMP%]{fill:orange}button[_ngcontent-%COMP%] + button[_ngcontent-%COMP%]{margin-left:16px}.buttons[_ngcontent-%COMP%]{display:flex;flex-direction:row;gap:1rem;margin-top:2rem}"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurveyComponent, [{
        type: Component,
        args: [{ selector: 'survey', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div id=\"connectionNotice\"\n\t *ngIf=\"networkUnavailable && networkStatus.observers.length === 0\"\n\t [matTooltip]=\"connectionUnavailableMessage\">\n\t<svg xmlns=\"http://www.w3.org/2000/svg\"\n\t\t viewBox=\"0 0 24 24\"\n\t\t fill=\"orange\"\n\t\t width=\"36px\"\n\t\t height=\"36px\">\n\t\t<path d=\"M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z\" />\n\t</svg>\n</div>\n\n<div *ngIf=\"survey?.items.length\"\n\t cdkTrapFocus\n\t class=\"spacing-lg\">\n\n\t<ng-container *ngFor=\"let group of survey.items | surveyGroups;index as i\">\n\t\t<ng-container *ngIf=\"currentPage === i\">\n\n\t\t\t<h2 *ngIf=\"currentPage === 0 || ( (survey.items | surveyGroups).length > 2 && survey.presentation !== SurveyPresentation.PSYCHOMETRIC )\">\n\t\t\t\t<ng-container *ngIf=\"currentPage > 0 && theme !== 'MyAlpha'\">{{i}}. </ng-container>{{group.title}}\n\t\t\t</h2>\n\n\t\t\t<question-list [questions]=\"group.items | surveyQuestions : {orderCustom: survey.presentation === SurveyPresentation.PSYCHOMETRIC && currentPage > 0 }\"\n\t\t\t\t\t\t   [linear]=\"linear\"\n\t\t\t\t\t\t   [disabled]=\"networkUnavailable || criticalError\"\n\t\t\t\t\t\t   [enumerate]=\"group.enumerate\"\n\t\t\t\t\t\t   [survey]=\"survey\"\n\t\t\t\t\t\t   (change)=\"onQuestionChange( $event )\"\n\t\t\t\t\t\t   (complete)=\"onSectionComplete()\"></question-list>\n\n\t\t\t<ng-container *ngIf=\"(survey.items | surveyGroups).length > 0 \">\n\n\t\t\t\t<ng-container *ngIf=\"options.theme == 'MyAlpha'\">\n\t\t\t\t\t<button *ngIf=\"i > 0\" mat-flat-button class=\"prev\" color=\"warn\" (click)=\"gotoPreviousPage()\"\n\t\t\t\t\t\ti18n=\"common.words|Common Words - Previous@@commonWordPrevious\">Previous</button>\n\t\t\t\t\n\t\t\t\t\t<button *ngIf=\"currentPage < (survey.items | surveyGroups).length - 1\" mat-flat-button class=\"next\" color=\"warn\"\n\t\t\t\t\t\t[disabled]=\"linear && !group.complete && errorStyle === 'form-disable'\" (click)=\"gotoNextPage(group)\"\n\t\t\t\t\t\ti18n=\"common.buttons|Next Button@@NextButton\">Next</button>\n\t\t\t\t\n\t\t\t\t\t<button\n\t\t\t\t\t\t*ngIf=\"!surveyWasCompleted && currentPage === (survey.items | surveyGroups).length - 1 && ( ! userSurvey || !userSurvey.complete)\"\n\t\t\t\t\t\tmat-flat-button class=\"submit\" color=\"warn\"\n\t\t\t\t\t\t[disabled]=\"submitting || (linear && !group.complete && errorStyle === 'form-disable')\"\n\t\t\t\t\t\t(click)=\"onCompleteClick()\" i18n=\"common.words|Common Words - Submit@@commonWordSubmit\">Submit</button>\n\t\t\t\t\n\t\t\t\t</ng-container>\n\t\t\t\t<ng-container *ngIf=\"options.theme == 'MRB'\">\n\t\t\t\t\t<div class=\"buttons\">\n\t\t\t\t\t\t<lib-mrb-buttons variant=\"secondary\" \n\t\t\t\t\t\t\t*ngIf=\"i > 0\" class=\"prev\" (buttonClick)=\"gotoPreviousPage()\"\n\t\t\t\t\t\t\ti18n=\"common.words|Common Words - Previous@@commonWordPrevious\">Previous\n\t\t\t\t\t\t</lib-mrb-buttons>\n\t\t\t\t\t\t<lib-mrb-buttons \n\t\t\t\t\t\t\t*ngIf=\"currentPage < (survey.items | surveyGroups).length - 1\"\n\t\t\t\t\t\t\tclass=\"next\" [disabled]=\"linear && !group.complete && errorStyle === 'form-disable'\"\n\t\t\t\t\t\t\t(buttonClick)=\"gotoNextPage(group)\" i18n=\"common.buttons|Next Button@@NextButton\">Next\n\t\t\t\t\t\t</lib-mrb-buttons>\n\t\t\t\t\t\t<lib-mrb-buttons \n\t\t\t\t\t\t\t*ngIf=\"!surveyWasCompleted && currentPage === (survey.items | surveyGroups).length - 1 && ( ! userSurvey || !userSurvey.complete)\"\n\t\t\t\t\t\t\tclass=\"submit\" [disabled]=\"submitting || (linear && !group.complete && errorStyle === 'form-disable')\"\n\t\t\t\t\t\t\t(buttonClick)=\"onCompleteClick()\" i18n=\"common.words|Common Words - Submit@@commonWordSubmit\">Submit\n\t\t\t\t\t\t</lib-mrb-buttons>\n\t\t\t\t\t</div>\n\t\t\t\t</ng-container>\n\t\t\t</ng-container>\n\t\t</ng-container>\n\t</ng-container>\n\n</div>\n\n<ng-template #completedModal\n\t\t\t let-dialogRef='dialogRef'>\n\t<h2 i18n=\"alpha.surveys|Survey complete no access title@@surveyCompleteNoAccessTitle\">This survey has been\n\t\tcompleted.</h2>\n\t<!--<p i18n=\"alpha.surveys|Survey complete no access message@@surveyCompleteNoAccessMsg\">You can review your answers but cannot change them.</p>-->\n\t<p i18n=\"alpha.surveys|Survey complete with editability message@@surveyCompleteCanEditMsg\">You can review and update\n\t\tyour answers. If you update your answers, please let your course administrator know so they can get an updated\n\t\treport.</p>\n\t<button mat-flat-button\n\t\t\tcolor=\"warn\"\n\t\t\t(click)=\"dialogRef.close()\"\n\t\t\ti18n=\"common.words|Common Words - OK@@commonWordOK\">OK</button>\n</ng-template>\n\n<ng-template #unavailableModal\n\t\t\t let-dialogRef='dialogRef'>\n\t<p i18n=\"alpha.surveys|Survey Technical Difficulties@@surveyTechnicalIssue\">We are experiencing technical difficulties. The survey will be disabled until the issue has been resolved. Please come back later to try again.</p>\n</ng-template>\n\n<ng-template #offlineModal\n\t\t\t let-dialogRef='dialogRef'>\n\t<p [innerHTML]=\"connectionUnavailableMessage\"></p>\n</ng-template>\n\n<ng-template #notFoundModal\n\t\t\t let-dialogRef='dialogRef'>\n\t<p i18n=\"alpha.surveys|Survey Not Found Error@@surveyNotFoundError\">This survey no longer exists. Please contact\n\t\tyour course administrator for more information.</p>\n</ng-template>\n\n<ng-template #surveyLockedModal\n\t\t\t let-dialogRef='dialogRef'>\n\t<p i18n=\"alpha.surveys|Survey Session Locked@@surveySessionLocked\">This survey is locked because it is active in another window.</p>\n</ng-template>\n", styles: [":host{display:block}#connectionNotice{position:fixed;right:10px;top:10px;z-index:1000}#connectionNotice svg{fill:orange}button+button{margin-left:16px}.buttons{display:flex;flex-direction:row;gap:1rem;margin-top:2rem}\n"] }]
    }], function () { return [{ type: SurveyService }, { type: i0.ChangeDetectorRef }, { type: i1$3.MatDialog }, { type: undefined, decorators: [{
                type: Inject,
                args: [ALPHA_SURVEYS_OPTIONS]
            }] }]; }, { survey: [{
            type: Input
        }], userLink: [{
            type: Input
        }], userSurvey: [{
            type: Input
        }], currentPage: [{
            type: Input
        }], linear: [{
            type: Input
        }], errorStyle: [{
            type: Input
        }], theme: [{
            type: Input
        }], enforceSession: [{
            type: Input
        }], questionChange: [{
            type: Output
        }], surveyComplete: [{
            type: Output
        }], networkStatus: [{
            type: Output
        }], currentPageChange: [{
            type: Output
        }], clientCompleteModal: [{
            type: ContentChild,
            args: ['completedModal', { static: false }]
        }], completeModal: [{
            type: ViewChild,
            args: ['completedModal', { static: true }]
        }], unavailableModal: [{
            type: ViewChild,
            args: ['unavailableModal', { static: true }]
        }], notFoundModal: [{
            type: ViewChild,
            args: ['notFoundModal', { static: true }]
        }], surveyLockedModal: [{
            type: ViewChild,
            args: ['surveyLockedModal', { static: true }]
        }], offlineModal: [{
            type: ViewChild,
            args: ['offlineModal', { static: true }]
        }] }); })();

class SurveyQuestionComponent {
    get element() {
        return this.el.nativeElement;
    }
    constructor(injector, componentFactoryResolver, appRef, el, surveyComponent) {
        this.injector = injector;
        this.componentFactoryResolver = componentFactoryResolver;
        this.appRef = appRef;
        this.el = el;
        this.surveyComponent = surveyComponent;
    }
    focus() {
        if (this.componentRef.instance.focus) {
            this.componentRef.instance.focus();
        }
        else {
            const focusable = this.el.nativeElement.querySelector('input,select,textarea');
            if (focusable) {
                focusable.focus();
            }
        }
    }
    ngOnInit() {
        this.portalHost = new DomPortalHost(this.el.nativeElement, this.componentFactoryResolver, this.appRef, this.injector);
        const component = QuestionComponentMap.get(this.question.questionType);
        if (!component) {
            console.error('No Component registered for: ' + this.question.questionType);
            return;
        }
        const injector = new PortalInjector(this.injector, new WeakMap([
            [QUESTION, this.question],
            [Survey, this.survey]
        ]));
        this.portal = new ComponentPortal(component, null, injector);
        this.componentRef = this.portalHost.attach(this.portal);
        this.componentRef.instance.disabled = this.disabled;
    }
    ngOnChanges(c) {
        if (c.disabled && this.componentRef) {
            // add setTimeout to fix ExpressionChangedAfterItHasBeenCheckedError
            setTimeout(() => {
                this.componentRef.instance.disabled = c.disabled.currentValue;
            });
        }
    }
}
SurveyQuestionComponent.ɵfac = function SurveyQuestionComponent_Factory(t) { return new (t || SurveyQuestionComponent)(i0.ɵɵdirectiveInject(i0.Injector), i0.ɵɵdirectiveInject(i0.ComponentFactoryResolver), i0.ɵɵdirectiveInject(i0.ApplicationRef), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(SurveyComponent, 8)); };
SurveyQuestionComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: SurveyQuestionComponent, selectors: [["survey-question"]], inputs: { survey: "survey", question: "question", disabled: "disabled" }, features: [i0.ɵɵNgOnChangesFeature], decls: 0, vars: 0, template: function SurveyQuestionComponent_Template(rf, ctx) { }, styles: ["[_nghost-%COMP%]{display:block}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurveyQuestionComponent, [{
        type: Component,
        args: [{ selector: 'survey-question', template: ``, styles: [":host{display:block}\n"] }]
    }], function () { return [{ type: i0.Injector }, { type: i0.ComponentFactoryResolver }, { type: i0.ApplicationRef }, { type: i0.ElementRef }, { type: SurveyComponent, decorators: [{
                type: Optional
            }] }]; }, { survey: [{
            type: Input
        }], question: [{
            type: Input
        }], disabled: [{
            type: Input
        }] }); })();

class QuestionPicklistModule {
}
QuestionPicklistModule.ɵfac = function QuestionPicklistModule_Factory(t) { return new (t || QuestionPicklistModule)(); };
QuestionPicklistModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionPicklistModule });
QuestionPicklistModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        FormsModule,
        SurveyMaterialModule,
        QuestionTitleModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionPicklistModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    SurveyMaterialModule,
                    QuestionTitleModule
                ],
                declarations: [
                    QuestionPicklistComponent
                ],
                exports: [
                    QuestionPicklistComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionPicklistModule, { declarations: [QuestionPicklistComponent], imports: [CommonModule,
        FormsModule,
        SurveyMaterialModule,
        QuestionTitleModule], exports: [QuestionPicklistComponent] }); })();

class QuestionChecklistModule {
}
QuestionChecklistModule.ɵfac = function QuestionChecklistModule_Factory(t) { return new (t || QuestionChecklistModule)(); };
QuestionChecklistModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionChecklistModule });
QuestionChecklistModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        SurveyMaterialModule,
        QuestionTitleModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionChecklistModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    SurveyMaterialModule,
                    QuestionTitleModule
                ],
                declarations: [
                    QuestionChecklistComponent,
                ],
                exports: [
                    QuestionChecklistComponent,
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionChecklistModule, { declarations: [QuestionChecklistComponent], imports: [CommonModule,
        SurveyMaterialModule,
        QuestionTitleModule], exports: [QuestionChecklistComponent] }); })();

class QuestionRadiosetModule {
}
QuestionRadiosetModule.ɵfac = function QuestionRadiosetModule_Factory(t) { return new (t || QuestionRadiosetModule)(); };
QuestionRadiosetModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionRadiosetModule });
QuestionRadiosetModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        SurveyMaterialModule,
        QuestionTitleModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionRadiosetModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    SurveyMaterialModule,
                    QuestionTitleModule
                ],
                declarations: [
                    QuestionRadiosetComponent,
                ],
                exports: [
                    QuestionRadiosetComponent,
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionRadiosetModule, { declarations: [QuestionRadiosetComponent], imports: [CommonModule,
        SurveyMaterialModule,
        QuestionTitleModule], exports: [QuestionRadiosetComponent] }); })();

class QuestionTextModule {
}
QuestionTextModule.ɵfac = function QuestionTextModule_Factory(t) { return new (t || QuestionTextModule)(); };
QuestionTextModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionTextModule });
QuestionTextModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        FormsModule,
        QuestionTitleModule,
        SurveyMaterialModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionTextModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    QuestionTitleModule,
                    SurveyMaterialModule
                ],
                declarations: [
                    QuestionTextComponent
                ],
                exports: [
                    QuestionTextComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionTextModule, { declarations: [QuestionTextComponent], imports: [CommonModule,
        FormsModule,
        QuestionTitleModule,
        SurveyMaterialModule], exports: [QuestionTextComponent] }); })();

class QuestionTextareaModule {
}
QuestionTextareaModule.ɵfac = function QuestionTextareaModule_Factory(t) { return new (t || QuestionTextareaModule)(); };
QuestionTextareaModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionTextareaModule });
QuestionTextareaModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        FormsModule,
        QuestionTitleModule,
        SurveyMaterialModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionTextareaModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    QuestionTitleModule,
                    SurveyMaterialModule
                ],
                declarations: [
                    QuestionTextareaComponent
                ],
                exports: [
                    QuestionTextareaComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionTextareaModule, { declarations: [QuestionTextareaComponent], imports: [CommonModule,
        FormsModule,
        QuestionTitleModule,
        SurveyMaterialModule], exports: [QuestionTextareaComponent] }); })();

class QuestionDatePickerModule {
}
QuestionDatePickerModule.ɵfac = function QuestionDatePickerModule_Factory(t) { return new (t || QuestionDatePickerModule)(); };
QuestionDatePickerModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionDatePickerModule });
QuestionDatePickerModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        FormsModule,
        SurveyMaterialModule,
        QuestionTitleModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionDatePickerModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    SurveyMaterialModule,
                    QuestionTitleModule
                ],
                declarations: [
                    QuestionDatePickerComponent
                ],
                exports: [
                    QuestionDatePickerComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionDatePickerModule, { declarations: [QuestionDatePickerComponent], imports: [CommonModule,
        FormsModule,
        SurveyMaterialModule,
        QuestionTitleModule], exports: [QuestionDatePickerComponent] }); })();

class QuestionScaleModule {
}
QuestionScaleModule.ɵfac = function QuestionScaleModule_Factory(t) { return new (t || QuestionScaleModule)(); };
QuestionScaleModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionScaleModule });
QuestionScaleModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({});
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionScaleModule, [{
        type: NgModule,
        args: [{
                declarations: [
                    QuestionScaleComponent
                ],
                exports: [
                    QuestionScaleComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionScaleModule, { declarations: [QuestionScaleComponent], exports: [QuestionScaleComponent] }); })();

class QuestionNumberModule {
}
QuestionNumberModule.ɵfac = function QuestionNumberModule_Factory(t) { return new (t || QuestionNumberModule)(); };
QuestionNumberModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionNumberModule });
QuestionNumberModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        FormsModule,
        SurveyMaterialModule,
        QuestionTitleModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionNumberModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    SurveyMaterialModule,
                    QuestionTitleModule
                ],
                declarations: [
                    QuestionNumberComponent
                ],
                exports: [
                    QuestionNumberComponent,
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionNumberModule, { declarations: [QuestionNumberComponent], imports: [CommonModule,
        FormsModule,
        SurveyMaterialModule,
        QuestionTitleModule], exports: [QuestionNumberComponent] }); })();

class SurveyQuestionsModule {
}
SurveyQuestionsModule.ɵfac = function SurveyQuestionsModule_Factory(t) { return new (t || SurveyQuestionsModule)(); };
SurveyQuestionsModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: SurveyQuestionsModule });
SurveyQuestionsModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        SurveyMaterialModule,
        YesNoUncertainQuestionModule,
        QuestionPicklistModule,
        QuestionTextModule,
        QuestionDatePickerModule,
        QuestionScaleModule,
        QuestionNumberModule,
        BooleanQuestionModule,
        QuestionChecklistModule,
        QuestionRadiosetModule,
        QuestionTextareaModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurveyQuestionsModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    SurveyMaterialModule,
                    YesNoUncertainQuestionModule,
                    QuestionPicklistModule,
                    QuestionTextModule,
                    QuestionDatePickerModule,
                    QuestionScaleModule,
                    QuestionNumberModule,
                    BooleanQuestionModule,
                    QuestionChecklistModule,
                    QuestionRadiosetModule,
                    QuestionTextareaModule
                ],
                declarations: [
                    SurveyQuestionComponent,
                ],
                exports: [
                    SurveyQuestionComponent,
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SurveyQuestionsModule, { declarations: [SurveyQuestionComponent], imports: [CommonModule,
        SurveyMaterialModule,
        YesNoUncertainQuestionModule,
        QuestionPicklistModule,
        QuestionTextModule,
        QuestionDatePickerModule,
        QuestionScaleModule,
        QuestionNumberModule,
        BooleanQuestionModule,
        QuestionChecklistModule,
        QuestionRadiosetModule,
        QuestionTextareaModule], exports: [SurveyQuestionComponent] }); })();

function QuestionListComponent_div_1_span_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 6);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const question_r1 = i0.ɵɵnextContext().$implicit;
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1("", ctx_r3.getQuestionIndex(question_r1), ".");
} }
function QuestionListComponent_div_1_ng_container_3_div_1_Template(rf, ctx) { if (rf & 1) {
    const _r9 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 8)(1, "survey-question", 4);
    i0.ɵɵlistener("change", function QuestionListComponent_div_1_ng_container_3_div_1_Template_survey_question_change_1_listener($event) { i0.ɵɵrestoreView(_r9); const ctx_r8 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r8.onQuestionChange($event)); });
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const conditionalQuestion_r6 = i0.ɵɵnextContext().$implicit;
    const ctx_r7 = i0.ɵɵnextContext(2);
    i0.ɵɵclassProp("disabled", ctx_r7.isQuestionDisabled(conditionalQuestion_r6))("current", conditionalQuestion_r6 === ctx_r7.currentQuestion)("complete", conditionalQuestion_r6.response !== undefined)("incomplete", conditionalQuestion_r6.response === undefined && ctx_r7.linear && !conditionalQuestion_r6.optional)("invalid", conditionalQuestion_r6.error);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("question", conditionalQuestion_r6)("survey", ctx_r7.survey)("disabled", ctx_r7.isQuestionDisabled(conditionalQuestion_r6));
} }
function QuestionListComponent_div_1_ng_container_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, QuestionListComponent_div_1_ng_container_3_div_1_Template, 2, 13, "div", 7);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const conditionalQuestion_r6 = ctx.$implicit;
    const question_r1 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", question_r1.isConditionSatisfied(conditionalQuestion_r6));
} }
function QuestionListComponent_div_1_Template(rf, ctx) { if (rf & 1) {
    const _r13 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 2);
    i0.ɵɵtemplate(1, QuestionListComponent_div_1_span_1_Template, 2, 1, "span", 3);
    i0.ɵɵelementStart(2, "survey-question", 4);
    i0.ɵɵlistener("change", function QuestionListComponent_div_1_Template_survey_question_change_2_listener($event) { i0.ɵɵrestoreView(_r13); const ctx_r12 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r12.onQuestionChange($event)); });
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(3, QuestionListComponent_div_1_ng_container_3_Template, 2, 1, "ng-container", 5);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const question_r1 = ctx.$implicit;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵclassProp("disabled", ctx_r0.isQuestionDisabled(question_r1))("current", question_r1 === ctx_r0.currentQuestion || ctx_r0.currentQuestion && ctx_r0.currentQuestion.optional && ctx_r0.survey.isQuestionAfter(question_r1, ctx_r0.currentQuestion))("complete", question_r1.response !== undefined)("incomplete", question_r1.response === undefined && ctx_r0.linear)("invalid", question_r1.error);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r0.enumerate);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("question", question_r1)("survey", ctx_r0.survey)("disabled", ctx_r0.isQuestionDisabled(question_r1));
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", question_r1 == null ? null : question_r1.conditionalQuestions == null ? null : question_r1.conditionalQuestions.items);
} }
class QuestionListComponent {
    // this needs to stay, seems to trigger the change detection needed for the css classes
    onKeyDown(e) { }
    constructor(cd, zone) {
        this.cd = cd;
        this.zone = zone;
        this.questions = [];
        this.linear = true;
        this.enumerate = false;
        this.complete = new EventEmitter();
    }
    get currentQuestion() {
        return this._currentQuestion;
    }
    get nextQuestion() {
        return this._getNextUnansweredQuestion(this._currentQuestion);
    }
    getQuestionIndex(question) {
        return this.survey ? this.survey.getQuestionNumerateIndex(question) : 0;
    }
    /**
     * bind to break point of 890px to set the stepper to a collapsed state
     */
    ngOnInit() {
        this._currentQuestion = this._getNextUnansweredQuestion();
        this.questionsChange = combineLatest(this.questions.map(q => q.events)).subscribe(e => {
            this.cd.detectChanges();
        });
    }
    ngAfterViewInit() {
        this.zone.onStable.pipe(take(1))
            .subscribe(() => this.items.first.focus());
    }
    ngOnDestroy() {
        this.questionsChange.unsubscribe();
    }
    isQuestionDisabled(question) {
        if (this.disabled) {
            return true;
        }
        if (!this.linear) {
            return false;
        }
        // if it's been answered it's editable
        if (Object.keys(question).includes('response') && question.response !== null) { // null is a skipped question
            return false;
        }
        // enable the next question as soon as there is a value in the response ( don't wait until change event, this allows for better tabbing )
        if (question === this.nextQuestion && this.currentQuestion?.response) {
            return false;
        }
        // if the next question to be answered is optional and this question comes after it, it's enabled
        if (this.nextQuestion?.optional && this.survey.isQuestionAfter(question, this.nextQuestion)) {
            return false;
        }
        // if the question is optional, and we have reached the later questions, it's enabled
        if (question.optional && (this.survey.isQuestionEarlier(question, this.currentQuestion) || this.currentQuestion === null)) {
            return false;
        }
        if (question.conditional) {
            const pq = this._findParentQuestionforConditionalQuestion(question);
            if (Object.keys(pq).includes('response') && question.optional) {
                return false;
            }
        }
        // if it's not the current question it's disabled
        if (this.currentQuestion !== question) {
            return true;
        }
        return false;
    }
    /**
     * When a question changes
     *   - dispatch complete event if no more unanswered questions
     *   - scroll to and focus next element ( if changed question is first change )
     */
    onQuestionChange(e) {
        //const questionElement = ( e.target as HTMLElement ).closest( '.question' );
        this.cd.detach();
        this._currentQuestion = this._getNextUnansweredQuestion();
        if (this._currentQuestion) {
            this._currentQuestion.error = null;
        }
        this.cd.detectChanges();
        this.cd.reattach();
        if (!this._currentQuestion) {
            this.complete.emit();
        }
        if (e.isFirstChange) {
            // 	const index = this._currentQuestion ? this.questions.indexOf( this._currentQuestion ) : Array.from( questionElement.parentElement.children ).indexOf( questionElement );
            // 	// focus the next survey-question
            // 	let questionComponent = Array.from( this.items )[ index ];
            let questionComponent;
            const currentQuestion = this._currentQuestion || e.question;
            const questionComponentArray = Array.from(this.items);
            for (let component of questionComponentArray) {
                if (component.question === currentQuestion) {
                    questionComponent = component;
                }
            }
            setTimeout(() => {
                questionComponent.focus();
                const questionEl = questionComponent.element.closest('.question ');
                window.scrollTo({
                    top: questionEl.offsetTop - questionEl.offsetHeight - 40,
                    behavior: 'smooth'
                });
            });
        }
    }
    _getNextUnansweredQuestion(startFromQuestion = null) {
        let startIndex = 0;
        if (startFromQuestion !== null) {
            if (!startFromQuestion.conditional) {
                startIndex = this.questions.indexOf(startFromQuestion);
            }
            else {
                const parentQuestion = this._findParentQuestionforConditionalQuestion(startFromQuestion);
                startIndex = this.questions.indexOf(parentQuestion);
            }
        }
        const len = this.questions.length;
        for (let i = startIndex; i < len; i++) {
            if (this.questions[i].response === undefined
                || (!this.questions[i].meta.optionalText && this.questions[i].response === null) // null needs to be allowed as a recorded answer, a checkbox for an optional sets it as null, however maybe we need to check this against the optional settings
            ) {
                // skipped optional question
                if (this.questions[i].optional && this.survey.hasAnsweredQuestionAfter(this.questions[i])) {
                    continue;
                }
                return this.questions[i];
            }
            if (typeof this.questions[i].conditionalQuestions !== 'undefined') {
                for (let conditionalQuestion of this.questions[i].conditionalQuestions.items) {
                    conditionalQuestion = conditionalQuestion;
                    if (this.questions[i].isConditionSatisfied(conditionalQuestion) && typeof conditionalQuestion.response == "undefined" && !conditionalQuestion.optional) {
                        return conditionalQuestion;
                    }
                }
            }
        }
        return null;
    }
    _findParentQuestionforConditionalQuestion(conditionalQuestion) {
        for (const question of this.questions) {
            if (typeof question.conditionalQuestions !== 'undefined' && question.conditionalQuestions.items.includes(conditionalQuestion)) {
                return question;
            }
        }
        return null;
    }
}
QuestionListComponent.ɵfac = function QuestionListComponent_Factory(t) { return new (t || QuestionListComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.NgZone)); };
QuestionListComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: QuestionListComponent, selectors: [["question-list"]], viewQuery: function QuestionListComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(SurveyQuestionComponent, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.items = _t);
    } }, hostBindings: function QuestionListComponent_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("keydown", function QuestionListComponent_keydown_HostBindingHandler($event) { return ctx.onKeyDown($event); });
    } }, inputs: { disabled: "disabled", questions: "questions", linear: "linear", enumerate: "enumerate", survey: "survey" }, outputs: { complete: "complete" }, decls: 2, vars: 1, consts: [[1, "spacing-lg"], ["class", "question", 3, "disabled", "current", "complete", "incomplete", "invalid", 4, "ngFor", "ngForOf"], [1, "question"], ["class", "question-index", 4, "ngIf"], [3, "question", "survey", "disabled", "change"], [4, "ngFor", "ngForOf"], [1, "question-index"], ["class", "question conditional", 3, "disabled", "current", "complete", "incomplete", "invalid", 4, "ngIf"], [1, "question", "conditional"]], template: function QuestionListComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵtemplate(1, QuestionListComponent_div_1_Template, 4, 15, "div", 1);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngForOf", ctx.questions);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, SurveyQuestionComponent], styles: ["[_nghost-%COMP%]{display:block}.question[_ngcontent-%COMP%]{transition:opacity .2s ease-out;display:flex}.question-index[_ngcontent-%COMP%]{margin-right:6px}.question[_ngcontent-%COMP%]:not(.current).incomplete, .question.disabled[_ngcontent-%COMP%]{opacity:.6}.question[_ngcontent-%COMP%]   h6[_ngcontent-%COMP%]{margin-bottom:12}.question.conditional[_ngcontent-%COMP%]{flex:1 0 100%}.question.conditional[_ngcontent-%COMP%]:last-child{margin-bottom:0}mat-radio-group[_ngcontent-%COMP%]{display:flex;align-items:center;justify-content:space-between;width:100%;max-width:320px}"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionListComponent, [{
        type: Component,
        args: [{ selector: 'question-list', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"spacing-lg\">\n\t<div *ngFor=\"let question of questions; index as i\"\n\t\tclass=\"question\"\n\t\t[class.disabled]=\"isQuestionDisabled(question)\"\n\t\t[class.current]=\"question === currentQuestion || ( currentQuestion && currentQuestion.optional && survey.isQuestionAfter( question, currentQuestion ) )\"\n\t\t[class.complete]=\"question.response !== undefined\"\n\t\t[class.incomplete]=\"question.response === undefined && linear\"\n\t\t[class.invalid]=\"question.error\">\n\n\t\t<span *ngIf=\"enumerate\"\n\t\t\tclass=\"question-index\">{{ getQuestionIndex(question) }}.</span>\n\n\t\t<survey-question [question]=\"question\"\n\t\t\t\t\t\t[survey]=\"survey\"\n\t\t\t\t\t\t[disabled]=\"isQuestionDisabled(question)\"\n\t\t\t\t\t\t(change)=\"onQuestionChange($event)\"></survey-question>\n\n\n\t\t<ng-container *ngFor=\"let conditionalQuestion of question?.conditionalQuestions?.items\">\n\t\t\t<div *ngIf=\"question.isConditionSatisfied(conditionalQuestion)\"\n\t\t\t\tclass=\"question conditional\"\n\t\t\t\t[class.disabled]=\"isQuestionDisabled(conditionalQuestion)\"\n\t\t\t\t[class.current]=\"conditionalQuestion === currentQuestion\"\n\t\t\t\t[class.complete]=\"conditionalQuestion.response !== undefined\"\n\t\t\t\t[class.incomplete]=\"conditionalQuestion.response === undefined && linear && !conditionalQuestion.optional\"\n\t\t\t\t[class.invalid]=\"conditionalQuestion.error\">\n\n\t\t\t\t<survey-question [question]=\"conditionalQuestion\"\n\t\t\t\t\t\t\t\t[survey]=\"survey\"\n\t\t\t\t\t\t\t\t[disabled]=\"isQuestionDisabled(conditionalQuestion)\"\n\t\t\t\t\t\t\t\t(change)=\"onQuestionChange($event)\"></survey-question>\n\n\t\t\t</div>\n\t\t</ng-container>\n\n\t</div>\n</div>", styles: [":host{display:block}.question{transition:opacity .2s ease-out;display:flex}.question-index{margin-right:6px}.question:not(.current).incomplete,.question.disabled{opacity:.6}.question h6{margin-bottom:12}.question.conditional{flex:1 0 100%}.question.conditional:last-child{margin-bottom:0}mat-radio-group{display:flex;align-items:center;justify-content:space-between;width:100%;max-width:320px}\n"] }]
    }], function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.NgZone }]; }, { disabled: [{
            type: Input
        }], questions: [{
            type: Input
        }], linear: [{
            type: Input
        }], enumerate: [{
            type: Input
        }], survey: [{
            type: Input
        }], complete: [{
            type: Output
        }], items: [{
            type: ViewChildren,
            args: [SurveyQuestionComponent]
        }], onKeyDown: [{
            type: HostListener,
            args: ['keydown', ['$event']]
        }] }); })();

class QuestionListModule {
}
QuestionListModule.ɵfac = function QuestionListModule_Factory(t) { return new (t || QuestionListModule)(); };
QuestionListModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: QuestionListModule });
QuestionListModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        SurveyMaterialModule,
        SurveyQuestionsModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionListModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    SurveyMaterialModule,
                    SurveyQuestionsModule
                ],
                declarations: [
                    QuestionListComponent
                ],
                exports: [
                    QuestionListComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(QuestionListModule, { declarations: [QuestionListComponent], imports: [CommonModule,
        SurveyMaterialModule,
        SurveyQuestionsModule], exports: [QuestionListComponent] }); })();

class QuestionsPage {
    constructor(title = '') {
        this.title = title;
        this.items = [];
        this.groups = [];
    }
    add(group) {
        this.items = this.items.concat(group.items);
        this.groups.push(group);
    }
    get enumerate() {
        for (let i = 0; i < this.groups.length; i++) {
            if (this.groups[i].enumerate) {
                return true;
            }
        }
        return false;
    }
    get complete() {
        for (let i = 0; i < this.groups.length; i++) {
            if (!this.groups[i].complete) {
                return false;
            }
        }
        return true;
    }
    validate() {
        let valid = true;
        for (let i = 0; i < this.groups.length; i++) {
            if (!this.groups[i].validate()) {
                valid = false;
            }
        }
        return valid;
    }
}

class SurveyQuestionsPipe {
    transform(surveyItems, args = null) {
        let questions = [];
        surveyItems.forEach(item => this.mapQuestions(item, questions));
        if (args?.orderCustom) {
            questions = questions.sort((a, b) => {
                let an = parseInt(a.meta.customOrder ?? questions.length);
                let bn = parseInt(b.meta.customOrder ?? questions.length);
                if (an < bn) {
                    return -1;
                }
                if (an > bn) {
                    return 1;
                }
                return 0;
            });
        }
        return questions;
    }
    mapQuestions(item, questions) {
        if (item.type === SurveyItemType.QUESTION) {
            questions.push(item);
        }
        else if (item.type === SurveyItemType.QUESTION_GROUP) {
            item.items.forEach(subItem => this.mapQuestions(subItem, questions));
        }
    }
}
SurveyQuestionsPipe.ɵfac = function SurveyQuestionsPipe_Factory(t) { return new (t || SurveyQuestionsPipe)(); };
SurveyQuestionsPipe.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "surveyQuestions", type: SurveyQuestionsPipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurveyQuestionsPipe, [{
        type: Pipe,
        args: [{
                name: 'surveyQuestions'
            }]
    }], null, null); })();
class SurveyGroupsPipe {
    transform(rootItems) {
        if (this.groups) {
            return this.groups;
        }
        this.groups = this.buildPages(rootItems);
        return this.groups;
    }
    buildPages(rootItems) {
        let currentPage = new QuestionsPage();
        let groups = [];
        const hasPagesInChildren = (item) => {
            if (item.type === SurveyItemType.QUESTION) {
                return false;
            }
            for (let child of item.items) {
                if (child.meta.ownPage) {
                    return true;
                }
                if (hasPagesInChildren(child) === true) {
                    return true;
                }
            }
            return false;
        };
        const _processItems = (items) => {
            for (let item of items) {
                if (hasPagesInChildren(item)) {
                    _processItems(item.items);
                    continue;
                }
                // the next item wants to be on it's own page
                if (item.meta.ownPage) {
                    // if there are items in the current page, push them onto the pages array and create a new one for this group
                    if (currentPage.items.length) {
                        groups.push(currentPage);
                    }
                    // add this group to it's own page
                    currentPage = new QuestionsPage(item.title);
                    currentPage.add(item);
                    groups.push(currentPage);
                    // create the next page
                    currentPage = new QuestionsPage();
                }
                // item is not on it's own page, add it to the current page collection
                else {
                    currentPage.title = item.title;
                    currentPage.add(item);
                }
            }
        };
        _processItems(rootItems);
        // push the last page onto the pages array
        if (currentPage.items.length) {
            groups.push(currentPage);
        }
        return groups;
    }
}
SurveyGroupsPipe.ɵfac = function SurveyGroupsPipe_Factory(t) { return new (t || SurveyGroupsPipe)(); };
SurveyGroupsPipe.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "surveyGroups", type: SurveyGroupsPipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurveyGroupsPipe, [{
        type: Pipe,
        args: [{
                name: 'surveyGroups'
            }]
    }], null, null); })();

const _c0$3 = ["*"];
class MrbButtonsComponent {
    constructor() {
        this.disabled = false;
        this.size = "small";
        this.variant = "primary";
        this.buttonClick = new EventEmitter();
        this.mouseDown = new EventEmitter();
    }
    onClick() {
        if (!this.disabled) {
            this.buttonClick.emit();
        }
    }
    onMouseDown() {
        this.mouseDown.emit();
    }
}
MrbButtonsComponent.ɵfac = function MrbButtonsComponent_Factory(t) { return new (t || MrbButtonsComponent)(); };
MrbButtonsComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: MrbButtonsComponent, selectors: [["lib-mrb-buttons"]], inputs: { disabled: "disabled", size: "size", variant: "variant" }, outputs: { buttonClick: "buttonClick", mouseDown: "mouseDown" }, ngContentSelectors: _c0$3, decls: 2, vars: 3, consts: [[3, "disabled", "mousedown", "click"]], template: function MrbButtonsComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "button", 0);
        i0.ɵɵlistener("mousedown", function MrbButtonsComponent_Template_button_mousedown_0_listener() { return ctx.onMouseDown(); })("click", function MrbButtonsComponent_Template_button_click_0_listener() { return ctx.onClick(); });
        i0.ɵɵprojection(1);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("disabled", ctx.disabled);
        i0.ɵɵattribute("variant", ctx.variant)("size", ctx.size);
    } }, styles: ["button[_ngcontent-%COMP%]{line-height:1.1;font-weight:500;min-width:7rem;box-sizing:border-box;cursor:pointer;text-align:center;border-radius:100px;width:100%}button[_ngcontent-%COMP%]:hover{transition:background-color .25s ease-out,color .25s ease-out}button[variant=primary][_ngcontent-%COMP%]{background-color:#e42312;color:#fff;border:0}button[variant=primary][_ngcontent-%COMP%]:hover{background-color:#b81c0e}button[variant=primary][_ngcontent-%COMP%]:disabled{background-color:#e5e4e3;pointer-events:none;transition:background-color 0ms,color 0ms}button[variant=primary][_ngcontent-%COMP%]:disabled:hover{background-color:#e5e4e3}button[variant=secondary][_ngcontent-%COMP%]{border:2px solid #9b9b9b;background:#fff;color:#212122}button[variant=secondary][_ngcontent-%COMP%]:hover{color:#09090a;border-color:#6b6a6a;background-color:#e5e4e3}button[variant=secondary][_ngcontent-%COMP%]:disabled{color:#cccbcb;border-color:#e5e4e3;pointer-events:none}button[variant=secondary][_ngcontent-%COMP%]:disabled:hover{background-color:#fff}button[size=large][_ngcontent-%COMP%]{font-size:16px;min-height:3.5rem;min-width:10rem;padding:.25rem 1.5rem}button[size=small][_ngcontent-%COMP%]{font-size:14px;min-height:2.5rem;min-width:8rem;padding:.25rem 1.25rem}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MrbButtonsComponent, [{
        type: Component,
        args: [{ selector: 'lib-mrb-buttons', template: "<button\n  [disabled]=\"disabled\"\n  [attr.variant]=\"variant\"\n  [attr.size]=\"size\"\n  (mousedown)=\"onMouseDown()\"\n  (click)=\"onClick()\"\n>\n  <ng-content></ng-content>\n</button>\n", styles: ["button{line-height:1.1;font-weight:500;min-width:7rem;box-sizing:border-box;cursor:pointer;text-align:center;border-radius:100px;width:100%}button:hover{transition:background-color .25s ease-out,color .25s ease-out}button[variant=primary]{background-color:#e42312;color:#fff;border:0}button[variant=primary]:hover{background-color:#b81c0e}button[variant=primary]:disabled{background-color:#e5e4e3;pointer-events:none;transition:background-color 0ms,color 0ms}button[variant=primary]:disabled:hover{background-color:#e5e4e3}button[variant=secondary]{border:2px solid #9b9b9b;background:#fff;color:#212122}button[variant=secondary]:hover{color:#09090a;border-color:#6b6a6a;background-color:#e5e4e3}button[variant=secondary]:disabled{color:#cccbcb;border-color:#e5e4e3;pointer-events:none}button[variant=secondary]:disabled:hover{background-color:#fff}button[size=large]{font-size:16px;min-height:3.5rem;min-width:10rem;padding:.25rem 1.5rem}button[size=small]{font-size:14px;min-height:2.5rem;min-width:8rem;padding:.25rem 1.25rem}\n"] }]
    }], function () { return []; }, { disabled: [{
            type: Input
        }], size: [{
            type: Input
        }], variant: [{
            type: Input
        }], buttonClick: [{
            type: Output
        }], mouseDown: [{
            type: Output
        }] }); })();

class MrbButtonsModule {
}
MrbButtonsModule.ɵfac = function MrbButtonsModule_Factory(t) { return new (t || MrbButtonsModule)(); };
MrbButtonsModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: MrbButtonsModule });
MrbButtonsModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MrbButtonsModule, [{
        type: NgModule,
        args: [{
                declarations: [
                    MrbButtonsComponent
                ],
                imports: [
                    CommonModule
                ],
                exports: [
                    MrbButtonsComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(MrbButtonsModule, { declarations: [MrbButtonsComponent], imports: [CommonModule], exports: [MrbButtonsComponent] }); })();

class SurveyModule {
}
SurveyModule.ɵfac = function SurveyModule_Factory(t) { return new (t || SurveyModule)(); };
SurveyModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: SurveyModule });
SurveyModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        SurveyMaterialModule,
        SurveyQuestionsModule,
        QuestionListModule,
        MrbButtonsModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurveyModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    SurveyMaterialModule,
                    SurveyQuestionsModule,
                    QuestionListModule,
                    MrbButtonsModule
                ],
                declarations: [
                    SurveyComponent,
                    SurveyQuestionsPipe,
                    SurveyGroupsPipe
                ],
                exports: [
                    SurveyComponent,
                    SurveyGroupsPipe
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SurveyModule, { declarations: [SurveyComponent,
        SurveyQuestionsPipe,
        SurveyGroupsPipe], imports: [CommonModule,
        SurveyMaterialModule,
        SurveyQuestionsModule,
        QuestionListModule,
        MrbButtonsModule], exports: [SurveyComponent,
        SurveyGroupsPipe] }); })();
i0.ɵɵsetComponentScope(SurveyComponent, [i1$2.NgForOf, i1$2.NgIf, i2.MatButton, i1$1.CdkTrapFocus, i8.MatTooltip, QuestionListComponent, MrbButtonsComponent], [SurveyQuestionsPipe,
    SurveyGroupsPipe]);

/**
 * This is a default interceptor that just passes the request on.
 * It can be overriden using the 'httpInterceptor' property on the module config
 */
class AuthInterceptor {
    intercept(req, next) {
        return next.handle(req);
    }
}
AuthInterceptor.ɵfac = function AuthInterceptor_Factory(t) { return new (t || AuthInterceptor)(); };
AuthInterceptor.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AuthInterceptor, factory: AuthInterceptor.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthInterceptor, [{
        type: Injectable
    }], null, null); })();

function surveyProviderFactory() {
    return new Survey();
}
const defaultSurveyOptions$1 = {
    showRequiredAsterisks: false,
    showOptionalLabel: false,
    theme: ''
};
class SurveysModule {
    static forRoot(config = {}) {
        return {
            ngModule: SurveysModule,
            providers: [
                {
                    provide: WP_SITE_URL,
                    useValue: config.wpSiteUrl || './'
                }, {
                    provide: HTTP_INTERCEPTORS,
                    useClass: config.httpInterceptor || AuthInterceptor,
                    multi: true
                }
            ]
        };
    }
    static forChild(config = {}) {
        return {
            ngModule: SurveysModule,
            providers: [
                {
                    provide: WP_SITE_URL,
                    useValue: config.wpSiteUrl || './'
                }, {
                    provide: HTTP_INTERCEPTORS,
                    useExisting: config.httpInterceptor || AuthInterceptor,
                    multi: true
                }
            ]
        };
    }
}
SurveysModule.ɵfac = function SurveysModule_Factory(t) { return new (t || SurveysModule)(); };
SurveysModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: SurveysModule });
SurveysModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [
        SurveyService,
        {
            provide: Survey,
            useFactory: surveyProviderFactory
        },
        {
            provide: ALPHA_SURVEYS_OPTIONS, useValue: defaultSurveyOptions$1
        }
    ], imports: [HttpClientModule, SurveyModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurveysModule, [{
        type: NgModule,
        args: [{
                imports: [
                    HttpClientModule
                ],
                exports: [
                    SurveyModule
                ],
                providers: [
                    SurveyService,
                    {
                        provide: Survey,
                        useFactory: surveyProviderFactory
                    },
                    {
                        provide: ALPHA_SURVEYS_OPTIONS, useValue: defaultSurveyOptions$1
                    }
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SurveysModule, { imports: [HttpClientModule], exports: [SurveyModule] }); })();

/*
 * Public API Surface of surveys
 */

var CompareUserSurveyQuestionState;
(function (CompareUserSurveyQuestionState) {
    CompareUserSurveyQuestionState[CompareUserSurveyQuestionState["MATCH_SUCCESS"] = 0] = "MATCH_SUCCESS";
    CompareUserSurveyQuestionState[CompareUserSurveyQuestionState["MISMATCH"] = 1] = "MISMATCH";
    CompareUserSurveyQuestionState[CompareUserSurveyQuestionState["MATCH_FAIL"] = 2] = "MATCH_FAIL";
})(CompareUserSurveyQuestionState || (CompareUserSurveyQuestionState = {}));
/**
 * Compare User Survey Report
 * Take 1 or more UserSurveys and combine the responses, setting state on the Questions
 */
class CompareUserSurveyReport {
    constructor(surveys) {
        this.surveys = surveys;
        let errors = {};
        // all surveys must be complete
        surveys.forEach(survey => {
            if (!survey.complete) {
                errors.surveys_incomplete = true;
            }
        });
        if (Object.keys(errors).length) {
            this.errors = errors;
            return;
        }
        // clone the survey from the first user to use as structure
        const survey = surveys[0].survey.clone();
        survey.items.forEach((item, index) => this.assignResponses(item, index));
        this.compiledSurvey = new Survey(survey);
        this.questionGroups = this.initQuestionGroups();
    }
    initQuestionGroups() {
        const getGroups = (item, groups) => {
            if (item.type === SurveyItemType.QUESTION) {
                return [];
            }
            const q = item.items.find(i => i.type === SurveyItemType.QUESTION);
            if (q) {
                groups.push(item);
            }
            item.items.forEach(subItem => getGroups(subItem, groups));
        };
        let allGroups = [];
        this.compiledSurvey.items.forEach(item => getGroups(item, allGroups));
        return allGroups;
    }
    /**
     * Handle traversing a QuestionGroup or Question
     * Return the number of matches in a group
     * @param number
     */
    assignResponses(item, index, subIndex = '') {
        if (item.type === SurveyItemType.QUESTION_GROUP) {
            let groupItemMatches = 0;
            item.items.forEach((subItem, sIndex) => {
                let s = subIndex + '.' + (sIndex + 1);
                let matches = this.assignResponses(subItem, index, s);
                groupItemMatches += matches;
            });
            item.index = index + subIndex;
            let totalQuestions = item.questions.filter(q => q.meta.preferredResponse).length; // is a complex getter, hence the assignment here
            item.stats = {
                totalQuestions: totalQuestions,
                success: groupItemMatches,
                pct: groupItemMatches / totalQuestions
            };
            return groupItemMatches;
        }
        else {
            return this.handleUserQuestion(item);
        }
    }
    handleUserQuestion(item) {
        let i = 0, len = this.surveys.length;
        item.userResponses = [];
        for (i; i < len; i++) {
            let userSurvey = this.surveys[i];
            let userQuestion = userSurvey.survey.getQuestionById(item.id);
            let userResponse = userQuestion.response;
            item.userResponses.push(userResponse);
            /**
             * Assign the state for the individual question against the preferred response
             */
            if (!userQuestion.meta.preferredResponse) {
                userQuestion.state = CompareUserSurveyQuestionState.MATCH_SUCCESS;
            }
            else if (userQuestion.response === YNULabelValues.UNCERTAIN) {
                userQuestion.state = CompareUserSurveyQuestionState.MATCH_FAIL;
            }
            else if (userQuestion.response === userQuestion.meta.preferredResponse) {
                userQuestion.state = CompareUserSurveyQuestionState.MATCH_SUCCESS;
            }
            else {
                userQuestion.state = CompareUserSurveyQuestionState.MISMATCH;
            }
        }
        /**
         * Assign state of comparison between  guests
         */
        if (!item.meta.preferredResponse) {
            item.state = CompareUserSurveyQuestionState.MATCH_SUCCESS;
            return 0;
        }
        const uniqueAnswers = Array.from(new Set(item.userResponses));
        if (uniqueAnswers.length > 1) {
            item.state = CompareUserSurveyQuestionState.MISMATCH;
        }
        else if (uniqueAnswers.length === 1 && uniqueAnswers[0] === item.meta.preferredResponse) {
            item.state = CompareUserSurveyQuestionState.MATCH_SUCCESS;
        }
        else {
            item.state = CompareUserSurveyQuestionState.MATCH_FAIL;
        }
        return item.state === CompareUserSurveyQuestionState.MATCH_SUCCESS ? 1 : 0;
    }
}

const NG_MATERIALS = [
    MatCardModule,
    MatButtonModule
];
class ReportsMaterialModule {
}
ReportsMaterialModule.ɵfac = function ReportsMaterialModule_Factory(t) { return new (t || ReportsMaterialModule)(); };
ReportsMaterialModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: ReportsMaterialModule });
ReportsMaterialModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [MatCardModule,
        MatButtonModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ReportsMaterialModule, [{
        type: NgModule,
        args: [{
                exports: NG_MATERIALS
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(ReportsMaterialModule, { exports: [MatCardModule,
        MatButtonModule] }); })();

class QuestionResponsePipe {
    constructor(locale) {
        this.locale = locale;
    }
    transform(question, value) {
        value = value || question.response;
        switch (question.questionType) {
            case QuestionType.DATE:
                return this.transformDate(value, question);
            case QuestionType.TEXT:
            case QuestionType.TEXTAREA:
                return value;
            case QuestionType.YES_NO_UNCERTAIN:
                return this.transformYNU(value);
            case QuestionType.NUMBER:
                return this.transformNumber(value);
            case QuestionType.PICKLIST:
            case QuestionType.CHECKLIST:
            case QuestionType.RADIOSET:
                return this.transformListset(value, question.meta.listValues);
            default:
                return value;
        }
    }
    transformListset(value, options) {
        let listsetVal = options.find(p => p.id === value);
        if (listsetVal) {
            return listsetVal.label;
        }
        else {
            return `Listset value not found for id:${value}`;
        }
    }
    transformNumber(value) {
        if (!this.numberPipe) {
            this.numberPipe = new DecimalPipe(this.locale);
        }
        return this.numberPipe.transform(value);
    }
    transformDate(value, question) {
        if (!this.datePipe) {
            this.datePipe = new DatePipe(this.locale);
        }
        if (value) {
            return this.datePipe.transform(value);
        }
        else {
            return question.meta.optionalText;
        }
    }
    transformYNU(value) {
        if (!this.ynuLabelPipe) {
            this.ynuLabelPipe = new YesNoUncertainLabelPipe();
        }
        return this.ynuLabelPipe.transform(value);
    }
}
QuestionResponsePipe.ɵfac = function QuestionResponsePipe_Factory(t) { return new (t || QuestionResponsePipe)(i0.ɵɵdirectiveInject(LOCALE_ID, 16)); };
QuestionResponsePipe.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "questionResponse", type: QuestionResponsePipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QuestionResponsePipe, [{
        type: Pipe,
        args: [{
                name: 'questionResponse'
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [LOCALE_ID]
            }] }]; }, null); })();

function CompareQuestionGroupComponent_div_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "div", 5);
} }
function CompareQuestionGroupComponent_div_11_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 5);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const userSurvey_r3 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(userSurvey_r3.meta.name);
} }
function CompareQuestionGroupComponent_div_13_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 16);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const question_r4 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1("Q", question_r4.meta.customOrder, "");
} }
function CompareQuestionGroupComponent_div_13_span_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "questionResponse");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const question_r4 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, question_r4, question_r4.meta.preferredResponse), " ");
} }
function CompareQuestionGroupComponent_div_13_div_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 5);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "questionResponse");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const userResponse_r11 = ctx.$implicit;
    const question_r4 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵclassProp("color-accent", question_r4.meta.preferredResponse && userResponse_r11 !== question_r4.meta.preferredResponse);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind2(2, 3, question_r4, userResponse_r11));
} }
function CompareQuestionGroupComponent_div_13_p_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p", 17);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const question_r4 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(question_r4.meta.feedbackMismatch);
} }
function CompareQuestionGroupComponent_div_13_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 10);
    i0.ɵɵtemplate(1, CompareQuestionGroupComponent_div_13_div_1_Template, 2, 1, "div", 11);
    i0.ɵɵelementStart(2, "div", 5);
    i0.ɵɵtemplate(3, CompareQuestionGroupComponent_div_13_span_3_Template, 3, 4, "span", 12);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(4, CompareQuestionGroupComponent_div_13_div_4_Template, 3, 6, "div", 13);
    i0.ɵɵelementStart(5, "div", 14);
    i0.ɵɵtext(6);
    i0.ɵɵtemplate(7, CompareQuestionGroupComponent_div_13_p_7_Template, 2, 1, "p", 15);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const question_r4 = ctx.$implicit;
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r2.report.compiledSurvey.presentation === ctx_r2.SurveyPresentation.PSYCHOMETRIC);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", question_r4.meta.preferredResponse);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", question_r4.userResponses);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", question_r4.title, " ");
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", question_r4.state !== ctx_r2.CompareUserSurveyQuestionState.MATCH_SUCCESS);
} }
class CompareQuestionGroupComponent {
    constructor() {
        this.SurveyPresentation = SurveyPresentation;
        this.CompareUserSurveyQuestionState = CompareUserSurveyQuestionState;
    }
}
CompareQuestionGroupComponent.ɵfac = function CompareQuestionGroupComponent_Factory(t) { return new (t || CompareQuestionGroupComponent)(); };
CompareQuestionGroupComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: CompareQuestionGroupComponent, selectors: [["compare-question-group"]], inputs: { questionGroup: "questionGroup", report: "report" }, decls: 14, vars: 8, consts: function () { let i18n_0; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Common Words - Preferred
         * @meaning common.words
         */
        const MSG_EXTERNAL_commonWordPreferred$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_QUESTION_GROUP_QUESTION_GROUP_COMPONENT_TS_1 = goog.getMsg("Preferred");
        i18n_0 = MSG_EXTERNAL_commonWordPreferred$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_QUESTION_GROUP_QUESTION_GROUP_COMPONENT_TS_1;
    }
    else {
        i18n_0 = $localize `:common.words|Common Words - Preferred@@commonWordPreferred␟cebf36f1777b9089ce8027b21482a0e042357fdd␟3692161159662408398:Preferred`;
    } return [[1, "question-group-title"], [1, "title-index"], [1, "title-percent", "color-accent"], [1, "questions-header"], ["class", "question-column", 4, "ngIf"], [1, "question-column"], i18n_0, ["class", "question-column", 4, "ngFor", "ngForOf"], [1, "questions-list"], ["class", "question-report", 4, "ngFor", "ngForOf"], [1, "question-report"], ["class", "question-column order", 4, "ngIf"], [4, "ngIf"], ["class", "question-column", 3, "color-accent", 4, "ngFor", "ngForOf"], [1, "question-column", "title"], ["class", "color-accent feedback", 4, "ngIf"], [1, "question-column", "order"], [1, "color-accent", "feedback"]]; }, template: function CompareQuestionGroupComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "h2", 0)(1, "span", 1);
        i0.ɵɵtext(2);
        i0.ɵɵelementEnd();
        i0.ɵɵtext(3);
        i0.ɵɵelementStart(4, "span", 2);
        i0.ɵɵtext(5);
        i0.ɵɵpipe(6, "percent");
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(7, "div", 3);
        i0.ɵɵtemplate(8, CompareQuestionGroupComponent_div_8_Template, 1, 0, "div", 4);
        i0.ɵɵelementStart(9, "div", 5);
        i0.ɵɵi18n(10, 6);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(11, CompareQuestionGroupComponent_div_11_Template, 2, 1, "div", 7);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(12, "div", 8);
        i0.ɵɵtemplate(13, CompareQuestionGroupComponent_div_13_Template, 8, 5, "div", 9);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵtextInterpolate(ctx.questionGroup.index);
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate1(" ", ctx.questionGroup.title, " ");
        i0.ɵɵadvance(2);
        i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(6, 6, ctx.questionGroup.stats.pct));
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("ngIf", ctx.report.compiledSurvey.presentation === ctx.SurveyPresentation.PSYCHOMETRIC);
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("ngForOf", ctx.report.surveys);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngForOf", ctx.questionGroup.items);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i1$2.PercentPipe, QuestionResponsePipe], styles: ["[_nghost-%COMP%]{display:block}.questions-header[_ngcontent-%COMP%]{display:flex;align-items:center;margin-bottom:24px}.question-group-title[_ngcontent-%COMP%]{margin-bottom:35px}.title-percent[_ngcontent-%COMP%]{margin-left:18px}.question-column[_ngcontent-%COMP%]{width:100px;margin-right:25px;flex:0 0 100px}.question-column.title[_ngcontent-%COMP%]{width:auto;margin-right:0;flex:1 1 auto}.question-column.order[_ngcontent-%COMP%]{color:#e42312}.question-report[_ngcontent-%COMP%]{display:flex;align-items:center;border-top:1px solid #ccc;padding:25px 0}@media print{.question-report[_ngcontent-%COMP%]{padding:11px 0;break-inside:avoid-page}}.feedback[_ngcontent-%COMP%]{font-style:italic;white-space:pre-wrap}.feedback[_ngcontent-%COMP%]:last-child{margin-bottom:0}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CompareQuestionGroupComponent, [{
        type: Component,
        args: [{ selector: 'compare-question-group', template: "<h2 class=\"question-group-title\">\n\t<span class=\"title-index\">{{questionGroup.index}}</span>\n\t{{questionGroup.title}}\n\t<span class=\"title-percent color-accent\">{{questionGroup.stats.pct | percent }}</span>\n</h2>\n\n<div class=\"questions-header\">\n\n\t<div class=\"question-column\"\n\t\t *ngIf=\"report.compiledSurvey.presentation === SurveyPresentation.PSYCHOMETRIC\"></div>\n\n\t<div class=\"question-column\"\n\t\t i18n=\"common.words|Common Words - Preferred@@commonWordPreferred\">Preferred</div>\n\t<div *ngFor=\"let userSurvey of report.surveys\"\n\t\t class=\"question-column\">{{userSurvey.meta.name}}</div>\n</div>\n\n<div class=\"questions-list\">\n\n\t<div *ngFor=\"let question of questionGroup.items\"\n\t\t class=\"question-report\">\n\n\t\t<div class=\"question-column order\"\n\t\t\t *ngIf=\"report.compiledSurvey.presentation === SurveyPresentation.PSYCHOMETRIC\">Q{{question.meta.customOrder}}</div>\n\t\t<div class=\"question-column\">\n\t\t\t<span *ngIf=\"question.meta.preferredResponse\">\n\t\t\t\t{{question | questionResponse:question.meta.preferredResponse }}\n\t\t\t</span>\n\t\t</div>\n\n\t\t<div *ngFor=\"let userResponse of question.userResponses\"\n\t\t\t class=\"question-column\"\n\t\t\t [class.color-accent]=\"question.meta.preferredResponse && userResponse !== question.meta.preferredResponse\">{{question | questionResponse:userResponse}}</div>\n\n\t\t<div class=\"question-column title\">\n\t\t\t{{question.title}}\n\t\t\t<p class=\"color-accent feedback\"\n\t\t\t   *ngIf=\"question.state !== CompareUserSurveyQuestionState.MATCH_SUCCESS\">{{question.meta.feedbackMismatch}}</p>\n\t\t</div>\n\n\n\t</div>\n\n</div>\n", styles: [":host{display:block}.questions-header{display:flex;align-items:center;margin-bottom:24px}.question-group-title{margin-bottom:35px}.title-percent{margin-left:18px}.question-column{width:100px;margin-right:25px;flex:0 0 100px}.question-column.title{width:auto;margin-right:0;flex:1 1 auto}.question-column.order{color:#e42312}.question-report{display:flex;align-items:center;border-top:1px solid #ccc;padding:25px 0}@media print{.question-report{padding:11px 0;break-inside:avoid-page}}.feedback{font-style:italic;white-space:pre-wrap}.feedback:last-child{margin-bottom:0}\n"] }]
    }], null, { questionGroup: [{
            type: Input
        }], report: [{
            type: Input
        }] }); })();

class GuestOverviewGroupsPipe {
    transform(surveyRootElements) {
        return surveyRootElements.filter(el => el.type === SurveyItemType.QUESTION_GROUP && el.meta.showOnGuestOverview);
    }
}
GuestOverviewGroupsPipe.ɵfac = function GuestOverviewGroupsPipe_Factory(t) { return new (t || GuestOverviewGroupsPipe)(); };
GuestOverviewGroupsPipe.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "guestOverviewGroups", type: GuestOverviewGroupsPipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GuestOverviewGroupsPipe, [{
        type: Pipe,
        args: [{
                name: 'guestOverviewGroups'
            }]
    }], null, null); })();

const _c0$2 = function (a0) { return { $implicit: a0 }; };
function GuestsOverviewComponent_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0, 3);
} if (rf & 2) {
    const survey_r3 = ctx.$implicit;
    i0.ɵɵnextContext();
    const _r1 = i0.ɵɵreference(3);
    i0.ɵɵproperty("ngTemplateOutlet", _r1)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c0$2, survey_r3));
} }
function GuestsOverviewComponent_ng_template_2_div_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 13)(1, "span");
    i0.ɵɵi18n(2, 14);
    i0.ɵɵelementEnd();
    i0.ɵɵtext(3, ": ");
    i0.ɵɵelementStart(4, "span");
    i0.ɵɵi18n(5, 15);
    i0.ɵɵelementEnd();
    i0.ɵɵtext(6);
    i0.ɵɵpipe(7, "percent");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const userSurvey_r4 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(6);
    i0.ɵɵtextInterpolate1(" (", i0.ɵɵpipeBind1(7, 1, userSurvey_r4.pctComplete), ") ");
} }
function GuestsOverviewComponent_ng_template_2_div_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 13)(1, "span");
    i0.ɵɵi18n(2, 16);
    i0.ɵɵelementEnd();
    i0.ɵɵtext(3, ": ");
    i0.ɵɵelementStart(4, "span");
    i0.ɵɵi18n(5, 17);
    i0.ɵɵelementEnd()();
} }
function GuestsOverviewComponent_ng_template_2_div_9_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 18);
    i0.ɵɵi18n(1, 19);
    i0.ɵɵpipe(2, "date");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const userSurvey_r4 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(2);
    i0.ɵɵi18nExp(i0.ɵɵpipeBind1(2, 1, userSurvey_r4.completed_on));
    i0.ɵɵi18nApply(1);
} }
function GuestsOverviewComponent_ng_template_2_div_15_div_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 23)(1, "span", 24);
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "span", 25);
    i0.ɵɵtext(4);
    i0.ɵɵpipe(5, "questionResponse");
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const question_r13 = ctx.$implicit;
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(question_r13.title);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(5, 2, question_r13));
} }
function GuestsOverviewComponent_ng_template_2_div_15_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 20)(1, "div", 21);
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(3, GuestsOverviewComponent_ng_template_2_div_15_div_3_Template, 6, 4, "div", 22);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const group_r11 = ctx.$implicit;
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(group_r11.title);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", group_r11.items);
} }
function GuestsOverviewComponent_ng_template_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "mat-card", 4)(1, "mat-card-header")(2, "mat-card-title", 5)(3, "span", 6);
    i0.ɵɵi18n(4, 7);
    i0.ɵɵelementEnd();
    i0.ɵɵtext(5);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(6, "mat-card-subtitle");
    i0.ɵɵtemplate(7, GuestsOverviewComponent_ng_template_2_div_7_Template, 8, 3, "div", 8);
    i0.ɵɵtemplate(8, GuestsOverviewComponent_ng_template_2_div_8_Template, 6, 0, "div", 8);
    i0.ɵɵtemplate(9, GuestsOverviewComponent_ng_template_2_div_9_Template, 3, 3, "div", 9);
    i0.ɵɵelementStart(10, "div", 10)(11, "span");
    i0.ɵɵi18n(12, 11);
    i0.ɵɵelementEnd();
    i0.ɵɵtext(13);
    i0.ɵɵelementEnd()()();
    i0.ɵɵelementStart(14, "mat-card-content");
    i0.ɵɵtemplate(15, GuestsOverviewComponent_ng_template_2_div_15_Template, 4, 2, "div", 12);
    i0.ɵɵpipe(16, "guestOverviewGroups");
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const userSurvey_r4 = ctx.$implicit;
    i0.ɵɵadvance(5);
    i0.ɵɵtextInterpolate1(":", userSurvey_r4.meta.name, " ");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", !userSurvey_r4.complete);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", userSurvey_r4.complete);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", userSurvey_r4.complete);
    i0.ɵɵadvance(4);
    i0.ɵɵtextInterpolate1(": ", userSurvey_r4.email, " ");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngForOf", i0.ɵɵpipeBind1(16, 6, userSurvey_r4.survey.items));
} }
class GuestsOverviewComponent {
}
GuestsOverviewComponent.ɵfac = function GuestsOverviewComponent_Factory(t) { return new (t || GuestsOverviewComponent)(); };
GuestsOverviewComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: GuestsOverviewComponent, selectors: [["guests-overview"]], inputs: { userSurveys: "userSurveys" }, decls: 4, vars: 1, consts: function () { let i18n_1; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Guest Name
         * @meaning guests
         */
        const MSG_EXTERNAL_guestName$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_GUESTS_OVERVIEW_GUESTS_OVERVIEW_COMPONENT_TS__2 = goog.getMsg("Guest Name");
        i18n_1 = MSG_EXTERNAL_guestName$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_GUESTS_OVERVIEW_GUESTS_OVERVIEW_COMPONENT_TS__2;
    }
    else {
        i18n_1 = $localize `:guests|Guest Name@@guestName␟4ec0161760045b229760c3c8de845b2ace207b11␟3446301320580350131:Guest Name`;
    } let i18n_3; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Email Label
         * @meaning common.label
         */
        const MSG_EXTERNAL_labelEmail$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_GUESTS_OVERVIEW_GUESTS_OVERVIEW_COMPONENT_TS__4 = goog.getMsg("Email");
        i18n_3 = MSG_EXTERNAL_labelEmail$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_GUESTS_OVERVIEW_GUESTS_OVERVIEW_COMPONENT_TS__4;
    }
    else {
        i18n_3 = $localize `:common.label|Email Label@@labelEmail␟3a8538623b708591ae702c35da146a763c88928c␟248144114869096051:Email`;
    } let i18n_5; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Common Words - Status
         * @meaning common.words
         */
        const MSG_EXTERNAL_commonWordStatus$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_GUESTS_OVERVIEW_GUESTS_OVERVIEW_COMPONENT_TS___6 = goog.getMsg("Status");
        i18n_5 = MSG_EXTERNAL_commonWordStatus$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_GUESTS_OVERVIEW_GUESTS_OVERVIEW_COMPONENT_TS___6;
    }
    else {
        i18n_5 = $localize `:common.words|Common Words - Status@@commonWordStatus␟6856d3c4eb4623491b66ec620968d0f9781416ad␟5948021482931605675:Status`;
    } let i18n_7; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Common Words - Incomplete
         * @meaning common.words
         */
        const MSG_EXTERNAL_commonWordIncomplete$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_GUESTS_OVERVIEW_GUESTS_OVERVIEW_COMPONENT_TS___8 = goog.getMsg("Incomplete");
        i18n_7 = MSG_EXTERNAL_commonWordIncomplete$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_GUESTS_OVERVIEW_GUESTS_OVERVIEW_COMPONENT_TS___8;
    }
    else {
        i18n_7 = $localize `:common.words|Common Words - Incomplete@@commonWordIncomplete␟dfd16d02b73541e7d6c304fe996565d6aa5188cd␟1202294036539313280:Incomplete`;
    } let i18n_9; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Common Words - Status
         * @meaning common.words
         */
        const MSG_EXTERNAL_commonWordStatus$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_GUESTS_OVERVIEW_GUESTS_OVERVIEW_COMPONENT_TS___10 = goog.getMsg("Status");
        i18n_9 = MSG_EXTERNAL_commonWordStatus$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_GUESTS_OVERVIEW_GUESTS_OVERVIEW_COMPONENT_TS___10;
    }
    else {
        i18n_9 = $localize `:common.words|Common Words - Status@@commonWordStatus␟6856d3c4eb4623491b66ec620968d0f9781416ad␟5948021482931605675:Status`;
    } let i18n_11; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Common Words - Complete
         * @meaning common.words
         */
        const MSG_EXTERNAL_commonWordsComplete$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_GUESTS_OVERVIEW_GUESTS_OVERVIEW_COMPONENT_TS___12 = goog.getMsg("Complete");
        i18n_11 = MSG_EXTERNAL_commonWordsComplete$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_GUESTS_OVERVIEW_GUESTS_OVERVIEW_COMPONENT_TS___12;
    }
    else {
        i18n_11 = $localize `:common.words|Common Words - Complete@@commonWordsComplete␟a7be0162a13b33bb52bfa80da0dc04c5a4660f6b␟2269914983679650499:Complete`;
    } let i18n_13; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Completed on Date
         * @meaning common.phrase
         */
        const MSG_EXTERNAL_completedOnDate$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_GUESTS_OVERVIEW_GUESTS_OVERVIEW_COMPONENT_TS___14 = goog.getMsg("Completed on: {$date}", { "date": "\uFFFD0\uFFFD" }, { original_code: { "date": "{{userSurvey.completed_on | date// i18n: (ph=\"date\")\n\t\t\t\t\t  }}" } });
        i18n_13 = MSG_EXTERNAL_completedOnDate$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_GUESTS_OVERVIEW_GUESTS_OVERVIEW_COMPONENT_TS___14;
    }
    else {
        i18n_13 = $localize `:common.phrase|Completed on Date@@completedOnDate␟15a79014e3ee4cb681605439506b458770cf40c3␟4152543100755157927:Completed on: ${"\uFFFD0\uFFFD"}:DATE:`;
    } return [["id", "compareOverview"], [3, "ngTemplateOutlet", "ngTemplateOutletContext", 4, "ngFor", "ngForOf"], ["userSurveyOverview", ""], [3, "ngTemplateOutlet", "ngTemplateOutletContext"], [1, "guest"], [1, "guest-name"], [1, "guest-name-label"], i18n_1, ["class", "guest-status guest-subtitle", 4, "ngIf"], ["class", "guest-status-time guest-subtitle", 4, "ngIf"], [1, "guest-status-email", "guest-subtitle"], i18n_3, ["class", "guest-overview-group", 4, "ngFor", "ngForOf"], [1, "guest-status", "guest-subtitle"], i18n_5, i18n_7, i18n_9, i18n_11, [1, "guest-status-time", "guest-subtitle"], i18n_13, [1, "guest-overview-group"], [1, "guest-overview-group-title"], ["class", "guest-overview-group-content-item", 4, "ngFor", "ngForOf"], [1, "guest-overview-group-content-item"], [1, "guest-overview-group-content-item-title"], [1, "guest-overview-group-content-item-value"]]; }, template: function GuestsOverviewComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵtemplate(1, GuestsOverviewComponent_ng_container_1_Template, 1, 4, "ng-container", 1);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(2, GuestsOverviewComponent_ng_template_2_Template, 17, 8, "ng-template", null, 2, i0.ɵɵtemplateRefExtractor);
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngForOf", ctx.userSurveys);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i1$2.NgTemplateOutlet, i2$1.MatCard, i2$1.MatCardContent, i2$1.MatCardHeader, i2$1.MatCardSubtitle, i2$1.MatCardTitle, i1$2.PercentPipe, i1$2.DatePipe, QuestionResponsePipe, GuestOverviewGroupsPipe], styles: ["[_nghost-%COMP%]{display:block}#compareOverview[_ngcontent-%COMP%]{display:flex;justify-content:center;align-items:baseline;flex-flow:row wrap}#compareOverview[_ngcontent-%COMP%] > *[_ngcontent-%COMP%]{max-width:380px;width:100%;margin:16px}.mat-card[_ngcontent-%COMP%]   .guest-name[_ngcontent-%COMP%]{margin-bottom:20px}.mat-card[_ngcontent-%COMP%]   .guest-subtitle[_ngcontent-%COMP%]{margin-top:6px}.guest-overview-group-title[_ngcontent-%COMP%]{margin-bottom:8px;font-size:110%}.guest-overview-group-content-item[_ngcontent-%COMP%]{display:flex;justify-content:space-between;margin-bottom:4px}@media print{#compareOverview[_ngcontent-%COMP%]{justify-content:space-around}#compareOverview[_ngcontent-%COMP%]   .mat-card[_ngcontent-%COMP%]{box-shadow:none;border-radius:0;margin:15px;padding:15px;box-sizing:border-box;flex:1 1 30%;min-width:300px;max-width:480px;border-bottom:1px solid #ccc}#compareOverview[_ngcontent-%COMP%]   .mat-card[_ngcontent-%COMP%]     .mat-card-header-text{margin:0}.guest-overview-group-content-item-value[_ngcontent-%COMP%]{margin-left:16px}}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GuestsOverviewComponent, [{
        type: Component,
        args: [{ selector: 'guests-overview', template: "<!-- Information about the guests-->\n<div id=\"compareOverview\">\n\n\t<ng-container *ngFor=\"let survey of userSurveys\"\n\t\t\t\t  [ngTemplateOutlet]=\"userSurveyOverview\"\n\t\t\t\t  [ngTemplateOutletContext]=\"{$implicit: survey}\"></ng-container>\n\n</div>\n\n<!-- Guest Survey Overview: displays information about the guest and their survey -->\n<ng-template #userSurveyOverview\n\t\t\t let-userSurvey>\n\n\t<mat-card class='guest'>\n\n\t\t<mat-card-header>\n\n\t\t\t<mat-card-title class='guest-name'>\n\t\t\t\t<span class='guest-name-label'\n\t\t\t\t\t  i18n=\"guests|Guest Name@@guestName\">Guest Name</span>:{{userSurvey.meta.name}}\n\t\t\t</mat-card-title>\n\n\t\t\t<mat-card-subtitle>\n\n\t\t\t\t<div class='guest-status guest-subtitle'\n\t\t\t\t\t *ngIf=\"!userSurvey.complete\">\n\n\t\t\t\t\t<span i18n=\"common.words|Common Words - Status@@commonWordStatus\">Status</span>:\n\t\t\t\t\t<span i18n=\"common.words|Common Words - Incomplete@@commonWordIncomplete\">Incomplete</span> ({{userSurvey.pctComplete | percent }})\n\t\t\t\t</div>\n\n\t\t\t\t<div class='guest-status guest-subtitle'\n\t\t\t\t\t *ngIf=\"userSurvey.complete\">\n\t\t\t\t\t<span i18n=\"common.words|Common Words - Status@@commonWordStatus\">Status</span>:\n\t\t\t\t\t<span i18n=\"common.words|Common Words - Complete@@commonWordsComplete\">Complete</span>\n\t\t\t\t</div>\n\n\t\t\t\t<div class='guest-status-time guest-subtitle'\n\t\t\t\t\t *ngIf=\"userSurvey.complete\"\n\t\t\t\t\t i18n=\"common.phrase|Completed on Date@@completedOnDate\">Completed on: {{userSurvey.completed_on | date// i18n: (ph=\"date\")\n\t\t\t\t\t  }}</div>\n\n\t\t\t\t<div class=\"guest-status-email guest-subtitle\">\n\t\t\t\t\t<span i18n=\"common.label|Email Label@@labelEmail\">Email</span>: {{userSurvey.email}}\n\t\t\t\t</div>\n\n\t\t\t</mat-card-subtitle>\n\n\t\t</mat-card-header>\n\n\t\t<mat-card-content>\n\n\t\t\t<div class=\"guest-overview-group\"\n\t\t\t\t *ngFor=\"let group of userSurvey.survey.items | guestOverviewGroups\">\n\n\t\t\t\t<div class=\"guest-overview-group-title\">{{group.title}}</div>\n\n\t\t\t\t<div class=\"guest-overview-group-content-item\"\n\t\t\t\t\t *ngFor=\"let question of group.items\">\n\n\t\t\t\t\t<span class=\"guest-overview-group-content-item-title\">{{question.title}}</span><span class=\"guest-overview-group-content-item-value\">{{question | questionResponse}}</span>\n\n\t\t\t\t</div>\n\n\t\t\t</div>\n\n\t\t</mat-card-content>\n\n\n\t</mat-card>\n\n</ng-template>\n", styles: [":host{display:block}#compareOverview{display:flex;justify-content:center;align-items:baseline;flex-flow:row wrap}#compareOverview>*{max-width:380px;width:100%;margin:16px}.mat-card .guest-name{margin-bottom:20px}.mat-card .guest-subtitle{margin-top:6px}.guest-overview-group-title{margin-bottom:8px;font-size:110%}.guest-overview-group-content-item{display:flex;justify-content:space-between;margin-bottom:4px}@media print{#compareOverview{justify-content:space-around}#compareOverview .mat-card{box-shadow:none;border-radius:0;margin:15px;padding:15px;box-sizing:border-box;flex:1 1 30%;min-width:300px;max-width:480px;border-bottom:1px solid #ccc}#compareOverview .mat-card ::ng-deep .mat-card-header-text{margin:0}.guest-overview-group-content-item-value{margin-left:16px}}\n"] }]
    }], null, { userSurveys: [{
            type: Input
        }] }); })();

/**
 * Return groups that are not marked 'showOnGuestOverview'
 */
class ReportGroupsPipe {
    transform(surveyRootElements) {
        return surveyRootElements.filter(el => el.type === SurveyItemType.QUESTION_GROUP && !el.meta.showOnGuestOverview);
    }
}
ReportGroupsPipe.ɵfac = function ReportGroupsPipe_Factory(t) { return new (t || ReportGroupsPipe)(); };
ReportGroupsPipe.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "reportGroups", type: ReportGroupsPipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ReportGroupsPipe, [{
        type: Pipe,
        args: [{
                name: 'reportGroups'
            }]
    }], null, null); })();

const _c2 = function (a0) { return { $implicit: a0 }; };
function ReportOverviewComponent_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0, 3);
} if (rf & 2) {
    const section_r3 = ctx.$implicit;
    i0.ɵɵnextContext();
    const _r1 = i0.ɵɵreference(5);
    i0.ɵɵproperty("ngTemplateOutlet", _r1)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c2, section_r3));
} }
function ReportOverviewComponent_ng_template_4_ng_container_0_ng_container_7_li_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "li");
    i0.ɵɵelementContainer(1, 3);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const subSection_r7 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵnextContext(3);
    const _r1 = i0.ɵɵreference(5);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngTemplateOutlet", _r1)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c2, subSection_r7));
} }
function ReportOverviewComponent_ng_template_4_ng_container_0_ng_container_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, ReportOverviewComponent_ng_template_4_ng_container_0_ng_container_7_li_1_Template, 2, 4, "li", 4);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const subSection_r7 = ctx.$implicit;
    const ctx_r6 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", subSection_r7.type === ctx_r6.SurveyItemType.QUESTION_GROUP);
} }
function ReportOverviewComponent_ng_template_4_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "h4");
    i0.ɵɵtext(2);
    i0.ɵɵelementStart(3, "span", 5);
    i0.ɵɵtext(4);
    i0.ɵɵpipe(5, "percent");
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(6, "ul");
    i0.ɵɵtemplate(7, ReportOverviewComponent_ng_template_4_ng_container_0_ng_container_7_Template, 2, 1, "ng-container", 6);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const section_r4 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate2("", section_r4.index, " ", section_r4.title, " ");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(5, 4, section_r4.stats == null ? null : section_r4.stats.pct));
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngForOf", section_r4.items);
} }
function ReportOverviewComponent_ng_template_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵtemplate(0, ReportOverviewComponent_ng_template_4_ng_container_0_Template, 8, 6, "ng-container", 4);
} if (rf & 2) {
    const section_r4 = ctx.$implicit;
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngIf", section_r4.type === ctx_r2.SurveyItemType.QUESTION_GROUP);
} }
class ReportOverviewComponent {
    constructor() {
        this.SurveyItemType = SurveyItemType;
    }
}
ReportOverviewComponent.ɵfac = function ReportOverviewComponent_Factory(t) { return new (t || ReportOverviewComponent)(); };
ReportOverviewComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: ReportOverviewComponent, selectors: [["report-overview"]], inputs: { report: "report" }, decls: 6, vars: 3, consts: function () { let i18n_0; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Report Comparison - Title
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_reportCompareHeaderTitle$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_REPORT_OVERVIEW_REPORT_OVERVIEW_COMPONENT_TS_1 = goog.getMsg("Agreement with each other and preferred response");
        i18n_0 = MSG_EXTERNAL_reportCompareHeaderTitle$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_REPORT_OVERVIEW_REPORT_OVERVIEW_COMPONENT_TS_1;
    }
    else {
        i18n_0 = $localize `:alpha.surveys|Report Comparison - Title@@reportCompareHeaderTitle␟2d3e38a5d3ffde097ea6eb769df2a3734b6683bd␟795879442584182273:Agreement with each other and preferred response`;
    } return [i18n_0, [3, "ngTemplateOutlet", "ngTemplateOutletContext", 4, "ngFor", "ngForOf"], ["sectionOverview", ""], [3, "ngTemplateOutlet", "ngTemplateOutletContext"], [4, "ngIf"], [1, "pct", "color-accent"], [4, "ngFor", "ngForOf"]]; }, template: function ReportOverviewComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "h2");
        i0.ɵɵi18n(1, 0);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(2, ReportOverviewComponent_ng_container_2_Template, 1, 4, "ng-container", 1);
        i0.ɵɵpipe(3, "reportGroups");
        i0.ɵɵtemplate(4, ReportOverviewComponent_ng_template_4_Template, 1, 1, "ng-template", null, 2, i0.ɵɵtemplateRefExtractor);
    } if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngForOf", i0.ɵɵpipeBind1(3, 1, ctx.report.compiledSurvey.items));
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i1$2.NgTemplateOutlet, i1$2.PercentPipe, ReportGroupsPipe], styles: ["ul[_ngcontent-%COMP%]{list-style:none;padding-left:40px}ul[_ngcontent-%COMP%]:not(:last-child){border-bottom:1px solid #ccc}ul[_ngcontent-%COMP%]   ul[_ngcontent-%COMP%]{border-bottom:none}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ReportOverviewComponent, [{
        type: Component,
        args: [{ selector: 'report-overview', template: "<h2 i18n=\"alpha.surveys|Report Comparison - Title@@reportCompareHeaderTitle\">Agreement with each other and preferred response</h2>\n\n<!-- Render the Main section overview which will display the tree overview-->\n<ng-container *ngFor=\"let section of report.compiledSurvey.items | reportGroups\"\n\t\t\t  [ngTemplateOutlet]=\"sectionOverview\"\n\t\t\t  [ngTemplateOutletContext]=\"{$implicit: section}\">\n</ng-container>\n\n\n\n\n<!-- Section Overview, displays group labels and completion stats, is recursive and uses itself on child elements -->\n<ng-template #sectionOverview\n\t\t\t let-section>\n\n\t<ng-container *ngIf=\"section.type === SurveyItemType.QUESTION_GROUP\">\n\n\t\t<h4>{{section.index}} {{section.title}} <span class='pct color-accent'>{{ section.stats?.pct | percent }}</span></h4>\n\n\t\t<ul>\n\n\t\t\t<ng-container *ngFor=\"let subSection of section.items\">\n\n\t\t\t\t<li *ngIf=\"subSection.type === SurveyItemType.QUESTION_GROUP\">\n\n\t\t\t\t\t<ng-container [ngTemplateOutlet]=\"sectionOverview\"\n\t\t\t\t\t\t\t\t  [ngTemplateOutletContext]=\"{$implicit: subSection}\"></ng-container>\n\n\t\t\t\t</li>\n\n\t\t\t</ng-container>\n\n\t\t</ul>\n\n\t</ng-container>\n\n</ng-template>\n", styles: ["ul{list-style:none;padding-left:40px}ul:not(:last-child){border-bottom:1px solid #ccc}ul ul{border-bottom:none}\n"] }]
    }], null, { report: [{
            type: Input
        }] }); })();

const _c0$1 = ["defaultLabelTemplate"];
const _c1 = function (a0) { return { step: a0 }; };
function StepperComponent_ng_template_0_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    const _r10 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "span", 7);
    i0.ɵɵlistener("click", function StepperComponent_ng_template_0_ng_container_1_Template_span_click_1_listener() { i0.ɵɵrestoreView(_r10); const ctx_r9 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r9.selectedIndex = ctx_r9.selectedIndex - 1); });
    i0.ɵɵnamespaceSVG();
    i0.ɵɵelementStart(2, "svg", 8);
    i0.ɵɵelement(3, "path", 9);
    i0.ɵɵelementEnd();
    i0.ɵɵnamespaceHTML();
    i0.ɵɵelementContainer(4, 10);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r7 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(4);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r7.getLabelTemplate(ctx_r7.selectedIndex - 1))("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c1, ctx_r7.getStep(ctx_r7.selectedIndex - 1)));
} }
function StepperComponent_ng_template_0_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    const _r12 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "span", 11);
    i0.ɵɵlistener("click", function StepperComponent_ng_template_0_ng_container_2_Template_span_click_1_listener() { i0.ɵɵrestoreView(_r12); const ctx_r11 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r11.selectedIndex = ctx_r11.selectedIndex + 1); });
    i0.ɵɵelementContainer(2, 10);
    i0.ɵɵnamespaceSVG();
    i0.ɵɵelementStart(3, "svg", 8);
    i0.ɵɵelement(4, "path", 12);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r8 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r8.getLabelTemplate(ctx_r8.selectedIndex + 1))("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c1, ctx_r8.getStep(ctx_r8.selectedIndex + 1)));
} }
function StepperComponent_ng_template_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 5);
    i0.ɵɵtemplate(1, StepperComponent_ng_template_0_ng_container_1_Template, 5, 4, "ng-container", 6);
    i0.ɵɵtemplate(2, StepperComponent_ng_template_0_ng_container_2_Template, 5, 4, "ng-container", 6);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.selectedIndex > 0);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.selectedIndex < ctx_r1.steps.length - 1);
} }
function StepperComponent_ng_template_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵtext(0);
} if (rf & 2) {
    const step_r13 = ctx.step;
    i0.ɵɵtextInterpolate1(" ", step_r13.label, "\n");
} }
function StepperComponent_ng_container_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0, 13);
} if (rf & 2) {
    i0.ɵɵnextContext();
    const _r0 = i0.ɵɵreference(1);
    i0.ɵɵproperty("ngTemplateOutlet", _r0);
} }
function StepperComponent_ng_container_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 14);
    i0.ɵɵelementContainer(2, 13);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const step_r14 = ctx.$implicit;
    const ctx_r5 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("hidden", ctx_r5.selected !== step_r14);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngTemplateOutlet", step_r14.content);
} }
function StepperComponent_ng_container_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0, 13);
} if (rf & 2) {
    i0.ɵɵnextContext();
    const _r0 = i0.ɵɵreference(1);
    i0.ɵɵproperty("ngTemplateOutlet", _r0);
} }
/**
 * Stepper Component
 */
class StepperComponent extends CdkStepper {
    constructor(dir, changeDetectorRef, el) {
        super(dir, changeDetectorRef, el);
        this.changeDetectorRef = changeDetectorRef;
        this.el = el;
    }
    getLabelTemplate(index) {
        const step = this.getStep(index);
        return step.stepLabel ? step.stepLabel.template : this.defaultLabelTemplate;
    }
    getStep(index) {
        return this.steps.toArray()[index];
    }
}
StepperComponent.ɵfac = function StepperComponent_Factory(t) { return new (t || StepperComponent)(i0.ɵɵdirectiveInject(i6.Directionality), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.ElementRef)); };
StepperComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StepperComponent, selectors: [["ab-stepper"]], viewQuery: function StepperComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0$1, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.defaultLabelTemplate = _t.first);
    } }, inputs: { navigationHidden: "navigationHidden" }, features: [i0.ɵɵProvidersFeature([{ provide: CdkStepper, useExisting: StepperComponent }]), i0.ɵɵInheritDefinitionFeature], decls: 8, vars: 3, consts: [["stepNavigation", ""], ["defaultLabelTemplate", ""], [3, "ngTemplateOutlet", 4, "ngIf"], [1, "stepper-content"], [4, "ngFor", "ngForOf"], [1, "step-label-navigation"], [4, "ngIf"], [1, "step-label", "step-label-previous", 3, "click"], ["width", "24", "height", "24"], ["d", "M8,11h12v2H8v3l-4-4l4-4V11z"], [3, "ngTemplateOutlet", "ngTemplateOutletContext"], [1, "step-label", "step-label-next", 3, "click"], ["d", "M16.01 11H4v2h12.01v3L20 12l-3.99-4z"], [3, "ngTemplateOutlet"], [1, "step-content", 3, "hidden"]], template: function StepperComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, StepperComponent_ng_template_0_Template, 3, 2, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
        i0.ɵɵtemplate(2, StepperComponent_ng_template_2_Template, 1, 1, "ng-template", null, 1, i0.ɵɵtemplateRefExtractor);
        i0.ɵɵtemplate(4, StepperComponent_ng_container_4_Template, 1, 1, "ng-container", 2);
        i0.ɵɵelementStart(5, "div", 3);
        i0.ɵɵtemplate(6, StepperComponent_ng_container_6_Template, 3, 2, "ng-container", 4);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(7, StepperComponent_ng_container_7_Template, 1, 1, "ng-container", 2);
    } if (rf & 2) {
        i0.ɵɵadvance(4);
        i0.ɵɵproperty("ngIf", !ctx.navigationHidden);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngForOf", ctx.steps);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", !ctx.navigationHidden);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i1$2.NgTemplateOutlet], styles: ["[_nghost-%COMP%]{display:flex;flex-flow:column nowrap;flex-grow:1}.step-content[_ngcontent-%COMP%]{flex-grow:1;overflow:hidden}.step-content[hidden][_ngcontent-%COMP%]{display:block!important;visibility:hidden;height:0;overflow:hidden}.step-label-navigation[_ngcontent-%COMP%]{display:flex;align-items:center;justify-content:space-between}.step-label-navigation[_ngcontent-%COMP%]:first-child{margin-bottom:35px}.step-label-navigation[_ngcontent-%COMP%]:last-child{margin-top:35px}.step-label[_ngcontent-%COMP%]{display:flex;align-items:center;cursor:pointer}.step-label-previous[_ngcontent-%COMP%]   mat-icon[_ngcontent-%COMP%]{transform:rotateY(180deg)}.step-label-next[_ngcontent-%COMP%]{margin-left:auto}.step-label-next[_ngcontent-%COMP%]   mat-icon[_ngcontent-%COMP%]{margin-left:8px}.stepper-content[_ngcontent-%COMP%]{width:100%;flex-grow:1;display:flex;flex-flow:column nowrap}@media print{.stepper-content[_ngcontent-%COMP%]{display:block}.step-content[_ngcontent-%COMP%], .step-content[hidden][_ngcontent-%COMP%]{display:block;visibility:visible;height:auto;break-after:page;padding-top:20px}.step-label-navigation[_ngcontent-%COMP%]{display:none!important}}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StepperComponent, [{
        type: Component,
        args: [{ selector: 'ab-stepper', providers: [{ provide: CdkStepper, useExisting: StepperComponent }], template: "<ng-template #stepNavigation>\n\n\t<div class=\"step-label-navigation\">\n\n\t\t<ng-container *ngIf=\"selectedIndex > 0\">\n\n\t\t\t<span class=\"step-label step-label-previous\"\n\t\t\t\t  (click)=\"selectedIndex = selectedIndex - 1\">\n\n\t\t\t\t<svg width=\"24\"\n\t\t\t\t\t height=\"24\">\n\t\t\t\t\t<path d=\"M8,11h12v2H8v3l-4-4l4-4V11z\" /></svg>\n\n\t\t\t\t<ng-container [ngTemplateOutlet]=\"getLabelTemplate( selectedIndex - 1 )\"\n\t\t\t\t\t\t\t  [ngTemplateOutletContext]=\"{ step : getStep( selectedIndex - 1 ) }\"></ng-container>\n\t\t\t</span>\n\n\t\t</ng-container>\n\n\t\t<ng-container *ngIf=\"selectedIndex < steps.length - 1\">\n\n\t\t\t<span class=\"step-label step-label-next\"\n\t\t\t\t  (click)=\"selectedIndex = selectedIndex + 1\">\n\t\t\t\t<ng-container [ngTemplateOutlet]=\"getLabelTemplate( selectedIndex + 1 )\"\n\t\t\t\t\t\t\t  [ngTemplateOutletContext]=\"{ step : getStep( selectedIndex + 1 ) }\"></ng-container>\n\t\t\t\t<svg width=\"24\"\n\t\t\t\t\t height=\"24\">\n\t\t\t\t\t<path d=\"M16.01 11H4v2h12.01v3L20 12l-3.99-4z\" /></svg>\n\n\t\t\t</span>\n\n\t\t</ng-container>\n\n\t</div>\n\n</ng-template>\n\n<ng-template #defaultLabelTemplate\n\t\t\t let-step=\"step\">\n\t{{step.label}}\n</ng-template>\n\n<!-- Top Navigation -->\n<ng-container *ngIf=\"!navigationHidden\"\n\t\t\t  [ngTemplateOutlet]=\"stepNavigation\"></ng-container>\n\n<!-- Stepper Content -->\n<div class=\"stepper-content\">\n\t<!--<ng-container [ngTemplateOutlet]=\"selected.content\"></ng-container>-->\n\t<ng-container *ngFor=\"let step of steps\">\n\t\t<div class=\"step-content\"\n\t\t\t [hidden]=\"selected !== step\">\n\t\t\t<ng-container [ngTemplateOutlet]=\"step.content\"></ng-container>\n\t\t</div>\n\n\t</ng-container>\n</div>\n\n<!-- Bottom Navigation -->\n<ng-container *ngIf=\"!navigationHidden\"\n\t\t\t  [ngTemplateOutlet]=\"stepNavigation\"></ng-container>\n", styles: [":host{display:flex;flex-flow:column nowrap;flex-grow:1}.step-content{flex-grow:1;overflow:hidden}.step-content[hidden]{display:block!important;visibility:hidden;height:0;overflow:hidden}.step-label-navigation{display:flex;align-items:center;justify-content:space-between}.step-label-navigation:first-child{margin-bottom:35px}.step-label-navigation:last-child{margin-top:35px}.step-label{display:flex;align-items:center;cursor:pointer}.step-label-previous mat-icon{transform:rotateY(180deg)}.step-label-next{margin-left:auto}.step-label-next mat-icon{margin-left:8px}.stepper-content{width:100%;flex-grow:1;display:flex;flex-flow:column nowrap}@media print{.stepper-content{display:block}.step-content,.step-content[hidden]{display:block;visibility:visible;height:auto;break-after:page;padding-top:20px}.step-label-navigation{display:none!important}}\n"] }]
    }], function () { return [{ type: i6.Directionality }, { type: i0.ChangeDetectorRef }, { type: i0.ElementRef }]; }, { defaultLabelTemplate: [{
            type: ViewChild,
            args: ['defaultLabelTemplate', { static: false }]
        }], navigationHidden: [{
            type: Input
        }] }); })();

function CompareResultsChartComponent_div_12_ng_container_2_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "div", 21);
} if (rf & 2) {
    const stat_r6 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵproperty("barScale", stat_r6.value)("barColor", stat_r6.color)("barDataLabelColor", stat_r6.dataLabelColor);
    i0.ɵɵattribute("percent", stat_r6.value);
} }
function CompareResultsChartComponent_div_12_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, CompareResultsChartComponent_div_12_ng_container_2_div_1_Template, 1, 4, "div", 20);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const stat_r6 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", stat_r6.value);
} }
function CompareResultsChartComponent_div_12_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 17)(1, "div", 18);
    i0.ɵɵtemplate(2, CompareResultsChartComponent_div_12_ng_container_2_Template, 2, 1, "ng-container", 19);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const person_r4 = ctx.$implicit;
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngForOf", person_r4.stats);
} }
function CompareResultsChartComponent_div_14_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 22);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const person_r9 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(person_r9.name);
} }
function CompareResultsChartComponent_div_16_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 23);
    i0.ɵɵelement(1, "div", 24);
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const item_r10 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵstyleProp("background-color", item_r10.value.color);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", item_r10.value.name, " ");
} }
/**
 * Chart Component
 */
class CompareResultsChartComponent {
    constructor(el, zone, cd) {
        this.el = el;
        this.zone = zone;
        this.cd = cd;
        this.groupTicksIndexes = [];
        this.chartDimensions = [1000, 400];
    }
    ngOnChanges(c) {
        if (c.report) {
            this.createReport(c.report.currentValue);
            this.createPreferredReport(c.report.currentValue);
        }
    }
    /**
     * After the view is stable, assign tick classes
     */
    ngAfterViewInit() {
        this.zone.onStable.pipe(take(1))
            .subscribe(() => {
            this.assignTickClasses();
            this.moveXAxisLabel();
            this.setDimensions();
        });
    }
    /**
     * Assign .group-label-tick class to <g class='tick'> query results that are in the group Indexes array
     */
    assignTickClasses() {
        const ticks = this.el.nativeElement.querySelectorAll('.y.axis .tick');
        ticks.forEach((tick, i) => {
            if (this.groupTicksIndexes.indexOf(i) >= 0) {
                tick.classList.add('group-label-tick');
            }
        });
        this.setDimensions();
    }
    moveXAxisLabel() {
        // get our custom label
        const label = this.el.nativeElement.querySelector('g#categoryLabel');
        label.firstElementChild.setAttribute('x', '50%');
        label.firstElementChild.setAttribute('y', '20');
        // get the <svg> chart element
        const chartSVG = this.el.nativeElement.querySelector('svg.ngx-charts');
        // the <g> element that contains the chart
        const barChart = this.el.nativeElement.querySelector('g.bar-chart');
        // a custom g element we'll wrap the chart in to offset it by the label
        const groupChart = this.el.nativeElement.querySelector('g#chartHolder');
        //groupChart.setAttribute( 'transform', 'translate(0,40)' );
        chartSVG.prepend(groupChart);
        groupChart.append(barChart);
        chartSVG.prepend(label);
    }
    /**
     * Create the model for the bar report
     */
    createReport(report) {
        const rows = [];
        const colors = [];
        let groupCcount = 0;
        let count = 0;
        let ticksIndexes = [];
        const labelF = (num) => {
            let n = '';
            for (let i = 0; i < num; i++) {
                n += ' ';
            }
            return n;
        };
        const collectGroups = (item, depth) => {
            if (item.meta.doNotScore) {
                return;
            }
            if (item.type === SurveyItemType.QUESTION_GROUP) {
                const q = item.items.find(i => i.type === SurveyItemType.QUESTION);
                if (depth === 1) {
                    ticksIndexes.push(count);
                }
                else if (!q) {
                    return;
                }
                const label = item.index + " " + item.title;
                colors.push({ name: label, value: depth === 1 ? '#3c8e9e' : '#4bb2c5' });
                rows.push({
                    name: label,
                    value: Math.round((item.stats.pct ? item.stats.pct : 0) * 100)
                });
                item.items.forEach(subItem => collectGroups(subItem, depth + 1));
                count++;
            }
            if (depth === 1) {
                rows.push({ name: labelF(groupCcount++), value: '' });
                count++;
            }
        };
        report.compiledSurvey.items.forEach(item => collectGroups(item, 1));
        this.customColors = colors;
        this.reportModel = rows;
        this.groupTicksIndexes = ticksIndexes;
    }
    /**
     * Create the model for the preferred percentages report
     */
    createPreferredReport(report) {
        // create the legend
        const ynupipe = new YesNoUncertainLabelPipe();
        const legend = {};
        legend[CompareUserSurveyQuestionState.MATCH_SUCCESS] = { color: "#4bb2c5", name: ynupipe.transform(YNULabelValues.YES) };
        legend[CompareUserSurveyQuestionState.MISMATCH] = { color: "#eaa228", name: ynupipe.transform(YNULabelValues.NO) };
        legend[CompareUserSurveyQuestionState.MATCH_FAIL] = { color: "#c5b47f", name: ynupipe.transform(YNULabelValues.UNCERTAIN) };
        /**
         * Create the stats rows for the person
         */
        const createStats = (stats) => {
            const series = [];
            for (let response in stats.results) {
                let stat = stats.results[response];
                series.push({
                    name: legend[response].name || "Response: " + response,
                    value: Math.round((stat / stats.total) * 100),
                    color: legend[response].color || '#ccc',
                    dataLabelColor: "#736a6a"
                });
            }
            return series;
        };
        /**
         * The model for the preferred percentages component
         */
        this.preferredModel = {
            items: report.surveys.map(survey => {
                return {
                    name: survey.meta.name,
                    stats: createStats(survey.getStats())
                };
            }),
            legend: legend
        };
    }
    onBeforePrint() {
        // before printing set the size of the chart
        this.printing = true;
        this.chartDimensions = [720, null];
    }
    onAfterPrint() {
        // before printing set the size of the chart
        this.printing = false;
        this.setDimensions();
    }
    /**
     *
     */
    setDimensions() {
        if (this.printing) {
            return;
        }
        const $container = this.el.nativeElement;
        const $chartContainer = $container.querySelector('#chartContainer');
        const maxHeight = window.innerHeight - 130;
        const titleOffset = $container.querySelector('#chart').offsetTop;
        const availWidth = $chartContainer.clientWidth - 10;
        const availHeight = $container.parentElement.clientHeight - titleOffset > maxHeight ? maxHeight : $container.parentElement.clientHeight - titleOffset;
        const chartWidth = availWidth < 0 ? 0 : availWidth;
        const chartHeight = availHeight <= 480 ? 480 : availHeight;
        this.chartDimensions = [chartWidth, chartHeight];
    }
}
CompareResultsChartComponent.ɵfac = function CompareResultsChartComponent_Factory(t) { return new (t || CompareResultsChartComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); };
CompareResultsChartComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: CompareResultsChartComponent, selectors: [["compare-chart"]], hostBindings: function CompareResultsChartComponent_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("beforeprint", function CompareResultsChartComponent_beforeprint_HostBindingHandler() { return ctx.onBeforePrint(); }, false, i0.ɵɵresolveWindow)("afterprint", function CompareResultsChartComponent_afterprint_HostBindingHandler() { return ctx.onAfterPrint(); }, false, i0.ɵɵresolveWindow)("resize", function CompareResultsChartComponent_resize_HostBindingHandler() { return ctx.setDimensions(); }, false, i0.ɵɵresolveWindow);
    } }, inputs: { report: "report" }, features: [i0.ɵɵNgOnChangesFeature], decls: 18, vars: 15, consts: function () { let i18n_0; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Categories
         * @meaning common.words
         */
        const MSG_EXTERNAL_commonWordsCategories$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_CHARTS_CHART_COMPONENT_TS_1 = goog.getMsg("Categories");
        i18n_0 = MSG_EXTERNAL_commonWordsCategories$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_CHARTS_CHART_COMPONENT_TS_1;
    }
    else {
        i18n_0 = $localize `:common.words|Categories@@commonWordsCategories␟d20add8644ed6319a65356d4468f6509c6ef7976␟7752409151371435775:Categories`;
    } let i18n_2; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Report Comparison - Preferred Percentages
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_reportPreferredPct$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_CHARTS_CHART_COMPONENT_TS_3 = goog.getMsg("Preferred Percentages");
        i18n_2 = MSG_EXTERNAL_reportPreferredPct$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_CHARTS_CHART_COMPONENT_TS_3;
    }
    else {
        i18n_2 = $localize `:alpha.surveys|Report Comparison - Preferred Percentages@@reportPreferredPct␟20067831f6aae26f6c333f6fa5215ae490907cf2␟9205925939030207123:Preferred Percentages`;
    } return [["id", "chartContainer"], [2, "display", "none"], ["categoriesLabel", ""], ["id", "categoryLabel"], ["stroke-width", "0.01", "text-anchor", "middle"], i18n_0, ["id", "chartHolder", "transform", "translate(0,40)"], ["id", "chart", 3, "animations", "view", "customColors", "tooltipDisabled", "results", "xAxis", "yAxis", "showDataLabel", "trimYAxisTicks", "noBarWhenZero"], ["id", "preferredContainer"], [1, "chart-label"], i18n_2, [1, "preferred-report"], ["class", "preferred-report-person", 4, "ngFor", "ngForOf"], [1, "person-labels"], ["class", "person-label", 4, "ngFor", "ngForOf"], [1, "legend"], ["class", "legend-item", 4, "ngFor", "ngForOf"], [1, "preferred-report-person"], [1, "preferred-report-bar"], [4, "ngFor", "ngForOf"], ["class", "preferred-report-bar-section", 3, "barScale", "barColor", "barDataLabelColor", 4, "ngIf"], [1, "preferred-report-bar-section", 3, "barScale", "barColor", "barDataLabelColor"], [1, "person-label"], [1, "legend-item"], [1, "legend-square"]]; }, template: function CompareResultsChartComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵnamespaceSVG();
        i0.ɵɵelementStart(1, "svg", 1, 2)(3, "g", 3)(4, "text", 4);
        i0.ɵɵi18n(5, 5);
        i0.ɵɵelementEnd()();
        i0.ɵɵelement(6, "g", 6);
        i0.ɵɵelementEnd();
        i0.ɵɵnamespaceHTML();
        i0.ɵɵelement(7, "ngx-charts-bar-horizontal", 7);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(8, "div", 8)(9, "div", 9);
        i0.ɵɵi18n(10, 10);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(11, "div", 11);
        i0.ɵɵtemplate(12, CompareResultsChartComponent_div_12_Template, 3, 1, "div", 12);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(13, "div", 13);
        i0.ɵɵtemplate(14, CompareResultsChartComponent_div_14_Template, 2, 1, "div", 14);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(15, "div", 15);
        i0.ɵɵtemplate(16, CompareResultsChartComponent_div_16_Template, 3, 3, "div", 16);
        i0.ɵɵpipe(17, "keyvalue");
        i0.ɵɵelementEnd()();
    } if (rf & 2) {
        i0.ɵɵadvance(7);
        i0.ɵɵproperty("animations", false)("view", ctx.chartDimensions)("customColors", ctx.customColors)("tooltipDisabled", true)("results", ctx.reportModel)("xAxis", true)("yAxis", true)("showDataLabel", true)("trimYAxisTicks", false)("noBarWhenZero", true);
        i0.ɵɵadvance(5);
        i0.ɵɵproperty("ngForOf", ctx.preferredModel.items);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngForOf", ctx.preferredModel.items);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngForOf", i0.ɵɵpipeBind1(17, 13, ctx.preferredModel.legend));
    } }, dependencies: function () { return [i1$2.NgForOf, i1$2.NgIf, i2$2.BarHorizontalComponent, BarScaleDirective, i1$2.KeyValuePipe]; }, styles: ["[_nghost-%COMP%]{display:flex;align-items:flex-start}[_nghost-%COMP%]     ngx-charts-chart{display:block}[_nghost-%COMP%]     .ngx-charts{width:100%!important}[_nghost-%COMP%]     .ngx-charts-outer{width:auto!important}[_nghost-%COMP%]     .ngx-charts .group-label-tick{font-weight:700}[_nghost-%COMP%]     .ngx-charts .bar{cursor:default}.chart-label[_ngcontent-%COMP%]{margin-bottom:16px}#chart[_ngcontent-%COMP%]{display:block;overflow:hidden}#chartContainer[_ngcontent-%COMP%]{flex-grow:1;text-align:center;position:relative}#preferredContainer[_ngcontent-%COMP%]{text-align:center;display:flex;flex-flow:column nowrap;align-self:stretch;margin-left:45px;min-width:200px}.preferred-report[_ngcontent-%COMP%]{display:flex;justify-content:center;flex-grow:1}.preferred-report-person[_ngcontent-%COMP%]{display:flex;align-self:stretch;flex-flow:column nowrap;width:60px;margin-left:20px;margin-right:20px}.preferred-report-bar[_ngcontent-%COMP%]{display:flex;flex-grow:1;flex-flow:column-reverse nowrap;height:100%}.preferred-report-bar-section[_ngcontent-%COMP%]{display:flex;align-items:center;justify-content:center;min-height:20px}@media not print{.preferred-report-bar-section[_ngcontent-%COMP%]{width:100%!important}}.preferred-report-bar-section[_ngcontent-%COMP%]:after{content:attr(percent) \"%\"}.person-label[_ngcontent-%COMP%]{flex-basis:50%;margin-top:8px}.person-labels[_ngcontent-%COMP%]{display:flex;justify-content:center}.legend[_ngcontent-%COMP%]{text-align:left;margin:16px auto 0}.legend-item[_ngcontent-%COMP%]{vertical-align:middle;margin-bottom:5px}.legend-square[_ngcontent-%COMP%]{width:14px;height:14px;display:inline-block}@media print,(max-width: 768px){[_nghost-%COMP%]{display:block}.preferred-report-person[_ngcontent-%COMP%]{flex-flow:column nowrap;width:100%;margin:0;height:35px}.preferred-report-bar[_ngcontent-%COMP%]{flex-flow:row nowrap}.preferred-report-bar-section[_ngcontent-%COMP%]{height:auto!important;min-width:30px}#preferredContainer[_ngcontent-%COMP%]{flex-flow:row wrap;margin:55px 0}#preferredContainer[_ngcontent-%COMP%]   .chart-label[_ngcontent-%COMP%]{order:1;flex-basis:100%}#preferredContainer[_ngcontent-%COMP%]   .person-labels[_ngcontent-%COMP%]{order:2;flex-flow:column nowrap;justify-content:space-around;margin-right:16px}#preferredContainer[_ngcontent-%COMP%]   .person-labels[_ngcontent-%COMP%]   .person-label[_ngcontent-%COMP%]{margin:20px 0;flex-basis:auto}#preferredContainer[_ngcontent-%COMP%]   .preferred-report[_ngcontent-%COMP%]{order:3;flex-flow:row wrap;align-content:space-around}#preferredContainer[_ngcontent-%COMP%]   .legend[_ngcontent-%COMP%]{order:4;flex-basis:100%;display:flex;justify-content:center}#preferredContainer[_ngcontent-%COMP%]   .legend-item[_ngcontent-%COMP%]{margin:0 8px}}"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CompareResultsChartComponent, [{
        type: Component,
        args: [{ selector: 'compare-chart', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div id=\"chartContainer\">\n\n\t<svg #categoriesLabel\n\t\t style=\"display:none;\">\n\t\t<g id=\"categoryLabel\">\n\t\t\t<text stroke-width=\"0.01\"\n\t\t\t\t  text-anchor=\"middle\"\n\t\t\t\t  i18n=\"common.words|Categories@@commonWordsCategories\">Categories</text>\n\t\t</g>\n\t\t<g id=\"chartHolder\"\n\t\t   transform=\"translate(0,40)\"></g>\n\t</svg>\n\n\t<ngx-charts-bar-horizontal id=\"chart\"\n\t\t\t\t\t\t\t   [animations]=\"false\"\n\t\t\t\t\t\t\t   [view]=\"chartDimensions\"\n\t\t\t\t\t\t\t   [customColors]=\"customColors\"\n\t\t\t\t\t\t\t   [tooltipDisabled]=\"true\"\n\t\t\t\t\t\t\t   [results]=\"reportModel\"\n\t\t\t\t\t\t\t   [xAxis]=\"true\"\n\t\t\t\t\t\t\t   [yAxis]=\"true\"\n\t\t\t\t\t\t\t   [showDataLabel]=\"true\"\n\t\t\t\t\t\t\t   [trimYAxisTicks]=\"false\"\n\t\t\t\t\t\t\t   [noBarWhenZero]=\"true\">\n\t</ngx-charts-bar-horizontal>\n\n</div>\n\n<div id=\"preferredContainer\">\n\n\t<div class=\"chart-label\"\n\t\t i18n=\"alpha.surveys|Report Comparison - Preferred Percentages@@reportPreferredPct\">Preferred Percentages</div>\n\n\t<div class=\"preferred-report\">\n\n\t\t<div *ngFor=\"let person of preferredModel.items\"\n\t\t\t class=\"preferred-report-person\">\n\n\t\t\t<div class=\"preferred-report-bar\">\n\n\t\t\t\t<ng-container *ngFor=\"let stat of person.stats\">\n\t\t\t\t\t<div *ngIf=\"stat.value\"\n\t\t\t\t\t\t class=\"preferred-report-bar-section\"\n\t\t\t\t\t\t [attr.percent]=\"stat.value\"\n\t\t\t\t\t\t [barScale]=\"stat.value\"\n\t\t\t\t\t\t [barColor]=\"stat.color\"\n\t\t\t\t\t\t [barDataLabelColor]=\"stat.dataLabelColor\"></div>\n\t\t\t\t</ng-container>\n\n\n\t\t\t</div>\n\n\t\t</div>\n\n\t</div>\n\n\t<div class=\"person-labels\">\n\t\t<div *ngFor=\"let person of preferredModel.items\"\n\t\t\t class=\"person-label\">{{person.name}}</div>\n\t</div>\n\n\t<div class=\"legend\">\n\n\t\t<div class=\"legend-item\"\n\t\t\t *ngFor=\"let item of preferredModel.legend | keyvalue\">\n\n\t\t\t<div class=\"legend-square\"\n\t\t\t\t [style.backgroundColor]=\"item.value.color\"></div>\n\t\t\t{{item.value.name}}\n\t\t</div>\n\n\t</div>\n\n</div>\n", styles: [":host{display:flex;align-items:flex-start}:host ::ng-deep ngx-charts-chart{display:block}:host ::ng-deep .ngx-charts{width:100%!important}:host ::ng-deep .ngx-charts-outer{width:auto!important}:host ::ng-deep .ngx-charts .group-label-tick{font-weight:700}:host ::ng-deep .ngx-charts .bar{cursor:default}.chart-label{margin-bottom:16px}#chart{display:block;overflow:hidden}#chartContainer{flex-grow:1;text-align:center;position:relative}#preferredContainer{text-align:center;display:flex;flex-flow:column nowrap;align-self:stretch;margin-left:45px;min-width:200px}.preferred-report{display:flex;justify-content:center;flex-grow:1}.preferred-report-person{display:flex;align-self:stretch;flex-flow:column nowrap;width:60px;margin-left:20px;margin-right:20px}.preferred-report-bar{display:flex;flex-grow:1;flex-flow:column-reverse nowrap;height:100%}.preferred-report-bar-section{display:flex;align-items:center;justify-content:center;min-height:20px}@media not print{.preferred-report-bar-section{width:100%!important}}.preferred-report-bar-section:after{content:attr(percent) \"%\"}.person-label{flex-basis:50%;margin-top:8px}.person-labels{display:flex;justify-content:center}.legend{text-align:left;margin:16px auto 0}.legend-item{vertical-align:middle;margin-bottom:5px}.legend-square{width:14px;height:14px;display:inline-block}@media print,(max-width: 768px){:host{display:block}.preferred-report-person{flex-flow:column nowrap;width:100%;margin:0;height:35px}.preferred-report-bar{flex-flow:row nowrap}.preferred-report-bar-section{height:auto!important;min-width:30px}#preferredContainer{flex-flow:row wrap;margin:55px 0}#preferredContainer .chart-label{order:1;flex-basis:100%}#preferredContainer .person-labels{order:2;flex-flow:column nowrap;justify-content:space-around;margin-right:16px}#preferredContainer .person-labels .person-label{margin:20px 0;flex-basis:auto}#preferredContainer .preferred-report{order:3;flex-flow:row wrap;align-content:space-around}#preferredContainer .legend{order:4;flex-basis:100%;display:flex;justify-content:center}#preferredContainer .legend-item{margin:0 8px}}\n"] }]
    }], function () { return [{ type: i0.ElementRef }, { type: i0.NgZone }, { type: i0.ChangeDetectorRef }]; }, { report: [{
            type: Input
        }], onBeforePrint: [{
            type: HostListener,
            args: ['window:beforeprint']
        }], onAfterPrint: [{
            type: HostListener,
            args: ['window:afterprint']
        }], setDimensions: [{
            type: HostListener,
            args: ['window:resize']
        }] }); })();
/**
 * Directive for the bar element
 */
class BarScaleDirective {
    onResize() { this.render(); }
    constructor(el, zone) {
        this.el = el;
        this.zone = zone;
    }
    ngAfterViewInit() {
        this.zone.onStable.pipe(take(1))
            .subscribe(() => this.render());
    }
    render() {
        this.el.nativeElement.style.backgroundColor = this.barColor;
        this.el.nativeElement.style.color = this.barDataLabelColor;
        this.el.nativeElement.style.height = this.barScale + '%';
        this.el.nativeElement.style.width = this.barScale + '%';
    }
}
BarScaleDirective.ɵfac = function BarScaleDirective_Factory(t) { return new (t || BarScaleDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.NgZone)); };
BarScaleDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: BarScaleDirective, selectors: [["", "barScale", ""]], hostBindings: function BarScaleDirective_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("resize", function BarScaleDirective_resize_HostBindingHandler() { return ctx.onResize(); }, false, i0.ɵɵresolveWindow);
    } }, inputs: { barScale: "barScale", barColor: "barColor", barDataLabelColor: "barDataLabelColor" } });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BarScaleDirective, [{
        type: Directive,
        args: [{
                selector: '[barScale]'
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: i0.NgZone }]; }, { onResize: [{
            type: HostListener,
            args: ['window:resize']
        }], barScale: [{
            type: Input
        }], barColor: [{
            type: Input
        }], barDataLabelColor: [{
            type: Input
        }] }); })();

const _c0 = ["stepper"];
function CompareSurveyResultsComponent_ng_container_0_ng_template_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵi18n(1, 9);
    i0.ɵɵelementEnd();
} }
function CompareSurveyResultsComponent_ng_container_0_button_6_Template(rf, ctx) { if (rf & 1) {
    const _r11 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 10);
    i0.ɵɵlistener("click", function CompareSurveyResultsComponent_ng_container_0_button_6_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r11); const ctx_r10 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r10.currentStep = 1); });
    i0.ɵɵi18n(1, 11);
    i0.ɵɵelementEnd();
} }
function CompareSurveyResultsComponent_ng_container_0_lib_mrb_buttons_7_Template(rf, ctx) { if (rf & 1) {
    const _r13 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "lib-mrb-buttons", 12);
    i0.ɵɵlistener("buttonClick", function CompareSurveyResultsComponent_ng_container_0_lib_mrb_buttons_7_Template_lib_mrb_buttons_buttonClick_0_listener() { i0.ɵɵrestoreView(_r13); const ctx_r12 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r12.currentStep = 1); });
    i0.ɵɵi18n(1, 13);
    i0.ɵɵelementEnd();
} }
function CompareSurveyResultsComponent_ng_container_0_ng_template_9_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵi18n(1, 14);
    i0.ɵɵelementEnd();
} }
function CompareSurveyResultsComponent_ng_container_0_ng_template_12_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵi18n(1, 15);
    i0.ɵɵelementEnd();
} }
function CompareSurveyResultsComponent_ng_container_0_cdk_step_14_ng_template_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const questionGroup_r14 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate2("", questionGroup_r14.index, " ", questionGroup_r14.title, "");
} }
function CompareSurveyResultsComponent_ng_container_0_cdk_step_14_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "cdk-step");
    i0.ɵɵtemplate(1, CompareSurveyResultsComponent_ng_container_0_cdk_step_14_ng_template_1_Template, 2, 2, "ng-template", 3);
    i0.ɵɵelement(2, "compare-question-group", 16);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const questionGroup_r14 = ctx.$implicit;
    const ctx_r9 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("questionGroup", questionGroup_r14)("report", ctx_r9.compareReport);
} }
function CompareSurveyResultsComponent_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    const _r18 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "ab-stepper", 1, 2);
    i0.ɵɵlistener("selectionChange", function CompareSurveyResultsComponent_ng_container_0_Template_ab_stepper_selectionChange_1_listener($event) { i0.ɵɵrestoreView(_r18); const ctx_r17 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r17.onStepperSelect($event)); });
    i0.ɵɵelementStart(3, "cdk-step");
    i0.ɵɵtemplate(4, CompareSurveyResultsComponent_ng_container_0_ng_template_4_Template, 2, 0, "ng-template", 3);
    i0.ɵɵelement(5, "guests-overview", 4);
    i0.ɵɵtemplate(6, CompareSurveyResultsComponent_ng_container_0_button_6_Template, 2, 0, "button", 5);
    i0.ɵɵtemplate(7, CompareSurveyResultsComponent_ng_container_0_lib_mrb_buttons_7_Template, 2, 0, "lib-mrb-buttons", 6);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(8, "cdk-step");
    i0.ɵɵtemplate(9, CompareSurveyResultsComponent_ng_container_0_ng_template_9_Template, 2, 0, "ng-template", 3);
    i0.ɵɵelement(10, "compare-chart", 7);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(11, "cdk-step");
    i0.ɵɵtemplate(12, CompareSurveyResultsComponent_ng_container_0_ng_template_12_Template, 2, 0, "ng-template", 3);
    i0.ɵɵelement(13, "report-overview", 7);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(14, CompareSurveyResultsComponent_ng_container_0_cdk_step_14_Template, 3, 2, "cdk-step", 8);
    i0.ɵɵpipe(15, "reportGroups");
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("navigationHidden", ctx_r0.currentStep === 0)("selectedIndex", ctx_r0.currentStep);
    i0.ɵɵadvance(4);
    i0.ɵɵproperty("userSurveys", ctx_r0.compareReport.surveys);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r0.options.theme != "MRB");
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r0.options.theme == "MRB");
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("report", ctx_r0.compareReport);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("report", ctx_r0.compareReport);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", i0.ɵɵpipeBind1(15, 8, ctx_r0.compareReport.questionGroups));
} }
function CompareSurveyResultsComponent_p_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p");
    i0.ɵɵi18n(1, 17);
    i0.ɵɵelementEnd();
} }
function CompareSurveyResultsComponent_p_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p");
    i0.ɵɵi18n(1, 18);
    i0.ɵɵelementEnd();
} }
/**
 * Main component for comparing the results of 2 surveys
 */
class CompareSurveyResultsComponent {
    constructor(service, cd, options) {
        this.service = service;
        this.cd = cd;
        this.options = options;
        this.currentStep = 0;
        this.errors = {};
    }
    ngOnChanges(c) {
        if (c.reportLink && c.reportLink.currentValue) {
            this.loadReport(c.reportLink.currentValue);
        }
        if (c.compareReport && c.compareReport.currentValue) {
            this.compareReport = c.compareReport.currentValue;
        }
    }
    loadReport(surveyLink) {
        // survey links must be 13 characters each
        if (surveyLink.length % 13 !== 0) {
            this.errors.invalid_survey_link = true;
            return;
        }
        const reports = surveyLink.match(/[\w]{13}/g).map(link => {
            return this.service.getUserSurvey(link);
        });
        forkJoin(reports).subscribe((results) => {
            const report = new CompareUserSurveyReport(results);
            this.compareReport = report;
            this.cd.markForCheck();
            this.cd.detectChanges();
        });
    }
    onStepperSelect(event) {
        this.currentStep = event.selectedIndex;
        window.scrollTo({ top: 0 });
        requestAnimationFrame(() => {
            window.dispatchEvent(new Event('resize'));
        });
    }
}
CompareSurveyResultsComponent.ɵfac = function CompareSurveyResultsComponent_Factory(t) { return new (t || CompareSurveyResultsComponent)(i0.ɵɵdirectiveInject(SurveyService), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(ALPHA_SURVEYS_OPTIONS)); };
CompareSurveyResultsComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: CompareSurveyResultsComponent, selectors: [["compare-survey-results"]], viewQuery: function CompareSurveyResultsComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0, 7);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.stepper = _t.first);
    } }, inputs: { reportLink: "reportLink", compareReport: "compareReport" }, features: [i0.ɵɵNgOnChangesFeature], decls: 3, vars: 3, consts: function () { let i18n_1; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Report Comparison Section - Guests Overview
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_guestsOverview$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_COMPARE_RESULTS_COMPONENT_TS___2 = goog.getMsg("Guests Overview");
        i18n_1 = MSG_EXTERNAL_guestsOverview$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_COMPARE_RESULTS_COMPONENT_TS___2;
    }
    else {
        i18n_1 = $localize `:alpha.surveys|Report Comparison Section - Guests Overview@@guestsOverview␟69d9cf65c319cd2b2c3fb0181342550b17e016f8␟1732101281129455534:Guests Overview`;
    } let i18n_3; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc View Report
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_viewReport$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_COMPARE_RESULTS_COMPONENT_TS___4 = goog.getMsg("View Report");
        i18n_3 = MSG_EXTERNAL_viewReport$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_COMPARE_RESULTS_COMPONENT_TS___4;
    }
    else {
        i18n_3 = $localize `:alpha.surveys|View Report@@viewReport␟aa12fbd9ba4422a2113992fd84c1e8a16eda1e52␟4014818056964486278:View Report`;
    } let i18n_5; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc View Report
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_viewReport$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_COMPARE_RESULTS_COMPONENT_TS___6 = goog.getMsg("View Report ");
        i18n_5 = MSG_EXTERNAL_viewReport$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_COMPARE_RESULTS_COMPONENT_TS___6;
    }
    else {
        i18n_5 = $localize `:alpha.surveys|View Report@@viewReport␟631594d3f9035eaacbe4a0173a51b8c0976ec1ee␟2564634028566027936:View Report `;
    } let i18n_7; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Report Comparison Section - Report Overview
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_reportOverview$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_COMPARE_RESULTS_COMPONENT_TS___8 = goog.getMsg("Report Overview");
        i18n_7 = MSG_EXTERNAL_reportOverview$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_COMPARE_RESULTS_COMPONENT_TS___8;
    }
    else {
        i18n_7 = $localize `:alpha.surveys|Report Comparison Section - Report Overview@@reportOverview␟df5854c51617c175a4cfda90160d91d9e9e5a344␟1769776393345684671:Report Overview`;
    } let i18n_9; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Report Comparison Section - Report Overview
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_reportOverview$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_COMPARE_RESULTS_COMPONENT_TS___10 = goog.getMsg("Report Overview");
        i18n_9 = MSG_EXTERNAL_reportOverview$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_COMPARE_RESULTS_COMPONENT_TS___10;
    }
    else {
        i18n_9 = $localize `:alpha.surveys|Report Comparison Section - Report Overview@@reportOverview␟df5854c51617c175a4cfda90160d91d9e9e5a344␟1769776393345684671:Report Overview`;
    } let i18n_11; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc View Report Incomplete Error
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_reportErrorIncomplete$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_COMPARE_RESULTS_COMPONENT_TS__12 = goog.getMsg("Cannot view report, one of the guests has not completed their survey.");
        i18n_11 = MSG_EXTERNAL_reportErrorIncomplete$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_COMPARE_RESULTS_COMPONENT_TS__12;
    }
    else {
        i18n_11 = $localize `:alpha.surveys|View Report Incomplete Error@@reportErrorIncomplete␟434c96c761b1adcdfff7894ce6b0aa09edfb661a␟108668215020324657:Cannot view report, one of the guests has not completed their survey.`;
    } let i18n_13; if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc Invalid Report Link
         * @meaning alpha.surveys
         */
        const MSG_EXTERNAL_reportErrorLink$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_COMPARE_RESULTS_COMPONENT_TS__14 = goog.getMsg("This is an invalid report link.");
        i18n_13 = MSG_EXTERNAL_reportErrorLink$$_____________________USERS_VINCENTCHAN_CODE_ALPHA_SURVEYS_NG_COMPONENTS_SRC_REPORTS_SRC_COMPONENTS_COMPARE_RESULTS_COMPARE_RESULTS_COMPONENT_TS__14;
    }
    else {
        i18n_13 = $localize `:alpha.surveys|Invalid Report Link@@reportErrorLink␟9eec96b0c4e7e135edecddf75dac5e948ce6b6b7␟5720818919631700028:This is an invalid report link.`;
    } return [[4, "ngIf"], [3, "navigationHidden", "selectedIndex", "selectionChange"], ["stepper", ""], ["cdkStepLabel", ""], [3, "userSurveys"], ["mat-flat-button", "", "id", "viewReportButton", "color", "accent", 3, "click", 4, "ngIf"], ["class", "report-button", "style", "display: block;", "id", "viewReportButton", 3, "buttonClick", 4, "ngIf"], [3, "report"], [4, "ngFor", "ngForOf"], i18n_1, ["mat-flat-button", "", "id", "viewReportButton", "color", "accent", 3, "click"], i18n_3, ["id", "viewReportButton", 1, "report-button", 2, "display", "block", 3, "buttonClick"], i18n_5, i18n_7, i18n_9, [3, "questionGroup", "report"], i18n_11, i18n_13]; }, template: function CompareSurveyResultsComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, CompareSurveyResultsComponent_ng_container_0_Template, 16, 10, "ng-container", 0);
        i0.ɵɵtemplate(1, CompareSurveyResultsComponent_p_1_Template, 2, 0, "p", 0);
        i0.ɵɵtemplate(2, CompareSurveyResultsComponent_p_2_Template, 2, 0, "p", 0);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.compareReport && !ctx.compareReport.errors);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.compareReport == null ? null : ctx.compareReport.errors == null ? null : ctx.compareReport.errors.surveys_incomplete);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.errors.invalid_survey_link);
    } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i2.MatButton, StepperComponent, i5$2.CdkStep, i5$2.CdkStepLabel, CompareResultsChartComponent, MrbButtonsComponent, CompareQuestionGroupComponent, GuestsOverviewComponent, ReportOverviewComponent, ReportGroupsPipe], styles: ["[_nghost-%COMP%]{display:flex;flex-flow:column nowrap}compare-chart[_ngcontent-%COMP%]{flex-grow:1;width:100%}#viewReportButton[_ngcontent-%COMP%]{margin:35px auto 0;display:block;padding:5px 35px}@media print{[_nghost-%COMP%]     button{display:none!important}}.report-button[_ngcontent-%COMP%]{display:flex;max-width:10rem;align-items:center;justify-content:center}"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CompareSurveyResultsComponent, [{
        type: Component,
        args: [{ selector: 'compare-survey-results', changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-container *ngIf=\"compareReport && ! compareReport.errors\">\n\n\t<ab-stepper #stepper\n\t\t\t\t[navigationHidden]=\"currentStep === 0\"\n\t\t\t\t[selectedIndex]=\"currentStep\"\n\t\t\t\t(selectionChange)=\"onStepperSelect( $event )\">\n\n\t\t<!-- Guests Overview Step-->\n\t\t<cdk-step>\n\n\t\t\t<ng-template cdkStepLabel>\n\t\t\t\t<span i18n=\"alpha.surveys|Report Comparison Section - Guests Overview@@guestsOverview\">Guests Overview</span>\n\t\t\t</ng-template>\n\n\t\t\t<guests-overview [userSurveys]=\"compareReport.surveys\"></guests-overview>\n\n\t\t\t<button *ngIf=\"options.theme != 'MRB'\" mat-flat-button\n\t\t\t\t\tid=\"viewReportButton\"\n\t\t\t\t\tcolor=\"accent\"\n\t\t\t\t\t(click)=\"currentStep = 1\"\n\t\t\t\t\ti18n=\"alpha.surveys|View Report@@viewReport\">View Report</button>\n\t\t\t<lib-mrb-buttons class=\"report-button\" *ngIf=\"options.theme == 'MRB'\" style=\"display: block;\" id=\"viewReportButton\"\n\t\t\t\t(buttonClick)=\"currentStep = 1\" i18n=\"alpha.surveys|View Report@@viewReport\">View Report\n\t\t\t</lib-mrb-buttons>\n\n\t\t</cdk-step>\n\n\t\t<!-- Report Chart Step-->\n\t\t<cdk-step>\n\n\t\t\t<ng-template cdkStepLabel>\n\t\t\t\t<span i18n=\"alpha.surveys|Report Comparison Section - Report Overview@@reportOverview\">Report Overview</span>\n\t\t\t</ng-template>\n\n\t\t\t<compare-chart [report]=\"compareReport\"></compare-chart>\n\t\t</cdk-step>\n\n\t\t<!-- Report Overview Step-->\n\t\t<cdk-step>\n\n\t\t\t<ng-template cdkStepLabel>\n\t\t\t\t<span i18n=\"alpha.surveys|Report Comparison Section - Report Overview@@reportOverview\">Report Overview</span>\n\t\t\t</ng-template>\n\n\t\t\t<report-overview [report]=\"compareReport\"></report-overview>\n\t\t</cdk-step>\n\n\t\t<!-- Question Group Steps-->\n\t\t<cdk-step *ngFor=\"let questionGroup of compareReport.questionGroups | reportGroups\">\n\n\t\t\t<ng-template cdkStepLabel>\n\t\t\t\t<span>{{$any(questionGroup).index}} {{questionGroup.title}}</span>\n\t\t\t</ng-template>\n\n\t\t\t<compare-question-group [questionGroup]=\"questionGroup\"\n\t\t\t\t\t\t\t\t\t[report]=\"compareReport\"></compare-question-group>\n\n\t\t</cdk-step>\n\n\t</ab-stepper>\n\n</ng-container>\n\n\n<p *ngIf=\"compareReport?.errors?.surveys_incomplete\"\n   i18n=\"alpha.surveys|View Report Incomplete Error@@reportErrorIncomplete\">Cannot view report, one of the guests has not completed their survey.</p>\n\n<p *ngIf=\"errors.invalid_survey_link\"\n   i18n=\"alpha.surveys|Invalid Report Link@@reportErrorLink\">This is an invalid report link.</p>\n", styles: [":host{display:flex;flex-flow:column nowrap}compare-chart{flex-grow:1;width:100%}#viewReportButton{margin:35px auto 0;display:block;padding:5px 35px}@media print{:host ::ng-deep button{display:none!important}}.report-button{display:flex;max-width:10rem;align-items:center;justify-content:center}\n"] }]
    }], function () { return [{ type: SurveyService }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                type: Inject,
                args: [ALPHA_SURVEYS_OPTIONS]
            }] }]; }, { reportLink: [{
            type: Input
        }], compareReport: [{
            type: Input
        }], stepper: [{
            type: ViewChild,
            args: ['stepper', { static: true }]
        }] }); })();

class StepperModule {
}
StepperModule.ɵfac = function StepperModule_Factory(t) { return new (t || StepperModule)(); };
StepperModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: StepperModule });
StepperModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        CdkStepperModule,
        ReportsMaterialModule, CdkStepperModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StepperModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    CdkStepperModule,
                    ReportsMaterialModule
                ],
                declarations: [
                    StepperComponent
                ],
                exports: [
                    StepperComponent,
                    CdkStepperModule
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(StepperModule, { declarations: [StepperComponent], imports: [CommonModule,
        CdkStepperModule,
        ReportsMaterialModule], exports: [StepperComponent,
        CdkStepperModule] }); })();

class ReportsCommonModule {
}
ReportsCommonModule.ɵfac = function ReportsCommonModule_Factory(t) { return new (t || ReportsCommonModule)(); };
ReportsCommonModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: ReportsCommonModule });
ReportsCommonModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({});
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ReportsCommonModule, [{
        type: NgModule,
        args: [{
                declarations: [
                    QuestionResponsePipe,
                    GuestOverviewGroupsPipe,
                    ReportGroupsPipe
                ],
                exports: [
                    QuestionResponsePipe,
                    GuestOverviewGroupsPipe,
                    ReportGroupsPipe
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(ReportsCommonModule, { declarations: [QuestionResponsePipe,
        GuestOverviewGroupsPipe,
        ReportGroupsPipe], exports: [QuestionResponsePipe,
        GuestOverviewGroupsPipe,
        ReportGroupsPipe] }); })();

class CompareResultsChartModule {
}
CompareResultsChartModule.ɵfac = function CompareResultsChartModule_Factory(t) { return new (t || CompareResultsChartModule)(); };
CompareResultsChartModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: CompareResultsChartModule });
CompareResultsChartModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [NgxChartsModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CompareResultsChartModule, [{
        type: NgModule,
        args: [{
                imports: [
                    NgxChartsModule
                ],
                declarations: [
                    BarScaleDirective,
                    CompareResultsChartComponent
                ],
                exports: [
                    CompareResultsChartComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CompareResultsChartModule, { declarations: [BarScaleDirective,
        CompareResultsChartComponent], imports: [NgxChartsModule], exports: [CompareResultsChartComponent] }); })();

class CompareSurveyResultsModule {
}
CompareSurveyResultsModule.ɵfac = function CompareSurveyResultsModule_Factory(t) { return new (t || CompareSurveyResultsModule)(); };
CompareSurveyResultsModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: CompareSurveyResultsModule });
CompareSurveyResultsModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
        ReportsMaterialModule,
        ReportsCommonModule,
        StepperModule,
        YesNoUncertainQuestionModule,
        CompareResultsChartModule,
        MrbButtonsModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CompareSurveyResultsModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    ReportsMaterialModule,
                    ReportsCommonModule,
                    StepperModule,
                    YesNoUncertainQuestionModule,
                    CompareResultsChartModule,
                    MrbButtonsModule
                ],
                declarations: [
                    CompareSurveyResultsComponent,
                    CompareQuestionGroupComponent,
                    GuestsOverviewComponent,
                    ReportOverviewComponent
                ],
                exports: [
                    CompareSurveyResultsComponent
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CompareSurveyResultsModule, { declarations: [CompareSurveyResultsComponent,
        CompareQuestionGroupComponent,
        GuestsOverviewComponent,
        ReportOverviewComponent], imports: [CommonModule,
        ReportsMaterialModule,
        ReportsCommonModule,
        StepperModule,
        YesNoUncertainQuestionModule,
        CompareResultsChartModule,
        MrbButtonsModule], exports: [CompareSurveyResultsComponent] }); })();

const defaultSurveyOptions = {
    showRequiredAsterisks: false,
    showOptionalLabel: false,
    theme: ''
};
class SurveyReportsModule {
}
SurveyReportsModule.ɵfac = function SurveyReportsModule_Factory(t) { return new (t || SurveyReportsModule)(); };
SurveyReportsModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: SurveyReportsModule });
SurveyReportsModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [
        {
            provide: ALPHA_SURVEYS_OPTIONS, useValue: defaultSurveyOptions
        }
    ], imports: [SurveysModule.forRoot(),
        YesNoUncertainQuestionModule, CompareSurveyResultsModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurveyReportsModule, [{
        type: NgModule,
        args: [{
                imports: [
                    SurveysModule.forRoot(),
                    YesNoUncertainQuestionModule
                ],
                exports: [
                    CompareSurveyResultsModule
                ],
                providers: [
                    {
                        provide: ALPHA_SURVEYS_OPTIONS, useValue: defaultSurveyOptions
                    }
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SurveyReportsModule, { imports: [SurveysModule, YesNoUncertainQuestionModule], exports: [CompareSurveyResultsModule] }); })();

/**
 * Generated bundle index. Do not edit.
 */

export { ALPHA_SURVEYS_OPTIONS, CompareSurveyResultsComponent, CompareSurveyResultsModule, CompareUserSurveyQuestionState, CompareUserSurveyReport, ConditionalQuestionTypes, ConditionalResponseComponent, NumerateType, QUESTION, Question, QuestionChangeEvent, QuestionGroup, QuestionType, QuestionYNUComponent, RecommendationItem, SURVEY_EDITOR_CONFIG, Survey, SurveyComponent, SurveyEditorComponent, SurveyEditorConfig, SurveyEditorModule, SurveyGroupComponent, SurveyGroupsPipe, SurveyIntent, SurveyIntentChangeEvent, SurveyItem, SurveyItemComponent, SurveyItemType, SurveyModule, SurveyPresentation, SurveyPresentationChangeEvent, SurveyQuestionsPipe, SurveyReportsModule, SurveyService, SurveysModule, UserSurvey, UserSurveyError, WP_SITE_URL, YNULabelSets, YNULabelValues, YesNoUncertainLabelDirective, YesNoUncertainLabelPipe, YesNoUncertainQuestionModule, cloneConfig, editSurveyFactory, editorConfigFactory, surveyProviderFactory, transformYNULabel };
